import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, Injectable, Input, ContentChildren, ViewChild, forwardRef, Output, HostBinding, Self, HostListener, EventEmitter, NgModule, Pipe, Component, ChangeDetectionStrategy, ContentChild, ViewChildren, Optional, SkipSelf, ViewContainerRef, ChangeDetectorRef, ViewEncapsulation, ElementRef, Injector, TemplateRef } from '@angular/core';
import { A11yModule, FocusKeyManager } from '@angular/cdk/a11y';
import { Subject, fromEvent, merge, animationFrameScheduler, BehaviorSubject, timer, forkJoin, combineLatest, of } from 'rxjs';
import { tap, filter, map, takeUntil, debounceTime, take, distinctUntilChanged, skip, switchMap, windowToggle, mergeAll, pairwise } from 'rxjs/operators';
import * as i1 from '@cds/angular/shared';
import { enableScroll, getStyles, removeStyles, disableScroll, calculateFixedElementPosition, setStyles, QuadrantPositionStrategy, CdsHighlightableComponent, ResizeObserverSubject, pluralize, createComponent, CdsTraversableComponent, CdsSortDirection, MutationObserverSubject, getOptionsFromColumn, flatten, getDateParts, combineDateparts, getDateObject, getMonths, getWeekDays, getYears, isNavigationKey, calculateCalendarDates, DirectionalPositionStrategy, appendComponentToBody, removeComponentFromBody, getLocaleWeekStart, dateComparatorFn, calculateCalendarWeekdays } from '@cds/angular/shared';
export { CdsDataFilter, CdsDataPage, CdsDataSort, CdsGroupedListItem, CdsInputValidationOptions, CdsListItem, CdsSortDirection, GridThresholdWarning, pluralize as pluralizeTransform } from '@cds/angular/shared';
import * as i1$3 from '@angular/router';
import { RouterModule, NavigationEnd, RouterLink } from '@angular/router';
import * as i1$2 from '@angular/forms';
import { UntypedFormControl, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators, ControlContainer, FormGroupDirective } from '@angular/forms';
import { isSameDay, subMonths, addMonths, startOfMonth, setMonth, setYear, getDaysInMonth, setDate, getMonth, isValid, getDate, getYear, startOfDay, differenceInMinutes, startOfWeek, addDays, format, differenceInMonths, isAfter, isBefore, getHours, differenceInWeeks, differenceInDays } from 'date-fns';

/* eslint-disable no-shadow */
var AlertType;
(function (AlertType) {
    AlertType["Primary"] = "primary";
    AlertType["Secondary"] = "secondary";
})(AlertType || (AlertType = {}));
var AlertDisplay;
(function (AlertDisplay) {
    AlertDisplay["Inline"] = "inline";
    AlertDisplay["Toast"] = "toast";
})(AlertDisplay || (AlertDisplay = {}));
var AlertLevel;
(function (AlertLevel) {
    AlertLevel["Info"] = "info";
    AlertLevel["Warning"] = "warning";
    AlertLevel["Success"] = "success";
    AlertLevel["Error"] = "error";
})(AlertLevel || (AlertLevel = {}));

class CdsInputPrefixDirective {
}
CdsInputPrefixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsInputPrefixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsInputPrefixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsInputPrefixDirective, selector: "[cdsInputPrefix]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsInputPrefixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsInputPrefix]',
                }]
        }] });

class CdsInputSuffixDirective {
}
CdsInputSuffixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsInputSuffixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsInputSuffixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsInputSuffixDirective, selector: "[cdsInputSuffix]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsInputSuffixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsInputSuffix]',
                }]
        }] });

/**
 * A class directly or indirectly inherited by all input components.
 */
class CdsControlValueAccessorComponent {
    constructor() {
        /**
         * An event containing the current state of this control.
         */
        this.change = new Subject();
        /**
         * @internal
         */
        this.onChange = (value) => { };
        /**
         * @internal
         */
        this.onTouch = () => { };
        /**
         * @internal
         */
        this.registerOnTouched = (fn) => {
            this.onTouch = fn;
        };
        /**
         * @internal
         */
        this.registerOnChange = (fn) => {
            this.onChange = (value) => {
                fn(value);
                this.change.next(value);
            };
        };
        /**
         * @internal
         */
        this.setDisabledState = (disabled) => {
            this.disabled = disabled;
        };
        /**
         * @internal
         */
        this.getControlValue = () => this.control.value;
        this.destroy = () => {
            this.change.complete();
        };
    }
}
CdsControlValueAccessorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsControlValueAccessorComponent, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsControlValueAccessorComponent.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsControlValueAccessorComponent });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsControlValueAccessorComponent, decorators: [{
            type: Injectable
        }] });

/**
 * Abstract class for input components that accept keyboard characters.
 */
// eslint-disable-next-line @angular-eslint/directive-selector
// eslint-disable-next-line @angular-eslint/directive-class-suffix
class CdsAbstractTextInputComponent {
    constructor(controlValueAccessor) {
        this.controlValueAccessor = controlValueAccessor;
        /**
         * @internal
         */
        this.registerOnChange = this.controlValueAccessor.registerOnChange;
        /**
         * @internal
         */
        this.registerOnTouched = this.controlValueAccessor.registerOnTouched;
        /**
         * @internal
         */
        this.setDisabledState = this.controlValueAccessor.setDisabledState;
    }
    /**
     * @internal
     */
    get disabled() {
        return this.controlValueAccessor.disabled;
    }
}
CdsAbstractTextInputComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAbstractTextInputComponent, deps: [{ token: CdsControlValueAccessorComponent }], target: i0.ɵɵFactoryTarget.Directive });
CdsAbstractTextInputComponent.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsAbstractTextInputComponent, selector: "foo", inputs: { name: "name", autocomplete: "autocomplete", placeholder: "placeholder" }, queries: [{ propertyName: "prefixes", predicate: CdsInputPrefixDirective, descendants: true }, { propertyName: "suffixes", predicate: CdsInputSuffixDirective, descendants: true }], viewQueries: [{ propertyName: "inputElement", first: true, predicate: ["input"], descendants: true, static: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAbstractTextInputComponent, decorators: [{
            type: Directive,
            args: [{ selector: 'foo' }]
        }], ctorParameters: function () { return [{ type: CdsControlValueAccessorComponent }]; }, propDecorators: { name: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], prefixes: [{
                type: ContentChildren,
                args: [CdsInputPrefixDirective, { descendants: true }]
            }], suffixes: [{
                type: ContentChildren,
                args: [CdsInputSuffixDirective, { descendants: true }]
            }], inputElement: [{
                type: ViewChild,
                args: ['input', { static: true }]
            }] } });

class CdsAccordionPanelDirective {
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.renderer.addClass(this.elementRef.nativeElement, 'cds-accordion-content');
    }
}
CdsAccordionPanelDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAccordionPanelDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
CdsAccordionPanelDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsAccordionPanelDirective, selector: "[cdsAccordionPanel]", exportAs: ["accordionPanel"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAccordionPanelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsAccordionPanel]',
                    exportAs: 'accordionPanel',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; } });

class ContentToggle {
}

/**
 * Abstract class for a component/presenter that toggles the display of its elements.
 */
class CdsExpandable {
    constructor() {
        this.$change = new Subject();
        this.change = this.$change.asObservable();
    }
    get expanded() {
        return !!this._expanded;
    }
    expand(...args) {
        this._expanded = true;
        this.$change.next(this._expanded);
    }
    collapse(...args) {
        this._expanded = false;
        this.$change.next(this._expanded);
    }
    destroy(...args) {
        this.$change.complete();
    }
}

/**
 * Manages the visibility of the contents of an expandable accordion.
 */
class AccordionPresenter extends CdsExpandable {
}
AccordionPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AccordionPresenter, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
AccordionPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AccordionPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AccordionPresenter, decorators: [{
            type: Injectable
        }] });

class CdsAccordionDirective {
    constructor(presenter) {
        this.presenter = presenter;
        this.change = this.presenter.change;
    }
    set expanded(expanded) {
        if (expanded) {
            this.presenter.expand();
        }
        else {
            this.presenter.collapse();
        }
    }
    get expanded() {
        return this.presenter.expanded;
    }
    /**
     * Hides the accordion.
     */
    collapse() {
        this.presenter.collapse();
    }
    /**
     * Shows the accordion.
     */
    expand() {
        this.presenter.expand();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.presenter.destroy();
    }
    offsetHeight(element) {
        return element.offsetHeight;
    }
    ;
}
CdsAccordionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAccordionDirective, deps: [{ token: AccordionPresenter }], target: i0.ɵɵFactoryTarget.Directive });
CdsAccordionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsAccordionDirective, selector: "[cdsAccordion]", inputs: { anchor: "anchor", panel: "panel", expanded: "expanded" }, outputs: { change: "change" }, providers: [
        AccordionPresenter,
        { provide: ContentToggle, useExisting: forwardRef(() => CdsAccordionDirective) }
    ], exportAs: ["cdsAccordion"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAccordionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsAccordion]',
                    exportAs: 'cdsAccordion',
                    providers: [
                        AccordionPresenter,
                        { provide: ContentToggle, useExisting: forwardRef(() => CdsAccordionDirective) }
                    ],
                }]
        }], ctorParameters: function () { return [{ type: AccordionPresenter }]; }, propDecorators: { anchor: [{
                type: Input
            }], panel: [{
                type: Input
            }], expanded: [{
                type: Input
            }], change: [{
                type: Output
            }] } });

/**
 * Applies attributes to atoms conditionally based on an expression.
 */
class CdsAriaAttributeDirective {
    get hasPopup() {
        return this.attributes ? this.attributes['aria-haspopup'] : null;
    }
    get label() {
        return this.attributes ? this.attributes['aria-label'] : null;
    }
    get expanded() {
        return this.attributes ? this.attributes['aria-expanded'] : null;
    }
    get title() {
        return this.attributes ? this.attributes['title'] : null;
    }
}
CdsAriaAttributeDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAriaAttributeDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsAriaAttributeDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsAriaAttributeDirective, selector: "[cdsAriaAttr]", inputs: { attributes: ["cdsAriaAttr", "attributes"] }, host: { properties: { "attr.aria-haspopup": "this.hasPopup", "attr.aria-label": "this.label", "attr.aria-expanded": "this.expanded", "attr.title": "this.title" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAriaAttributeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsAriaAttr]',
                }]
        }], propDecorators: { hasPopup: [{
                type: HostBinding,
                args: ['attr.aria-haspopup']
            }], label: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }], expanded: [{
                type: HostBinding,
                args: ['attr.aria-expanded']
            }], title: [{
                type: HostBinding,
                args: ['attr.title']
            }], attributes: [{
                type: Input,
                args: ['cdsAriaAttr']
            }] } });

class CdsDropdownPanelDirective {
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.renderer.addClass(this.elementRef.nativeElement, 'cds-dropdown-content');
    }
}
CdsDropdownPanelDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownPanelDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
CdsDropdownPanelDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsDropdownPanelDirective, selector: "[cdsDropdownPanel]", exportAs: ["dropdownPanel"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownPanelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsDropdownPanel]',
                    exportAs: 'dropdownPanel',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; } });

function stopPropagation(obs) {
    return obs.pipe(tap((event) => event.stopPropagation()));
}
function preventDefault(obs) {
    return obs.pipe(tap((event) => event.preventDefault()));
}
function filterKeyboardEvent(value) {
    return (observable) => observable.pipe(filter(event => event.key === value));
}

class CdsDropdownToggleButtonDirective {
    constructor(toggle) {
        this.toggle = toggle;
        this.$cancelSubscriptions = new Subject();
        this.toggleChange = this.toggle.change
            .pipe(map(expanded => ({ expanded, source: this.source })));
    }
    ngOnInit() {
        this.createListeners(this.toggle.anchor);
    }
    createListeners(element) {
        // prevent defaults
        const keyupListener = fromEvent(element, 'keyup')
            .pipe(tap(event => event.preventDefault()));
        const mouseupListener = fromEvent(element, 'mouseup')
            .pipe(tap(event => event.preventDefault()));
        // expand and collapse
        this.createExpansionEvents(element);
        merge(keyupListener, mouseupListener)
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe();
    }
    createExpansionEvents(element) {
        const arrowdownListener = fromEvent(element, 'keydown')
            .pipe(filterKeyboardEvent('ArrowDown'), preventDefault, tap(() => {
            this.source = 'keydown';
            this.toggle.expand();
        }));
        const spaceListener = fromEvent(element, 'keydown')
            .pipe(filterKeyboardEvent(' '), preventDefault, stopPropagation, tap(() => {
            this.source = 'click';
            this.toggleExpansion();
        }));
        const mousedownListener = fromEvent(element, 'mousedown')
            .pipe(preventDefault, tap(() => {
            this.source = 'click';
            this.toggleExpansion();
        }));
        merge(spaceListener, arrowdownListener, mousedownListener)
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe();
    }
    toggleExpansion() {
        if (this.toggle.expanded) {
            this.toggle.collapse();
        }
        else {
            this.toggle.expand();
        }
    }
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsDropdownToggleButtonDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownToggleButtonDirective, deps: [{ token: ContentToggle, self: true }], target: i0.ɵɵFactoryTarget.Directive });
CdsDropdownToggleButtonDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsDropdownToggleButtonDirective, selector: "[buttonTrigger]", outputs: { toggleChange: "toggleChange" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownToggleButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: `[buttonTrigger]`
                }]
        }], ctorParameters: function () { return [{ type: ContentToggle, decorators: [{
                    type: Self
                }] }]; }, propDecorators: { toggleChange: [{
                type: Output
            }] } });

/**
 * Emits an event when focus is removed from an element by clicking elsewhere on the page.
 */
class ClickAwayPresenter {
    constructor() {
        this.$clickAway = new Subject();
        this.clickAway = this.$clickAway.asObservable();
        this.$cancelSubscriptions = new Subject();
        this.onClick = (event, ...elements) => {
            const clickTarget = event.target;
            if (!elements.some((element) => element.contains(clickTarget))) {
                this.$clickAway.next(event);
            }
        };
    }
    watch(...elements) {
        fromEvent(document, 'click')
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe((event) => this.onClick(event, ...elements));
    }
    unwatch() {
        this.$cancelSubscriptions.next();
    }
    destroy() {
        this.unwatch();
        this.$cancelSubscriptions.complete();
    }
}

/**
 * Emits an event when a dropdown toggle is scrolled offscreen.
 */
class ScrollObserverPresenter {
    constructor() {
        this.$scrollAway = new Subject();
        this.scrollAway = this.$scrollAway.asObservable();
        this.$cancelSubscriptions = new Subject();
    }
    watch(element, callback) {
        const scrollEvents = [
            fromEvent(window, 'content-container-scroll')
                .pipe(map(event => event.target), tap(scrollContainer => {
                callback(scrollContainer);
                this.onScrollContentContainer(scrollContainer, element);
            })),
            fromEvent(window, 'scroll')
                .pipe(map(event => event.target.documentElement), tap(scrollContainer => {
                callback(scrollContainer);
                this.onScrollDocument(element);
            }))
        ];
        merge(...scrollEvents)
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe();
    }
    onScrollContentContainer(scrollContainer, element) {
        if (scrollContainer.contains(element)) {
            const anchorCoordinates = element.getBoundingClientRect();
            const enclosingContainerCoordinates = scrollContainer.getBoundingClientRect();
            if (anchorCoordinates.bottom < enclosingContainerCoordinates.top ||
                enclosingContainerCoordinates.bottom < anchorCoordinates.top) {
                this.$scrollAway.next();
            }
        }
    }
    onScrollDocument(element) {
        const anchorCoordinates = element.getBoundingClientRect();
        if (anchorCoordinates.top > document.documentElement.clientHeight || anchorCoordinates.bottom < 0) {
            this.$scrollAway.next();
        }
    }
    unwatch() {
        this.$cancelSubscriptions.next();
    }
    destroy() {
        this.unwatch();
        this.$cancelSubscriptions.complete();
    }
}

/**
 * Emits an event when focus is removed from an element by tabbing away.
 */
class TabAwayPresenter {
    constructor() {
        this.$tabAway = new Subject();
        this.tabAway = this.$tabAway.asObservable();
        this.$cancelSubscriptions = new Subject();
        this.onKeyDown = (event, ...elements) => {
            setTimeout(() => {
                if (!elements.some((element) => element.contains(document.activeElement))) {
                    this.$tabAway.next(event);
                }
            });
            // eslint-disable-next-line @typescript-eslint/semi, @typescript-eslint/member-delimiter-style
        };
    }
    watch(...elements) {
        const events = elements.map((element) => fromEvent(element, 'keydown').pipe(filter((event) => event.key === 'Tab')));
        merge(...events)
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe((event) => this.onKeyDown(event, ...elements));
    }
    unwatch() {
        this.$cancelSubscriptions.next();
    }
    destroy() {
        this.unwatch();
        this.$cancelSubscriptions.complete();
    }
}

/* eslint-disable max-classes-per-file */
/**
 * @internal
 */
class CdsDropdownPresenter extends CdsExpandable {
    constructor() {
        super(...arguments);
        this.$cancelSubscriptions = new Subject();
        this.clickAwayPresenter = new ClickAwayPresenter();
        this.tabAwayPresenter = new TabAwayPresenter();
        this.scrollAwayPresenter = new ScrollObserverPresenter();
        this.removeFocus = merge(this.clickAwayPresenter.clickAway, this.tabAwayPresenter.tabAway, this.scrollAwayPresenter.scrollAway);
        /**
         * Hides the the panel dropdown.
         */
        this.collapse = (panel) => {
            if (this.expanded) {
                enableScroll();
                super.collapse();
                this.unwatch(panel);
            }
        };
        /**
         * Shows the panel dropdown.
         */
        this.expand = (strategy, panel, toggle) => {
            if (!this.expanded) {
                super.expand();
                this.originalStyles = getStyles(panel);
                this.recalculatePosition(strategy, toggle, panel);
                this.watch(strategy, panel, toggle);
            }
        };
        this.recalculatePosition = (strategy, anchor, panel) => {
            removeStyles(panel, this.originalStyles);
            if (strategy.config.preventBackgroundScroll) {
                disableScroll();
            }
            const { styles, options } = calculateFixedElementPosition(strategy, anchor, panel);
            const rect = anchor instanceof Element
                ? anchor.getBoundingClientRect()
                : new DOMRect(anchor.x, anchor.y);
            setStyles(panel, {
                ...styles,
                minWidth: `${rect.width}px`
            }, options);
        };
    }
    watch(strategy, panel, toggle) {
        fromEvent(window, 'resize')
            .pipe(takeUntil(this.$cancelSubscriptions), debounceTime(0, animationFrameScheduler), filter(() => this.expanded))
            .subscribe(() => this.recalculatePosition(strategy, toggle, panel));
        const targets = [panel];
        if (toggle instanceof Element) {
            this.scrollAwayPresenter.watch(toggle, () => this.recalculatePosition(strategy, toggle, panel));
            targets.push(toggle);
        }
        setTimeout(() => {
            this.clickAwayPresenter.watch(...targets);
            this.tabAwayPresenter.watch(...targets);
        });
    }
    unwatch(panel) {
        removeStyles(panel, this.originalStyles);
        this.clickAwayPresenter.unwatch();
        this.tabAwayPresenter.unwatch();
        this.scrollAwayPresenter.unwatch();
        this.$cancelSubscriptions.next();
    }
    destroy(panel) {
        this.unwatch(panel);
        this.clickAwayPresenter.destroy();
        this.tabAwayPresenter.destroy();
        this.scrollAwayPresenter.destroy();
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
        super.destroy();
    }
}

/**
 * Polyfill for ChildNode.Remove() (https://developer.mozilla.org/en-US/docs/Web/API/ChildNode/remove)
 * from:https://github.com/jserz/js_piece/blob/master/DOM/ChildNode/remove()/remove().md
 */
const ChildNodeRemove = (node) => {
    if (node.hasOwnProperty('remove')) {
        return node.remove;
    }
    return node.parentNode.removeChild(node);
};
/**
 * Polyfill for String.PadStart()
 * from:https://github.com/behnammodi/polyfill/blob/master/string.polyfill.js
 */
const StringPadStart = (originalString, targetLength, fillString) => {
    if (!String.prototype.padStart) {
        // eslint-disable-next-line no-bitwise
        targetLength = targetLength >> 0; // floor if number or convert non-number to 0;
        fillString = String(typeof fillString !== 'undefined' ? fillString : ' ');
        if (originalString.length > targetLength) {
            return String(originalString);
        }
        else {
            targetLength = targetLength - originalString.length;
            if (targetLength > fillString.length) {
                fillString += fillString.repeat(targetLength / fillString.length);
            }
            return fillString.slice(0, targetLength) + String(originalString);
        }
    }
    else {
        return originalString.padStart(targetLength, fillString);
    }
};
const polyfillCustomEvent = () => {
    if (typeof window['CustomEvent'] === 'function') {
        return;
    }
    const CustomEvent = (event, params) => {
        params = params || { bubbles: false, cancelable: false, detail: undefined };
        const evt = document.createEvent('CustomEvent');
        evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
        return evt;
    };
    CustomEvent.prototype = window['Event'].prototype;
    window['CustomEvent'] = CustomEvent;
};

class ScrollableAncestorService {
    constructor() {
        this.$cancelSubscriptions = new Subject();
        this.panels = new Map();
        this.contentContainers = new Map();
        polyfillCustomEvent();
    }
    registerDropdown(panel) {
        // watch for window resize and reattach all ancestor watchers
        const nearestScrollingAncestor = getNearestScrollingAncestor(panel);
        if (this.contentContainers.has(nearestScrollingAncestor)) {
            this.contentContainers.get(nearestScrollingAncestor).unsubscribe();
        }
        this.setupScrollingAncestor(nearestScrollingAncestor);
        this.panels.set(panel, nearestScrollingAncestor);
    }
    unregister(panel) {
        const ancestor = this.panels.get(panel);
        this.panels.delete(panel);
        const panels = Array.from(this.panels.entries()).filter(([_, savedAncestor]) => ancestor === savedAncestor);
        if (panels.length === 0) {
            this.contentContainers.get(ancestor)?.unsubscribe();
            this.contentContainers.delete(ancestor);
        }
    }
    setupScrollingAncestor(element) {
        const subscription = fromEvent(element, 'scroll')
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(onScroll);
        this.contentContainers.set(element, subscription);
    }
}
ScrollableAncestorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ScrollableAncestorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ScrollableAncestorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ScrollableAncestorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ScrollableAncestorService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });
function onScroll(event) {
    const contentContainerScrollEvent = document.createEvent('CustomEvent');
    contentContainerScrollEvent.initCustomEvent('content-container-scroll', true, true, { target: event.target });
    event.target.dispatchEvent(contentContainerScrollEvent);
}
function getNearestScrollingAncestor(element) {
    const parentNode = element.parentNode;
    if (parentNode === document.documentElement) {
        return parentNode;
    }
    return hasOverflowProperty(parentNode) || hasContentContainerClass(parentNode)
        ? parentNode
        : getNearestScrollingAncestor(parentNode);
}
function hasOverflowProperty(element) {
    const computedStyle = window.getComputedStyle(element);
    return element.scrollHeight > element.clientHeight
        && computedStyle.overflowY === 'auto' || computedStyle.overflowY === 'scroll';
}
function hasContentContainerClass(element) {
    return ['content-container', 'cds-page-layout-content', 'cds-modal-content']
        .some(klass => element.classList.contains(klass));
}

class CdsDropdownDirective {
    /**
     * @internal
     */
    constructor(contentContainerService) {
        this.contentContainerService = contentContainerService;
        this.$cancelSubscriptions = new Subject();
        this.presenter = new CdsDropdownPresenter();
        this.removeFocus = this.presenter.removeFocus;
        this.change = this.presenter.change;
    }
    get expanded() {
        return this.presenter && this.presenter.expanded;
    }
    /**
     * Hides the the panel dropdown.
     */
    collapse() {
        this.presenter.collapse(this.panel);
        this.contentContainerService.unregister(this.panel);
    }
    /**
     * Shows the panel dropdown.
     */
    expand(anchor, panel, strategy) {
        if (this.presenter.expanded) {
            return;
        }
        panel = panel || this.panel;
        anchor = anchor || this.anchor;
        const { scrollableX = true, scrollableY = true } = this.options || {};
        const options = { ...this.options, scrollableX, scrollableY };
        strategy = strategy || this.strategy || new QuadrantPositionStrategy(options);
        if (anchor instanceof Element) {
            fromEvent(anchor, 'keydown')
                .pipe(filterKeyboardEvent('Escape'), tap(event => {
                if (this.expanded) {
                    event.stopPropagation();
                }
            }), takeUntil(this.$cancelSubscriptions)).subscribe(() => this.collapse());
        }
        fromEvent(panel, 'keydown')
            .pipe(filterKeyboardEvent('Escape'), stopPropagation, tap(() => panel && this.wasFocusedFn && this.wasFocusedFn()), takeUntil(this.$cancelSubscriptions)).subscribe(() => this.collapse());
        this.presenter.expand(strategy, panel, anchor);
        this.contentContainerService.registerDropdown(panel);
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.presenter.destroy(this.panel);
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsDropdownDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownDirective, deps: [{ token: ScrollableAncestorService }], target: i0.ɵɵFactoryTarget.Directive });
CdsDropdownDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsDropdownDirective, selector: "[cdsDropdown]", inputs: { anchor: "anchor", panel: "panel", options: "options", strategy: "strategy", wasFocusedFn: "wasFocusedFn" }, outputs: { removeFocus: "removeFocus", change: "change" }, providers: [{ provide: ContentToggle, useExisting: forwardRef(() => CdsDropdownDirective) }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsDropdown]',
                    providers: [{ provide: ContentToggle, useExisting: forwardRef(() => CdsDropdownDirective) }]
                }]
        }], ctorParameters: function () { return [{ type: ScrollableAncestorService }]; }, propDecorators: { anchor: [{
                type: Input
            }], panel: [{
                type: Input
            }], options: [{
                type: Input
            }], strategy: [{
                type: Input
            }], removeFocus: [{
                type: Output
            }], change: [{
                type: Output
            }], wasFocusedFn: [{
                type: Input
            }] } });

class CdsElementRefDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    get nativeElement() {
        return this.elementRef.nativeElement;
    }
}
CdsElementRefDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsElementRefDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsElementRefDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsElementRefDirective, selector: "[cdsElementRef]", exportAs: ["cdsElementRef"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsElementRefDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsElementRef]',
                    exportAs: 'cdsElementRef'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

/**
 * Allows for focusing of an element within the context of a list or group.
 */
class CdsFocusableItemDirective {
    constructor(element, component) {
        this.element = element;
        this.component = component;
    }
}
CdsFocusableItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFocusableItemDirective, deps: [{ token: i0.ElementRef }, { token: i1.CdsTraversableComponent }], target: i0.ɵɵFactoryTarget.Directive });
CdsFocusableItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsFocusableItemDirective, selector: "[cdsFocusableItem]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFocusableItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsFocusableItem]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.CdsTraversableComponent }]; } });

/* eslint-disable @typescript-eslint/member-ordering */
/**
 * Sets and removes 'highlighted' class on a focusable element that is focused via keyboard.
 */
class CdsKeyboardHighlightableDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        /**
         * @internal
         */
        this.highlightable = new CdsHighlightableComponent();
    }
    /**
     * @internal
     */
    get element() {
        return this.elementRef.nativeElement;
    }
    // A disabled element cannot be highlighted as well, so intercept changes to the element's disabled attribute
    // and remove the highlighted class.
    // @TODO: write up a blurb about this in coding guidelines
    // @HACK: would rather use the native [disabled] property, but that property isn't set on the element if the below input
    // has the same name, instead it is assigned to the below input.
    set isDisabled(value) {
        if (value) {
            this.highlightable.removeHighlightedClass(this.elementRef.nativeElement);
        }
    }
    addHighlightedClass() {
        this.highlightable.addHighlightedClass(this.elementRef.nativeElement);
    }
    removeHighlightedClass() {
        this.highlightable.removeHighlightedClass(this.elementRef.nativeElement);
    }
}
CdsKeyboardHighlightableDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsKeyboardHighlightableDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsKeyboardHighlightableDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsKeyboardHighlightableDirective, selector: "[cdsHighlightable]", inputs: { isDisabled: "isDisabled" }, host: { listeners: { "keyup.tab": "addHighlightedClass()", "keyup.shift.tab": "addHighlightedClass()", "focusout": "removeHighlightedClass()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsKeyboardHighlightableDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsHighlightable]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { isDisabled: [{
                type: Input
            }], addHighlightedClass: [{
                type: HostListener,
                args: ['keyup.tab']
            }, {
                type: HostListener,
                args: ['keyup.shift.tab']
            }], removeHighlightedClass: [{
                type: HostListener,
                args: ['focusout']
            }] } });

/**
 * Triggers the transfer of items from one array to another when a specified HTML element overflows
 * its browser width.
 */
class CdsOverflowObserverPresenter {
    constructor() {
        this.containerItems = [];
        this.overflowItems = [];
        this.$buffer = new Subject();
        this.$change = new Subject();
        this.change = this.$change.asObservable();
        this.onResize = () => {
            if (!this.overflowContainer) {
                return;
            }
            const clientWidth = this.overflowContainer.clientWidth;
            const scrollWidth = this.overflowContainer.scrollWidth;
            /**
             * If client width is decreasing (overflow), scroll width starts out greater than client width
             * but will eventually become lower than client width as items are taken out, triggering reverse overflow.
             * If client width is increasing (reverse overflow), scroll width starts out less than client width but will eventually become greater
             * as items are put in, triggering overflow again.
             * If client width is stable, scroll width should eventually decrease to meet client width as items are taken out on overflow.
             */
            if (clientWidth < scrollWidth) {
                this.onOverflow();
            }
            else {
                this.onReverseOverflow();
            }
        };
        this.onOverflow = () => {
            // If the current scroll width is the same as the current client width
            // Find all non-sticky items, then match them with items that are currently in the row
            const nonStickyItems = this.containerItems.filter((rowItem) => this.items.filter((item) => !item.sticky).includes(rowItem));
            if (nonStickyItems.length <= 0) {
                return;
            }
            this.previousStartingWidth = this.overflowContainer.scrollWidth;
            const overflowItem = nonStickyItems.pop();
            this.containerItems.splice(this.containerItems.indexOf(overflowItem), 1);
            this.overflowItems.splice(0, 0, overflowItem);
            this.$change.next({
                containerItems: this.containerItems,
                overflowItems: this.overflowItems,
            });
            // eslint-disable-next-line @typescript-eslint/semi,@typescript-eslint/member-delimiter-style
        };
        this.onReverseOverflow = () => {
            // the container has been shrunk instead of expanded
            if (!this.previousStartingWidth) {
                return;
            }
            // if previously recorded client width is the same as current client width and the current scroll width is
            // less than the previously recorded client width, equilibrium has been reached.
            // Therefore proceed only if client width is the same as, or greater than scroll width (i.e. container is expanding)
            // and client width is greater than previously recorded client width.
            if (this.previousStartingWidth >= this.overflowContainer.scrollWidth) {
                return;
            }
            if (this.overflowItems.length <= 0) {
                return;
            }
            this.previousStartingWidth = this.overflowContainer.scrollWidth;
            const firstItem = this.overflowItems.shift();
            const insertionIndex = this.items.indexOf(firstItem);
            if (this.items.length === insertionIndex + 1) {
                // if the last child was originally removed
                this.containerItems.push(firstItem);
            }
            else {
                this.containerItems.splice(insertionIndex, 0, firstItem);
            }
            this.$change.next({
                containerItems: this.containerItems,
                overflowItems: this.overflowItems,
            });
            // eslint-disable-next-line @typescript-eslint/semi, @typescript-eslint/member-delimiter-style
        };
        this.$buffer.subscribe(() => this.onResize());
    }
    watch(overflowContainer, items) {
        this.unwatch();
        this.overflowContainer = overflowContainer;
        this.init(items);
    }
    unwatch() {
        if (this.resizeObserver) {
            this.resizeObserver.unobserve(this.overflowContainer);
        }
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
    }
    init(items) {
        this.items = items;
        this.containerItems = [...this.items];
        this.overflowItems = [];
        this.resizeObserver = new ResizeObserver(() => this.$buffer.next());
        this.resizeObserver.observe(this.overflowContainer);
        this.mutationObserver = new MutationObserver(() => this.$buffer.next());
        this.mutationObserver.observe(this.overflowContainer, { childList: true });
        this.$change.next({
            containerItems: this.containerItems,
            overflowItems: this.overflowItems,
        });
    }
    destroy() {
        this.unwatch();
        this.$change.complete();
        this.$buffer.complete();
    }
}
CdsOverflowObserverPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsOverflowObserverPresenter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsOverflowObserverPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsOverflowObserverPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsOverflowObserverPresenter, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class CdsOverflowObserverDirective {
    constructor(presenter, element, renderer) {
        this.presenter = presenter;
        this.element = element;
        this.renderer = renderer;
        this.change = this.presenter.change;
        this.renderer.addClass(this.element.nativeElement, 'cds-overflow-container');
    }
    ngAfterViewInit() {
        this.reset();
    }
    reset() {
        this.presenter.unwatch();
        this.presenter.watch(this.element.nativeElement, this.items);
    }
    ngOnDestroy() {
        this.presenter.destroy();
    }
}
CdsOverflowObserverDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsOverflowObserverDirective, deps: [{ token: CdsOverflowObserverPresenter }, { token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
CdsOverflowObserverDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsOverflowObserverDirective, selector: "[cdsOverflowObserver]", inputs: { items: "items" }, outputs: { change: "change" }, providers: [CdsOverflowObserverPresenter], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsOverflowObserverDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/component-selector
                    selector: '[cdsOverflowObserver]',
                    providers: [CdsOverflowObserverPresenter],
                }]
        }], ctorParameters: function () { return [{ type: CdsOverflowObserverPresenter }, { type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { items: [{
                type: Input
            }], change: [{
                type: Output
            }] } });

class CdsResizeService {
    constructor() {
        this.$resize = new Subject();
        this.$resizeEnd = new Subject();
        this.$resizeStart = new Subject();
        this.resize = this.$resize.asObservable();
        this.resizeEnd = this.$resizeEnd.asObservable();
        this.resizeStart = this.$resizeStart.asObservable();
    }
    onResize(obj) {
        this.$resize.next(obj);
    }
    onResizeStart(source) {
        this.$resizeStart.next(source);
    }
    onResizeEnd() {
        this.$resizeEnd.next();
    }
    destroy() {
        this.$resize.complete();
        this.$resizeStart.complete();
        this.$resizeEnd.complete();
    }
}
CdsResizeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsResizeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizeService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizeService, decorators: [{
            type: Injectable
        }] });

/**
 * Directive for element that triggers resizing of a resizable parent element.
 */
class CdsResizeHandlerDirective {
    constructor(element, renderer, resizeService) {
        this.element = element;
        this.renderer = renderer;
        this.resizeService = resizeService;
        this.$cancelSubscription = new Subject();
    }
    onResizeStart(event) {
        event.preventDefault();
        this._resizing = true;
        this.renderer.addClass(this.element.nativeElement, '-dragging');
        this.resizeService.onResizeStart(this);
        fromEvent(window.document, 'mousemove')
            .pipe(takeUntil(this.$cancelSubscription))
            .subscribe((mousemoveEvent) => this.onResize(mousemoveEvent));
        fromEvent(window.document, 'mouseup')
            .pipe(takeUntil(this.$cancelSubscription))
            .subscribe((mouseupEvent) => this.onResizeEnd(mouseupEvent));
    }
    onResize(event) {
        event.preventDefault();
        if (this._resizing) {
            const width = this.calculateWidth(this.target.nativeElement.getBoundingClientRect().left, event.clientX);
            if (width) {
                this.resizeService.onResize({
                    element: this.target.nativeElement,
                    width,
                });
            }
        }
    }
    onResizeEnd(event) {
        event.preventDefault();
        if (this._resizing) {
            this._resizing = false;
            this.renderer.removeClass(this.element.nativeElement, '-dragging');
            this.resizeService.onResizeEnd();
        }
        this.$cancelSubscription.next();
    }
    calculateWidth(elementStart, mousePosition) {
        const minWidthString = this.minWidth || getComputedStyle(this.target.nativeElement).minWidth; // value in pixels | 'none'
        const minWidth = +(minWidthString === 'none'
            ? 0
            : minWidthString.split('px')[0]);
        return `${Math.max(minWidth, mousePosition - elementStart)}px`;
    }
}
CdsResizeHandlerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizeHandlerDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: CdsResizeService }], target: i0.ɵɵFactoryTarget.Directive });
CdsResizeHandlerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsResizeHandlerDirective, selector: "[cdsResizeHandler]", inputs: { target: "target", minWidth: "minWidth", maxWidth: "maxWidth" }, host: { listeners: { "mousedown": "onResizeStart($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizeHandlerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsResizeHandler]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: CdsResizeService }]; }, propDecorators: { target: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], onResizeStart: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }] } });

class CdsSelectableItemPresenter {
    constructor() {
        this.$cancelSubscriptions = new Subject();
        this.$select = new Subject();
        this.select = this.$select.asObservable();
    }
    watch(element) {
        const keydowns = fromEvent(element, 'keydown').pipe(filter((event) => event.key === 'Enter' || event.key === ' '), tap((event) => {
            event.preventDefault();
            event.stopPropagation();
        }));
        const clicks = fromEvent(element, 'click');
        merge(keydowns, clicks)
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => this.$select.next());
    }
    unwatch() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
        this.$select.complete();
    }
    destroy() {
        this.unwatch();
    }
}
CdsSelectableItemPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSelectableItemPresenter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsSelectableItemPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSelectableItemPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSelectableItemPresenter, decorators: [{
            type: Injectable
        }] });

/**
 * Allows for selection of items within a list or group.
 */
class CdsSelectableItemDirective {
    constructor(element, presenter) {
        this.element = element;
        this.presenter = presenter;
        this.select = this.presenter.select;
        this.presenter.watch(this.element.nativeElement);
    }
    ngOnDestroy() {
        this.presenter.destroy();
    }
}
CdsSelectableItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSelectableItemDirective, deps: [{ token: i0.ElementRef }, { token: CdsSelectableItemPresenter }], target: i0.ɵɵFactoryTarget.Directive });
CdsSelectableItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSelectableItemDirective, selector: "[cdsSelectableItem]", outputs: { select: "select" }, providers: [CdsSelectableItemPresenter], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSelectableItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsSelectableItem]',
                    providers: [CdsSelectableItemPresenter],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: CdsSelectableItemPresenter }]; }, propDecorators: { select: [{
                type: Output
            }] } });

class CdsResizeObserverDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.$cancelSubscriptions = new Subject();
        this.cdsResize = new EventEmitter();
    }
    set active(active) {
        this.unwatch();
        if (!!active) {
            setTimeout(() => this.watch());
        }
    }
    watch() {
        this.unwatch();
        ResizeObserverSubject(this.elementRef.nativeElement)
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => {
            this.cdsResize.emit();
        });
    }
    unwatch() {
        this.$cancelSubscriptions.next();
    }
    ngOnDestroy() {
        this.unwatch();
        this.$cancelSubscriptions.complete();
    }
}
CdsResizeObserverDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizeObserverDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsResizeObserverDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsResizeObserverDirective, selector: "[cdsResizeObserver]", inputs: { active: "active" }, outputs: { cdsResize: "cdsResize" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizeObserverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsResizeObserver]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { active: [{
                type: Input
            }], cdsResize: [{
                type: Output
            }] } });

class CdsDirectivesModule {
}
CdsDirectivesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDirectivesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsDirectivesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsDirectivesModule, declarations: [CdsDropdownDirective,
        CdsDropdownPanelDirective,
        CdsAccordionPanelDirective,
        CdsAccordionDirective,
        CdsFocusableItemDirective,
        CdsSelectableItemDirective,
        CdsAriaAttributeDirective,
        CdsResizeHandlerDirective,
        CdsKeyboardHighlightableDirective,
        CdsOverflowObserverDirective,
        CdsAbstractTextInputComponent,
        CdsDropdownToggleButtonDirective,
        CdsElementRefDirective,
        CdsResizeObserverDirective], imports: [A11yModule], exports: [CdsDropdownDirective,
        CdsDropdownPanelDirective,
        CdsAccordionPanelDirective,
        CdsAccordionDirective,
        CdsFocusableItemDirective,
        CdsSelectableItemDirective,
        CdsAriaAttributeDirective,
        CdsResizeHandlerDirective,
        CdsKeyboardHighlightableDirective,
        CdsOverflowObserverDirective,
        CdsDropdownToggleButtonDirective,
        CdsElementRefDirective,
        CdsResizeObserverDirective] });
CdsDirectivesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDirectivesModule, imports: [A11yModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDirectivesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsDropdownDirective,
                        CdsDropdownPanelDirective,
                        CdsAccordionPanelDirective,
                        CdsAccordionDirective,
                        CdsFocusableItemDirective,
                        CdsSelectableItemDirective,
                        CdsAriaAttributeDirective,
                        CdsResizeHandlerDirective,
                        CdsKeyboardHighlightableDirective,
                        CdsOverflowObserverDirective,
                        CdsAbstractTextInputComponent,
                        CdsDropdownToggleButtonDirective,
                        CdsElementRefDirective,
                        CdsResizeObserverDirective
                    ],
                    exports: [
                        CdsDropdownDirective,
                        CdsDropdownPanelDirective,
                        CdsAccordionPanelDirective,
                        CdsAccordionDirective,
                        CdsFocusableItemDirective,
                        CdsSelectableItemDirective,
                        CdsAriaAttributeDirective,
                        CdsResizeHandlerDirective,
                        CdsKeyboardHighlightableDirective,
                        CdsOverflowObserverDirective,
                        CdsDropdownToggleButtonDirective,
                        CdsElementRefDirective,
                        CdsResizeObserverDirective
                    ],
                    imports: [A11yModule]
                }]
        }] });

class CdsPluralizePipe {
    transform(value) {
        return pluralize(value);
    }
}
CdsPluralizePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPluralizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
CdsPluralizePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsPluralizePipe, name: "pluralize" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPluralizePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'pluralize'
                }]
        }] });

class CdsKebabCasePipe {
    transform(value) {
        return kebabCaseTransform(value);
    }
}
CdsKebabCasePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsKebabCasePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
CdsKebabCasePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsKebabCasePipe, name: "kebabCase" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsKebabCasePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'kebabCase',
                }]
        }] });
function kebabCaseTransform(value) {
    return value ? value.toLowerCase().replace(/[^A-Za-z\d]/g, '-') : '';
}

/**
 * Replace strings with the format pattern {KEY|Type|Callback} where Callback is optional
 */
const stringInterpolatorTransform = (inputString, replacements) => {
    const placeholderRegex = /\{\{([^}]+)\}\}/g;
    // Return the label as-is if no placeholders
    if (!placeholderRegex.test(inputString)) {
        return inputString;
    }
    let returnString = inputString;
    inputString.match(placeholderRegex).forEach((stringToReplace) => {
        const parameterRegex = /([^{|}\s*]+)/g;
        const [key] = stringToReplace.match(parameterRegex);
        returnString = returnString.replace(stringToReplace, replacements[key]);
    });
    return returnString;
};

/*
 * Use the DynamicStringInterpolator in a component template.
 *
 * Usage:
 *   stringToReplace | interpolate:dataset
 *
 * Where dataset = the dataset that contains the values that the keys in the string should be substituted for.
 *
 * Example:
 * const replacements = { FOO: 12 }
 *
 * "{FOO} is a number" | interpolate:replacements
 *
 * will return "12 is a number"
 */
class CdsStringInterpolatorPipe {
    transform(inputString, replacements) {
        return stringInterpolatorTransform(inputString, replacements);
    }
}
CdsStringInterpolatorPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsStringInterpolatorPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
CdsStringInterpolatorPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsStringInterpolatorPipe, name: "interpolate" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsStringInterpolatorPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'interpolate'
                }]
        }] });

class CdsPipesModule {
}
CdsPipesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsPipesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsPipesModule, declarations: [CdsPluralizePipe,
        CdsKebabCasePipe,
        CdsStringInterpolatorPipe], imports: [CommonModule], exports: [CdsPluralizePipe,
        CdsKebabCasePipe,
        CdsStringInterpolatorPipe] });
CdsPipesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPipesModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPipesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsPluralizePipe,
                        CdsKebabCasePipe,
                        CdsStringInterpolatorPipe
                    ],
                    exports: [
                        CdsPluralizePipe,
                        CdsKebabCasePipe,
                        CdsStringInterpolatorPipe
                    ],
                    imports: [
                        CommonModule
                    ]
                }]
        }] });

/**
 * ### Description
 * A control block is a utility UI control that adds advanced control to components.
 */
class CdsControlBlockComponent {
    get pointerEvents() {
        return this.disabled ? 'none' : 'auto';
    }
    /**
     * @internal
     *
     * Manually set the focus state of the control block.
     */
    focus() {
        setTimeout(() => this.atom.nativeElement.focus());
    }
    /**
     * @internal
     * Adds a highlighted state to the control block element.
     */
    addHighlight() {
        this.highlightable.addHighlightedClass();
    }
    /**
     * @internal
     * Removes the highlighted state from the control block element.
     */
    removeHighlight() {
        this.highlightable.removeHighlightedClass();
    }
}
CdsControlBlockComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsControlBlockComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsControlBlockComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: { name: "name", size: "size", disabled: "disabled", tabindex: "tabindex", active: "active", attributes: "attributes" }, host: { properties: { "style.pointerEvents": "this.pointerEvents" } }, viewQueries: [{ propertyName: "highlightable", first: true, predicate: CdsKeyboardHighlightableDirective, descendants: true, static: true }, { propertyName: "atom", first: true, predicate: ["atom"], descendants: true, static: true }], ngImport: i0, template: "<button\n  #atom\n  [id]=\"'cds-control-block-' + name | kebabCase\"\n  class=\"cds-control-block\"\n  type=\"button\"\n  [disabled]=\"disabled\"\n  [isDisabled]=\"disabled\"\n  [tabindex]=\"tabindex\"\n  [class.-sm]=\"size === 'small'\"\n  [class.-xs]=\"size === 'extraSmall'\"\n  [class.-active]=\"active && !disabled\"\n  [cdsAriaAttr]=\"attributes\"\n  cdsHighlightable\n>\n  <div class=\"cds-control-block-content\">\n    <ng-content></ng-content>\n  </div>\n</button>\n", dependencies: [{ kind: "directive", type: CdsAriaAttributeDirective, selector: "[cdsAriaAttr]", inputs: ["cdsAriaAttr"] }, { kind: "directive", type: CdsKeyboardHighlightableDirective, selector: "[cdsHighlightable]", inputs: ["isDisabled"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsControlBlockComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-control-block', template: "<button\n  #atom\n  [id]=\"'cds-control-block-' + name | kebabCase\"\n  class=\"cds-control-block\"\n  type=\"button\"\n  [disabled]=\"disabled\"\n  [isDisabled]=\"disabled\"\n  [tabindex]=\"tabindex\"\n  [class.-sm]=\"size === 'small'\"\n  [class.-xs]=\"size === 'extraSmall'\"\n  [class.-active]=\"active && !disabled\"\n  [cdsAriaAttr]=\"attributes\"\n  cdsHighlightable\n>\n  <div class=\"cds-control-block-content\">\n    <ng-content></ng-content>\n  </div>\n</button>\n" }]
        }], propDecorators: { name: [{
                type: Input
            }], size: [{
                type: Input
            }], disabled: [{
                type: Input
            }], tabindex: [{
                type: Input
            }], active: [{
                type: Input
            }], attributes: [{
                type: Input
            }], highlightable: [{
                type: ViewChild,
                args: [CdsKeyboardHighlightableDirective, { static: true }]
            }], atom: [{
                type: ViewChild,
                args: ['atom', { static: true }]
            }], pointerEvents: [{
                type: HostBinding,
                args: ['style.pointerEvents']
            }] } });

class CdsControlBlockModule {
}
CdsControlBlockModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsControlBlockModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsControlBlockModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsControlBlockModule, declarations: [CdsControlBlockComponent], imports: [CommonModule, CdsPipesModule, CdsDirectivesModule], exports: [CdsControlBlockComponent] });
CdsControlBlockModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsControlBlockModule, imports: [CommonModule, CdsPipesModule, CdsDirectivesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsControlBlockModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsControlBlockComponent],
                    exports: [CdsControlBlockComponent],
                    imports: [CommonModule, CdsPipesModule, CdsDirectivesModule],
                }]
        }] });

/**
 * Wraps some HTML content for easy transclusion.
 */
class CdsHostWrapperComponent {
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
    }
    ngAfterViewInit() { }
}
CdsHostWrapperComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsHostWrapperComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsHostWrapperComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsHostWrapperComponent, selector: "cds-host-wrapper", viewQueries: [{ propertyName: "template", first: true, predicate: ["template"], descendants: true, static: true }], ngImport: i0, template: `
    <ng-template #template>
      <ng-content></ng-content>
    </ng-template>
  `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsHostWrapperComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-host-wrapper',
                    template: `
    <ng-template #template>
      <ng-content></ng-content>
    </ng-template>
  `,
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { template: [{
                type: ViewChild,
                args: ['template', { static: true }]
            }] } });

class CdsHostWrapperModule {
}
CdsHostWrapperModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsHostWrapperModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsHostWrapperModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsHostWrapperModule, declarations: [CdsHostWrapperComponent], imports: [CommonModule], exports: [CdsHostWrapperComponent] });
CdsHostWrapperModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsHostWrapperModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsHostWrapperModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsHostWrapperComponent],
                    exports: [CdsHostWrapperComponent],
                    imports: [CommonModule]
                }]
        }] });

class AlertComponentType {
    get template() {
        return null;
    }
}

/**
 * ### Alert Container
 * A container for [Alert Components](?path=/story/components-alert-inline-overview--page#properties),
 * either inline or toast. Note that the Alert Container reverses the order of the alerts
 * that are contained within it, and if you are iterating over an array of alerts the most recently added item in the array
 * will be displayed at the top of the Alert Container.
 *
 * A container for toast alerts can be placed anywhere in an application, but there should be only one instance of
 * the component per application.
 * This means you should have just one `<cds-alert-container display="toast"></cds-alert-container>` in your entire application markup.
 *
 *
 * We recommend creating a singleton service that manages toast alerts for the entire application.
 */
class CdsAlertContainerComponent {
    /**
     * @internal
     */
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        /**
         * Displays the alerts in this container as inline alerts or toast alerts. Defaults to 'inline'
         */
        this.display = AlertDisplay.Inline;
        /**
         * @internal
         */
        this.dismissed = [];
        /**
         * Emits the current list of displayed AlertComponents when an alert is dismissed.
         */
        this.change = new EventEmitter();
    }
    /**
     * @internal
     */
    get displayedAlerts() {
        return this.alerts.filter((alert) => !this.dismissed.includes(alert));
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        this.setupSubscription(this.alerts.toArray());
        this.alerts.changes
            .subscribe(alerts => {
            this.setupSubscription(alerts);
            setTimeout(() => this.changeDetector.detectChanges());
        });
    }
    /**
     * @internal
     */
    setupSubscription(alerts) {
        this.$cancelSubscriptions.next();
        alerts.map((alert) => {
            alert.close
                .pipe(takeUntil(this.$cancelSubscriptions))
                .subscribe(() => this.removeAlert(alert));
        });
    }
    /**
     * @internal
     *
     * Removes an alert from the array of alerts.
     */
    removeAlert(alert) {
        this.dismissed.push(alert);
        this.change.emit(this.displayedAlerts.slice());
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsAlertContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAlertContainerComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsAlertContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsAlertContainerComponent, selector: "cds-alert-container:not([display=toast])", inputs: { display: "display" }, outputs: { change: "change" }, queries: [{ propertyName: "alerts", predicate: AlertComponentType, descendants: true }], ngImport: i0, template: "<div\n  class=\"cds-alerts\"\n  [class.-inline]=\"display === 'inline'\"\n  [class.-toast]=\"display === 'toast'\"\n>\n  <ng-container *ngFor=\"let alert of displayedAlerts\">\n    <ng-template [ngTemplateOutlet]=\"alert.template\"></ng-template>\n  </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAlertContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-alert-container:not([display=toast])', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  class=\"cds-alerts\"\n  [class.-inline]=\"display === 'inline'\"\n  [class.-toast]=\"display === 'toast'\"\n>\n  <ng-container *ngFor=\"let alert of displayedAlerts\">\n    <ng-template [ngTemplateOutlet]=\"alert.template\"></ng-template>\n  </ng-container>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { display: [{
                type: Input
            }], change: [{
                type: Output
            }], alerts: [{
                type: ContentChildren,
                args: [AlertComponentType, { descendants: true }]
            }] } });

/**
 * Wraps a component's html (including selector) in a CdsHostWrapperComponent.
 */
class CdsWrappedComponent {
    constructor(elementRef, injector, appRef) {
        this.elementRef = elementRef;
        this.appRef = appRef;
        // use the hostWrapper to wrap the element in an ng-template
        this.hostWrapper = createComponent(CdsHostWrapperComponent, appRef.injector, injector, this.elementRef.nativeElement);
        // get access to the templateRef
        this.hostWrapper.instance.changeDetector.detectChanges();
        // instantiate the templateRef
        this.view = this.hostWrapper.instance.template.createEmbeddedView(null);
        // Attach hostWrapper view to appRef to trigger change detection
        appRef.attachView(this.hostWrapper.hostView); // attaches <cds-host-wrapper> to document object
    }
    get template() {
        return this.hostWrapper.instance.template;
    }
    destroy() {
        this.view.destroy();
        this.hostWrapper.destroy();
        this.appRef.detachView(this.hostWrapper.hostView);
    }
}
CdsWrappedComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsWrappedComponent, deps: [{ token: i0.ElementRef }, { token: i0.Injector }, { token: i0.ApplicationRef }], target: i0.ɵɵFactoryTarget.Injectable });
CdsWrappedComponent.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsWrappedComponent });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsWrappedComponent, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Injector }, { type: i0.ApplicationRef }]; } });

/**
 * ### Alert
 * Displays a notification banner. An alert must be placed within the Alert Container component.
 * An optional footer can be displayed if the Alert is in a toast Alert Container component.
 */
// eslint-disable-next-line max-classes-per-file
class CdsAlertComponent extends AlertComponentType {
    /**
     * @internal
     */
    constructor(renderer, container, wrappedAlert) {
        super();
        this.renderer = renderer;
        this.container = container;
        this.wrappedAlert = wrappedAlert;
        /**
         * @internal
         */
        this.$timeout = new BehaviorSubject(null);
        /**
         * The [alert level](?path=/story/types-alert--page#alertlevel).
         */
        this.level = AlertLevel.Info;
        /**
         * The [alert type](?path=/story/types-alert--page#alerttype).
         */
        this.type = AlertType.Primary;
        /**
         * Emits an event when the alert is dismissed.
         */
        this.close = new EventEmitter();
        if (!container) {
            throw new Error('Alert component should be placed within an AlertContainer component!');
        }
    }
    /**
     * @internal
     */
    get showFooter() {
        return this.container && this.container.display === AlertDisplay.Toast;
    }
    /**
     * @internal
     */
    get template() {
        return this.wrappedAlert.template;
    }
    /**
     * (Options) Specify, in seconds, how the alert should remain present on the screen before disappearing.
     */
    set timeout(timeout) {
        this.$timeout.next(timeout);
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.$timeout.subscribe((duration) => {
            if (!(+duration > 0)) {
                return;
            }
            const subscription = timer(duration * 1000).subscribe(() => {
                this.startToDismiss();
                subscription.unsubscribe();
            });
        });
    }
    /**
     * @internal
     */
    notifyParent(event) {
        if (event.animationName === 'fade-out' && !this.dismissed) {
            this.dismissed = true;
            this.close.next();
        }
    }
    /**
     * @internal
     *
     * Start the process of dismissing an alert by adding a class that triggers an animation.
     * When the animation is complete, the notifyParent() method is called.
     */
    startToDismiss() {
        this.renderer.addClass(this.alertElement.nativeElement, '-dismissed');
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.startToDismiss();
        this.$timeout.complete();
        this.wrappedAlert.destroy();
    }
}
CdsAlertComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAlertComponent, deps: [{ token: i0.Renderer2 }, { token: CdsAlertContainerComponent }, { token: CdsWrappedComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsAlertComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsAlertComponent, selector: "cds-alert", inputs: { title: "title", level: "level", type: "type", iconName: "iconName", ariaLive: "ariaLive", timeout: "timeout" }, outputs: { close: "close" }, providers: [
        CdsWrappedComponent,
        {
            provide: AlertComponentType,
            useExisting: forwardRef(() => CdsAlertComponent),
        },
    ], viewQueries: [{ propertyName: "alertElement", first: true, predicate: ["alertElement"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div\n  class=\"cds-alert\"\n  [class.-informational]=\"level === 'info'\"\n  [class.-warning]=\"level === 'warning'\"\n  [class.-successful]=\"level === 'success'\"\n  [class.-alert]=\"level === 'error'\"\n  [class.-primary]=\"!type || type === 'primary'\"\n  [class.-secondary]=\"type === 'secondary'\"\n  [style.display]=\"dismissed ? 'none' : null\"\n  (animationend)=\"notifyParent($event)\"\n  [attr.aria-live]=\"ariaLive || 'polite'\"\n  #alertElement\n>\n  <div\n    *ngIf=\"iconName\"\n    class=\"cds-alert-icon\"\n    [attr.icon-name]=\"iconName\"\n  ></div>\n\n  <div class=\"cds-alert-content\">\n    <span class=\"cds-assistive-text\">{{ level }}</span>\n    <div class=\"cds-alert-title\">{{ title }}</div>\n    <div class=\"cds-alert-body\">\n      <ng-content></ng-content>\n    </div>\n    <div class=\"cds-alert-footer\" *ngIf=\"showFooter\">\n      <ng-content select=\"[cdsAlertFooter]\"></ng-content>\n    </div>\n  </div>\n\n  <cds-control-block (click)=\"startToDismiss()\" class=\"cds-alert-close\">\n    <i class=\"material-icons\">close</i>\n  </cds-control-block>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: ["name", "size", "disabled", "tabindex", "active", "attributes"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-alert', providers: [
                        CdsWrappedComponent,
                        {
                            provide: AlertComponentType,
                            useExisting: forwardRef(() => CdsAlertComponent),
                        },
                    ], template: "<div\n  class=\"cds-alert\"\n  [class.-informational]=\"level === 'info'\"\n  [class.-warning]=\"level === 'warning'\"\n  [class.-successful]=\"level === 'success'\"\n  [class.-alert]=\"level === 'error'\"\n  [class.-primary]=\"!type || type === 'primary'\"\n  [class.-secondary]=\"type === 'secondary'\"\n  [style.display]=\"dismissed ? 'none' : null\"\n  (animationend)=\"notifyParent($event)\"\n  [attr.aria-live]=\"ariaLive || 'polite'\"\n  #alertElement\n>\n  <div\n    *ngIf=\"iconName\"\n    class=\"cds-alert-icon\"\n    [attr.icon-name]=\"iconName\"\n  ></div>\n\n  <div class=\"cds-alert-content\">\n    <span class=\"cds-assistive-text\">{{ level }}</span>\n    <div class=\"cds-alert-title\">{{ title }}</div>\n    <div class=\"cds-alert-body\">\n      <ng-content></ng-content>\n    </div>\n    <div class=\"cds-alert-footer\" *ngIf=\"showFooter\">\n      <ng-content select=\"[cdsAlertFooter]\"></ng-content>\n    </div>\n  </div>\n\n  <cds-control-block (click)=\"startToDismiss()\" class=\"cds-alert-close\">\n    <i class=\"material-icons\">close</i>\n  </cds-control-block>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: CdsAlertContainerComponent }, { type: CdsWrappedComponent }]; }, propDecorators: { title: [{
                type: Input
            }], level: [{
                type: Input
            }], type: [{
                type: Input
            }], iconName: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], timeout: [{
                type: Input
            }], close: [{
                type: Output
            }], alertElement: [{
                type: ViewChild,
                args: ['alertElement', { static: true }]
            }] } });

/**
 * ### Toast Alert Container
 * A container for [Alert Components](?path=/story/components-alert-inline-overview--page#properties),
 * either inline or toast. Note that the Alert Container reverses the order of the alerts
 * that are contained within it, and if you are iterating over an array of alerts the most recently added item in the array
 * will be displayed at the top of the Alert Container.
 *
 * A container for toast alerts can be placed anywhere in an application, but there should be only one instance of
 * the component per application.
 * This means you should have just one `<cds-alert-container display="toast"></cds-alert-container>` in your entire application markup.
 *
 * We recommend creating a singleton service that manages toast alerts for the entire application.
 */
let toastAlertCount = 0;
class CdsToastAlertContainerComponent extends CdsAlertContainerComponent {
    constructor() {
        super(...arguments);
        /**
         * Displays the alerts in this container as inline alerts or toast alerts.
         */
        this.display = AlertDisplay.Toast;
    }
    /**
     * @internal
     */
    ngOnInit() {
        if (toastAlertCount > 0) {
            throw new Error(`Another toast alert container has been detected in the application.
        There should be one toast alert container per application.`);
        }
        toastAlertCount++;
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        toastAlertCount--;
        super.ngOnDestroy();
    }
}
CdsToastAlertContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToastAlertContainerComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
CdsToastAlertContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsToastAlertContainerComponent, selector: "cds-alert-container[display=toast]", inputs: { display: "display" }, providers: [
        {
            provide: CdsAlertContainerComponent,
            useExisting: forwardRef(() => CdsToastAlertContainerComponent),
        },
    ], usesInheritance: true, ngImport: i0, template: "<div\n  class=\"cds-alerts\"\n  [class.-inline]=\"display === 'inline'\"\n  [class.-toast]=\"display === 'toast'\"\n>\n  <ng-container *ngFor=\"let alert of displayedAlerts\">\n    <ng-template [ngTemplateOutlet]=\"alert.template\"></ng-template>\n  </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToastAlertContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-alert-container[display=toast]', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            provide: CdsAlertContainerComponent,
                            useExisting: forwardRef(() => CdsToastAlertContainerComponent),
                        },
                    ], template: "<div\n  class=\"cds-alerts\"\n  [class.-inline]=\"display === 'inline'\"\n  [class.-toast]=\"display === 'toast'\"\n>\n  <ng-container *ngFor=\"let alert of displayedAlerts\">\n    <ng-template [ngTemplateOutlet]=\"alert.template\"></ng-template>\n  </ng-container>\n</div>\n" }]
        }], propDecorators: { display: [{
                type: Input
            }] } });

class CdsAlertModule {
}
CdsAlertModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsAlertModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsAlertModule, declarations: [CdsAlertComponent,
        CdsAlertContainerComponent,
        CdsToastAlertContainerComponent], imports: [CommonModule, CdsControlBlockModule, CdsHostWrapperModule], exports: [CdsAlertComponent,
        CdsAlertContainerComponent,
        CdsToastAlertContainerComponent] });
CdsAlertModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAlertModule, imports: [CommonModule, CdsControlBlockModule, CdsHostWrapperModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAlertModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsAlertComponent,
                        CdsAlertContainerComponent,
                        CdsToastAlertContainerComponent,
                    ],
                    exports: [
                        CdsAlertComponent,
                        CdsAlertContainerComponent,
                        CdsToastAlertContainerComponent,
                    ],
                    imports: [CommonModule, CdsControlBlockModule, CdsHostWrapperModule],
                }]
        }] });

var CdsBadgeLevel;
(function (CdsBadgeLevel) {
    CdsBadgeLevel["Info"] = "info";
    CdsBadgeLevel["Warning"] = "warning";
    CdsBadgeLevel["Success"] = "success";
    CdsBadgeLevel["Error"] = "error";
})(CdsBadgeLevel || (CdsBadgeLevel = {}));

/**
 * ### Description
 * A component that is used to wrap some content and attach a Badge to the wrapped content.
 */
class CdsBadgeContainerComponent {
    constructor() {
        /**
         * Sets a different color for custom levels of badges.
         */
        this.level = CdsBadgeLevel.Error;
    }
    /**
     * @internal
     */
    getOffsetStyle() {
        let styles = '';
        if (this.customInsetX) {
            styles += '--customInsetX:' + this.customInsetX + ';';
        }
        if (this.customInsetY) {
            styles += '--customInsetY:' + this.customInsetY + ';';
        }
        return styles;
    }
}
CdsBadgeContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBadgeContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsBadgeContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsBadgeContainerComponent, selector: "cds-badge-container", inputs: { name: "name", dot: "dot", position: "position", grow: "grow", insetX: "insetX", insetY: "insetY", customInsetX: "customInsetX", customInsetY: "customInsetY", level: "level" }, ngImport: i0, template: "<span\n  class=\"cds-badge-container\"\n  [id]=\"'cds-badge-container-' + (name | kebabCase)\"\n>\n  <span class=\"cds-badge-content\">\n    <ng-content></ng-content>\n  </span>\n  <sup\n    #badge\n    [id]=\"'cds-badge-' + (name | kebabCase)\"\n    class=\"cds-badge\"\n    [attr.style]=\"getOffsetStyle()\"\n    [class.-informational]=\"level==='info'\"\n    [class.-warning]=\"level==='warning'\"\n    [class.-success]=\"level==='success'\"\n    [class.-error]=\"level==='error'\"\n    [ngClass]=\"{\n      '-dot': dot,\n      '-top-left': position === 'top-left',\n      '-top-right': position === 'top-right',\n      '-bottom-left': position === 'bottom-left',\n      '-bottom-right': position === 'bottom-right',\n      '-middle-left': position === 'middle-left',\n      '-middle-right': position === 'middle-right',\n      '-inward': grow === 'inward',\n      '-outward': grow === 'outward',\n      '-inset-x': insetX,\n      '-inset-y': insetY\n    }\"\n  >\n    <span class=\"cds-badge-label\" *ngIf=\"!dot\">\n      <ng-content select=\"[cdsBadgeLabel]\"></ng-content>\n    </span>\n  </sup>\n</span>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBadgeContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-badge-container', template: "<span\n  class=\"cds-badge-container\"\n  [id]=\"'cds-badge-container-' + (name | kebabCase)\"\n>\n  <span class=\"cds-badge-content\">\n    <ng-content></ng-content>\n  </span>\n  <sup\n    #badge\n    [id]=\"'cds-badge-' + (name | kebabCase)\"\n    class=\"cds-badge\"\n    [attr.style]=\"getOffsetStyle()\"\n    [class.-informational]=\"level==='info'\"\n    [class.-warning]=\"level==='warning'\"\n    [class.-success]=\"level==='success'\"\n    [class.-error]=\"level==='error'\"\n    [ngClass]=\"{\n      '-dot': dot,\n      '-top-left': position === 'top-left',\n      '-top-right': position === 'top-right',\n      '-bottom-left': position === 'bottom-left',\n      '-bottom-right': position === 'bottom-right',\n      '-middle-left': position === 'middle-left',\n      '-middle-right': position === 'middle-right',\n      '-inward': grow === 'inward',\n      '-outward': grow === 'outward',\n      '-inset-x': insetX,\n      '-inset-y': insetY\n    }\"\n  >\n    <span class=\"cds-badge-label\" *ngIf=\"!dot\">\n      <ng-content select=\"[cdsBadgeLabel]\"></ng-content>\n    </span>\n  </sup>\n</span>\n" }]
        }], propDecorators: { name: [{
                type: Input
            }], dot: [{
                type: Input
            }], position: [{
                type: Input
            }], grow: [{
                type: Input
            }], insetX: [{
                type: Input
            }], insetY: [{
                type: Input
            }], customInsetX: [{
                type: Input
            }], customInsetY: [{
                type: Input
            }], level: [{
                type: Input
            }] } });

class CdsBadgeModule {
}
CdsBadgeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBadgeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsBadgeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsBadgeModule, declarations: [CdsBadgeContainerComponent], imports: [CommonModule, CdsPipesModule], exports: [CdsBadgeContainerComponent] });
CdsBadgeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBadgeModule, imports: [CommonModule, CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBadgeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsBadgeContainerComponent],
                    exports: [CdsBadgeContainerComponent],
                    imports: [CommonModule, CdsPipesModule],
                }]
        }] });

/* eslint-disable max-classes-per-file */
class CdsBreadcrumbLinkComponent {
    /**
     * @internal
     */
    constructor(elementRef, wrappedLink) {
        this.elementRef = elementRef;
        this.wrappedLink = wrappedLink;
    }
    /**
     * @internal
     */
    get template() {
        return this.wrappedLink.template;
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.wrappedLink.destroy();
    }
}
CdsBreadcrumbLinkComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbLinkComponent, deps: [{ token: i0.ElementRef }, { token: CdsWrappedComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsBreadcrumbLinkComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsBreadcrumbLinkComponent, selector: "[cdsBreadcrumbLink]:not([cdsCurrent])", inputs: { sticky: "sticky" }, providers: [CdsWrappedComponent], ngImport: i0, template: `<ng-content></ng-content>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbLinkComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 
                    // eslint-disable-next-line @angular-eslint/component-selector
                    '[cdsBreadcrumbLink]:not([cdsCurrent])',
                    template: `<ng-content></ng-content>`,
                    providers: [CdsWrappedComponent]
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: CdsWrappedComponent }]; }, propDecorators: { sticky: [{
                type: Input
            }] } });
// eslint-disable-next-line max-classes-per-file
class CdsBreadcrumbCurrentLinkComponent extends CdsBreadcrumbLinkComponent {
    constructor(
    /** @internal */ elementRef, 
    /** @internal */ wrappedLink) {
        super(elementRef, wrappedLink);
    }
}
CdsBreadcrumbCurrentLinkComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbCurrentLinkComponent, deps: [{ token: i0.ElementRef }, { token: CdsWrappedComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsBreadcrumbCurrentLinkComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsBreadcrumbCurrentLinkComponent, selector: "[cdsBreadcrumbLink][cdsCurrent]", providers: [CdsWrappedComponent], usesInheritance: true, ngImport: i0, template: `<ng-content></ng-content>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbCurrentLinkComponent, decorators: [{
            type: Component,
            args: [{
                    // eslint-disable-next-line @angular-eslint/component-selector
                    selector: '[cdsBreadcrumbLink][cdsCurrent]',
                    template: `<ng-content></ng-content>`,
                    providers: [CdsWrappedComponent]
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: CdsWrappedComponent }]; } });

class CdsBreadcrumbLinkDirective extends CdsKeyboardHighlightableDirective {
    constructor(elementRef) {
        super(elementRef);
    }
}
CdsBreadcrumbLinkDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbLinkDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsBreadcrumbLinkDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsBreadcrumbLinkDirective, selector: "[cdsBreadcrumbLink]", usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsBreadcrumbLink]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

class CdsToolbarPresenter {
    constructor(overflowPresenter, element) {
        this.overflowPresenter = overflowPresenter;
        this.element = element;
        this.$cancelSubscriptions = new Subject();
        this.$change = new Subject();
        this.overflowItems = [];
        this.containerItems = [];
        this.change = this.$change.asObservable();
    }
    init() {
        this.overflowPresenter.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe((event) => {
            this.updateItems(event);
        });
        this.items.changes.subscribe(() => {
            this.reset();
        });
        this.reset();
    }
    setItems(items) {
        this.items = items;
    }
    setDropdownDirective(dropdown) {
        this.dropdown = dropdown;
    }
    updateItems({ containerItems, overflowItems }) {
        this.containerItems = [...containerItems];
        this.overflowItems = [...overflowItems];
        this.$change.next();
    }
    reset() {
        this.overflowPresenter.watch(this.element.nativeElement, this.items.toArray());
    }
    shiftFocusIntoDropdownLtr(event, last) {
        if (!last) {
            return;
        }
        this.shiftFocusIntoDropdown(event, false);
    }
    shiftFocusIntoDropdown(event, rtl) {
        if (!this.dropdown) {
            return;
        }
        event.preventDefault();
        this.dropdown.expand();
        const dropdownItems = this.element.nativeElement.querySelectorAll('.overflow-item');
        const focusTarget = rtl ? dropdownItems[dropdownItems.length - 1] : dropdownItems[0];
        const anchor = focusTarget.querySelector('a');
        const focusElem = anchor ? anchor : focusTarget;
        focusElem.focus();
    }
    destroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
        this.overflowPresenter?.destroy();
        this.$change.complete();
    }
}
CdsToolbarPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToolbarPresenter, deps: [{ token: CdsOverflowObserverPresenter }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Injectable });
CdsToolbarPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToolbarPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToolbarPresenter, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CdsOverflowObserverPresenter }, { type: i0.ElementRef }]; } });

// @todo: https://github.com/angular/components/issues/8258
// Breadcrumb links will not be transcluded if wrapped in an `<ng-container>` element.
/**
 * ### Description
 * Provides the user with an understanding of how the currently active page URL relates to the application's homepage.
 * Accepts anchor elements as children.
 *
 * You should denote the Homepage of the application with the `[cdsBreadcrumbHome]` attribute, the currently active page using
 * the [cdsBreadcrumbParent] attribute, and the previously active page using the `[cdsBreadcrumbParent]` attribute. See below
 * for an example.
 */
class CdsBreadcrumbsComponent {
    /**
     * @internal
     */
    constructor(overflowPresenter, changeDetector, toolbarPresenter) {
        this.overflowPresenter = overflowPresenter;
        this.changeDetector = changeDetector;
        this.toolbarPresenter = toolbarPresenter;
    }
    /**
     * @internal
     */
    get overflowItems() {
        return this.toolbarPresenter.overflowItems;
    }
    ;
    /**
     * @internal
     */
    get containerItems() {
        return this.toolbarPresenter.containerItems;
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.toolbarPresenter = new CdsToolbarPresenter(this.overflowPresenter, this.overflowContainerElement);
        this.toolbarPresenter.change.subscribe(event => {
            this.toolbarPresenter.setDropdownDirective(this.dropdown);
            this.changeDetector.detectChanges();
        });
        this.toolbarPresenter.setItems(this.links);
        this.toolbarPresenter.init();
    }
    /**
     * @internal
     */
    get toggleAttributes() {
        return {
            'aria-haspopup': 'true',
            'aria-label': 'Show More',
            'aria-expanded': this.dropdown && this.dropdown.expanded,
            'title': 'Show More'
        };
    }
    /**
     * @internal
     */
    onFocusRemoved() {
        this.dropdown.collapse();
    }
    /**
     * @internal
     */
    onExpandedStateChanged(expanded) {
        this.expanded = expanded;
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.toolbarPresenter.destroy();
    }
}
CdsBreadcrumbsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbsComponent, deps: [{ token: CdsOverflowObserverPresenter }, { token: i0.ChangeDetectorRef }, { token: CdsToolbarPresenter }], target: i0.ɵɵFactoryTarget.Component });
CdsBreadcrumbsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsBreadcrumbsComponent, selector: "cds-breadcrumbs", inputs: { name: "name" }, providers: [CdsOverflowObserverPresenter, CdsToolbarPresenter], queries: [{ propertyName: "current", first: true, predicate: CdsBreadcrumbCurrentLinkComponent, descendants: true }, { propertyName: "links", predicate: CdsBreadcrumbLinkComponent, descendants: true }], viewQueries: [{ propertyName: "overflowContainerElement", first: true, predicate: ["overflowContainer"], descendants: true, static: true }, { propertyName: "dropdown", first: true, predicate: CdsDropdownDirective, descendants: true }, { propertyName: "toolbarContent", predicate: ["toolbarContent"], descendants: true }], ngImport: i0, template: "<nav aria-label=\"breadcrumb\">\n  <ol class=\"cds-breadcrumb\" #overflowContainer>\n    <li class=\"cds-breadcrumb-item\"\n        *ngFor=\"let item of containerItems; let last = last\"\n        (keydown.tab)=\"toolbarPresenter.shiftFocusIntoDropdownLtr($event, last)\">\n      <ng-template [ngTemplateOutlet]=\"item.template\"></ng-template>\n    </li>\n\n    <li\n        class=\"cds-dropdown\"\n        [class.-open]=\"expanded\"\n        *ngIf=\"overflowItems?.length > 0\">\n      <cds-control-block\n        cdsElementRef\n        #toggle=\"cdsElementRef\"\n        size=\"extraSmall\"\n        [name]=\"name + '-overflow-container'\"\n        [attributes]=\"toggleAttributes\"\n        tabindex=\"-1\"\n      >\n        <i class=\"material-icons\">more_horiz</i>\n      </cds-control-block>\n\n      <div \n        cdsDropdownPanel\n        #breadcrumbPanel\n        [id]=\"name + '-breadcrumb-panel' | kebabCase\">\n        <ol class=\"cds-list\">\n          <li class=\"cds-breadcrumb-item cds-breadcrumb-dropdown-item overflow-item\" *ngFor=\"let item of overflowItems\">\n            <ng-template [ngTemplateOutlet]=\"item.template\"></ng-template>\n          </li>\n        </ol>\n      </div>\n\n      <ng-container\n        cdsDropdown\n        buttonTrigger\n        [anchor]=\"toggle.nativeElement\"\n        [panel]=\"breadcrumbPanel\"\n        (removeFocus)=\"onFocusRemoved()\"\n        (change)=\"onExpandedStateChanged($event)\"\n      >\n      </ng-container>\n    </li>\n\n    <li class=\"cds-breadcrumb-item\" aria-current=\"page\" (keydown.shift.tab)=\"toolbarPresenter.shiftFocusIntoDropdown($event, true)\">\n      <ng-template *ngIf=\"current\" [ngTemplateOutlet]=\"current.template\"></ng-template>\n    </li>\n  </ol>\n</nav>\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: CdsDropdownDirective, selector: "[cdsDropdown]", inputs: ["anchor", "panel", "options", "strategy", "wasFocusedFn"], outputs: ["removeFocus", "change"] }, { kind: "directive", type: CdsDropdownPanelDirective, selector: "[cdsDropdownPanel]", exportAs: ["dropdownPanel"] }, { kind: "directive", type: CdsDropdownToggleButtonDirective, selector: "[buttonTrigger]", outputs: ["toggleChange"] }, { kind: "directive", type: CdsElementRefDirective, selector: "[cdsElementRef]", exportAs: ["cdsElementRef"] }, { kind: "component", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: ["name", "size", "disabled", "tabindex", "active", "attributes"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-breadcrumbs', providers: [CdsOverflowObserverPresenter, CdsToolbarPresenter], template: "<nav aria-label=\"breadcrumb\">\n  <ol class=\"cds-breadcrumb\" #overflowContainer>\n    <li class=\"cds-breadcrumb-item\"\n        *ngFor=\"let item of containerItems; let last = last\"\n        (keydown.tab)=\"toolbarPresenter.shiftFocusIntoDropdownLtr($event, last)\">\n      <ng-template [ngTemplateOutlet]=\"item.template\"></ng-template>\n    </li>\n\n    <li\n        class=\"cds-dropdown\"\n        [class.-open]=\"expanded\"\n        *ngIf=\"overflowItems?.length > 0\">\n      <cds-control-block\n        cdsElementRef\n        #toggle=\"cdsElementRef\"\n        size=\"extraSmall\"\n        [name]=\"name + '-overflow-container'\"\n        [attributes]=\"toggleAttributes\"\n        tabindex=\"-1\"\n      >\n        <i class=\"material-icons\">more_horiz</i>\n      </cds-control-block>\n\n      <div \n        cdsDropdownPanel\n        #breadcrumbPanel\n        [id]=\"name + '-breadcrumb-panel' | kebabCase\">\n        <ol class=\"cds-list\">\n          <li class=\"cds-breadcrumb-item cds-breadcrumb-dropdown-item overflow-item\" *ngFor=\"let item of overflowItems\">\n            <ng-template [ngTemplateOutlet]=\"item.template\"></ng-template>\n          </li>\n        </ol>\n      </div>\n\n      <ng-container\n        cdsDropdown\n        buttonTrigger\n        [anchor]=\"toggle.nativeElement\"\n        [panel]=\"breadcrumbPanel\"\n        (removeFocus)=\"onFocusRemoved()\"\n        (change)=\"onExpandedStateChanged($event)\"\n      >\n      </ng-container>\n    </li>\n\n    <li class=\"cds-breadcrumb-item\" aria-current=\"page\" (keydown.shift.tab)=\"toolbarPresenter.shiftFocusIntoDropdown($event, true)\">\n      <ng-template *ngIf=\"current\" [ngTemplateOutlet]=\"current.template\"></ng-template>\n    </li>\n  </ol>\n</nav>\n" }]
        }], ctorParameters: function () { return [{ type: CdsOverflowObserverPresenter }, { type: i0.ChangeDetectorRef }, { type: CdsToolbarPresenter }]; }, propDecorators: { name: [{
                type: Input
            }], current: [{
                type: ContentChild,
                args: [CdsBreadcrumbCurrentLinkComponent]
            }], links: [{
                type: ContentChildren,
                args: [CdsBreadcrumbLinkComponent, { descendants: true }]
            }], overflowContainerElement: [{
                type: ViewChild,
                args: ['overflowContainer', { static: true }]
            }], toolbarContent: [{
                type: ViewChildren,
                args: ['toolbarContent']
            }], dropdown: [{
                type: ViewChild,
                args: [CdsDropdownDirective]
            }] } });

class CdsBreadcrumbsModule {
}
CdsBreadcrumbsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsBreadcrumbsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbsModule, declarations: [CdsBreadcrumbsComponent,
        CdsBreadcrumbLinkComponent,
        CdsBreadcrumbCurrentLinkComponent,
        CdsBreadcrumbLinkDirective], imports: [CommonModule,
        RouterModule,
        CdsPipesModule,
        CdsDirectivesModule,
        CdsHostWrapperModule,
        CdsControlBlockModule], exports: [CdsBreadcrumbsComponent,
        CdsBreadcrumbLinkComponent,
        CdsBreadcrumbCurrentLinkComponent,
        CdsBreadcrumbLinkDirective] });
CdsBreadcrumbsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbsModule, imports: [CommonModule,
        RouterModule,
        CdsPipesModule,
        CdsDirectivesModule,
        CdsHostWrapperModule,
        CdsControlBlockModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBreadcrumbsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsBreadcrumbsComponent,
                        CdsBreadcrumbLinkComponent,
                        CdsBreadcrumbCurrentLinkComponent,
                        CdsBreadcrumbLinkDirective,
                    ],
                    exports: [
                        CdsBreadcrumbsComponent,
                        CdsBreadcrumbLinkComponent,
                        CdsBreadcrumbCurrentLinkComponent,
                        CdsBreadcrumbLinkDirective,
                    ],
                    imports: [
                        CommonModule,
                        RouterModule,
                        CdsPipesModule,
                        CdsDirectivesModule,
                        CdsHostWrapperModule,
                        CdsControlBlockModule
                    ],
                }]
        }] });

// FIREFOX ISSUE
// Chrome and IE generate a MouseEvent when a button is activated via Enter.
// Firefox generates a MouseEvent when a button is activated via Enter or Spacebar.
// This click event may trigger the submission of a form when Spacebar is hit, regardless of if the button is of type "Submit".
// It's possible to avoid the creation of these synthetic MouseEvents by codeventing the default behaviour the keyup event.
// We want the synthetic events generated by the Enter keycodess to work normally, but we want to cancel Spacebar events.
/**
 * ### Description
 * Buttons are clickable items used to evoke an event or perform an action.
 */
class CdsButtonComponent {
    constructor(element) {
        this.element = element;
        /**
         * The Button type.
         */
        this.type = 'primary';
        /**
         * The Button size.
         */
        this.size = 'default';
        /**
         * What is the responsibility of this button?
         *
         * <code>"submit"</code>
         * The button is being used to trigger the submission of a form.
         *
         * <code>"reset"</code>
         * The button is being used to reset all the fields in a form.
         *
         * <code>"button"</code>
         * The button is being used for all other non-form applications.
         */
        this.designation = 'button';
        /**
         * @internal
         */
        this.onClick = (event) => {
            if (this.disabled) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        };
        this.element.nativeElement.addEventListener('click', this.onClick, true);
    }
    /**
     * @internal
     */
    get iconOnly() {
        return this.iconName && !this.text;
    }
    /**
     * @internal
     */
    get leftIcon() {
        return this.iconName && (this.alignIcon === 'left' || !this.alignIcon);
    }
    /**
     * @internal
     */
    get rightIcon() {
        return this.iconName && this.alignIcon === 'right';
    }
    /**
     * @internal
     */
    focus(opts = {}) {
        this.elementRef.nativeElement.focus(opts);
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.element.nativeElement.removeEventListener('click', this.onClick);
    }
}
CdsButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
CdsButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsButtonComponent, selector: "cds-button", inputs: { name: "name", text: "text", type: "type", size: "size", disabled: "disabled", underline: "underline", iconName: "iconName", alignIcon: "alignIcon", designation: "designation", ariaLabel: "ariaLabel", link: "link" }, viewQueries: [{ propertyName: "elementRef", first: true, predicate: ["button"], descendants: true, static: true }], ngImport: i0, template: "<button\n  [id]=\"'cds-button-' + name | kebabCase\"\n  class=\"cds-button\"\n  [class.-icon-only]=\"!text\"\n  [class.-link]=\"link\"\n  [disabled]=\"disabled\"\n  [isDisabled]=\"disabled\"\n  [attr.type]=\"designation || 'button'\"\n  [attr.icon-name]=\"iconName\"\n  [attr.aria-label]=\"ariaLabel ? ariaLabel : !text && iconName ? iconName : null\"\n  [attr.tabindex]=\"disabled ? -1 : 0\"\n  [ngClass]=\"{\n    '-primary': type === 'primary',\n    '-secondary': type === 'secondary',\n    '-tertiary': type === 'tertiary',\n    '-sm': size === 'small',\n    '-underline': type === 'tertiary' && underline\n  }\"\n  cdsHighlightable\n  #button\n>\n  <ng-content\n    *ngIf=\"!leftIcon; else icon\"\n    select=\"[cdsButtonPrefix]\"\n  ></ng-content>\n  <span *ngIf=\"text\" class=\"cds-button-label\">{{ text }}</span>\n  <ng-content\n    *ngIf=\"!rightIcon; else icon\"\n    select=\"[cdsButtonSuffix]\"\n  ></ng-content>\n</button>\n\n<ng-template #icon>\n  <i class=\"material-icons\"> {{ iconName }} </i>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: CdsKeyboardHighlightableDirective, selector: "[cdsHighlightable]", inputs: ["isDisabled"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-button', template: "<button\n  [id]=\"'cds-button-' + name | kebabCase\"\n  class=\"cds-button\"\n  [class.-icon-only]=\"!text\"\n  [class.-link]=\"link\"\n  [disabled]=\"disabled\"\n  [isDisabled]=\"disabled\"\n  [attr.type]=\"designation || 'button'\"\n  [attr.icon-name]=\"iconName\"\n  [attr.aria-label]=\"ariaLabel ? ariaLabel : !text && iconName ? iconName : null\"\n  [attr.tabindex]=\"disabled ? -1 : 0\"\n  [ngClass]=\"{\n    '-primary': type === 'primary',\n    '-secondary': type === 'secondary',\n    '-tertiary': type === 'tertiary',\n    '-sm': size === 'small',\n    '-underline': type === 'tertiary' && underline\n  }\"\n  cdsHighlightable\n  #button\n>\n  <ng-content\n    *ngIf=\"!leftIcon; else icon\"\n    select=\"[cdsButtonPrefix]\"\n  ></ng-content>\n  <span *ngIf=\"text\" class=\"cds-button-label\">{{ text }}</span>\n  <ng-content\n    *ngIf=\"!rightIcon; else icon\"\n    select=\"[cdsButtonSuffix]\"\n  ></ng-content>\n</button>\n\n<ng-template #icon>\n  <i class=\"material-icons\"> {{ iconName }} </i>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { name: [{
                type: Input
            }], text: [{
                type: Input
            }], type: [{
                type: Input
            }], size: [{
                type: Input
            }], disabled: [{
                type: Input
            }], underline: [{
                type: Input
            }], iconName: [{
                type: Input
            }], alignIcon: [{
                type: Input
            }], designation: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], link: [{
                type: Input
            }], elementRef: [{
                type: ViewChild,
                args: ['button', { static: true }]
            }] } });

class CdsButtonModule {
}
CdsButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonModule, declarations: [CdsButtonComponent], imports: [CommonModule, CdsPipesModule, CdsDirectivesModule], exports: [CdsButtonComponent] });
CdsButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonModule, imports: [CommonModule, CdsPipesModule, CdsDirectivesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsButtonComponent],
                    exports: [CdsButtonComponent],
                    imports: [CommonModule, CdsPipesModule, CdsDirectivesModule],
                }]
        }] });

/**
 * ### Description
 * A group of related buttons, where each button represents an action that is available within a particular context, such as a modal dialog.
 *
 * Note that the tab order of the buttons in the Button Group is not left-to-right (ltr), instead it is based on the order
 * in which the buttons are placed within the Button Group markup.
 */
class CdsButtonGroupComponent {
}
CdsButtonGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsButtonGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsButtonGroupComponent, selector: "cds-button-group", inputs: { stacked: "stacked" }, ngImport: i0, template: "<div class=\"cds-button-group\" [class.-stacked]=\"stacked\" #buttonGroup>\n  <ng-content></ng-content>\n</div>\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-button-group', template: "<div class=\"cds-button-group\" [class.-stacked]=\"stacked\" #buttonGroup>\n  <ng-content></ng-content>\n</div>\n" }]
        }], propDecorators: { stacked: [{
                type: Input
            }] } });

class CdsLeftAlignedButtonDirective {
    constructor(element) {
        this.element = element;
    }
    ngAfterContentInit() {
        this.element.nativeElement
            .querySelector('button')
            .classList.add('-left-aligned');
    }
}
CdsLeftAlignedButtonDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLeftAlignedButtonDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsLeftAlignedButtonDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsLeftAlignedButtonDirective, selector: "cds-button[cdsAlignLeft]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLeftAlignedButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'cds-button[cdsAlignLeft]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

class CdsButtonGroupModule {
}
CdsButtonGroupModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonGroupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsButtonGroupModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonGroupModule, declarations: [CdsButtonGroupComponent, CdsLeftAlignedButtonDirective], imports: [CommonModule, CdsPipesModule], exports: [CdsButtonGroupComponent, CdsLeftAlignedButtonDirective] });
CdsButtonGroupModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonGroupModule, imports: [CommonModule, CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsButtonGroupModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsButtonGroupComponent, CdsLeftAlignedButtonDirective],
                    exports: [CdsButtonGroupComponent, CdsLeftAlignedButtonDirective],
                    imports: [CommonModule, CdsPipesModule],
                }]
        }] });

/* eslint-disable max-classes-per-file */
class CdsCardHeaderDirective {
}
CdsCardHeaderDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardHeaderDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsCardHeaderDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsCardHeaderDirective, selector: "[cdsCardHeader]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardHeaderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsCardHeader]',
                }]
        }] });
class CdsCardBodyDirective {
}
CdsCardBodyDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardBodyDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsCardBodyDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsCardBodyDirective, selector: "[cdsCardBody]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardBodyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsCardBody]',
                }]
        }] });
class CdsCardPrefixDirective {
}
CdsCardPrefixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardPrefixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsCardPrefixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsCardPrefixDirective, selector: "[cdsCardPrefix]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardPrefixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsCardPrefix]',
                }]
        }] });
class CdsCardSuffixDirective {
}
CdsCardSuffixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardSuffixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsCardSuffixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsCardSuffixDirective, selector: "[cdsCardSuffix]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardSuffixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsCardSuffix]',
                }]
        }] });

class CdsCardComponent {
    constructor() {
        /**
         * Enables a shadow effect on hover. Defaults to `true`.
         */
        this.hasHoverShadow = true;
    }
}
CdsCardComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsCardComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsCardComponent, selector: "cds-card", inputs: { name: "name", description: "description", hasHoverShadow: "hasHoverShadow" }, queries: [{ propertyName: "cardHeader", first: true, predicate: CdsCardHeaderDirective, descendants: true }, { propertyName: "cardBody", first: true, predicate: CdsCardBodyDirective, descendants: true }, { propertyName: "cardPrefix", first: true, predicate: CdsCardPrefixDirective, descendants: true }, { propertyName: "cardSuffix", first: true, predicate: CdsCardSuffixDirective, descendants: true }], ngImport: i0, template: "<div\n  class=\"cds-card\"\n  [class.-hover-enabled]=\"hasHoverShadow\"\n  [id]=\"name + '-card' | kebabCase\"\n>\n  <!-- Prefix -->\n  <div *ngIf=\"cardPrefix\" class=\"cds-card-prefix\">\n    <ng-content select=\"[cdsCardPrefix]\"></ng-content>\n  </div>\n\n  <!-- Content -->\n  <div class=\"cds-card-content\">\n    <!-- Header -->\n    <div\n      *ngIf=\"cardHeader\"\n      class=\"cds-card-header\"\n    >\n      <ng-content select=\"[cdsCardHeader]\"></ng-content>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"cardBody\"\n      class=\"cds-card-body\"\n    >\n      <ng-content select=\"[cdsCardBody]\"></ng-content>\n    </div>\n  </div>\n\n  <!-- Suffix -->\n  <div\n    *ngIf=\"cardSuffix\"\n    class=\"cds-card-suffix\"\n  >\n    <ng-content select=\"[cdsCardSuffix]\"></ng-content>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-card', template: "<div\n  class=\"cds-card\"\n  [class.-hover-enabled]=\"hasHoverShadow\"\n  [id]=\"name + '-card' | kebabCase\"\n>\n  <!-- Prefix -->\n  <div *ngIf=\"cardPrefix\" class=\"cds-card-prefix\">\n    <ng-content select=\"[cdsCardPrefix]\"></ng-content>\n  </div>\n\n  <!-- Content -->\n  <div class=\"cds-card-content\">\n    <!-- Header -->\n    <div\n      *ngIf=\"cardHeader\"\n      class=\"cds-card-header\"\n    >\n      <ng-content select=\"[cdsCardHeader]\"></ng-content>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"cardBody\"\n      class=\"cds-card-body\"\n    >\n      <ng-content select=\"[cdsCardBody]\"></ng-content>\n    </div>\n  </div>\n\n  <!-- Suffix -->\n  <div\n    *ngIf=\"cardSuffix\"\n    class=\"cds-card-suffix\"\n  >\n    <ng-content select=\"[cdsCardSuffix]\"></ng-content>\n  </div>\n</div>\n" }]
        }], propDecorators: { name: [{
                type: Input
            }], description: [{
                type: Input
            }], hasHoverShadow: [{
                type: Input
            }], cardHeader: [{
                type: ContentChild,
                args: [CdsCardHeaderDirective]
            }], cardBody: [{
                type: ContentChild,
                args: [CdsCardBodyDirective]
            }], cardPrefix: [{
                type: ContentChild,
                args: [CdsCardPrefixDirective]
            }], cardSuffix: [{
                type: ContentChild,
                args: [CdsCardSuffixDirective]
            }] } });

class CdsCardModule {
}
CdsCardModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsCardModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsCardModule, declarations: [CdsCardComponent,
        CdsCardHeaderDirective,
        CdsCardBodyDirective,
        CdsCardPrefixDirective,
        CdsCardSuffixDirective], imports: [CommonModule, CdsPipesModule], exports: [CdsCardComponent,
        CdsCardHeaderDirective,
        CdsCardBodyDirective,
        CdsCardPrefixDirective,
        CdsCardSuffixDirective] });
CdsCardModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardModule, imports: [CommonModule, CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsCardComponent,
                        CdsCardHeaderDirective,
                        CdsCardBodyDirective,
                        CdsCardPrefixDirective,
                        CdsCardSuffixDirective,
                    ],
                    exports: [
                        CdsCardComponent,
                        CdsCardHeaderDirective,
                        CdsCardBodyDirective,
                        CdsCardPrefixDirective,
                        CdsCardSuffixDirective,
                    ],
                    imports: [CommonModule, CdsPipesModule],
                }]
        }] });

/**
 * ### Description
 * A control for binary and indeterminate binary values.
 */
class CdsCheckboxComponent {
    constructor(
    /** @internal */
    control, controlValueAccessor) {
        this.control = control;
        this.controlValueAccessor = controlValueAccessor;
        /**
         * @internal
         */
        this._display = 'contents';
        /**
         * An event containing the new state of this control.
         */
        this.change = this.controlValueAccessor.change;
        /**
         * @internal
         */
        this.registerOnChange = this.controlValueAccessor.registerOnChange;
        /**
         * @internal
         */
        this.registerOnTouched = this.controlValueAccessor.registerOnTouched;
        /**
         * @internal
         */
        this.setDisabledState = this.controlValueAccessor.setDisabledState;
        /**
         * @internal
         */
        this.writeValue = (inputValue) => {
            this.checked = inputValue;
        };
        if (this.control) {
            this.control.valueAccessor = this;
        }
        else {
            throw new Error('FormControl is missing from Checkbox. ');
        }
    }
    /**
     * @internal
     */
    get indeterminate() {
        return this.checked === null;
    }
    /**
     * @internal
     */
    get id() {
        return 'cds-checkbox-' + kebabCaseTransform(this.name);
    }
    /**
     * @internal
     */
    get ariaChecked() {
        return this.indeterminate ? 'mixed' : '' + this.checked;
    }
    /**
     * @internal
     */
    get disabled() {
        return this._disabled || this.controlValueAccessor.disabled;
    }
    /**
     * @internal
     * You can think of this as "explicitly disabled"
     */
    // eslint-disable-next-line @angular-eslint/no-input-rename
    set disabled(disabled) {
        this._disabled = disabled;
    }
    get ariaControls() {
        return (this.controlLabels || []).join(' ') || null;
    }
    /**
     * @internal
     */
    onToggleCheck(event) {
        event.stopPropagation();
        this.checked = !!!this.checked;
        this.controlValueAccessor.onTouch();
        this.controlValueAccessor.onChange(this.checked);
    }
    /**
     * @internal
     *
     * Manually set the focus of the checkbox.
     */
    focus() {
        setTimeout(() => {
            this.atom.nativeElement.focus();
            this.atom.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }
    /**
     * @internal
     *
     * Required implementation for CdsTraversableComponent and @angular/cdk/a11y/FocusableItem
     */
    getLabel() {
        return String(this.label);
    }
}
CdsCheckboxComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxComponent, deps: [{ token: i1$2.NgControl, optional: true, self: true }, { token: CdsControlValueAccessorComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsCheckboxComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsCheckboxComponent, selector: "cds-checkbox", inputs: { disabled: ["isDisabled", "disabled"], label: "label", hideLabel: "hideLabel", controlLabels: "controlLabels", name: "name", leftAlignedLabel: "leftAlignedLabel" }, outputs: { change: "change" }, host: { properties: { "style.display": "this._display" } }, providers: [
        CdsControlValueAccessorComponent,
        { provide: CdsTraversableComponent, useExisting: forwardRef(() => CdsCheckboxComponent) }
    ], viewQueries: [{ propertyName: "atom", first: true, predicate: ["atom"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"cds-checkbox-wrapper\">\n  <input\n    #atom\n    type=\"checkbox\"\n    class=\"cds-checkbox\"\n    [attr.aria-label]=\"label\"\n    [id]=\"id\"\n    [checked]=\"checked\"\n    [indeterminate]=\"indeterminate\"\n    [attr.disabled]=\"disabled || null\"\n    [attr.aria-checked]=\"ariaChecked\"\n    [attr.aria-controls]=\"ariaControls\"\n    (change)=\"onToggleCheck($event)\"\n  />\n\n  <label\n    [for]=\"id\"\n    class=\"cds-checkbox-label\"\n    [class.-hidden]=\"hideLabel\"\n    [class.-left-aligned]=\"leftAlignedLabel\"\n    [class.-right-aligned]=\"!leftAlignedLabel\"\n  >\n    <span>{{ label }}</span>\n  </label>\n</div>\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-checkbox', providers: [
                        CdsControlValueAccessorComponent,
                        { provide: CdsTraversableComponent, useExisting: forwardRef(() => CdsCheckboxComponent) }
                    ], template: "<div class=\"cds-checkbox-wrapper\">\n  <input\n    #atom\n    type=\"checkbox\"\n    class=\"cds-checkbox\"\n    [attr.aria-label]=\"label\"\n    [id]=\"id\"\n    [checked]=\"checked\"\n    [indeterminate]=\"indeterminate\"\n    [attr.disabled]=\"disabled || null\"\n    [attr.aria-checked]=\"ariaChecked\"\n    [attr.aria-controls]=\"ariaControls\"\n    (change)=\"onToggleCheck($event)\"\n  />\n\n  <label\n    [for]=\"id\"\n    class=\"cds-checkbox-label\"\n    [class.-hidden]=\"hideLabel\"\n    [class.-left-aligned]=\"leftAlignedLabel\"\n    [class.-right-aligned]=\"!leftAlignedLabel\"\n  >\n    <span>{{ label }}</span>\n  </label>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: CdsControlValueAccessorComponent }]; }, propDecorators: { _display: [{
                type: HostBinding,
                args: ['style.display']
            }], disabled: [{
                type: Input,
                args: ['isDisabled']
            }], label: [{
                type: Input
            }], hideLabel: [{
                type: Input
            }], controlLabels: [{
                type: Input
            }], name: [{
                type: Input
            }], leftAlignedLabel: [{
                type: Input
            }], change: [{
                type: Output
            }], atom: [{
                type: ViewChild,
                args: ['atom', { static: true }]
            }] } });

class CdsCheckboxModule {
}
CdsCheckboxModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsCheckboxModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxModule, declarations: [CdsCheckboxComponent], imports: [CdsPipesModule, CommonModule], exports: [CdsCheckboxComponent] });
CdsCheckboxModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxModule, imports: [CdsPipesModule, CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsCheckboxComponent],
                    exports: [CdsCheckboxComponent],
                    imports: [CdsPipesModule, CommonModule],
                }]
        }] });

/**
 * @deprecated
 * ### Description
 * Provides a label for *-Field Components (e.g. FormField).
 */
class CdsLabelComponent {
    constructor() {
        /**
         * The weight of the Label font.
         */
        this.fontWeight = 'regular';
        /**
         * The alignment of the label text within its container.
         */
        this.align = 'left';
    }
}
CdsLabelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsLabelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsLabelComponent, selector: "cds-label", inputs: { label: "label", for: "for", fontWeight: "fontWeight", align: "align" }, ngImport: i0, template: "<label\n  class=\"cds-label\"\n  [id]=\"'cds-label-' + label | kebabCase\"\n  [class.-regular]=\"!fontWeight || fontWeight === 'regular'\"\n  [class.-medium]=\"fontWeight === 'medium'\"\n  [class.-left-aligned]=\"!align || align === 'left'\"\n  [class.-right-aligned]=\"align === 'right'\"\n  [for]=\"for | kebabCase\"\n>\n  {{ label }}\n</label>\n", dependencies: [{ kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLabelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-label', template: "<label\n  class=\"cds-label\"\n  [id]=\"'cds-label-' + label | kebabCase\"\n  [class.-regular]=\"!fontWeight || fontWeight === 'regular'\"\n  [class.-medium]=\"fontWeight === 'medium'\"\n  [class.-left-aligned]=\"!align || align === 'left'\"\n  [class.-right-aligned]=\"align === 'right'\"\n  [for]=\"for | kebabCase\"\n>\n  {{ label }}\n</label>\n" }]
        }], propDecorators: { label: [{
                type: Input
            }], for: [{
                type: Input
            }], fontWeight: [{
                type: Input
            }], align: [{
                type: Input
            }] } });

/**
 * ### Description
 * Provides a label for *-Field Components (e.g. FormField).
 */
class CdsFormLabelDirective {
    constructor() {
        /**
         * The weight of the Label font.
         */
        this.fontWeight = 'regular';
        /**
         * The alignment of the label text within its container.
         */
        this.align = 'left';
        /**
         * @internal
         */
        this.klass = true;
    }
    /**
     * @internal
     */
    get id() {
        return kebabCaseTransform('cds-label-' + this.label);
    }
    /**
     * @internal
     */
    get _fontWeight() {
        return !this.fontWeight || this.fontWeight === 'regular';
    }
    /**
     * @internal
     */
    get medium() {
        return this.fontWeight === 'medium';
    }
    /**
     * @internal
     */
    get leftAligned() {
        return !this.align || this.align === 'left';
    }
    /**
     * @internal
     */
    get rightAligned() {
        return this.align === 'right';
    }
}
CdsFormLabelDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFormLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsFormLabelDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsFormLabelDirective, selector: "[cdsFormLabel]", inputs: { label: "label", for: "for", fontWeight: "fontWeight", align: "align" }, host: { properties: { "class.cds-label": "this.klass", "id": "this.id", "class.-regular": "this._fontWeight", "class.-medium": "this.medium", "class.-left-aligned": "this.leftAligned", "class.-right-aligned": "this.rightAligned" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFormLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsFormLabel]',
                }]
        }], propDecorators: { label: [{
                type: Input
            }], for: [{
                type: Input
            }], fontWeight: [{
                type: Input
            }], align: [{
                type: Input
            }], klass: [{
                type: HostBinding,
                args: ['class.cds-label']
            }], id: [{
                type: HostBinding,
                args: ['id']
            }], _fontWeight: [{
                type: HostBinding,
                args: ['class.-regular']
            }], medium: [{
                type: HostBinding,
                args: ['class.-medium']
            }], leftAligned: [{
                type: HostBinding,
                args: ['class.-left-aligned']
            }], rightAligned: [{
                type: HostBinding,
                args: ['class.-right-aligned']
            }] } });

class CdsLabelModule {
}
CdsLabelModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLabelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsLabelModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsLabelModule, declarations: [CdsLabelComponent, CdsFormLabelDirective], imports: [CommonModule, CdsPipesModule], exports: [CdsLabelComponent, CdsFormLabelDirective] });
CdsLabelModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLabelModule, imports: [CommonModule, CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLabelModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsLabelComponent, CdsFormLabelDirective],
                    exports: [CdsLabelComponent, CdsFormLabelDirective],
                    imports: [CommonModule, CdsPipesModule],
                }]
        }] });

class CdsCheckboxGroupService {
    constructor() {
        this.checkboxGroups = [];
    }
    registerCheckboxGroup(component) {
        this.checkboxGroups.push(component);
    }
    unregisterCheckboxGroup(component) {
        this.checkboxGroups.splice(this.checkboxGroups.indexOf(component), 1);
    }
    getCheckboxGroup(componentId) {
        if (!componentId) {
            return null;
        }
        return this.checkboxGroups.find(directive => directive.component.id === componentId).component;
    }
    getCheckboxDirective(component) {
        return this.checkboxGroups.find(group => group.component === component);
    }
    ngOnDestroy() {
        this.checkboxGroups = [];
    }
}
CdsCheckboxGroupService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsCheckboxGroupService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * ### Description
 * A Checkbox Group provides a high-level means of controlling child checkboxes and checkbox groups.
 * The Checkbox Group itself does not have a `FormControl`, however, when there is a change to
 * its state or the state of any of its children, it emits an event describing its current state:
 *
 *
 * `indeterminate` when its' children have mixed boolean values,
 * `true` when all of its children are checked,
 * `false` when all of its children are unchecked.
 */
class CdsCheckboxGroupComponent {
    /**
     * @internal
     */
    constructor(service) {
        this.service = service;
        /**
         * @internal
         */
        this.groupState = new UntypedFormControl(false);
        /**
         * @internal
         */
        this.children = [];
        /**
         * @internal
         */
        this.$children = new BehaviorSubject([]);
        /**
         * @internal
         */
        this.accordionExpanded = true;
        /**
         * When `true`, the master checkbox control for this Checkbox Group is hidden.
         */
        this.labelOnly = false;
        /**
         * @internal
         */
        this.collapsible = false;
        /**
         * An event containing the current state of the Checkbox Group.
         */
        // eslint-disable-next-line @angular-eslint/no-output-native
        this.change = new EventEmitter();
    }
    /**
     * Disables all children of this Checkbox Group.
     */
    // You can think of this as "explicitly disabled"
    set disabled(disabled) {
        this._disabled = disabled;
        if (this.children.length > 0) {
            this.children.forEach(child => child.component.disabled = disabled);
        }
    }
    /**
     * @internal
     * Checkbox group can still be disabled, even if not done explicity using the "_disabled" property,
     * if all of its children are disabled.
     */
    get disabled() {
        // If explicitly disabled or if all children are disabled
        return this._disabled || (this.children.length > 0 && this.children.every(child => !!child.component.disabled));
    }
    /**
     * @internal
     */
    get hasDisabledChildren() {
        return (this.children || []).reduce((overallStatus, child) => {
            const disabledChild = child.component.children
                ? child.component.hasDisabledChildren
                : child.component.disabled;
            return overallStatus || disabledChild;
        }, false);
    }
    /**
     * @internal
     */
    get ariaControlLabels() {
        return this.checkboxes.map((checkbox) => checkbox.id);
    }
    /**
     * @internal
     */
    registerChild(child) {
        this.children.push(child);
        this.$children.next(this.children);
        this.groupState.setValue(this.getCurrentState());
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        setTimeout(() => this.groupState.setValue(this.getCurrentState()));
    }
    /**
     * @internal
     */
    unregisterChild(child) {
        const index = this.children.indexOf(child);
        this.children.splice(index, 1);
        this.$children.next(this.children);
        this.groupState.setValue(this.getCurrentState());
    }
    /**
     * @internal
     * Get the checked state of all descendants
     */
    getCurrentState() {
        const checked = this.children.every((child) => child.checked === true);
        const unchecked = this.children.every((child) => child.checked === false);
        // if fully checked or fully unchecked, the value of "checked" will reflect the current state
        // If not, return "null" for indeterminate
        return checked || unchecked ? checked : null;
    }
    /**
     * @internal
     * @param value: the new value of the checkbox that controls this checkbox group
     *
     * Updates the checked state when the groupStateCheckbox is clicked
     */
    onChange(value) {
        // if some checkboxes are disabled, the current state is indeterminate,
        if (this.hasDisabledChildren && this.getCurrentState() === null) {
            // if all enabled checkboxes are checked, the next state should be false
            // else the next state should be true
            value = !this.children.filter(child => !child.component.disabled).every((child) => child.checked);
        }
        this.updateChildren(value);
        this.service.getCheckboxDirective(this).notifyParent();
    }
    /**
     * @internal
     * Updates the children of this checkbox group
     */
    updateChildren(value) {
        this.children.forEach((child) => {
            child.update(value);
        });
        this.groupState.setValue(this.getCurrentState());
    }
    onExpandedStateChanged(expanded) {
        this.accordionExpanded = expanded;
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$children.complete();
    }
}
CdsCheckboxGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupComponent, deps: [{ token: CdsCheckboxGroupService }], target: i0.ɵɵFactoryTarget.Component });
CdsCheckboxGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsCheckboxGroupComponent, selector: "cds-checkbox-group", inputs: { id: "id", name: "name", labelOnly: "labelOnly", label: "label", inline: "inline", disabled: "disabled", collapsible: "collapsible" }, outputs: { change: "change" }, queries: [{ propertyName: "checkboxes", predicate: CdsCheckboxComponent, descendants: true }], viewQueries: [{ propertyName: "panel", first: true, predicate: ["panel"], descendants: true, static: true }, { propertyName: "groupStateCheckbox", first: true, predicate: CdsCheckboxComponent, descendants: true }], ngImport: i0, template: "<div\n  class=\"cds-checkbox-group\"\n  [class.-inline]=\"inline\"\n  [class.-collapsible]=\"collapsible\"\n  [class.cds-accordion]=\"collapsible\"\n  [class.-open]=\"accordionExpanded\"\n  role=\"group\"\n  aria-labelledby=\"group-label-01\"\n>\n  <div class=\"cds-checkbox-group-header\">\n    <label *ngIf=\"labelOnly\" [id]=\"label | kebabCase\" cdsFormLabel>{{ label }}</label>\n\n    <cds-checkbox\n      *ngIf=\"!labelOnly\"\n      [name]=\"name + '-' + label + '-checkbox-group-header'\"\n      [label]=\"label\"\n      [formControl]=\"groupState\"\n      [controlLabels]=\"ariaControlLabels\"\n      (change)=\"onChange($event)\"\n      [isDisabled]=\"disabled\"\n      cdsCheckboxGroupMaster\n    >\n    </cds-checkbox>\n\n    <ng-container *ngIf=\"collapsible\">\n      <cds-control-block\n        size=\"small\"\n        class=\"cds-checkbox-group-toggle\"\n        cdsElementRef\n        #toggle=\"cdsElementRef\"\n      >\n        <i class=\"material-icons\" [class.-collapse]=\"accordionExpanded\">expand_more</i>\n      </cds-control-block>\n\n      <ng-container\n        cdsAccordion\n        buttonTrigger\n        [anchor]=\"toggle.nativeElement\"\n        [panel]=\"panel.nativeElement\"\n        [expanded]=\"true\"\n        (change)=\"onExpandedStateChanged($event)\"\n      >\n      </ng-container>\n    </ng-container>\n  </div>\n\n  <ul\n    class=\"cds-checkboxes\"\n    [class.cds-accordion-content]=\"collapsible\"\n    cdsElementRef\n    #panel=\"cdsElementRef\"\n  >\n    <li *ngFor=\"let child of ($children | async)\">\n      <ng-template [ngTemplateOutlet]=\"child.template\"></ng-template>\n    </li>\n  </ul>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: CdsCheckboxComponent, selector: "cds-checkbox", inputs: ["isDisabled", "label", "hideLabel", "controlLabels", "name", "leftAlignedLabel"], outputs: ["change"] }, { kind: "directive", type: CdsFormLabelDirective, selector: "[cdsFormLabel]", inputs: ["label", "for", "fontWeight", "align"] }, { kind: "directive", type: CdsAccordionDirective, selector: "[cdsAccordion]", inputs: ["anchor", "panel", "expanded"], outputs: ["change"], exportAs: ["cdsAccordion"] }, { kind: "directive", type: CdsDropdownToggleButtonDirective, selector: "[buttonTrigger]", outputs: ["toggleChange"] }, { kind: "directive", type: CdsElementRefDirective, selector: "[cdsElementRef]", exportAs: ["cdsElementRef"] }, { kind: "component", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: ["name", "size", "disabled", "tabindex", "active", "attributes"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-checkbox-group', template: "<div\n  class=\"cds-checkbox-group\"\n  [class.-inline]=\"inline\"\n  [class.-collapsible]=\"collapsible\"\n  [class.cds-accordion]=\"collapsible\"\n  [class.-open]=\"accordionExpanded\"\n  role=\"group\"\n  aria-labelledby=\"group-label-01\"\n>\n  <div class=\"cds-checkbox-group-header\">\n    <label *ngIf=\"labelOnly\" [id]=\"label | kebabCase\" cdsFormLabel>{{ label }}</label>\n\n    <cds-checkbox\n      *ngIf=\"!labelOnly\"\n      [name]=\"name + '-' + label + '-checkbox-group-header'\"\n      [label]=\"label\"\n      [formControl]=\"groupState\"\n      [controlLabels]=\"ariaControlLabels\"\n      (change)=\"onChange($event)\"\n      [isDisabled]=\"disabled\"\n      cdsCheckboxGroupMaster\n    >\n    </cds-checkbox>\n\n    <ng-container *ngIf=\"collapsible\">\n      <cds-control-block\n        size=\"small\"\n        class=\"cds-checkbox-group-toggle\"\n        cdsElementRef\n        #toggle=\"cdsElementRef\"\n      >\n        <i class=\"material-icons\" [class.-collapse]=\"accordionExpanded\">expand_more</i>\n      </cds-control-block>\n\n      <ng-container\n        cdsAccordion\n        buttonTrigger\n        [anchor]=\"toggle.nativeElement\"\n        [panel]=\"panel.nativeElement\"\n        [expanded]=\"true\"\n        (change)=\"onExpandedStateChanged($event)\"\n      >\n      </ng-container>\n    </ng-container>\n  </div>\n\n  <ul\n    class=\"cds-checkboxes\"\n    [class.cds-accordion-content]=\"collapsible\"\n    cdsElementRef\n    #panel=\"cdsElementRef\"\n  >\n    <li *ngFor=\"let child of ($children | async)\">\n      <ng-template [ngTemplateOutlet]=\"child.template\"></ng-template>\n    </li>\n  </ul>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: CdsCheckboxGroupService }]; }, propDecorators: { panel: [{
                type: ViewChild,
                args: ['panel', { static: true }]
            }], id: [{
                type: Input
            }], name: [{
                type: Input
            }], labelOnly: [{
                type: Input
            }], label: [{
                type: Input
            }], inline: [{
                type: Input
            }], disabled: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], change: [{
                type: Output
            }], groupStateCheckbox: [{
                type: ViewChild,
                args: [CdsCheckboxComponent]
            }], checkboxes: [{
                type: ContentChildren,
                args: [CdsCheckboxComponent, { descendants: true }]
            }] } });

class CdsCheckboxGroupSubGroupDirective {
    constructor(component, injectedParent, service, wrappedComponent) {
        this.component = component;
        this.injectedParent = injectedParent;
        this.service = service;
        this.wrappedComponent = wrappedComponent;
        this.$destroyed = new Subject();
        this.change = this.component.change.asObservable();
    }
    get checked() {
        const checked = this.component.children.every((child) => child.checked === true);
        const unchecked = this.component.children.every((child) => child.checked === false);
        if (checked) {
            return checked; // true
        }
        else if (unchecked) {
            return checked; // false
        }
        else {
            return null;
        }
    }
    get template() {
        return this.wrappedComponent.template;
    }
    ngOnInit() {
        if (this.service && this.service.getCheckboxGroup(this.parentId)) {
            this.actualParent = this.service.getCheckboxGroup(this.parentId);
        }
        else {
            this.actualParent = this.injectedParent;
        }
        if (this.actualParent) {
            this.actualParent.registerChild(this);
        }
        this.service.registerCheckboxGroup(this);
        this.component.disabled = this.component.disabled || (this.actualParent && this.actualParent.disabled);
    }
    update(value) {
        this.component.updateChildren(value);
    }
    notifyParent() {
        this.component.groupState.setValue(this.component.getCurrentState());
        if (this.actualParent) {
            const parent = this.service.getCheckboxDirective(this.actualParent);
            parent.notifyParent();
            return;
        }
        this.component.change.emit(this.component.groupState.value);
    }
    updateParent() {
        if (this.actualParent) {
            const parent = this.service.getCheckboxDirective(this.actualParent);
            parent.updateParent();
        }
        this.component.groupState.setValue(this.component.getCurrentState());
    }
    ngOnDestroy() {
        this.service.unregisterCheckboxGroup(this);
        if (this.actualParent) {
            this.actualParent.unregisterChild(this);
        }
        this.wrappedComponent.destroy();
        this.$destroyed.next();
        this.$destroyed.complete();
    }
}
CdsCheckboxGroupSubGroupDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupSubGroupDirective, deps: [{ token: CdsCheckboxGroupComponent, self: true }, { token: CdsCheckboxGroupComponent, optional: true, skipSelf: true }, { token: CdsCheckboxGroupService }, { token: CdsWrappedComponent }], target: i0.ɵɵFactoryTarget.Directive });
CdsCheckboxGroupSubGroupDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsCheckboxGroupSubGroupDirective, selector: "cds-checkbox-group", inputs: { parentId: "parentId" }, providers: [CdsWrappedComponent], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupSubGroupDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'cds-checkbox-group',
                    providers: [CdsWrappedComponent],
                }]
        }], ctorParameters: function () { return [{ type: CdsCheckboxGroupComponent, decorators: [{
                    type: Self
                }] }, { type: CdsCheckboxGroupComponent, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }, { type: CdsCheckboxGroupService }, { type: CdsWrappedComponent }]; }, propDecorators: { parentId: [{
                type: Input
            }] } });

class CdsCheckboxGroupCheckboxDirective {
    constructor(component, injectedParent, service, wrappedComponent) {
        this.component = component;
        this.injectedParent = injectedParent;
        this.service = service;
        this.wrappedComponent = wrappedComponent;
        this.$destroyed = new Subject();
        this.change = this.component.change.asObservable();
        this.component.writeValue = this.updateParent(this.component.writeValue);
    }
    get checked() {
        return this.component.checked;
    }
    get template() {
        return this.wrappedComponent.template;
    }
    ngOnInit() {
        if (this.service && this.service.getCheckboxGroup(this.parentId)) {
            this.actualParent = this.service.getCheckboxGroup(this.parentId);
        }
        else {
            this.actualParent = this.injectedParent;
        }
        if (this.actualParent) {
            this.actualParent.registerChild(this);
        }
        this.component.disabled = this.actualParent && this.actualParent.disabled;
        this.component.change
            .pipe(takeUntil(this.$destroyed))
            .subscribe(() => {
            this.notifyParent();
        });
    }
    notifyParent() {
        if (this.actualParent) {
            const parent = this.service.getCheckboxDirective(this.actualParent);
            parent.notifyParent();
            return;
        }
    }
    updateParent(writeValue) {
        return (value) => {
            writeValue(value);
            if (this.actualParent) {
                const parent = this.service.getCheckboxDirective(this.actualParent);
                parent.updateParent();
            }
        };
    }
    update(value) {
        if (!this.component.disabled) {
            this.component.control.control.setValue(value, { emitEvents: false });
        }
    }
    ngOnDestroy() {
        if (this.actualParent) {
            this.actualParent.unregisterChild(this);
        }
        this.wrappedComponent.destroy();
        this.$destroyed.next();
        this.$destroyed.complete();
    }
}
CdsCheckboxGroupCheckboxDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupCheckboxDirective, deps: [{ token: CdsCheckboxComponent, self: true }, { token: CdsCheckboxGroupComponent, optional: true, skipSelf: true }, { token: CdsCheckboxGroupService }, { token: CdsWrappedComponent }], target: i0.ɵɵFactoryTarget.Directive });
CdsCheckboxGroupCheckboxDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsCheckboxGroupCheckboxDirective, selector: "cds-checkbox:not([cdsCheckboxGroupMaster])", inputs: { parentId: "parentId" }, providers: [CdsWrappedComponent], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupCheckboxDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'cds-checkbox:not([cdsCheckboxGroupMaster])',
                    providers: [CdsWrappedComponent],
                }]
        }], ctorParameters: function () { return [{ type: CdsCheckboxComponent, decorators: [{
                    type: Self
                }] }, { type: CdsCheckboxGroupComponent, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }, { type: CdsCheckboxGroupService }, { type: CdsWrappedComponent }]; }, propDecorators: { parentId: [{
                type: Input
            }] } });

class CdsCheckboxGroupModule {
}
CdsCheckboxGroupModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsCheckboxGroupModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupModule, declarations: [CdsCheckboxGroupComponent,
        CdsCheckboxGroupCheckboxDirective,
        CdsCheckboxGroupSubGroupDirective], imports: [FormsModule,
        ReactiveFormsModule,
        CdsCheckboxModule,
        CdsLabelModule,
        CdsHostWrapperModule,
        CdsDirectivesModule,
        CdsControlBlockModule,
        CdsPipesModule,
        CommonModule], exports: [CdsCheckboxGroupComponent,
        CdsCheckboxGroupCheckboxDirective,
        CdsCheckboxGroupSubGroupDirective] });
CdsCheckboxGroupModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupModule, imports: [FormsModule,
        ReactiveFormsModule,
        CdsCheckboxModule,
        CdsLabelModule,
        CdsHostWrapperModule,
        CdsDirectivesModule,
        CdsControlBlockModule,
        CdsPipesModule,
        CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCheckboxGroupModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsCheckboxGroupComponent,
                        CdsCheckboxGroupCheckboxDirective,
                        CdsCheckboxGroupSubGroupDirective
                    ],
                    exports: [
                        CdsCheckboxGroupComponent,
                        CdsCheckboxGroupCheckboxDirective,
                        CdsCheckboxGroupSubGroupDirective,
                    ],
                    imports: [
                        FormsModule,
                        ReactiveFormsModule,
                        CdsCheckboxModule,
                        CdsLabelModule,
                        CdsHostWrapperModule,
                        CdsDirectivesModule,
                        CdsControlBlockModule,
                        CdsPipesModule,
                        CommonModule
                    ],
                }]
        }] });

class CdsRadioButtonGroupLabelDirective {
    constructor() {
        this.klass = true;
    }
}
CdsRadioButtonGroupLabelDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsRadioButtonGroupLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsRadioButtonGroupLabelDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsRadioButtonGroupLabelDirective, selector: "[cdsRadioButtonGroupLabel]", host: { properties: { "class.cds-radio-button-group-label": "this.klass" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsRadioButtonGroupLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsRadioButtonGroupLabel]'
                }]
        }], propDecorators: { klass: [{
                type: HostBinding,
                args: ['class.cds-radio-button-group-label']
            }] } });

// @hack: getting around circular dependencies
class CdsRadioButtonType {
    constructor() {
        /**
         * @internal
         * Emits an event containing the key of the selected Radio Button.
         */
        this.change = new Subject();
    }
}

/**
 * ### Description
 * A container for Radio Buttons. Radio Buttons should be used within a Radio Button group.
 *
 * You must pass a unique key into each Radio Button component that is a child of the Radio Button Group. When a radio button is selected,
 * the value of the FormControl in the Radio Button Group will be updated to reflect the key of the selected Radio Button.
 */
class CdsRadioButtonGroupComponent {
    constructor(
    /**  @internal */
    control, controlValueAccessor) {
        this.control = control;
        this.controlValueAccessor = controlValueAccessor;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        /**
         * An event containing the new state of this control.
         */
        this.change = this.controlValueAccessor.change;
        /**
         * @internal
         */
        this.registerOnChange = this.controlValueAccessor.registerOnChange;
        /**
         * @internal
         */
        this.registerOnTouched = this.controlValueAccessor.registerOnTouched;
        /**
         * @internal
         */
        this.setDisabledState = this.controlValueAccessor.setDisabledState;
        if (this.control) {
            this.control.valueAccessor = this;
        }
        else {
            throw new Error('FormControl is missing from Radio Button Group.');
        }
    }
    /**
     * @internal
     */
    get disabled() {
        return this.controlValueAccessor.disabled;
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        this.radioButtons.changes
            .pipe(takeUntil(forkJoin([this.$cancelSubscriptions])))
            .subscribe(() => this.createSubscriptions());
        this.createSubscriptions();
    }
    /**
     * @internal
     */
    createSubscriptions() {
        this.$cancelSubscriptions.next();
        this.radioButtons.forEach(radioButton => {
            radioButton.change
                .pipe(takeUntil(this.$cancelSubscriptions))
                .subscribe(() => {
                this.controlValueAccessor.onTouch();
                this.reset(radioButton);
                this.controlValueAccessor.onChange(radioButton.key);
            });
        });
        this.select();
    }
    /**
     * @internal
     */
    select() {
        const selected = this.radioButtons.toArray().find(radioButton => radioButton.key === this.selectedKey);
        this.reset(selected);
        if (selected) {
            selected.checked = true;
            selected.changeDetector.detectChanges();
        }
    }
    /**
     * @internal
     */
    reset(selected) {
        this.radioButtons.filter(radioButton => radioButton !== selected)
            .forEach(radioButton => {
            radioButton.checked = false;
            radioButton.changeDetector.detectChanges();
        });
    }
    /**
     * @internal
     */
    writeValue(key) {
        this.selectedKey = key;
        if (this.radioButtons) {
            this.select();
        }
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsRadioButtonGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsRadioButtonGroupComponent, deps: [{ token: i1$2.NgControl, optional: true, self: true }, { token: CdsControlValueAccessorComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsRadioButtonGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsRadioButtonGroupComponent, selector: "cds-radio-button-group", inputs: { name: "name", vertical: "vertical" }, outputs: { change: "change" }, providers: [CdsControlValueAccessorComponent], queries: [{ propertyName: "radioButtons", predicate: CdsRadioButtonType, descendants: true }], ngImport: i0, template: "<div\n  role=\"radiogroup\"\n  class=\"cds-radio-button-group\"\n  [class.-vertical]=\"vertical\"\n  [attr.aria-labelledby]=\"'cds-radio-button-group-' + (name | kebabCase)\"\n>\n  <ng-content></ng-content>\n</div>\n", dependencies: [{ kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsRadioButtonGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-radio-button-group', providers: [CdsControlValueAccessorComponent], template: "<div\n  role=\"radiogroup\"\n  class=\"cds-radio-button-group\"\n  [class.-vertical]=\"vertical\"\n  [attr.aria-labelledby]=\"'cds-radio-button-group-' + (name | kebabCase)\"\n>\n  <ng-content></ng-content>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: CdsControlValueAccessorComponent }]; }, propDecorators: { name: [{
                type: Input
            }], vertical: [{
                type: Input
            }], change: [{
                type: Output
            }], radioButtons: [{
                type: ContentChildren,
                args: [CdsRadioButtonType, { descendants: true }]
            }] } });

/**
 * ### Description
 * A control for binary values.
 */
class CdsRadioButtonComponent extends CdsRadioButtonType {
    constructor(
    /** @internal */ wrappedRadioButton, 
    /** @internal */ group, 
    /** @internal */ changeDetector) {
        super();
        this.wrappedRadioButton = wrappedRadioButton;
        this.group = group;
        this.changeDetector = changeDetector;
        if (!group) {
            console.warn('Radio Button component should be placed within a Radio Button Group component.');
        }
    }
    /**
     * @internal
     */
    get id() {
        return 'cds-radio-button-' + kebabCaseTransform(this.name);
    }
    /**
     * @internal
     */
    get template() {
        return this.wrappedRadioButton.template;
    }
    /**
     * @internal
     */
    get isDisabled() {
        return this.disabled || (this.group && this.group.control.disabled);
    }
    /**
     * @internal
     */
    onToggleCheck(event) {
        event.stopPropagation();
        if (!this.checked) {
            this.checked = true;
            this.change.next(this.checked);
        }
    }
    /**
     * @internal
     *
     * Required implementation for CdsTraversableComponent and @angular/cdk/a11y/FocusableItem
     */
    getLabel() {
        return String(this.label);
    }
    /**
     * @internal
     *
     * Manually set the focus of the radio button.
     */
    focus() {
        if (this.atom) {
            setTimeout(() => {
                this.atom.nativeElement.focus();
                this.atom.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.change.complete();
    }
}
CdsRadioButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsRadioButtonComponent, deps: [{ token: CdsWrappedComponent }, { token: CdsRadioButtonGroupComponent, optional: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsRadioButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsRadioButtonComponent, selector: "cds-radio-button", inputs: { label: "label", key: "key", name: "name", leftAlignedLabel: "leftAlignedLabel", disabled: "disabled" }, providers: [
        CdsWrappedComponent,
        {
            provide: CdsRadioButtonType,
            useExisting: forwardRef(() => CdsRadioButtonComponent),
        },
    ], viewQueries: [{ propertyName: "atom", first: true, predicate: ["atom"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"cds-radio-button-wrapper\">\n  <input\n    #atom\n    type=\"radio\"\n    class=\"cds-radio-button\"\n    [id]=\"id\"\n    [checked]=\"checked\"\n    [attr.aria-checked]=\"checked\"\n    [attr.disabled]=\"isDisabled || null\"\n    (change)=\"onToggleCheck($event)\"\n  />\n\n  <label\n    [for]=\"id\"\n    class=\"cds-radio-button-label\"\n    [class.-right-aligned]=\"!leftAlignedLabel\"\n    [class.-left-aligned]=\"leftAlignedLabel\"\n  >\n    <svg class=\"cds-radio-button-svg\">\n      <circle\n        class=\"cds-radio-button-circle\"\n        cx=\"9\"\n        cy=\"9\"\n        r=\"8\"\n      ></circle>\n      <circle\n        class=\"cds-radio-button-check\"\n        cx=\"9\"\n        cy=\"9\"\n        r=\"4\"\n      ></circle>\n    </svg>\n    <span> {{ label }} </span>\n  </label>\n</div>\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsRadioButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-radio-button', providers: [
                        CdsWrappedComponent,
                        {
                            provide: CdsRadioButtonType,
                            useExisting: forwardRef(() => CdsRadioButtonComponent),
                        },
                    ], template: "<div class=\"cds-radio-button-wrapper\">\n  <input\n    #atom\n    type=\"radio\"\n    class=\"cds-radio-button\"\n    [id]=\"id\"\n    [checked]=\"checked\"\n    [attr.aria-checked]=\"checked\"\n    [attr.disabled]=\"isDisabled || null\"\n    (change)=\"onToggleCheck($event)\"\n  />\n\n  <label\n    [for]=\"id\"\n    class=\"cds-radio-button-label\"\n    [class.-right-aligned]=\"!leftAlignedLabel\"\n    [class.-left-aligned]=\"leftAlignedLabel\"\n  >\n    <svg class=\"cds-radio-button-svg\">\n      <circle\n        class=\"cds-radio-button-circle\"\n        cx=\"9\"\n        cy=\"9\"\n        r=\"8\"\n      ></circle>\n      <circle\n        class=\"cds-radio-button-check\"\n        cx=\"9\"\n        cy=\"9\"\n        r=\"4\"\n      ></circle>\n    </svg>\n    <span> {{ label }} </span>\n  </label>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: CdsWrappedComponent }, { type: CdsRadioButtonGroupComponent, decorators: [{
                    type: Optional
                }] }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { label: [{
                type: Input
            }], key: [{
                type: Input
            }], name: [{
                type: Input
            }], leftAlignedLabel: [{
                type: Input
            }], disabled: [{
                type: Input
            }], atom: [{
                type: ViewChild,
                args: ['atom', { static: true }]
            }] } });

class CdsRadioButtonModule {
}
CdsRadioButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsRadioButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsRadioButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsRadioButtonModule, declarations: [CdsRadioButtonComponent, CdsRadioButtonGroupComponent, CdsRadioButtonGroupLabelDirective], imports: [CommonModule, CdsHostWrapperModule, CdsPipesModule], exports: [CdsRadioButtonComponent, CdsRadioButtonGroupComponent, CdsRadioButtonGroupLabelDirective] });
CdsRadioButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsRadioButtonModule, imports: [CommonModule, CdsHostWrapperModule, CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsRadioButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsRadioButtonComponent, CdsRadioButtonGroupComponent, CdsRadioButtonGroupLabelDirective],
                    exports: [CdsRadioButtonComponent, CdsRadioButtonGroupComponent, CdsRadioButtonGroupLabelDirective],
                    imports: [CommonModule, CdsHostWrapperModule, CdsPipesModule]
                }]
        }] });

/**
 * @internal
 * Abstract class for a directive that manages a list of focusable items,
 * where each item is a tuple containing an HTMLElement paired with
 * a traversable component (CdsTraversableComponent).
 */
class CdsFocusableListPresenter {
    constructor() {
        this.$cancelSubscriptions = new Subject();
    }
    watchDirective(items) {
        this.watch(items.map((item) => ({
            element: item.element.nativeElement,
            component: item.component,
        })));
    }
    /**
     * @param items: Array<{ element: HTMLElement, component: CdsTraversableComponent }>
     * Detects UI events on a CdsTraversableComponent by watching its containing element, and sets the active/focus state of an element
     * within the CdsTraversableComponent.
     */
    watch(items, options) {
        const { wrap = true, typeahead = true, horizontalOrientation, focusOnClick = true } = options || {};
        this.unwatch();
        this.$cancelSubscriptions = new Subject();
        const targets = items.map((item) => item.component);
        this.keyManager = new FocusKeyManager(targets);
        if (wrap) {
            this.keyManager = this.keyManager.withWrap();
        }
        if (typeahead) {
            this.keyManager = this.keyManager.withTypeAhead();
        }
        if (horizontalOrientation) {
            this.keyManager = this.keyManager
                .withHorizontalOrientation(horizontalOrientation)
                .withVerticalOrientation(false);
        }
        merge(...items.map((item) => fromEvent(item.element, 'click').pipe(takeUntil(this.$cancelSubscriptions), map(() => item.component)))).subscribe((component) => {
            if (!focusOnClick) {
                this.setActiveItem(component);
                return;
            }
            this.setAndFocusActiveItem(component);
        });
        merge(...items.map((item) => fromEvent(item.element, 'keydown').pipe(takeUntil(this.$cancelSubscriptions), filter((event) => this.isNavigationKey(event.key))))).subscribe((event) => this.onKeydown(event));
    }
    isNavigationKey(key) {
        return (key === 'Tab' ||
            key === 'Escape' ||
            key === 'Up' ||
            key === 'ArrowUp' ||
            key === 'Down' ||
            key === 'ArrowDown' ||
            key === 'Left' ||
            key === 'ArrowLeft' ||
            key === 'Right' ||
            key === 'ArrowRight');
    }
    onKeydown(event) {
        if (event.key === 'Tab' || event.key === 'Escape') {
            return;
        }
        event.stopPropagation();
        this.keyManager.onKeydown(event);
    }
    setActiveItem(item) {
        if (item) {
            this.keyManager.updateActiveItem(item);
        }
        else {
            this.keyManager.updateActiveItem(-1);
        }
    }
    setAndFocusActiveItem(item) {
        if (item) {
            this.keyManager.setActiveItem(item);
            return;
        }
        this.keyManager.setActiveItem(0);
    }
    getActiveItem() {
        return this.keyManager.activeItem;
    }
    getActiveItemIndex() {
        return this.keyManager.activeItemIndex;
    }
    unwatch() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
    destroy() {
        this.unwatch();
    }
}
CdsFocusableListPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFocusableListPresenter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsFocusableListPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFocusableListPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFocusableListPresenter, decorators: [{
            type: Injectable
        }] });

const DropdownTypeaheadMatchFn = (value = '', options = []) => {
    if (options.length <= 0) {
        return null;
    }
    return options.find((item) => String(item.label || '')
        .toLowerCase()
        .startsWith(value.toLowerCase()));
};
const DropdownTypeaheadMatchAllFn = (value = '', options = []) => {
    if (options.length <= 0) {
        return [];
    }
    return options.filter((item) => String(item.label || '')
        .toLowerCase()
        .startsWith(value.toLowerCase()));
};

class TypeaheadPresenter {
    constructor() {
        this.$reset = new Subject();
        this.chars = [];
        this.$change = new Subject();
        this.change = this.$change.asObservable()
            .pipe(debounceTime(300));
    }
    onType(event) {
        const regexp = /[a-zA-Z0-9]/;
        if (event.key.length !== 1 || !regexp.test(event.key)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.$reset.next();
        timer(500)
            .pipe(takeUntil(this.$reset))
            .subscribe(() => {
            this.chars = [];
        });
        this.chars = [...this.chars, event.key];
        this.$change.next({ value: this.chars.join(''), event });
    }
    destroy() {
        this.$reset.next();
        this.$reset.complete();
        this.$change.complete();
    }
}
function findNextMatchingElement(traversableItems, options, activeItemIndex) {
    const targets = traversableItems.filter((item, index) => options.some((match) => item.getLabel() === match.label));
    return (traversableItems.find((item, index) => options.some((match) => item.getLabel() === match.label) &&
        activeItemIndex < index) || targets[0]);
}

class SelectComponentType {
}

/**
 * ### Description
 * The Select Component displays an array of selectable CdsListItems in a dropdown menu.
 * The value passed into, and emitted from the SelectComponent should correspond with the `key` property of an item in the
 * CdsListItem array.
 */
class CdsSelectComponent {
    /**
     * @internal
     */
    constructor(control, focusableListPresenter, typeaheadPresenter, controlValueAccessor, renderer) {
        this.control = control;
        this.focusableListPresenter = focusableListPresenter;
        this.typeaheadPresenter = typeaheadPresenter;
        this.controlValueAccessor = controlValueAccessor;
        this.renderer = renderer;
        /**
         * @internal
         */
        this.registerOnChange = this.controlValueAccessor.registerOnChange;
        /**
         * @internal
         */
        this.registerOnTouched = this.controlValueAccessor.registerOnTouched;
        /**
         * @internal
         */
        this.setDisabledState = this.controlValueAccessor.setDisabledState;
        /**
         * An event containing the new state of this control.
         */
        this.change = this.controlValueAccessor.change;
        /**
         * @internal
         */
        this.focusButton = () => {
            this.button.focus({ preventScroll: true });
        };
        /**
         * @internal
         */
        this.focusFirstItem = () => {
            if (this.expanded) {
                const selectedItem = this.radioButtonList.toArray()
                    .find((radioButton) => radioButton.key === this.formControl.value);
                this.focusableListPresenter.setAndFocusActiveItem(selectedItem);
            }
        };
        /**
         * @internal
         */
        this.focusLastItem = () => {
            if (this.expanded) {
                const selectedItem = this.radioButtonList.toArray()
                    .find((radioButton) => radioButton.key === this.formControl.value);
                this.focusableListPresenter.setAndFocusActiveItem(selectedItem || this.radioButtonList.last);
            }
        };
        if (this.control) {
            this.control.valueAccessor = this;
        }
        else {
            throw new Error('FormControl is missing from Select Component. ');
        }
        this.typeaheadPresenter.change
            .subscribe(event => this.onTypehead(event));
    }
    /**
     * @internal
     */
    get text() {
        if (this.hideLabel) {
            return '';
        }
        const value = this.control.control?.value;
        const flattenedOptions = [].concat(...(this.options || []).map(option => option.children ? option.children : [option]));
        const selectedOption = flattenedOptions.find(option => option.key === value);
        return this.readout || selectedOption?.label || this.placeholder || 'Choose an option';
    }
    /**
     * @internal
     */
    get disabled() {
        return this.controlValueAccessor.disabled;
    }
    /**
     * @internal
     */
    get dropdownIcon() {
        return this.iconName
            ? this.iconName
            : (this.expanded ? 'arrow_drop_up' : 'arrow_drop_down');
    }
    /**
     * @internal
     */
    ngOnInit() {
        this.formControl = new UntypedFormControl(this.control.control.value);
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.radioButtonList.changes
            .subscribe(() => this.setupTraversableItems());
        this.setupTraversableItems();
    }
    /**
     * @internal
     */
    setupTraversableItems() {
        if (typeof this.radioButtonList !== 'undefined') {
            const registry = this.radioButtonList.map(radioButton => ({
                component: radioButton,
                element: radioButton.atom.nativeElement,
            }));
            this.focusableListPresenter.watch(registry);
        }
    }
    /**
     * @internal
     */
    closeDropdown(event) {
        if (this.expanded) {
            if (event) {
                event.stopPropagation();
            }
            this.dropdownToggle.collapse();
        }
    }
    /**
     * @internal
     */
    openDropdown() {
        if (!this.expanded) {
            this.dropdownToggle.expand();
        }
    }
    /**
     * @internal
     */
    onExpandedStateChanged({ expanded, source }) {
        this.expanded = expanded;
        this.renderer.setAttribute(this.button.elementRef.nativeElement, 'aria-expanded', `${this.expanded}`);
        const selectedItem = this.radioButtonList.toArray()
            .find((radioButton) => radioButton.key === this.formControl.value);
        if (expanded && source === 'keydown') {
            this.focusableListPresenter.setAndFocusActiveItem(selectedItem);
        }
        else {
            setTimeout(() => selectedItem?.atom?.nativeElement?.scrollIntoView({ behavior: 'auto', block: 'center' }));
        }
        if (!expanded) {
            this.controlValueAccessor.onTouch();
        }
    }
    /**
     * @internal
     */
    onValueChanged(value) {
        this.controlValueAccessor.onChange(value);
        this.closeDropdown();
        this.focusButton();
    }
    /**
     * @internal
     */
    handleTypeahead(event) {
        this.typeaheadPresenter.onType(event);
    }
    /**
     * @internal
     */
    onTypehead({ value }) {
        const traversableItems = this.radioButtonList.toArray();
        const flattenedOptions = this.radioButtonList.map((component) => ({ label: component.getLabel(), key: '' }));
        const matches = DropdownTypeaheadMatchAllFn(value, flattenedOptions);
        const activeItem = this.focusableListPresenter.getActiveItemIndex();
        const typeaheadTarget = findNextMatchingElement(traversableItems, matches, activeItem);
        if (typeaheadTarget) {
            this.openDropdown();
            this.focusableListPresenter.setAndFocusActiveItem(typeaheadTarget);
        }
    }
    /**
     * @internal
     */
    writeValue(value) {
        if (this.formControl) {
            this.formControl.setValue(value);
        }
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.focusableListPresenter.destroy();
        this.typeaheadPresenter.destroy();
    }
}
CdsSelectComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSelectComponent, deps: [{ token: i1$2.NgControl, optional: true, self: true }, { token: CdsFocusableListPresenter }, { token: TypeaheadPresenter }, { token: CdsControlValueAccessorComponent }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
CdsSelectComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSelectComponent, selector: "cds-select", inputs: { name: "name", options: "options", inline: "inline", placeholder: "placeholder", iconName: "iconName", hideLabel: "hideLabel", leftAligned: "leftAligned", readout: "readout" }, outputs: { change: "change" }, providers: [
        { provide: SelectComponentType, useExisting: forwardRef(() => CdsSelectComponent) },
        CdsFocusableListPresenter,
        CdsControlValueAccessorComponent,
        TypeaheadPresenter
    ], viewQueries: [{ propertyName: "dropdownToggle", first: true, predicate: CdsDropdownDirective, descendants: true, static: true }, { propertyName: "button", first: true, predicate: CdsButtonComponent, descendants: true, static: true }, { propertyName: "radioButtonList", predicate: CdsRadioButtonComponent, descendants: true }, { propertyName: "children", predicate: CdsFocusableItemDirective, descendants: true }], ngImport: i0, template: "<div\n  class=\"cds-select cds-dropdown\"\n  [class.-open]=\"expanded\"\n  [class.-invalid]=\"!control.disabled && !control.valid && control.touched\"\n  (keydown)=\"handleTypeahead($event)\"\n  (keydown.tab)=\"closeDropdown($event)\"\n  (keydown.shift.tab)=\"closeDropdown($event)\"\n>\n  <cds-button\n  #button\n  cdsElementRef\n  #toggle=\"cdsElementRef\"\n  [name]=\"name ? ('select-toggle-' + name | kebabCase) : ''\"\n  class=\"cds-dropdown-toggle\"\n  [class.-inline]=\"inline\"\n  [class.-icon-only]=\"hideLabel\"\n  [class.-left-aligned-icon]=\"!hideLabel && leftAligned\"\n  [class.-right-aligned-icon]=\"!hideLabel && !leftAligned\"\n  [disabled]=\"control.disabled\"\n  [text]=\"text\"\n  size=\"small\"\n  [type]=\"inline ? 'tertiary' : 'secondary'\"\n  ariaLabel=\"Open menu\"\n  (click)=\"focusButton()\"\n  (keydown.arrowdown)=\"focusFirstItem()\"\n  (keydown.arrowup)=\"focusLastItem()\"\n  >\n    <i class=\"material-icons\" cdsButtonSuffix *ngIf=\"!leftAligned\">{{ dropdownIcon }}</i>\n    <i class=\"material-icons\" cdsButtonPrefix *ngIf=\"leftAligned\">{{ dropdownIcon }}</i>\n  </cds-button>\n  <div\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n    cdsDropdownPanel\n    #selectPanel\n  >\n    <div class=\"cds-select-panel\">\n      <cds-radio-button-group (change)=\"onValueChanged($event)\" [vertical]=\"true\" [formControl]=\"formControl\">\n        <ng-container *ngFor=\"let option of options\">\n          <ng-container *ngIf=\"option.children;\">\n            <div class=\"cds-select-option-group\">\n              <span cdsRadioButtonGroupLabel>{{option.label}}</span>\n              <cds-radio-button\n                *ngFor=\"let child of option.children\"\n                [label]=\"child.label\"\n                [name]=\"'select-group-' + option.label + '-option-' + child.label + '-' + name\"\n                [key]=\"child.key\"\n                class=\"cds-select-option\"\n                [class.-wrap]=\"!!child.wrap\"\n                (keydown.enter)=\"onValueChanged(child.key);writeValue(child.key)\"\n              >\n              </cds-radio-button>\n            </div>\n          </ng-container>\n          <ng-container *ngIf=\"!option.children\">\n            <cds-radio-button\n              [label]=\"option.label\"\n              [name]=\"'select-option-' + option.label + '-' + name\"\n              [key]=\"option.key\"\n              class=\"cds-select-option\"\n              [class.-wrap]=\"!!option.wrap\"\n              (keydown.enter)=\"onValueChanged(option.key);writeValue(option.key)\"\n            >\n            </cds-radio-button>\n          </ng-container>\n\n        </ng-container>\n      </cds-radio-button-group>\n    </div>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  buttonTrigger\n  [anchor]=\"toggle.nativeElement\"\n  [panel]=\"selectPanel\"\n  [wasFocusedFn]=\"focusButton\"\n  (removeFocus)=\"closeDropdown()\"\n  (toggleChange)=\"onExpandedStateChanged($event)\"\n>\n</ng-container>\n", dependencies: [{ kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: CdsDropdownDirective, selector: "[cdsDropdown]", inputs: ["anchor", "panel", "options", "strategy", "wasFocusedFn"], outputs: ["removeFocus", "change"] }, { kind: "directive", type: CdsDropdownPanelDirective, selector: "[cdsDropdownPanel]", exportAs: ["dropdownPanel"] }, { kind: "directive", type: CdsDropdownToggleButtonDirective, selector: "[buttonTrigger]", outputs: ["toggleChange"] }, { kind: "directive", type: CdsElementRefDirective, selector: "[cdsElementRef]", exportAs: ["cdsElementRef"] }, { kind: "component", type: CdsButtonComponent, selector: "cds-button", inputs: ["name", "text", "type", "size", "disabled", "underline", "iconName", "alignIcon", "designation", "ariaLabel", "link"] }, { kind: "component", type: CdsRadioButtonComponent, selector: "cds-radio-button", inputs: ["label", "key", "name", "leftAlignedLabel", "disabled"] }, { kind: "component", type: CdsRadioButtonGroupComponent, selector: "cds-radio-button-group", inputs: ["name", "vertical"], outputs: ["change"] }, { kind: "directive", type: CdsRadioButtonGroupLabelDirective, selector: "[cdsRadioButtonGroupLabel]" }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-select', providers: [
                        { provide: SelectComponentType, useExisting: forwardRef(() => CdsSelectComponent) },
                        CdsFocusableListPresenter,
                        CdsControlValueAccessorComponent,
                        TypeaheadPresenter
                    ], template: "<div\n  class=\"cds-select cds-dropdown\"\n  [class.-open]=\"expanded\"\n  [class.-invalid]=\"!control.disabled && !control.valid && control.touched\"\n  (keydown)=\"handleTypeahead($event)\"\n  (keydown.tab)=\"closeDropdown($event)\"\n  (keydown.shift.tab)=\"closeDropdown($event)\"\n>\n  <cds-button\n  #button\n  cdsElementRef\n  #toggle=\"cdsElementRef\"\n  [name]=\"name ? ('select-toggle-' + name | kebabCase) : ''\"\n  class=\"cds-dropdown-toggle\"\n  [class.-inline]=\"inline\"\n  [class.-icon-only]=\"hideLabel\"\n  [class.-left-aligned-icon]=\"!hideLabel && leftAligned\"\n  [class.-right-aligned-icon]=\"!hideLabel && !leftAligned\"\n  [disabled]=\"control.disabled\"\n  [text]=\"text\"\n  size=\"small\"\n  [type]=\"inline ? 'tertiary' : 'secondary'\"\n  ariaLabel=\"Open menu\"\n  (click)=\"focusButton()\"\n  (keydown.arrowdown)=\"focusFirstItem()\"\n  (keydown.arrowup)=\"focusLastItem()\"\n  >\n    <i class=\"material-icons\" cdsButtonSuffix *ngIf=\"!leftAligned\">{{ dropdownIcon }}</i>\n    <i class=\"material-icons\" cdsButtonPrefix *ngIf=\"leftAligned\">{{ dropdownIcon }}</i>\n  </cds-button>\n  <div\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n    cdsDropdownPanel\n    #selectPanel\n  >\n    <div class=\"cds-select-panel\">\n      <cds-radio-button-group (change)=\"onValueChanged($event)\" [vertical]=\"true\" [formControl]=\"formControl\">\n        <ng-container *ngFor=\"let option of options\">\n          <ng-container *ngIf=\"option.children;\">\n            <div class=\"cds-select-option-group\">\n              <span cdsRadioButtonGroupLabel>{{option.label}}</span>\n              <cds-radio-button\n                *ngFor=\"let child of option.children\"\n                [label]=\"child.label\"\n                [name]=\"'select-group-' + option.label + '-option-' + child.label + '-' + name\"\n                [key]=\"child.key\"\n                class=\"cds-select-option\"\n                [class.-wrap]=\"!!child.wrap\"\n                (keydown.enter)=\"onValueChanged(child.key);writeValue(child.key)\"\n              >\n              </cds-radio-button>\n            </div>\n          </ng-container>\n          <ng-container *ngIf=\"!option.children\">\n            <cds-radio-button\n              [label]=\"option.label\"\n              [name]=\"'select-option-' + option.label + '-' + name\"\n              [key]=\"option.key\"\n              class=\"cds-select-option\"\n              [class.-wrap]=\"!!option.wrap\"\n              (keydown.enter)=\"onValueChanged(option.key);writeValue(option.key)\"\n            >\n            </cds-radio-button>\n          </ng-container>\n\n        </ng-container>\n      </cds-radio-button-group>\n    </div>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  buttonTrigger\n  [anchor]=\"toggle.nativeElement\"\n  [panel]=\"selectPanel\"\n  [wasFocusedFn]=\"focusButton\"\n  (removeFocus)=\"closeDropdown()\"\n  (toggleChange)=\"onExpandedStateChanged($event)\"\n>\n</ng-container>\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: CdsFocusableListPresenter }, { type: TypeaheadPresenter }, { type: CdsControlValueAccessorComponent }, { type: i0.Renderer2 }]; }, propDecorators: { name: [{
                type: Input
            }], options: [{
                type: Input
            }], inline: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], iconName: [{
                type: Input
            }], hideLabel: [{
                type: Input
            }], leftAligned: [{
                type: Input
            }], readout: [{
                type: Input
            }], change: [{
                type: Output
            }], dropdownToggle: [{
                type: ViewChild,
                args: [CdsDropdownDirective, { static: true }]
            }], button: [{
                type: ViewChild,
                args: [CdsButtonComponent, { static: true }]
            }], radioButtonList: [{
                type: ViewChildren,
                args: [CdsRadioButtonComponent]
            }], children: [{
                type: ViewChildren,
                args: [CdsFocusableItemDirective]
            }] } });

class CdsSelectModule {
}
CdsSelectModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsSelectModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsSelectModule, declarations: [CdsSelectComponent], imports: [FormsModule,
        ReactiveFormsModule,
        CommonModule,
        CdsDirectivesModule,
        CdsButtonModule,
        CdsRadioButtonModule,
        CdsPipesModule], exports: [CdsSelectComponent] });
CdsSelectModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSelectModule, imports: [FormsModule,
        ReactiveFormsModule,
        CommonModule,
        CdsDirectivesModule,
        CdsButtonModule,
        CdsRadioButtonModule,
        CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsSelectComponent],
                    exports: [CdsSelectComponent],
                    imports: [
                        FormsModule,
                        ReactiveFormsModule,
                        CommonModule,
                        CdsDirectivesModule,
                        CdsButtonModule,
                        CdsRadioButtonModule,
                        CdsPipesModule,
                    ],
                }]
        }] });

/**
 * ### Description
 * Provides an interactive interfact for paging through data in tables, lists etc.
 */
class CdsPaginatorComponent {
    /**
     * @internal
     * This is an OnPush component so angular does not perform change detection when it is generated via
     * angular core's createComponent() method. This functionality is needed by upstream components, unfortunately.
     * See: https://github.com/angular/angular/issues/36667
     */
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        /**
         * @internal
         */
        this.paginationControl = new UntypedFormControl(null);
        /**
         * @internal
         */
        this.defaultPageSizeOptions = [5, 10, 20, 30].map((pageSize) => ({
            label: `${pageSize}`,
            key: pageSize,
            value: pageSize,
        }));
        /**
         * How many page links should be displayed in `standard` mode?
         */
        this.pageLinks = 5;
        /**
         * Emits an event when the page size is changed via the dropdown.
         */
        this.pageSizeChange = new EventEmitter();
        /**
         * Emits an event when a new page is selected via the arrow keys or page links.
         */
        this.offsetChange = new EventEmitter();
    }
    /**
     * @internal
     */
    get currentPage() {
        return this.offset === undefined ? 0 : Math.ceil((this.offset + this.pageSize) / this.pageSize);
    }
    /**
     * @internal
     */
    get availablePages() {
        if (!this.totalResults || !this.currentPage || !this.pageSize) {
            return;
        }
        // use the requested page link size or the total number of pages, whichever is less
        const pageLinks = Math.min(this.pageLinks, this.lastPage);
        // get the page link size, exclusive of the current page
        const pageLinksExcludingCurrentPage = pageLinks - 1;
        // determine the interval between the current page and the last possible page.
        const rightOffset = this.lastPage - this.currentPage;
        // pageLinksExcludingCurrentPage / 2 gives the interval between the current page and the last page link.
        // If the right offset is smaller than the above calculated value, the end of the array has been reached.
        // Subtract the smaller value of the above two entries from the total count of page links (minus the current page!) to get the
        // number of steps between the current page and the first page link.
        const stepsBack = pageLinksExcludingCurrentPage -
            Math.min(rightOffset, Math.ceil(pageLinksExcludingCurrentPage / 2));
        let start = this.currentPage - stepsBack;
        start = start < 1 ? 1 : start;
        return Array.from({ length: pageLinks }, (_, index) => start + index);
    }
    /**
     * @internal
     */
    get lastPage() {
        return Math.max(Math.ceil(this.totalResults / this.pageSize), 1);
    }
    /**
     * @internal
     */
    get showingResultsText() {
        const pageStart = Math.min(this.offset + 1, this.totalResults);
        const pageEnd = Math.min(this.offset + this.pageSize, this.totalResults);
        return `Showing ${pageStart} to ${pageEnd} of ${this.totalResults} ${pluralize(this.recordLabel || 'record')}`;
    }
    /**
     *  The number of records per page.
     */
    set pageSize(value) {
        this.paginationControl.setValue(value);
    }
    get pageSize() {
        return this.paginationControl.value;
    }
    /**
     *  An array of page sizes to be displayed in the Page Size dropdown.
     */
    set pageSizes(pageSizes) {
        this.pageSizeOptions = pageSizes.map((pageSize) => ({
            label: `${pageSize}`,
            key: pageSize,
            value: pageSize,
        }));
    }
    /**
     * @internal
     */
    onOptionSelected(value) {
        this.pageSizeChange.emit(value);
    }
    /**
     * @internal
     */
    goToPage(pageNumber) {
        const pageSize = this.pageSize;
        const offset = (pageNumber * this.pageSize) - pageSize;
        this.offsetChange.emit(offset);
    }
}
CdsPaginatorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPaginatorComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsPaginatorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsPaginatorComponent, selector: "cds-paginator", inputs: { name: "name", offset: "offset", pageSize: "pageSize", totalResults: "totalResults", type: "type", pageSizes: "pageSizes", pageLinks: "pageLinks", hidePageSizeSelector: "hidePageSizeSelector", recordLabel: "recordLabel" }, outputs: { pageSizeChange: "pageSizeChange", offsetChange: "offsetChange" }, ngImport: i0, template: "<div class=\"cds-paginator\">\n  <div class=\"results-information\">\n    <span>\n      {{ showingResultsText }}\n      <ng-content select=\"[cdsResultsInformation]\"></ng-content>\n    </span>\n  </div>\n\n  <div class=\"pagination-controls\">\n    <ng-container *ngIf=\"!hidePageSizeSelector\">\n      <div class=\"page-size-control\">\n        <div class=\"page-size-label\">Results per page</div>\n        <cds-select [formControl]=\"paginationControl\"\n                    [name]=\"name + '-pagination'\"\n                    [options]=\"pageSizeOptions || defaultPageSizeOptions\"\n                    (change)=\"onOptionSelected($event)\">\n        </cds-select>\n      </div>\n    </ng-container>\n\n    <div class=\"pagination-links\">\n      <ng-container *ngIf=\"type==='standard'\">\n        <cds-control-block [name]=\"name + '-pagination-previous-page'\"\n                          size=\"small\"\n                          (click)=\"goToPage(1)\"\n                          [disabled]=\"currentPage === 1\">\n          <i class=\"material-icons\">first_page</i>\n        </cds-control-block>\n      </ng-container>\n\n      <cds-control-block [name]=\"name + '-pagination-previous-page'\"\n                        size=\"small\"\n                        (click)=\"goToPage(currentPage - 1)\"\n                        [disabled]=\"currentPage === 1\">\n        <i class=\"material-icons\">keyboard_arrow_left</i>\n      </cds-control-block>\n\n      <ng-container *ngIf=\"type==='standard'\">\n        <cds-control-block *ngFor=\"let page of availablePages\"\n                          [name]=\"name + '-pagination-' + page\"\n                          size=\"small\"\n                          [active]=\"currentPage === page\"\n                          (click)=\"goToPage(page)\">{{ page }}\n        </cds-control-block>\n      </ng-container>\n\n      <cds-control-block [name]=\"name + '-pagination-next-page'\"\n                        size=\"small\"\n                        (click)=\"goToPage(currentPage + 1)\"\n                        [disabled]=\"currentPage === lastPage\">\n        <i class=\"material-icons\">keyboard_arrow_right</i>\n      </cds-control-block>\n\n      <ng-container *ngIf=\"type==='standard'\">\n        <cds-control-block [name]=\"name + '-pagination-next-page'\"\n                          size=\"small\"\n                          (click)=\"goToPage(lastPage)\"\n                          [disabled]=\"currentPage === lastPage\">\n          <i class=\"material-icons\">last_page</i>\n        </cds-control-block>\n      </ng-container>\n    </div>\n  </div>\n</div>", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: ["name", "size", "disabled", "tabindex", "active", "attributes"] }, { kind: "component", type: CdsSelectComponent, selector: "cds-select", inputs: ["name", "options", "inline", "placeholder", "iconName", "hideLabel", "leftAligned", "readout"], outputs: ["change"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPaginatorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-paginator', template: "<div class=\"cds-paginator\">\n  <div class=\"results-information\">\n    <span>\n      {{ showingResultsText }}\n      <ng-content select=\"[cdsResultsInformation]\"></ng-content>\n    </span>\n  </div>\n\n  <div class=\"pagination-controls\">\n    <ng-container *ngIf=\"!hidePageSizeSelector\">\n      <div class=\"page-size-control\">\n        <div class=\"page-size-label\">Results per page</div>\n        <cds-select [formControl]=\"paginationControl\"\n                    [name]=\"name + '-pagination'\"\n                    [options]=\"pageSizeOptions || defaultPageSizeOptions\"\n                    (change)=\"onOptionSelected($event)\">\n        </cds-select>\n      </div>\n    </ng-container>\n\n    <div class=\"pagination-links\">\n      <ng-container *ngIf=\"type==='standard'\">\n        <cds-control-block [name]=\"name + '-pagination-previous-page'\"\n                          size=\"small\"\n                          (click)=\"goToPage(1)\"\n                          [disabled]=\"currentPage === 1\">\n          <i class=\"material-icons\">first_page</i>\n        </cds-control-block>\n      </ng-container>\n\n      <cds-control-block [name]=\"name + '-pagination-previous-page'\"\n                        size=\"small\"\n                        (click)=\"goToPage(currentPage - 1)\"\n                        [disabled]=\"currentPage === 1\">\n        <i class=\"material-icons\">keyboard_arrow_left</i>\n      </cds-control-block>\n\n      <ng-container *ngIf=\"type==='standard'\">\n        <cds-control-block *ngFor=\"let page of availablePages\"\n                          [name]=\"name + '-pagination-' + page\"\n                          size=\"small\"\n                          [active]=\"currentPage === page\"\n                          (click)=\"goToPage(page)\">{{ page }}\n        </cds-control-block>\n      </ng-container>\n\n      <cds-control-block [name]=\"name + '-pagination-next-page'\"\n                        size=\"small\"\n                        (click)=\"goToPage(currentPage + 1)\"\n                        [disabled]=\"currentPage === lastPage\">\n        <i class=\"material-icons\">keyboard_arrow_right</i>\n      </cds-control-block>\n\n      <ng-container *ngIf=\"type==='standard'\">\n        <cds-control-block [name]=\"name + '-pagination-next-page'\"\n                          size=\"small\"\n                          (click)=\"goToPage(lastPage)\"\n                          [disabled]=\"currentPage === lastPage\">\n          <i class=\"material-icons\">last_page</i>\n        </cds-control-block>\n      </ng-container>\n    </div>\n  </div>\n</div>" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { name: [{
                type: Input
            }], offset: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], totalResults: [{
                type: Input
            }], type: [{
                type: Input
            }], pageSizes: [{
                type: Input
            }], pageLinks: [{
                type: Input
            }], hidePageSizeSelector: [{
                type: Input
            }], recordLabel: [{
                type: Input
            }], pageSizeChange: [{
                type: Output
            }], offsetChange: [{
                type: Output
            }] } });

class CdsPaginatorModule {
}
CdsPaginatorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPaginatorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsPaginatorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsPaginatorModule, declarations: [CdsPaginatorComponent], imports: [CommonModule,
        ReactiveFormsModule,
        CdsControlBlockModule,
        CdsSelectModule], exports: [CdsPaginatorComponent] });
CdsPaginatorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPaginatorModule, imports: [CommonModule,
        ReactiveFormsModule,
        CdsControlBlockModule,
        CdsSelectModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPaginatorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsPaginatorComponent],
                    exports: [CdsPaginatorComponent],
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        CdsControlBlockModule,
                        CdsSelectModule,
                    ],
                }]
        }] });

class CdsSmartRowTemplateDirective {
    /**
     * @internal
     */
    constructor(template) {
        this.template = template;
    }
}
CdsSmartRowTemplateDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartRowTemplateDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsSmartRowTemplateDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSmartRowTemplateDirective, selector: "[cdsRowTemplate]", inputs: { keyProp: "keyProp" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartRowTemplateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsRowTemplate]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; }, propDecorators: { keyProp: [{
                type: Input
            }] } });

/**
 * @internal
 *
 * This class instantiates a row template for available data, or instantiates a loading row.
 *
 * Either of these actions returns a row component that is detectable by the @ContentChildren
 * property ('allRows') of the DatagridComponent, so there's no need to call `displayService.setDisplayedRows()`.
 */
class CdsSmartRowIteratorComponent {
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        this.data = [];
    }
    createRows(page, data) {
        if (!page) {
            this.data = [];
        }
        const pageSize = Math.min(page.pageSize, page.totalResults - page.offset);
        const entries = Array.from({ length: pageSize }, (_, index) => {
            const offset = page.offset + index;
            const rowData = data.get(offset) || {};
            const loading = Object.keys(rowData).length <= 0;
            Object.defineProperties(rowData, {
                _smartRowPosition: { value: offset, configurable: true },
                _smartRowKey: { value: rowData[this.template.keyProp], configurable: true },
                _loading: { value: loading, configurable: true }
            });
            return rowData;
        });
        this.data.splice(0, this.data.length, ...entries);
        this.changeDetector.detectChanges();
    }
    // @TODO: HACK
    // We need to call detectChanges() on the ViewContainerRef of the component in which the templates are defined
    // in order for the duplicated templates to actually be detected by angular
    // see: https://github.com/angular/angular/issues/38976#issuecomment-712506166
    update(page, data, templateRef, parentCd) {
        this.template = templateRef;
        this.createRows(page, data);
        parentCd.detectChanges();
    }
}
CdsSmartRowIteratorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartRowIteratorComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsSmartRowIteratorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSmartRowIteratorComponent, selector: "cds-smart-row-iterator", ngImport: i0, template: `
    <ng-template
      *ngFor="let row of data"
      [ngTemplateOutlet]="template.template"
      [ngTemplateOutletContext]="{ $implicit: row, loading: row._loading }">
    </ng-template>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartRowIteratorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-smart-row-iterator',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: `
    <ng-template
      *ngFor="let row of data"
      [ngTemplateOutlet]="template.template"
      [ngTemplateOutletContext]="{ $implicit: row, loading: row._loading }">
    </ng-template>
  `
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; } });

class CdsVirtualScrollerModule {
}
CdsVirtualScrollerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsVirtualScrollerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsVirtualScrollerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsVirtualScrollerModule, declarations: [CdsSmartRowIteratorComponent,
        CdsSmartRowTemplateDirective], imports: [CommonModule,
        CdsDirectivesModule,
        CdsPipesModule], exports: [CdsSmartRowTemplateDirective] });
CdsVirtualScrollerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsVirtualScrollerModule, imports: [CommonModule,
        CdsDirectivesModule,
        CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsVirtualScrollerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsSmartRowIteratorComponent,
                        CdsSmartRowTemplateDirective
                    ],
                    exports: [
                        CdsSmartRowTemplateDirective
                    ],
                    imports: [
                        CommonModule,
                        CdsDirectivesModule,
                        CdsPipesModule
                    ]
                }]
        }] });

/* eslint-disable no-shadow */
var CdsDataGridCellAlignment;
(function (CdsDataGridCellAlignment) {
    CdsDataGridCellAlignment["Left"] = "left";
    CdsDataGridCellAlignment["Right"] = "right";
    CdsDataGridCellAlignment["Center"] = "center";
})(CdsDataGridCellAlignment || (CdsDataGridCellAlignment = {}));

/**
 * @internal
 */
class CdsDataGridDisplayService {
    constructor() {
        this.additionalHeaders = [];
        this.$panel = new Subject();
        this._displayedHeaders = [];
        this._displayedRows = [];
        this.panel = this.$panel.asObservable();
        this.stickyHeaders = false;
    }
    // display a subset of available rows
    setDisplayedRows(rows) {
        this._displayedRows = rows;
    }
    removeAdditionalHeader(header) {
        const index = this.additionalHeaders.findIndex(entry => entry.header === header);
        if (index > -1) {
            this.additionalHeaders.splice(index);
        }
    }
    setAdditionalHeaders(header, index) {
        this.additionalHeaders.push({ header, index });
    }
    setPanel(panel) {
        this._panel = panel;
        this.$panel.next(this._panel);
    }
    setDisplayedHeaders(headers) {
        const withAddedHeaders = [...headers];
        this.additionalHeaders.forEach(customHeader => withAddedHeaders.splice(customHeader.index, 0, customHeader.header));
        const sortedFrozenHeaders = withAddedHeaders
            .filter((header) => !!header.frozen)
            .sort((a, b) => a.position - b.position);
        const sortedRegularHeaders = withAddedHeaders
            .filter((header) => !!!header.frozen)
            .sort((a, b) => a.position - b.position);
        this._displayedHeaders = [...sortedFrozenHeaders, ...sortedRegularHeaders];
    }
    getDisplayedRows() {
        return this._displayedRows.slice();
    }
    getDisplayedHeaders() {
        return this._displayedHeaders.slice();
    }
    destroy() {
        this.additionalHeaders = [];
    }
}
CdsDataGridDisplayService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridDisplayService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsDataGridDisplayService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridDisplayService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridDisplayService, decorators: [{
            type: Injectable
        }] });

/**
 * Keeps track of which cells have actually been rendered in the DOM.
 */
class CdsDataGridRenderedCellsService {
    constructor() {
        this._renderedCells = [];
        this.$renderedCells = new Subject();
        this.renderedCells = this.$renderedCells.asObservable();
    }
    setRow(row) {
        this.row = row;
    }
    getRow() {
        return this.row;
    }
    getRenderedCells() {
        return [...this._renderedCells];
    }
    onRender(cell) {
        if (this._renderedCells.includes(cell)) {
            return;
        }
        this._renderedCells.push(cell);
        this.$renderedCells.next(this._renderedCells);
    }
    onRemove(cell) {
        this._renderedCells.splice(this._renderedCells.indexOf(cell), 1);
    }
    destroy() {
        this._renderedCells = [];
        this.$renderedCells.complete();
    }
}
CdsDataGridRenderedCellsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRenderedCellsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsDataGridRenderedCellsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRenderedCellsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRenderedCellsService, decorators: [{
            type: Injectable
        }] });

class CdsDatagridCellDirective {
    constructor(elementRef, renderedCellsService) {
        this.elementRef = elementRef;
        this.renderedCellsService = renderedCellsService;
    }
    ngAfterContentInit() {
        this.renderedCellsService.onRender(this.elementRef.nativeElement);
    }
    ngOnDestroy() {
        this.renderedCellsService.onRemove(this.elementRef.nativeElement);
    }
}
CdsDatagridCellDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatagridCellDirective, deps: [{ token: i0.ElementRef }, { token: CdsDataGridRenderedCellsService }], target: i0.ɵɵFactoryTarget.Directive });
CdsDatagridCellDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsDatagridCellDirective, selector: "[cdsDgCell]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatagridCellDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsDgCell]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: CdsDataGridRenderedCellsService }]; } });

// Accessibility: Firefox has a bug that announces that all children of a container (row) that has a click
// event handler are clickable: https://bugzilla.mozilla.org/show_bug.cgi?id=1679991
// 11-12-21
class CdsDataGridCellComponent {
    constructor(displayService, renderedCellsService) {
        this.displayService = displayService;
        this.renderedCellsService = renderedCellsService;
        /**
         * @internal
         */
        this.isLinkCell = false;
        /**
         * Should the contents of this cell be truncated? Defaults to `true`.
         */
        this.truncate = true;
        /**
         * How many lines of content should be displayed before truncation? Defaults to `3`.
         */
        this.lineCount = 3;
    }
    /**
     * @internal
     */
    get header() {
        return this.displayService.getDisplayedHeaders()[this.row.cells.toArray().indexOf(this)];
    }
    /**
     * @internal
     */
    get leftAligned() {
        return !this.align || this.align === CdsDataGridCellAlignment.Left;
    }
    /**
     * @internal
     */
    get rightAligned() {
        return this.align === CdsDataGridCellAlignment.Right;
    }
    /**
     * @internal
     */
    get centerAligned() {
        return this.align === CdsDataGridCellAlignment.Center;
    }
    /**
     * @internal
     */
    get sortable() {
        return this.header ? this.header.sortable : null;
    }
    /**
     * @internal
     */
    get row() {
        return this.renderedCellsService.getRow();
    }
    /**
     * Applies left, right, or center alignment to the cell's contents.
     */
    set align(align) {
        this._align = align;
    }
    get align() {
        return this._align || this.header ? this.header.align : null;
    }
    /**
     * Should the contents of this cell wrap to multiple lines? Defaults to `true`.
     */
    set wrap(wrap) {
        this._wrap = wrap;
    }
    get wrap() {
        return this._wrap === undefined ? true : this._wrap;
    }
    /**
     * @internal
     */
    get cellElement() {
        const index = [...this.row.cells].indexOf(this);
        return this.renderedCellsService.getRenderedCells()[index];
    }
    get loading() {
        return this._loading || (this.row && this.row.useDefaultLoadingRow && this.row.loading);
    }
    /**
     * @internal
     */
    onClick(event) {
        const interactive = `
      a[href]:not([disabled]),
      button:not([disabled]),
      textarea:not([disabled]),
      input:not([disabled]),
      select:not([disabled]),
      [tabindex]:not([tabindex="-1"])
    `;
        const hasInteractiveParent = (element) => {
            if (element === this.cellElement || element.parentElement === this.cellElement) {
                return false;
            }
            const interactiveParent = hasInteractiveParent(element.parentElement);
            const interactiveSelf = Array.from(element.parentElement.querySelectorAll(interactive)).includes(element);
            return interactiveSelf || interactiveParent;
        };
        const target = event.target;
        if (hasInteractiveParent(target)) {
            event.stopPropagation();
        }
    }
}
CdsDataGridCellComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridCellComponent, deps: [{ token: CdsDataGridDisplayService }, { token: CdsDataGridRenderedCellsService }], target: i0.ɵɵFactoryTarget.Component });
CdsDataGridCellComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsDataGridCellComponent, selector: "cds-datagrid-cell", inputs: { isLinkCell: "isLinkCell", align: "align", name: "name", wrap: "wrap", truncate: "truncate", lineCount: "lineCount", _loading: ["loading", "_loading"] }, viewQueries: [{ propertyName: "template", first: true, predicate: ["template"], descendants: true, static: true }], ngImport: i0, template: "<ng-template #template>\n  <td\n    #cell\n    [class.-left-aligned]=\"leftAligned\"\n    [class.-right-aligned]=\"rightAligned\"\n    [class.-center-aligned]=\"centerAligned\"\n    [class.-sortable]=\"sortable\"\n    [class.-nowrap]=\"wrap === false\"\n    [class.-frozen]=\"header?.frozen\"\n    [class.-border-right]=\"header?.frozen && header?.frozenBorderRight\"\n    [class.cds-table-row-link]=\"isLinkCell\"\n    [class.skeleton-loading]=\"loading\"\n    [style.left.px]=\"header?.frozen ? header?.frozenOffsetLeft : null\"\n    [style.gridRow]=\"row?.gridRowIndex\"\n    [style.gridColumn]=\"header?.gridColumn\"\n    [id]=\"'cds-dg-cell-' + (this.name | kebabCase)\"\n    role=\"gridcell\"\n    (click)=\"onClick($event)\"\n    cdsDgCell\n  >\n    <div class=\"cell-content\">\n      <div\n        [class.cds-text-truncation]=\"wrap !== false && truncate\"\n        [style.-webkit-line-clamp]=\"truncate && lineCount ? lineCount : null\"\n      >\n        <ng-content></ng-content>\n      </div>\n    </div>\n  </td>\n</ng-template>\n", dependencies: [{ kind: "directive", type: CdsDatagridCellDirective, selector: "[cdsDgCell]" }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridCellComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-datagrid-cell', template: "<ng-template #template>\n  <td\n    #cell\n    [class.-left-aligned]=\"leftAligned\"\n    [class.-right-aligned]=\"rightAligned\"\n    [class.-center-aligned]=\"centerAligned\"\n    [class.-sortable]=\"sortable\"\n    [class.-nowrap]=\"wrap === false\"\n    [class.-frozen]=\"header?.frozen\"\n    [class.-border-right]=\"header?.frozen && header?.frozenBorderRight\"\n    [class.cds-table-row-link]=\"isLinkCell\"\n    [class.skeleton-loading]=\"loading\"\n    [style.left.px]=\"header?.frozen ? header?.frozenOffsetLeft : null\"\n    [style.gridRow]=\"row?.gridRowIndex\"\n    [style.gridColumn]=\"header?.gridColumn\"\n    [id]=\"'cds-dg-cell-' + (this.name | kebabCase)\"\n    role=\"gridcell\"\n    (click)=\"onClick($event)\"\n    cdsDgCell\n  >\n    <div class=\"cell-content\">\n      <div\n        [class.cds-text-truncation]=\"wrap !== false && truncate\"\n        [style.-webkit-line-clamp]=\"truncate && lineCount ? lineCount : null\"\n      >\n        <ng-content></ng-content>\n      </div>\n    </div>\n  </td>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: CdsDataGridDisplayService }, { type: CdsDataGridRenderedCellsService }]; }, propDecorators: { isLinkCell: [{
                type: Input
            }], align: [{
                type: Input
            }], name: [{
                type: Input
            }], wrap: [{
                type: Input
            }], truncate: [{
                type: Input
            }], lineCount: [{
                type: Input
            }], _loading: [{
                type: Input,
                args: ['loading']
            }], template: [{
                type: ViewChild,
                args: ['template', { static: true }]
            }] } });

/* eslint-disable no-shadow */
var CdsColumnOrientation;
(function (CdsColumnOrientation) {
    /**
     * Width is fixed.
     */
    CdsColumnOrientation["Fixed"] = "fixed";
    /**
     * Width is based on the column's contents.
     */
    CdsColumnOrientation["Content"] = "content";
    /**
     * Column expands to fill available space.
     */
    CdsColumnOrientation["Stretch"] = "stretch";
})(CdsColumnOrientation || (CdsColumnOrientation = {}));

/**
 * Workaround for NG3003 error.
 */
class CdsDataGridHeaderService {
    setHeader(header) {
        this.header = header;
    }
    getHeader() {
        return this.header;
    }
}
CdsDataGridHeaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridHeaderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsDataGridHeaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridHeaderService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridHeaderService, decorators: [{
            type: Injectable
        }] });

/**
 * Keeps track of which headers have finished rendering.
 */
class CdsDataGridRenderedHeadersService {
    constructor(displayService) {
        this.displayService = displayService;
        this._renderedHeaders = new Map();
        this.$renderedHeaders = new BehaviorSubject(this._renderedHeaders);
        this.renderedHeaders = this.$renderedHeaders.asObservable();
        this.$finishedRenderingHeaders = this.renderedHeaders
            .pipe(filter(renderedHeaders => {
            const displayedHeaders = this.displayService.getDisplayedHeaders();
            return displayedHeaders.length > 0 && displayedHeaders.every(header => !!renderedHeaders.get(header));
        }));
    }
    getHeaderElement(header) {
        return this._renderedHeaders.get(header);
    }
    getRenderedHeaders() {
        return new Map(this._renderedHeaders);
    }
    onRender(...headers) {
        headers.forEach(([component, element]) => {
            this._renderedHeaders.set(component, element);
        });
        this.$renderedHeaders.next(this._renderedHeaders);
    }
    onRemove(header) {
        this._renderedHeaders.delete(header);
    }
    destroy() {
        this._renderedHeaders.clear();
        this.$renderedHeaders.complete();
    }
}
CdsDataGridRenderedHeadersService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRenderedHeadersService, deps: [{ token: CdsDataGridDisplayService }], target: i0.ɵɵFactoryTarget.Injectable });
CdsDataGridRenderedHeadersService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRenderedHeadersService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRenderedHeadersService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CdsDataGridDisplayService }]; } });

class CdsDatagridHeaderDirective {
    constructor(elementRef, headerService, renderedHeaderService) {
        this.elementRef = elementRef;
        this.headerService = headerService;
        this.renderedHeaderService = renderedHeaderService;
    }
    ngAfterViewInit() {
        this.renderedHeaderService.onRender([this.headerService.getHeader(), this.elementRef.nativeElement]);
    }
    ngOnDestroy() {
        this.renderedHeaderService.onRemove(this.headerService.getHeader());
    }
}
CdsDatagridHeaderDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatagridHeaderDirective, deps: [{ token: i0.ElementRef }, { token: CdsDataGridHeaderService }, { token: CdsDataGridRenderedHeadersService }], target: i0.ɵɵFactoryTarget.Directive });
CdsDatagridHeaderDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsDatagridHeaderDirective, selector: "[cdsDgHeader]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatagridHeaderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsDgHeader]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: CdsDataGridHeaderService }, { type: CdsDataGridRenderedHeadersService }]; } });

class CdsDataGridHeaderComponent {
    /**
     * @internal
     */
    constructor(rendereredHeaderService, headerService, displayService) {
        this.rendereredHeaderService = rendereredHeaderService;
        this.headerService = headerService;
        this.displayService = displayService;
        /**
         * Should the width of this column be based on the contents of the column, or stretch to fill the available space?
         * Note that the `width` input takes precedence over this property.
         * Defaults to 'stretch'
         */
        this.orientation = CdsColumnOrientation.Stretch;
        /**
         * Emits a [sort object](?path=/story/types-sort--page) when sorting is triggered on the header.
         */
        this.sort = new EventEmitter();
        this.headerService.setHeader(this);
    }
    /**
     * @internal
     */
    get gridColumn() {
        const headers = this.displayService.getDisplayedHeaders();
        // value is 2 because gridColumn is 1-indexed and a hidden header is always present
        return headers.indexOf(this) + 1;
    }
    /**
     * @internal
     */
    get id() {
        return 'cds-dg-header-' + kebabCaseTransform(this.name);
    }
    /**
     * @internal
     */
    get nextSortDirection() {
        return this.sortDirection
            ? -1 * this.sortDirection
            : CdsSortDirection.Ascending;
    }
    /**
     * @internal
     */
    get ariaSort() {
        if (!this.sortable ||
            (this.sortDirection !== -1 && this.sortDirection !== 1)) {
            return null;
        }
        switch (this.sortDirection) {
            case 1:
                return 'ascending';
                break;
            case -1:
                return 'descending';
                break;
            default:
                return 'none';
                break;
        }
    }
    /**
     * @internal
     */
    get element() {
        return this.rendereredHeaderService.getHeaderElement(this);
    }
    /**
     * @internal
     */
    onClick() {
        if (!this.sortable) {
            return;
        }
        const sort = {
            key: null,
            direction: this.nextSortDirection,
        };
        this.sort.emit(sort);
    }
}
CdsDataGridHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridHeaderComponent, deps: [{ token: CdsDataGridRenderedHeadersService }, { token: CdsDataGridHeaderService }, { token: CdsDataGridDisplayService }], target: i0.ɵɵFactoryTarget.Component });
CdsDataGridHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsDataGridHeaderComponent, selector: "cds-datagrid-header", inputs: { name: "name", frozen: "frozen", wrap: "wrap", position: "position", resizable: "resizable", sortable: "sortable", sortActive: "sortActive", sortDirection: "sortDirection", minWidth: "minWidth", width: "width", align: "align", orientation: "orientation", interactive: "interactive" }, outputs: { sort: "sort" }, providers: [CdsDataGridHeaderService], viewQueries: [{ propertyName: "template", first: true, predicate: ["template"], descendants: true, static: true }], ngImport: i0, template: "<ng-template #template>\n  <th\n    [style.minWidth]=\"minWidth\"\n    [id]=\"id\"\n    [class.-frozen]=\"frozen\"\n    [class.-wrap]=\"wrap\"\n    [class.-border-right]=\"frozenBorderRight\"\n    [class.-left-aligned]=\"!align\"\n    [class.-right-aligned]=\"align === 'right'\"\n    [class.-center-aligned]=\"align === 'center'\"\n    [class.-sortable]=\"sortable\"\n    [class.-sort-active]=\"sortActive\"\n    [style.left.px]=\"frozenOffsetLeft\"\n    [attr.aria-sort]=\"ariaSort\"\n    role=\"columnheader\"\n    scope=\"col\"\n    cdsDgHeader\n    cdsElementRef\n    #header=\"cdsElementRef\"\n  >\n    <span\n      class=\"cds-assistive-text\"\n      *ngIf=\"sortable\"\n      [id]=\"id + '-sort-hint'\"\n      aria-hidden=\"true\"\n    >\n      <ng-container *ngIf=\"nextSortDirection === 1\"\n        >Click to sort rows in ascending order</ng-container\n      >\n      <ng-container *ngIf=\"nextSortDirection === -1\"\n        >Click to sort rows in descending order</ng-container\n      >\n    </span>\n\n    <div\n      class=\"cds-table-header\"\n      [tabindex]=\"interactive ? '-1' : '0'\"\n      [class.-interactive]=\"interactive\"\n      [attr.role]=\"sortable ? 'button' : null\"\n      [attr.aria-describedby]=\"id + '-sort-hint'\"\n      (click)=\"onClick()\"\n      (keydown.enter)=\"onClick()\"\n      (keydown.space)=\"onClick()\"\n      cdsHighlightable\n    >\n      <div class=\"cds-table-header-label\">\n        <ng-content></ng-content>\n      </div>\n  \n      <div\n        class=\"sort-preview\"\n        *ngIf=\"sortable\"\n        aria-hidden=\"true\"\n      >\n        <i class=\"material-icons current\">\n          <ng-container *ngIf=\"sortActive && !sortDirection\"></ng-container>\n          <ng-container *ngIf=\"sortActive && sortDirection === -1\">arrow_upward</ng-container>\n          <ng-container *ngIf=\"sortActive && sortDirection === 1\">arrow_downward</ng-container>\n        </i>\n        <i class=\"material-icons next\">\n          <ng-container *ngIf=\"nextSortDirection === -1\">arrow_upward</ng-container>\n          <ng-container *ngIf=\"nextSortDirection === 1\">arrow_downward</ng-container>\n        </i>\n      </div>\n    </div>\n\n    <span\n      *ngIf=\"resizable\"\n      class=\"cds-resize-handle\"\n      cdsResizeHandler\n      [target]=\"header\"\n      aria-hidden=\"true\"\n      (click)=\"$event.stopPropagation()\"\n    ></span>\n  </th>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: CdsResizeHandlerDirective, selector: "[cdsResizeHandler]", inputs: ["target", "minWidth", "maxWidth"] }, { kind: "directive", type: CdsKeyboardHighlightableDirective, selector: "[cdsHighlightable]", inputs: ["isDisabled"] }, { kind: "directive", type: CdsElementRefDirective, selector: "[cdsElementRef]", exportAs: ["cdsElementRef"] }, { kind: "directive", type: CdsDatagridHeaderDirective, selector: "[cdsDgHeader]" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-datagrid-header', providers: [CdsDataGridHeaderService], template: "<ng-template #template>\n  <th\n    [style.minWidth]=\"minWidth\"\n    [id]=\"id\"\n    [class.-frozen]=\"frozen\"\n    [class.-wrap]=\"wrap\"\n    [class.-border-right]=\"frozenBorderRight\"\n    [class.-left-aligned]=\"!align\"\n    [class.-right-aligned]=\"align === 'right'\"\n    [class.-center-aligned]=\"align === 'center'\"\n    [class.-sortable]=\"sortable\"\n    [class.-sort-active]=\"sortActive\"\n    [style.left.px]=\"frozenOffsetLeft\"\n    [attr.aria-sort]=\"ariaSort\"\n    role=\"columnheader\"\n    scope=\"col\"\n    cdsDgHeader\n    cdsElementRef\n    #header=\"cdsElementRef\"\n  >\n    <span\n      class=\"cds-assistive-text\"\n      *ngIf=\"sortable\"\n      [id]=\"id + '-sort-hint'\"\n      aria-hidden=\"true\"\n    >\n      <ng-container *ngIf=\"nextSortDirection === 1\"\n        >Click to sort rows in ascending order</ng-container\n      >\n      <ng-container *ngIf=\"nextSortDirection === -1\"\n        >Click to sort rows in descending order</ng-container\n      >\n    </span>\n\n    <div\n      class=\"cds-table-header\"\n      [tabindex]=\"interactive ? '-1' : '0'\"\n      [class.-interactive]=\"interactive\"\n      [attr.role]=\"sortable ? 'button' : null\"\n      [attr.aria-describedby]=\"id + '-sort-hint'\"\n      (click)=\"onClick()\"\n      (keydown.enter)=\"onClick()\"\n      (keydown.space)=\"onClick()\"\n      cdsHighlightable\n    >\n      <div class=\"cds-table-header-label\">\n        <ng-content></ng-content>\n      </div>\n  \n      <div\n        class=\"sort-preview\"\n        *ngIf=\"sortable\"\n        aria-hidden=\"true\"\n      >\n        <i class=\"material-icons current\">\n          <ng-container *ngIf=\"sortActive && !sortDirection\"></ng-container>\n          <ng-container *ngIf=\"sortActive && sortDirection === -1\">arrow_upward</ng-container>\n          <ng-container *ngIf=\"sortActive && sortDirection === 1\">arrow_downward</ng-container>\n        </i>\n        <i class=\"material-icons next\">\n          <ng-container *ngIf=\"nextSortDirection === -1\">arrow_upward</ng-container>\n          <ng-container *ngIf=\"nextSortDirection === 1\">arrow_downward</ng-container>\n        </i>\n      </div>\n    </div>\n\n    <span\n      *ngIf=\"resizable\"\n      class=\"cds-resize-handle\"\n      cdsResizeHandler\n      [target]=\"header\"\n      aria-hidden=\"true\"\n      (click)=\"$event.stopPropagation()\"\n    ></span>\n  </th>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: CdsDataGridRenderedHeadersService }, { type: CdsDataGridHeaderService }, { type: CdsDataGridDisplayService }]; }, propDecorators: { name: [{
                type: Input
            }], frozen: [{
                type: Input
            }], wrap: [{
                type: Input
            }], position: [{
                type: Input
            }], resizable: [{
                type: Input
            }], sortable: [{
                type: Input
            }], sortActive: [{
                type: Input
            }], sortDirection: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], width: [{
                type: Input
            }], align: [{
                type: Input
            }], orientation: [{
                type: Input
            }], sort: [{
                type: Output
            }], interactive: [{
                type: Input
            }], template: [{
                type: ViewChild,
                args: ['template', { static: true }]
            }] } });

/* eslint-disable no-shadow */
var CdsDataGridSelectionType;
(function (CdsDataGridSelectionType) {
    CdsDataGridSelectionType["Single"] = "single";
    CdsDataGridSelectionType["Multi"] = "multi";
})(CdsDataGridSelectionType || (CdsDataGridSelectionType = {}));

class CdsDataGridRowLinkDirective {
}
CdsDataGridRowLinkDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRowLinkDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsDataGridRowLinkDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsDataGridRowLinkDirective, selector: "[cdsDataGridRowLink]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRowLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsDataGridRowLink]'
                }]
        }] });

/**
 * Keeps track of which rows have finished rendering.
 */
class CdsDataGridRenderedRowsService {
    constructor(datagridDisplayService) {
        this.datagridDisplayService = datagridDisplayService;
        this.$cancelSubscriptions = new Subject();
        this._renderedRows = new Map();
        this.$renderedRows = new Subject();
        this.$finishedRenderingRows = new Subject();
        this.$renderedRows
            .pipe(takeUntil(this.$cancelSubscriptions), filter(renderedRows => {
            const displayedRows = this.datagridDisplayService.getDisplayedRows();
            return displayedRows.length > 0
                && displayedRows.every(row => !!renderedRows.get(row));
        })).subscribe(renderedRows => this.$finishedRenderingRows.next(renderedRows));
    }
    onRender(row, rowElement) {
        this._renderedRows.set(row, rowElement);
        this.$renderedRows.next(this._renderedRows);
    }
    getRenderedRows() {
        return new Map(this._renderedRows);
    }
    getFirstRow() {
        return Array.from(this._renderedRows.keys())[0];
    }
    onRemove(row) {
        this._renderedRows.delete(row);
    }
    destroy() {
        this._renderedRows.clear();
        this.$renderedRows.complete();
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsDataGridRenderedRowsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRenderedRowsService, deps: [{ token: CdsDataGridDisplayService }], target: i0.ɵɵFactoryTarget.Injectable });
CdsDataGridRenderedRowsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRenderedRowsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRenderedRowsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CdsDataGridDisplayService }]; } });

class CdsDatagridRowDirective {
    constructor(elementRef, renderedRowsService, renderedCellsService, datagridDisplayService) {
        this.elementRef = elementRef;
        this.renderedRowsService = renderedRowsService;
        this.renderedCellsService = renderedCellsService;
        this.datagridDisplayService = datagridDisplayService;
        this.$cancelSubscriptions = new Subject();
        this.renderedCellsService.renderedCells
            .pipe(takeUntil(this.$cancelSubscriptions), filter(renderedCells => renderedCells.length === this.datagridDisplayService.getDisplayedHeaders().length), take(1))
            .subscribe(() => {
            this.renderedRowsService.onRender(this.renderedCellsService.getRow(), this.elementRef.nativeElement);
        });
    }
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsDatagridRowDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatagridRowDirective, deps: [{ token: i0.ElementRef }, { token: CdsDataGridRenderedRowsService }, { token: CdsDataGridRenderedCellsService }, { token: CdsDataGridDisplayService }], target: i0.ɵɵFactoryTarget.Directive });
CdsDatagridRowDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsDatagridRowDirective, selector: "[cdsDgRow]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatagridRowDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsDgRow]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: CdsDataGridRenderedRowsService }, { type: CdsDataGridRenderedCellsService }, { type: CdsDataGridDisplayService }]; } });

let _cdsDataGridRowId = 0;
class CdsDataGridRowComponent {
    /**
     * @internal
     */
    constructor(tableDisplayService, renderedCellsService, renderedRowsService, changeDetector, renderer, injector) {
        this.tableDisplayService = tableDisplayService;
        this.renderedCellsService = renderedCellsService;
        this.renderedRowsService = renderedRowsService;
        this.changeDetector = changeDetector;
        this.renderer = renderer;
        this.injector = injector;
        /**
         * @internal
         */
        this.highlightable = new CdsHighlightableComponent();
        /**
         * @internal
         */
        this.selectionType = CdsDataGridSelectionType.Single;
        /**
         * Enter a descriptive message to be read out by assistive technology when the row is focused and not selected.
         */
        this.deselectedDescription = 'Select row';
        /**
         * Enter a descriptive message to be read out by assistive technology when the row is focused and selected.
         */
        this.selectedDescription = 'Selected';
        /**
         * Emits an event when the row is clicked and `selectable` is `true`.
         */
        this.select = new EventEmitter();
        /**
         * Emits an event when the row is focused and `selectable` is `true`.
         */
        this.active = new EventEmitter();
        /**
         * @internal
         */
        this.additionalCells = () => [];
        /**
         * @internal
         */
        this.focus = () => {
            this.focusWithoutHighlighting();
            // add the highlighted class manually when arrow keys are used for navigation
            this.highlightable.addHighlightedClass(this.rowElement);
        };
        /**
         * @internal
         */
        this.focusWithoutHighlighting = () => {
            this.setTabIndices(this.rowElement, '0');
            const firstFocusableElement = getFocusableElements$1(this.rowElement).item(0);
            firstFocusableElement?.focus();
        };
        /**
         * @internal
         */
        this.setTabIndices = (rowElement, tabindex) => {
            Array.from(getFocusableElements$1(rowElement)).forEach(element => this.renderer.setAttribute(element, 'tabindex', tabindex));
        };
        _cdsDataGridRowId += 1;
        this.rowId = _cdsDataGridRowId;
        this.renderedCellsService.setRow(this);
    }
    /**
     * @internal
     */
    get id() {
        return 'cds-dg-row-' + kebabCaseTransform(this.name);
    }
    /**
     * @internal
     */
    get headers() {
        return this.tableDisplayService.getDisplayedHeaders();
    }
    /**
     * @internal
     */
    get isLinkRow() {
        return !!this.rowLink || !!this.link;
    }
    /**
     * @internal
     */
    get gridRowIndex() {
        // when stickHeaders is false, row starts from offset 3 because the the <caption> element spans grid rows 1-2
        // and the <thead> spans grid rows 2-3
        // when stickyHeaders is true, row starts from offset 1 because the <tbody> element is a self-contained grid
        return this.tableDisplayService.getDisplayedRows().indexOf(this) +
            (this.tableDisplayService.stickyHeaders ? 1 : 3);
    }
    /**
     * @internal
     */
    get useDefaultLoadingRow() {
        return this._useDefaultLoadingRow === null || this._useDefaultLoadingRow === undefined
            ? true
            : this._useDefaultLoadingRow;
    }
    /**
     * @internal
     */
    get template() {
        return this.loading ? this._loadingTemplate : this._rowTemplate;
    }
    /**
     * @internal
     */
    get rowElement() {
        return this.renderedRowsService.getRenderedRows().get(this);
    }
    /**
     * @internal
     */
    ngOnInit() { }
    /**
     * @internal
     */
    selectRow(event) {
        if (this.selectable) {
            event?.preventDefault();
            event?.stopPropagation();
            this.select.emit();
        }
    }
    /**
     * @internal
     */
    getLabel() {
        return this.name;
    }
    /**
     * @internal
     */
    activateRow() {
        this.active.emit();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.renderedCellsService.destroy();
        this.renderedRowsService.onRemove(this);
    }
}
CdsDataGridRowComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRowComponent, deps: [{ token: CdsDataGridDisplayService }, { token: CdsDataGridRenderedCellsService }, { token: CdsDataGridRenderedRowsService }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
CdsDataGridRowComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsDataGridRowComponent, selector: "cds-datagrid-row", inputs: { name: "name", selected: "selected", selectable: "selectable", loading: "loading", expanded: "expanded", link: "link", _useDefaultLoadingRow: ["useDefaultLoadingRow", "_useDefaultLoadingRow"], deselectedDescription: "deselectedDescription", selectedDescription: "selectedDescription" }, outputs: { select: "select", active: "active" }, providers: [CdsDataGridRenderedCellsService], queries: [{ propertyName: "rowLink", first: true, predicate: CdsDataGridRowLinkDirective, descendants: true }, { propertyName: "cells", predicate: i0.forwardRef(function () { return CdsDataGridCellComponent; }), descendants: true }], viewQueries: [{ propertyName: "_rowTemplate", first: true, predicate: ["template"], descendants: true, static: true }, { propertyName: "_loadingTemplate", first: true, predicate: ["loadingTemplate"], descendants: true, static: true }], ngImport: i0, template: "<ng-template #template>\n  <tr\n    #row\n    [id]=\"id\"\n    class=\"cds-table-row\"\n    [class.-selected]=\"selected\"\n    [class.-selectable]=\"selectable\"\n    [class.-multi-select]=\"selectionType === 'multi'\"\n    [class.-has-link]=\"isLinkRow\"\n    role=\"row\"\n    (keydown.enter)=\"selectRow($event)\"\n    (keydown.space)=\"selectRow($event)\"\n    (click)=\"selectRow($event)\"\n    cdsHighlightable\n    cdsDgRow\n  >\n\n    <div\n      class=\"cds-datagrid-row-select-handle cds-assistive-text\"\n      [style.gridRow]=\"gridRowIndex\"\n      [id]=\"id + '-row-select-helper'\"\n      *ngIf=\"selectable && selectionType !== 'multi'\"\n    >\n      <ng-container *ngIf=\"selected\">{{ selectedDescription }}</ng-container>\n      <ng-container *ngIf=\"!selected\">{{ deselectedDescription }}</ng-container>\n    </div>\n\n    <div\n      class=\"cds-table-row-link\"\n      [id]=\"'cds-dg-link-cell-' + name + '-row-link'\"\n      [style.gridRow]=\"gridRowIndex\"\n      *ngIf=\"isLinkRow\"\n    >\n      <ng-content *ngIf=\"!!rowLink\" select=\"[cdsDataGridRowLink]\"></ng-content>\n      <a *ngIf=\"!rowLink\" [routerLink]=\"link\" (click)=\"$event.stopPropagation()\"></a>\n    </div>\n\n    <ng-container *ngFor=\"let cell of cells\" [ngTemplateOutlet]=\"cell.template\"></ng-container>\n  </tr>\n</ng-template>\n\n<ng-template #loadingTemplate>\n  <tr\n    [id]=\"id + '-loading'\"\n    class=\"cds-table-row\"\n    role=\"row\"\n    cdsDgRow\n  >\n    <ng-container *ngIf=\"useDefaultLoadingRow\">\n      <td\n        *ngFor=\"let header of headers;index as index\"\n        [id]=\"'cds-dg-cell-' + (name | kebabCase) + '-loading-cell'\"\n        [attr.aria-labelledby]=\"id + '-loading-cell-description'\"\n        class=\"skeleton-loading\"\n        [style.gridRow]=\"gridRowIndex\"\n        [style.gridColumn]=\"index + 1\"\n        role=\"gridcell\"\n        cdsDgCell\n      >\n        <div class=\"cds-text-truncation\">\n          <span\n            class=\"cds-assistive-text\"\n            aria-hidden=\"true\"\n            [id]=\"id + '-loading-cell-description'\"\n          >\n          </span>\n        </div>\n      </td>\n    </ng-container>\n\n    <ng-container *ngIf=\"!useDefaultLoadingRow\">\n      <ng-container *ngFor=\"let cell of cells\" [ngTemplateOutlet]=\"cell.template\"></ng-container>\n    </ng-container>\n  </tr>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$3.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: CdsKeyboardHighlightableDirective, selector: "[cdsHighlightable]", inputs: ["isDisabled"] }, { kind: "directive", type: CdsDatagridCellDirective, selector: "[cdsDgCell]" }, { kind: "directive", type: CdsDatagridRowDirective, selector: "[cdsDgRow]" }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridRowComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-datagrid-row', providers: [CdsDataGridRenderedCellsService], template: "<ng-template #template>\n  <tr\n    #row\n    [id]=\"id\"\n    class=\"cds-table-row\"\n    [class.-selected]=\"selected\"\n    [class.-selectable]=\"selectable\"\n    [class.-multi-select]=\"selectionType === 'multi'\"\n    [class.-has-link]=\"isLinkRow\"\n    role=\"row\"\n    (keydown.enter)=\"selectRow($event)\"\n    (keydown.space)=\"selectRow($event)\"\n    (click)=\"selectRow($event)\"\n    cdsHighlightable\n    cdsDgRow\n  >\n\n    <div\n      class=\"cds-datagrid-row-select-handle cds-assistive-text\"\n      [style.gridRow]=\"gridRowIndex\"\n      [id]=\"id + '-row-select-helper'\"\n      *ngIf=\"selectable && selectionType !== 'multi'\"\n    >\n      <ng-container *ngIf=\"selected\">{{ selectedDescription }}</ng-container>\n      <ng-container *ngIf=\"!selected\">{{ deselectedDescription }}</ng-container>\n    </div>\n\n    <div\n      class=\"cds-table-row-link\"\n      [id]=\"'cds-dg-link-cell-' + name + '-row-link'\"\n      [style.gridRow]=\"gridRowIndex\"\n      *ngIf=\"isLinkRow\"\n    >\n      <ng-content *ngIf=\"!!rowLink\" select=\"[cdsDataGridRowLink]\"></ng-content>\n      <a *ngIf=\"!rowLink\" [routerLink]=\"link\" (click)=\"$event.stopPropagation()\"></a>\n    </div>\n\n    <ng-container *ngFor=\"let cell of cells\" [ngTemplateOutlet]=\"cell.template\"></ng-container>\n  </tr>\n</ng-template>\n\n<ng-template #loadingTemplate>\n  <tr\n    [id]=\"id + '-loading'\"\n    class=\"cds-table-row\"\n    role=\"row\"\n    cdsDgRow\n  >\n    <ng-container *ngIf=\"useDefaultLoadingRow\">\n      <td\n        *ngFor=\"let header of headers;index as index\"\n        [id]=\"'cds-dg-cell-' + (name | kebabCase) + '-loading-cell'\"\n        [attr.aria-labelledby]=\"id + '-loading-cell-description'\"\n        class=\"skeleton-loading\"\n        [style.gridRow]=\"gridRowIndex\"\n        [style.gridColumn]=\"index + 1\"\n        role=\"gridcell\"\n        cdsDgCell\n      >\n        <div class=\"cds-text-truncation\">\n          <span\n            class=\"cds-assistive-text\"\n            aria-hidden=\"true\"\n            [id]=\"id + '-loading-cell-description'\"\n          >\n          </span>\n        </div>\n      </td>\n    </ng-container>\n\n    <ng-container *ngIf=\"!useDefaultLoadingRow\">\n      <ng-container *ngFor=\"let cell of cells\" [ngTemplateOutlet]=\"cell.template\"></ng-container>\n    </ng-container>\n  </tr>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: CdsDataGridDisplayService }, { type: CdsDataGridRenderedCellsService }, { type: CdsDataGridRenderedRowsService }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: i0.Injector }]; }, propDecorators: { name: [{
                type: Input
            }], selected: [{
                type: Input
            }], selectable: [{
                type: Input
            }], loading: [{
                type: Input
            }], expanded: [{
                type: Input
            }], link: [{
                type: Input
            }], _useDefaultLoadingRow: [{
                type: Input,
                args: ['useDefaultLoadingRow']
            }], deselectedDescription: [{
                type: Input
            }], selectedDescription: [{
                type: Input
            }], select: [{
                type: Output
            }], active: [{
                type: Output
            }], _rowTemplate: [{
                type: ViewChild,
                args: ['template', { static: true }]
            }], _loadingTemplate: [{
                type: ViewChild,
                args: ['loadingTemplate', { static: true }]
            }], rowLink: [{
                type: ContentChild,
                args: [CdsDataGridRowLinkDirective]
            }], cells: [{
                type: ContentChildren,
                args: [forwardRef(() => CdsDataGridCellComponent), { descendants: true }]
            }] } });
function getFocusableElements$1(element) {
    return element.querySelectorAll(`a[href]:not([disabled]),
    button:not([disabled]),
    textarea:not([disabled]),
    input:not([disabled]),
    select:not([disabled]),
    .cds-datagrid-row-select-handle`);
}

class CdsDataGridToolbarContentDirective {
}
CdsDataGridToolbarContentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridToolbarContentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsDataGridToolbarContentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsDataGridToolbarContentDirective, selector: "[cdsDataGridToolbarInfo], [cdsDataGridToolbarActions]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridToolbarContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsDataGridToolbarInfo], [cdsDataGridToolbarActions]',
                }]
        }] });

class SubscriptionManager {
    constructor() {
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
    }
    destroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
SubscriptionManager.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SubscriptionManager, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SubscriptionManager.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SubscriptionManager });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SubscriptionManager, decorators: [{
            type: Injectable
        }] });

function handleOverflow(datagrid, overflowing) {
    const overflow = datagrid.stickyHeaders && overflowing;
    const displayedHeaders = datagrid.displayService.getDisplayedHeaders();
    const lastHeader = [...displayedHeaders].pop();
    const headerElement = datagrid.renderedHeaderService.getRenderedHeaders().get(lastHeader);
    if (!headerElement) {
        return;
    }
    if (overflow) {
        headerElement.querySelector('.cds-table-header').style.paddingRight = '2rem';
        return;
    }
    headerElement.querySelector('.cds-table-header').style.paddingRight = '';
}
/**
 * @internal
 * Handle changes to header or cell widths
 */
function onColumnWidthChange(datagrid) {
    const displayedHeaders = datagrid.displayService.getDisplayedHeaders();
    updateColumnWidths(displayedHeaders, Array.from(datagrid.renderedRowService.getRenderedRows().values()), datagrid.tableElement.element.nativeElement, datagrid.stickyHeaders);
    freezeColumns(displayedHeaders.filter((header) => !!header.frozen));
    datagrid.changeDetector.detectChanges();
}
function updateColumnWidths(displayedHeaders, displayedRows, table, stickyHeaders) {
    if (!stickyHeaders) {
        table.style.gridTemplateColumns = displayedHeaders.slice(0, displayedHeaders.length)
            .map(header => header.width || `minmax(min-content, ${header.orientation === CdsColumnOrientation.Content ? 'max-content' : '1fr'})`)
            .join(' ');
        return;
    }
    const tableHead = table.querySelector('thead');
    const tableBody = table.querySelector('tbody');
    const headerColumnWidths = displayedHeaders.slice(0, displayedHeaders.length - 1)
        .map((header, index) => {
        const cells = displayedRows.map(row => row.querySelectorAll('td[cdsdgcell]')[index]);
        return calculateColumnWidth(header, cells || []);
    })
        .join(' ');
    const lastHeader = displayedHeaders[displayedHeaders.length - 1];
    const lastColumnCells = displayedRows.map(row => row.querySelectorAll('td[cdsdgcell]')[displayedHeaders.length - 1]);
    const lastHeaderColumnWidth = calculateColumnWidth(lastHeader, lastColumnCells || []);
    tableHead.style.gridTemplateColumns = `${headerColumnWidths} ${lastHeaderColumnWidth}`;
    const bodyColumnWidths = displayedHeaders.slice(0, displayedHeaders.length - 1)
        .map(header => `${header.element?.clientWidth}px`)
        .join(' ');
    // This will fix horizontal scroll for sticky headers
    const scrollWidth = tableBody.offsetWidth - tableBody.clientWidth;
    const lastBodyColumnWidth = `${displayedHeaders[displayedHeaders.length - 1].element?.clientWidth - (scrollWidth + 5)}px`;
    tableBody.style.gridTemplateColumns = stickyHeaders
        ? `${bodyColumnWidths} minmax(${lastBodyColumnWidth}, auto)`
        : '';
}
function calculateColumnWidth(header, cells = []) {
    let minColumnWidth = 'min-content';
    // 32 = header padding, 28 = sort icon + margin
    const headerWidth = header.element?.querySelector('.cds-table-header-label').scrollWidth + 32 + (header.sortable ? 28 : 0);
    cells = cells.filter(cell => !!cell);
    // 32 = cell padding
    const cellWidth = cells.length > 0
        ? Math.max(...cells.map(cell => cell.querySelector('.cell-content > div')
            ? cell.querySelector('.cell-content > div').scrollWidth + 32
            : headerWidth))
        : 0;
    // Of the cell width and header width, use whichever width is greater as the absolute minimum width of the column
    minColumnWidth = `${Math.max(cellWidth, headerWidth)}px`;
    if (header.width) {
        return header.width;
    }
    const maxColumnWidth = header.orientation === CdsColumnOrientation.Content
        ? 'max-content'
        : '1fr';
    return `minmax(${minColumnWidth}, ${maxColumnWidth})`;
}
function freezeColumns(frozenHeaders) {
    if (frozenHeaders.length <= 0) {
        return;
    }
    setFrozenColumnOffsets(frozenHeaders);
    setFrozenColumnBorder(frozenHeaders);
}
function setFrozenColumnOffsets(frozenHeaders) {
    // if the header is frozen, get the widths of all headers at lower indexes in the array.
    frozenHeaders.forEach((header, index) => {
        const offset = frozenHeaders
            .slice(0, index)
            .reduce((totalOffset, previousHeader) => totalOffset += previousHeader.element?.clientWidth, 0);
        header.frozenOffsetLeft = offset;
    });
}
function setFrozenColumnBorder(frozenHeaders) {
    const sortedFrozenHeaders = [...frozenHeaders].sort((a, b) => a.position - b.position);
    // give the last frozen header a right border
    sortedFrozenHeaders.forEach((header) => {
        header.frozenBorderRight = false;
    });
    sortedFrozenHeaders[sortedFrozenHeaders.length - 1].frozenBorderRight = true;
}

function updateRowLoadingState(datagrid, loading) {
    if (loading === undefined) {
        return;
    }
    datagrid.allRows.toArray()
        .forEach((row) => (row.loading = loading));
}

function watchForOverflow($finishedRenderingRows, table, $destroy) {
    const tableBody = table.querySelector('tbody');
    const $clientHeightChange = ResizeObserverSubject(tableBody)
        .pipe(takeUntil($destroy), map(() => tableBody.clientHeight), distinctUntilChanged());
    // Only emit after rows have finished and the height changes. Both events will trigger an emission, but only
    // after both events have emitted at least once
    return combineLatest([$finishedRenderingRows, $clientHeightChange])
        .pipe(map(() => tableBody.clientHeight < tableBody.scrollHeight));
}

const noop = () => { };
class CdsResizableContainerPresenter {
    constructor() {
        // self: HTMLElement;
        this.$cancelSubscriptions = new Subject();
        this.$change = new Subject();
        this.change = this.$change.asObservable()
            .pipe(skip(1), debounceTime(0, animationFrameScheduler));
    }
    watch(resizableElements, resizeService, callbacks) {
        this.cache = new Map();
        resizableElements.forEach((entry, index) => {
            const config = { width: '', resized: false };
            this.cache.set(entry, config);
        });
        this.setupSubscriptions(resizeService, callbacks);
    }
    setupSubscriptions(resizeService, callbacks) {
        const { onResizeStart = noop, onResize = noop, onResizeEnd = noop, } = callbacks;
        resizeService.resizeStart
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(event => {
            onResizeStart(event);
            this.prepForResize(event);
        });
        resizeService.resize
            .pipe(takeUntil(this.$cancelSubscriptions), debounceTime(0, animationFrameScheduler))
            .subscribe((event) => {
            onResize(event);
            this.updateWidth(event);
        });
        resizeService.resizeEnd
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => onResizeEnd());
    }
    prepForResize(event) {
        // Lock in the widths at the start of resize
        this.cache.forEach((value, key) => {
            if (value.resized === false) {
                value.width = `${key.clientWidth}px`;
            }
        });
        this.$change.next(this.cache);
    }
    updateWidth(event) {
        this.cache.get(event.element).width = event.width;
        this.cache.get(event.element).resized = true;
        this.$change.next(this.cache);
    }
    unwatch() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
    destroy() {
        this.unwatch();
        this.$change.complete();
    }
}
CdsResizableContainerPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizableContainerPresenter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsResizableContainerPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizableContainerPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizableContainerPresenter, decorators: [{
            type: Injectable
        }] });

class CdsResizableTableDirective {
    /**
     * @internal
     */
    constructor(resizeService, renderedHeaderService, resizablePresenter, renderer, table, cancelSubscriptions) {
        this.resizeService = resizeService;
        this.renderedHeaderService = renderedHeaderService;
        this.resizablePresenter = resizablePresenter;
        this.renderer = renderer;
        this.table = table;
        this.cancelSubscriptions = cancelSubscriptions;
        this.$pause = new Subject();
        /**
         * @internal
         */
        // eslint-disable-next-line @angular-eslint/no-output-native
        this.resizeChange = new EventEmitter();
        this.resizablePresenter.change
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions))
            .subscribe((event) => {
            this.headers.forEach(header => {
                const element = this.renderedHeaderService.getHeaderElement(header);
                header.width = event.get(element).width;
            });
            this.resizeChange.next();
        });
    }
    set on(on) {
        if (on) {
            this.watch();
        }
        else {
            this.unwatch();
        }
    }
    watch() {
        const callbacks = {
            onResizeStart: () => {
                const tableBody = this.table.nativeElement.querySelector('tbody');
                this.renderer.setStyle(tableBody, 'grid-template-columns', 'inherit');
            },
            onResizeEnd: () => {
                this.onResizeEnd();
            }
        };
        this.renderedHeaderService.renderedHeaders
            .pipe(filter(renderedHeaders => this.headers.every(header => !!renderedHeaders.get(header))), map(headerElements => Array.from(headerElements.values())))
            .subscribe(headerElements => {
            this.resizablePresenter.unwatch();
            this.resizablePresenter.watch(headerElements, this.resizeService, callbacks);
        });
    }
    unwatch() {
        this.resizablePresenter.unwatch();
    }
    ngOnDestroy() {
        this.cancelSubscriptions.destroy();
        this.resizablePresenter.destroy();
    }
}
CdsResizableTableDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizableTableDirective, deps: [{ token: CdsResizeService }, { token: CdsDataGridRenderedHeadersService }, { token: CdsResizableContainerPresenter }, { token: i0.Renderer2 }, { token: i0.ElementRef }, { token: SubscriptionManager }], target: i0.ɵɵFactoryTarget.Directive });
CdsResizableTableDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsResizableTableDirective, selector: "[resizableTable]", inputs: { onResizeEnd: "onResizeEnd", headers: "headers", on: ["resizableTable", "on"] }, outputs: { resizeChange: "resizeChange" }, providers: [CdsResizableContainerPresenter, SubscriptionManager], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsResizableTableDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[resizableTable]',
                    providers: [CdsResizableContainerPresenter, SubscriptionManager],
                }]
        }], ctorParameters: function () { return [{ type: CdsResizeService }, { type: CdsDataGridRenderedHeadersService }, { type: CdsResizableContainerPresenter }, { type: i0.Renderer2 }, { type: i0.ElementRef }, { type: SubscriptionManager }]; }, propDecorators: { onResizeEnd: [{
                type: Input
            }], headers: [{
                type: Input
            }], on: [{
                type: Input,
                args: ['resizableTable']
            }], resizeChange: [{
                type: Output
            }] } });

/**
 * ### Description
 * A Datagrid uses a semantic HTML table to display rows of data.
 */
class CdsDataGridComponent {
    /**
     * @internal
     */
    constructor(changeDetector, renderer, displayService, renderedHeaderService, renderedRowService, focusableListPresenter, elementRef, cancelSubscriptions) {
        this.changeDetector = changeDetector;
        this.renderer = renderer;
        this.displayService = displayService;
        this.renderedHeaderService = renderedHeaderService;
        this.renderedRowService = renderedRowService;
        this.focusableListPresenter = focusableListPresenter;
        this.elementRef = elementRef;
        this.cancelSubscriptions = cancelSubscriptions;
        /**
         * @internal
         */
        this.onColumnWidthChange = onColumnWidthChange;
        /**
         * @internal
         */
        this.$loading = new BehaviorSubject(undefined);
        /**
         * @internal
         */
        this.$panel = this.displayService.panel;
        /**
         * @internal
         */
        this.onResizeEnd = () => {
            this.changeDetector.detectChanges();
        };
    }
    /**
     * @internal
     */
    get id() {
        return 'cds-datagrid-' + kebabCaseTransform(this.name);
    }
    /**
     * @internal
     */
    get displayedHeaders() {
        return this.displayService._displayedHeaders;
    }
    /**
     * @internal
     */
    get displayedRows() {
        return this.displayService._displayedRows;
    }
    /**
     * Toggles skeleton loading for all the rows in the Datagrid.
     */
    set loading(loading) {
        this.$loading.next(loading);
    }
    /**
     * Always display the headers regardless of the table's vertical scroll position.
     */
    set stickyHeaders(stickyHeaders) {
        this._stickyHeaders = stickyHeaders;
        this.displayService.stickyHeaders = stickyHeaders;
    }
    get stickyHeaders() {
        return this._stickyHeaders || this.elementRef.nativeElement.hasAttribute('cdsVirtualScroll');
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        merge(this.renderedHeaderService.$finishedRenderingHeaders, this.renderedRowService.$finishedRenderingRows)
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions), tap(() => onColumnWidthChange(this)), switchMap(() => {
            const $contentChanged = MutationObserverSubject(this.tableElement.element.nativeElement, 0);
            const $headerResize = ResizeObserverSubject(this.tableElement.element.nativeElement.querySelector('thead'));
            return merge($contentChanged, $headerResize);
        }))
            .subscribe(() => onColumnWidthChange(this));
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.$loading
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions))
            .subscribe(loading => updateRowLoadingState(this, loading));
        this.onContentChangeSubscription();
        this.stickyHeaderOverflowSubscription();
        this.stickyHeaderOnScrollSubscription();
        this.displayService.setDisplayedHeaders(this.allHeaders.toArray());
        this.displayService.setDisplayedRows(this.allRows.toArray());
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    onContentChangeSubscription() {
        this.allHeaders.changes
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions), debounceTime(0))
            .subscribe(() => {
            this.displayService.setDisplayedHeaders(this.allHeaders.toArray());
            this.render();
        });
        this.allRows.changes
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions), debounceTime(0))
            .subscribe(() => {
            this.displayService.setDisplayedRows(this.allRows.toArray());
            this.render();
        });
        const $cancelSub = new Subject();
        this.renderedRowService.$finishedRenderingRows
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions))
            .subscribe(rows => {
            $cancelSub.next();
            const clearTabIndices = (row) => {
                [...rows].filter(([entry]) => entry !== row)
                    .forEach(([, rowElement]) => {
                    row.highlightable.removeHighlightedClass(row.rowElement);
                    row.setTabIndices(rowElement, '-1');
                });
            };
            const registry = Array.from(rows.entries()).map(([row, rowElement]) => {
                // listen for when focus is moved into a row, reset tab index and highlighted styles for other rows
                fromEvent(rowElement, 'focusin')
                    .pipe(takeUntil(merge($cancelSub, this.cancelSubscriptions.$cancelSubscriptions)))
                    .subscribe(() => {
                    clearTabIndices(row);
                    this.focusableListPresenter.setActiveItem(row);
                });
                // listen for when the row is clicked, focus the first item without highlighting the row
                fromEvent(rowElement, 'click')
                    .pipe(takeUntil(merge($cancelSub, this.cancelSubscriptions.$cancelSubscriptions)))
                    .subscribe(() => {
                    row.focusWithoutHighlighting();
                });
                return {
                    component: row,
                    element: rowElement,
                };
            });
            this.focusableListPresenter.watch(registry, {
                focusOnClick: false
            });
            const firstRow = [...rows.keys()][0];
            clearTabIndices(firstRow);
            firstRow.setTabIndices(firstRow.rowElement, '0');
        });
    }
    /**
     * @internal
     */
    stickyHeaderOnScrollSubscription() {
        // keep the thead's horizontal scroll in sync with the tbody scroll on sticky headers
        const tbody = this.tableElement.element.nativeElement.querySelector('tbody');
        const thead = this.tableElement.element.nativeElement.querySelector('thead');
        let scrollLeft = tbody.scrollLeft;
        fromEvent(tbody, 'scroll')
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions))
            .subscribe(() => {
            if (!this.stickyHeaders && thead.scrollLeft === tbody.scrollLeft) {
                return;
            }
            scrollLeft = tbody.scrollLeft;
            thead.scrollTo({ left: scrollLeft });
        });
    }
    /**
     * @internal
     */
    stickyHeaderOverflowSubscription() {
        const $contentChanged = MutationObserverSubject(this.tableElement.element.nativeElement, 0)
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions));
        const $headerResize = ResizeObserverSubject(this.tableElement.element.nativeElement.querySelector('thead'))
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions));
        merge($contentChanged, $headerResize)
            .subscribe(() => onColumnWidthChange(this));
        watchForOverflow(this.renderedRowService.$finishedRenderingRows.pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions)), this.tableElement.element.nativeElement, this.cancelSubscriptions.$cancelSubscriptions)
            .subscribe(overflowing => {
            handleOverflow(this, overflowing);
        });
        const tbody = this.tableElement.element.nativeElement.querySelector('tbody');
        const thead = this.tableElement.element.nativeElement.querySelector('thead');
        let scrollLeft = tbody.scrollLeft;
        fromEvent(tbody, 'scroll')
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions))
            .subscribe(() => {
            if (!this.stickyHeaders && thead.scrollLeft === tbody.scrollLeft) {
                return;
            }
            scrollLeft = tbody.scrollLeft;
            thead.scrollTo({ left: scrollLeft });
        });
        this.render();
    }
    /**
     * @internal
     * Triggers the render process.
     */
    render() {
        this.renderer.setStyle(this.tableElement.element.nativeElement, '--column-number', this.displayService.getDisplayedHeaders().length, 2);
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$loading.complete();
        this.cancelSubscriptions.destroy();
        this.displayService.destroy();
        this.renderedHeaderService.destroy();
        this.renderedRowService.destroy();
        this.focusableListPresenter.destroy();
    }
}
CdsDataGridComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: CdsDataGridDisplayService }, { token: CdsDataGridRenderedHeadersService }, { token: CdsDataGridRenderedRowsService }, { token: CdsFocusableListPresenter }, { token: i0.ElementRef }, { token: SubscriptionManager }], target: i0.ɵɵFactoryTarget.Component });
CdsDataGridComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsDataGridComponent, selector: "cds-datagrid", inputs: { name: "name", description: "description", compact: "compact", flexHeight: "flexHeight", loading: "loading", stickyHeaders: "stickyHeaders", scrollHeight: "scrollHeight", resizable: "resizable" }, providers: [
        CdsDataGridDisplayService,
        CdsDataGridRenderedHeadersService,
        CdsDataGridRenderedRowsService,
        CdsResizeService,
        SubscriptionManager,
        CdsFocusableListPresenter
    ], queries: [{ propertyName: "allHeaders", predicate: i0.forwardRef(function () { return CdsDataGridHeaderComponent; }), descendants: true }, { propertyName: "allRows", predicate: i0.forwardRef(function () { return CdsDataGridRowComponent; }), descendants: true }, { propertyName: "toolbarContent", predicate: CdsDataGridToolbarContentDirective, descendants: true }], viewQueries: [{ propertyName: "tableElement", first: true, predicate: ["table"], descendants: true, read: ViewContainerRef, static: true }, { propertyName: "paginationElement", first: true, predicate: ["pagination"], descendants: true, read: ViewContainerRef, static: true }, { propertyName: "toolbarActionContainer", first: true, predicate: ["toolbarActions"], descendants: true, read: ViewContainerRef, static: true }], ngImport: i0, template: "<div class=\"cds-data-table-container\" [class.-flex-height]=\"flexHeight\">\n  <span\n    class=\"cds-assistive-text\"\n    [id]=\"id + '-table-description'\"\n    aria-hidden=\"true\"\n    >{{ description }}</span\n  >\n\n  <div class=\"cds-toolbar\">\n    <div class=\"cds-toolbar-info\" [id]=\"id + '-toolbar-info'\">\n      <ng-content select=\"[cdsDataGridToolbarInfo]\"></ng-content>\n    </div>\n    <div class=\"cds-toolbar-action\">\n      <ng-container #toolbarActions>\n        <ng-content select=\"[cdsDataGridToolbarActions]\"></ng-content>\n      </ng-container>\n    </div>\n    <ng-content select=\"[cdsDataGridToolbar]\"></ng-content>\n  </div>\n\n  <div class=\"cds-data-table-content\">\n    <table\n      #table\n      role=\"grid\"\n      [id]=\"id\"\n      class=\"cds-data-table cds-data-grid\"\n      [class.-compact]=\"compact\"\n      [class.-sticky-headers]=\"stickyHeaders\"\n      [attr.aria-labelledby]=\"id + '-table-description'\"\n      [attr.aria-describedby]=\"id + '-toolbar-info'\"\n      [style.width]=\"($panel | async) ? width : null\"\n      [resizableTable]=\"resizable\"\n      [onResizeEnd]=\"onResizeEnd\"\n      [headers]=\"displayedHeaders\"\n      (resizeChange)=\"onColumnWidthChange(this)\"\n    >\n      <caption class=\"cds-data-table-caption\"></caption>\n  \n      <thead role=\"rowgroup\">\n        <tr role=\"row\">\n          <ng-template *ngFor=\"let header of displayedHeaders\" [ngTemplateOutlet]=\"header.template\"></ng-template>\n        </tr>\n      </thead>\n  \n      <tbody role=\"rowgroup\" #tableBody [style.maxHeight]=\"scrollHeight\">\n        <ng-template *ngFor=\"let row of displayedRows\" [ngTemplateOutlet]=\"row.template\"></ng-template>\n      </tbody>\n  \n      <tfoot class=\"cds-data-table-footer\"></tfoot>\n    </table>\n  \n    <ng-container *ngIf=\"$panel | async as panel\">\n      <ng-template [ngTemplateOutlet]=\"panel.template\"></ng-template>\n    </ng-container>\n  </div>\n\n  <div class=\"cds-data-table-pagination\">\n    <ng-content select=\"[cdsDataGridPaginator]\"></ng-content>\n    <ng-container #pagination></ng-container>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: CdsResizableTableDirective, selector: "[resizableTable]", inputs: ["onResizeEnd", "headers", "resizableTable"], outputs: ["resizeChange"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-datagrid', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        CdsDataGridDisplayService,
                        CdsDataGridRenderedHeadersService,
                        CdsDataGridRenderedRowsService,
                        CdsResizeService,
                        SubscriptionManager,
                        CdsFocusableListPresenter
                    ], template: "<div class=\"cds-data-table-container\" [class.-flex-height]=\"flexHeight\">\n  <span\n    class=\"cds-assistive-text\"\n    [id]=\"id + '-table-description'\"\n    aria-hidden=\"true\"\n    >{{ description }}</span\n  >\n\n  <div class=\"cds-toolbar\">\n    <div class=\"cds-toolbar-info\" [id]=\"id + '-toolbar-info'\">\n      <ng-content select=\"[cdsDataGridToolbarInfo]\"></ng-content>\n    </div>\n    <div class=\"cds-toolbar-action\">\n      <ng-container #toolbarActions>\n        <ng-content select=\"[cdsDataGridToolbarActions]\"></ng-content>\n      </ng-container>\n    </div>\n    <ng-content select=\"[cdsDataGridToolbar]\"></ng-content>\n  </div>\n\n  <div class=\"cds-data-table-content\">\n    <table\n      #table\n      role=\"grid\"\n      [id]=\"id\"\n      class=\"cds-data-table cds-data-grid\"\n      [class.-compact]=\"compact\"\n      [class.-sticky-headers]=\"stickyHeaders\"\n      [attr.aria-labelledby]=\"id + '-table-description'\"\n      [attr.aria-describedby]=\"id + '-toolbar-info'\"\n      [style.width]=\"($panel | async) ? width : null\"\n      [resizableTable]=\"resizable\"\n      [onResizeEnd]=\"onResizeEnd\"\n      [headers]=\"displayedHeaders\"\n      (resizeChange)=\"onColumnWidthChange(this)\"\n    >\n      <caption class=\"cds-data-table-caption\"></caption>\n  \n      <thead role=\"rowgroup\">\n        <tr role=\"row\">\n          <ng-template *ngFor=\"let header of displayedHeaders\" [ngTemplateOutlet]=\"header.template\"></ng-template>\n        </tr>\n      </thead>\n  \n      <tbody role=\"rowgroup\" #tableBody [style.maxHeight]=\"scrollHeight\">\n        <ng-template *ngFor=\"let row of displayedRows\" [ngTemplateOutlet]=\"row.template\"></ng-template>\n      </tbody>\n  \n      <tfoot class=\"cds-data-table-footer\"></tfoot>\n    </table>\n  \n    <ng-container *ngIf=\"$panel | async as panel\">\n      <ng-template [ngTemplateOutlet]=\"panel.template\"></ng-template>\n    </ng-container>\n  </div>\n\n  <div class=\"cds-data-table-pagination\">\n    <ng-content select=\"[cdsDataGridPaginator]\"></ng-content>\n    <ng-container #pagination></ng-container>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: CdsDataGridDisplayService }, { type: CdsDataGridRenderedHeadersService }, { type: CdsDataGridRenderedRowsService }, { type: CdsFocusableListPresenter }, { type: i0.ElementRef }, { type: SubscriptionManager }]; }, propDecorators: { name: [{
                type: Input
            }], description: [{
                type: Input
            }], compact: [{
                type: Input
            }], flexHeight: [{
                type: Input
            }], loading: [{
                type: Input
            }], stickyHeaders: [{
                type: Input
            }], scrollHeight: [{
                type: Input
            }], resizable: [{
                type: Input
            }], tableElement: [{
                type: ViewChild,
                args: ['table', { read: ViewContainerRef, static: true }]
            }], paginationElement: [{
                type: ViewChild,
                args: ['pagination', { read: ViewContainerRef, static: true }]
            }], toolbarActionContainer: [{
                type: ViewChild,
                args: ['toolbarActions', { read: ViewContainerRef, static: true }]
            }], allHeaders: [{
                type: ContentChildren,
                args: [forwardRef(() => CdsDataGridHeaderComponent), { descendants: true }]
            }], allRows: [{
                type: ContentChildren,
                args: [forwardRef(() => CdsDataGridRowComponent), { descendants: true }]
            }], toolbarContent: [{
                type: ContentChildren,
                args: [CdsDataGridToolbarContentDirective, { descendants: true }]
            }] } });

class CdsSmartRowDirective {
    /**
     * @internal
     */
    constructor(component) {
        this.component = component;
    }
    /**
     * @internal
     * Unique identifier for the record. The actual property value of the `keyProp` key passed into the SmartRowTemplate.
     */
    get rowKey() {
        return this._data._smartRowKey;
    }
    /**
     * @internal
     */
    get rowPosition() {
        return this._data._smartRowPosition;
    }
    /**
     * @internal
     */
    set data(data) {
        this._data = data;
        this.component.loading = this._data._loading;
    }
    /**
     * @internal
     */
    ngOnInit() { }
}
CdsSmartRowDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartRowDirective, deps: [{ token: CdsDataGridRowComponent }], target: i0.ɵɵFactoryTarget.Directive });
CdsSmartRowDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSmartRowDirective, selector: "cds-datagrid-row[data]", inputs: { data: "data" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartRowDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'cds-datagrid-row[data]',
                }]
        }], ctorParameters: function () { return [{ type: CdsDataGridRowComponent }]; }, propDecorators: { data: [{
                type: Input
            }] } });

class SelectableItemPresenter {
    constructor() {
        this.selection = [];
        this.$change = new Subject();
        this.change = this.$change.asObservable();
    }
    setSelection(data, emit) {
        this.selection = [...data];
        if (emit) {
            this.$change.next(this.selection);
        }
    }
    selectSingle(key) {
        if (this.selection.includes(key)) {
            this.select(false, key);
        }
        else {
            this.selection.splice(0, 1); // remove the current selection first
            this.select(true, key);
        }
    }
    select(select, ...keys) {
        if (select) {
            this.selection = this.selection.concat(keys.filter(key => !this.selection.includes(key)));
        }
        else {
            this.selection.filter(key => keys.includes(key))
                .forEach(key => {
                const index = this.selection.indexOf(key);
                this.selection.splice(index, 1);
            });
        }
        this.$change.next(this.selection);
    }
    getSelection() {
        return this.selection.slice();
    }
    isSelected(key) {
        return this.getSelection().includes(key);
    }
    destroy() {
        this.$change.complete();
    }
}
SelectableItemPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SelectableItemPresenter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SelectableItemPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SelectableItemPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SelectableItemPresenter, decorators: [{
            type: Injectable
        }] });

/**
 * Reusable service for instantiating a smart row iterator component
 */
class SmartRowIteratorService {
    constructor(appRef, injector) {
        this.appRef = appRef;
        this.injector = injector;
        this.createIterator();
    }
    get component() {
        return this.iteratorRef.instance;
    }
    /**
     * @internal
     */
    createIterator() {
        this.iteratorRef = createComponent(CdsSmartRowIteratorComponent, this.appRef.injector, this.injector);
        this.iteratorRef.changeDetectorRef.detectChanges();
        this.appRef.attachView(this.iteratorRef.hostView);
    }
    destroy() {
        if (this.iteratorRef) {
            this.iteratorRef.destroy();
        }
    }
}
SmartRowIteratorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SmartRowIteratorService, deps: [{ token: i0.ApplicationRef }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
SmartRowIteratorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SmartRowIteratorService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SmartRowIteratorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.ApplicationRef }, { type: i0.Injector }]; } });

function doFilter(filters, collection) {
    if (!filters || filters.some(filter => !filter.matchFn)) {
        return {
            state: null,
            data: collection
        };
    }
    return {
        state: filters.map(filter => ({ key: filter.key, value: filter.value })),
        data: collection.filter(entry => filters.every(filter => filter.matchFn ? filter.matchFn(filter.value, entry, collection) : true))
    };
}

class DataGridFnService {
    constructor() {
        /**
         * Custom function to sort items in the array using value of the sort key property.
         */
        this.comparatorFns = {};
        this.filterFns = {};
    }
}
DataGridFnService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DataGridFnService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DataGridFnService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DataGridFnService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DataGridFnService, decorators: [{
            type: Injectable
        }] });

function doPage(page, collection) {
    if (!page || !page.paginationFn) {
        return {
            state: null,
            data: collection
        };
    }
    return {
        state: { ...page, totalResults: collection.length },
        data: page.paginationFn(page.offset, page.pageSize, collection)
    };
}

function doSort(sort, collection) {
    if (!sort || !sort.comparatorFn) {
        return {
            state: null,
            data: collection,
        };
    }
    return {
        state: sort,
        data: [...collection].sort((item1, item2) => sort.comparatorFn(item1, item2, sort.direction, collection)),
    };
}

class CdsDataGridStateService {
    constructor() {
        this._state = {
            page: null,
            sort: null,
            filters: null
        };
        this.$requestedFilters = new Subject();
        this.$requestedSort = new Subject();
        this.$requestedPage = new Subject();
        this.requestedFilters = this.$requestedFilters.asObservable();
        this.requestedSort = this.$requestedSort.asObservable();
        this.requestedState = merge(this.$requestedFilters.pipe(map(filters => ({
            ...this._state,
            page: this._state.page ? { ...this._state.page, offset: 0 } : this._state.page,
            filters
        }))), this.$requestedSort.pipe(map(sort => ({
            ...this._state,
            page: this._state.page ? { ...this._state.page, offset: 0 } : this._state.page,
            sort
        }))), this.$requestedPage.pipe(map(page => ({
            ...this._state,
            page
        }))));
        this.$change = new Subject();
        this.change = this.$change.asObservable();
    }
    getState() {
        return { ...this._state };
    }
    getRequestedState() {
        return { ...this._state };
    }
    setState(state, notifyParent = true) {
        this._state = { ...state };
        this.$change.next({ state, notify: notifyParent });
    }
    requestPage(page) {
        this.$requestedPage.next(page);
    }
    requestFilters(filters) {
        const filtersCopy = this._state.filters || [];
        filters.forEach(filter => {
            const oldFilterIndex = filtersCopy.findIndex(entry => entry.key === filter.key);
            if (oldFilterIndex > -1) {
                filtersCopy.splice(oldFilterIndex, 1, filter);
            }
            else {
                filtersCopy.push(filter);
            }
        });
        this.$requestedFilters.next(filtersCopy);
    }
    requestSort(sort) {
        this.$requestedSort.next(sort);
    }
    destroy() {
        this.$requestedFilters.complete();
        this.$requestedSort.complete();
        this.$requestedPage.complete();
        this.$change.complete();
    }
}
CdsDataGridStateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridStateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsDataGridStateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridStateService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridStateService, decorators: [{
            type: Injectable
        }] });

/**
 * This directive exposes an API for selecting the data contained in the rows of a Smart DataGrid.
 *
 * To enable selection, you must add a `cdsSmartSelect` attribute to your Smart Datagrid row element.
 *
 * The Selectable Datagrid also does not support switching between Single- and Multiple-Row selection at this time.
 *
 * When a row is selected/deselected as a result of user interaction, the DataGrid will emit a `selectionChange` event
 * which contains an array where each entry is the data associated with a selected row.
 */
class CdsSmartDataGridSelectDirective {
    constructor(selectService, component, smartRow, changeDetector) {
        this.selectService = selectService;
        this.component = component;
        this.smartRow = smartRow;
        this.changeDetector = changeDetector;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        this.component.selectable = true;
        this.component.select
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => this.onSelect());
        this.selectService.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => {
            this.component.selected = this.selectService.isSelected(this.smartRow.rowKey);
            this.changeDetector.detectChanges();
        });
    }
    /**
     * @internal
     */
    ngOnInit() {
        this.component.selected = !!this.selectService.isSelected(this.smartRow.rowKey);
    }
    /**
     * @internal
     */
    onSelect() {
        this.selectService.selectSingle(this.smartRow.rowKey);
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsSmartDataGridSelectDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridSelectDirective, deps: [{ token: SelectableItemPresenter }, { token: CdsDataGridRowComponent }, { token: CdsSmartRowDirective }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsSmartDataGridSelectDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSmartDataGridSelectDirective, selector: "[cdsSmartDataGridSelect]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridSelectDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsSmartDataGridSelect]'
                }]
        }], ctorParameters: function () { return [{ type: SelectableItemPresenter }, { type: CdsDataGridRowComponent }, { type: CdsSmartRowDirective }, { type: i0.ChangeDetectorRef }]; } });

/* eslint-disable @angular-eslint/component-selector */
class CdsSmartDataGridCheckboxCellComponent {
    constructor(
    /** @internal */ cell, 
    /** @internal */ row, smartRow, selectService, changeDetector, renderer, smartSelect, renderedCellService) {
        this.cell = cell;
        this.row = row;
        this.smartRow = smartRow;
        this.selectService = selectService;
        this.changeDetector = changeDetector;
        this.renderer = renderer;
        this.smartSelect = smartSelect;
        this.renderedCellService = renderedCellService;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        /**
         * @internal
         */
        this.formControl = new UntypedFormControl(false);
        /**
         * @internal
         */
        this.onSelectRow = () => {
            this.selectService.select(!this.formControl.value, this.smartRow.rowKey);
            this.changeDetector.detectChanges();
        };
        this.cell.truncate = false;
        this.selectService.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => {
            this.formControl.setValue(this.selectService.isSelected(this.smartRow.rowKey));
            this.changeDetector.detectChanges();
        });
        this.smartSelect.onSelect = this.onSelectRow;
    }
    /**
     * @internal
     */
    get label() {
        return this.formControl.value
            ? this.row.selectedDescription
            : this.row.deselectedDescription;
    }
    /**
     * @internal
     */
    get name() {
        return `${this.row.name}-datagrid-row-${this.rowId}-checkbox`;
    }
    /**
     * @internal
     */
    get rowId() {
        return this.row.rowId;
    }
    /**
     * @internal
     */
    ngOnInit() {
        this.row.selectionType = CdsDataGridSelectionType.Multi;
        this.renderedCellService.renderedCells
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => {
            if (this.cell.cellElement) {
                this.renderer.addClass(this.cell.cellElement, 'cds-datagrid-checkbox-cell');
            }
        });
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        const selected = this.row.selected
            || this.selectService.isSelected(this.smartRow.rowKey);
        this.formControl.setValue(selected);
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    onSelectCheckbox(value) {
        this.selectService.select(value, this.smartRow.rowKey);
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsSmartDataGridCheckboxCellComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridCheckboxCellComponent, deps: [{ token: CdsDataGridCellComponent }, { token: CdsDataGridRowComponent }, { token: CdsSmartRowDirective }, { token: SelectableItemPresenter }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: CdsSmartDataGridSelectDirective }, { token: CdsDataGridRenderedCellsService }], target: i0.ɵɵFactoryTarget.Component });
CdsSmartDataGridCheckboxCellComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSmartDataGridCheckboxCellComponent, selector: "cds-smart-datagrid-checkbox-cell", viewQueries: [{ propertyName: "checkbox", first: true, predicate: CdsCheckboxComponent, descendants: true }], ngImport: i0, template: `
    <cds-checkbox
      class="cds-datagrid-smart-checkbox-cell"
      [name]="name"
      [label]="label"
      [hideLabel]="true"
      [formControl]="formControl"
      (change)="onSelectCheckbox($event)"
      (keydown.space)="$event.stopPropagation()"
      (click)="$event.stopPropagation()"
    >
    </cds-checkbox>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: CdsCheckboxComponent, selector: "cds-checkbox", inputs: ["isDisabled", "label", "hideLabel", "controlLabels", "name", "leftAlignedLabel"], outputs: ["change"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridCheckboxCellComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-smart-datagrid-checkbox-cell',
                    template: `
    <cds-checkbox
      class="cds-datagrid-smart-checkbox-cell"
      [name]="name"
      [label]="label"
      [hideLabel]="true"
      [formControl]="formControl"
      (change)="onSelectCheckbox($event)"
      (keydown.space)="$event.stopPropagation()"
      (click)="$event.stopPropagation()"
    >
    </cds-checkbox>
  `
                }]
        }], ctorParameters: function () { return [{ type: CdsDataGridCellComponent }, { type: CdsDataGridRowComponent }, { type: CdsSmartRowDirective }, { type: SelectableItemPresenter }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: CdsSmartDataGridSelectDirective }, { type: CdsDataGridRenderedCellsService }]; }, propDecorators: { checkbox: [{
                type: ViewChild,
                args: [CdsCheckboxComponent]
            }] } });

/**
 * This directive controls the display of content in the Datagrid based on the results of internal processes (filering, sorting,
 * selection etc). It is required if you wish to use any of the Smart Datagrid directives (SmartDatagridPaginator, SmartDatagridSort etc).
 *
 * To enable the Smart Datagrid and gain access to the functionality provided by the SmartDatagridPaginator etc, you must pass in a value
 * that represents the dataset to be displayed in the datagrid to `data` attribute of the `<cds-datagrid>` element.
 *
 * The DataGrid will emit a CdsDataGridStateChange event when a state change occurs as a result of user interaction.
 */
class CdsSmartDataGridDirective {
    /**
     * @internal
     */
    constructor(stateService, fnService, selectService, iterator, cancelSubscriptions, viewContainerRef) {
        this.stateService = stateService;
        this.fnService = fnService;
        this.selectService = selectService;
        this.iterator = iterator;
        this.cancelSubscriptions = cancelSubscriptions;
        this.viewContainerRef = viewContainerRef;
        /**
         * @internal
         */
        this.$selectionChange = new Subject();
        /**
         * Emits a [state object](?path=/story/types-data-grid-state--page) when the internal datagrid state changes.
         */
        this.stateChange = this.stateService.change
            .pipe(filter(val => val.notify), map(val => ({
            filters: val.state.filters ? val.state.filters.map(entry => ({ key: entry.key, value: entry.value })) : val.state.filters,
            sort: val.state.sort,
            page: val.state.page
        })));
        /**
         * Emits an array of data associated with the selected rows.
         */
        this.selectionChange = this.selectService.change.pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions), map((selection) => this.data.filter(entry => selection.includes(entry[this.keyProp]))));
    }
    /**
     * @internal
     */
    get keyProp() {
        return this.rowTemplate.keyProp;
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        this.stateService.requestedState
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions))
            .subscribe(requestedState => {
            this.handleStateRequest(requestedState);
        });
        const initialState = this.stateService.getState();
        //set initial selected rows
        this.selectService.setSelection(this.initialSelection || []);
        // perform initial data processing if data exists
        if (this.data.length > 0) {
            this.applyState(initialState);
            return;
        }
        // or create loading rows and wait for updates
        const page = initialState.page
            || { offset: 0, totalResults: 5, pageSize: 5 };
        this.updateRows(page, []);
    }
    /**
     * Returns the current internal state of the Datagrid.
     */
    getState() {
        return this.stateService.getState();
    }
    /**
     * Displays the passed-in collection as rows in the Datagrid, without further filtering, sorting or paging.
     * Also updates the state of the Datagrid based on the passed-in
     * [state object](?path=/story/types-data-grid-state--page),
     * if any.
     * If you're performing external filtering or sorting, you should be passing in a page object that reflects
     * the new offset/totalResults.
     */
    updateData(collection, state) {
        this.data = collection;
        let newState = this.stateService.getState();
        if (state) {
            newState = { ...newState, ...state };
        }
        this.stateService.setState(newState, false);
        this.updateRows(newState.page, this.data);
    }
    /**
     * Processes the passed-in collection based on the provided [state object](?path=/story/types-data-grid-state--page),
     * and displays the results as rows.
     */
    processData(collection, state) {
        this.data = collection;
        let newState = this.stateService.getState();
        if (state) {
            newState = { ...newState, ...state };
        }
        this.applyState(newState);
    }
    /**
     * @internal
     * Processing hook for state requests triggered internally.
     * If the request is triggered internally and the DG configuration specifies that filtering,
     * sorting or paging should be skipped, a state change is emitted.
     */
    handleStateRequest(state) {
        const page = state.page
            || { offset: 0, totalResults: this.data.length, pageSize: this.data.length };
        this.updateRows(page, []); // create loading rows
        // if complete internal processing is not enabled, notify parent of state change
        if (state.filters && Object.values(this.fnService.filterFns).some(entry => !entry.matchFn)) {
            this.stateService.setState(state);
            return;
        }
        // if internal sorting is disabled, notify parent of change
        if (state.sort && !this.fnService.comparatorFns[state.sort.key]) {
            this.stateService.setState(state);
            return;
        }
        if (state.page && !this.fnService.paginationFn) {
            // if internal paging is disabled, notify parent of change
            this.stateService.setState(state);
            return;
        }
        this.applyState(state);
    }
    /**
     * @internal
     * Processing hook for state requests.
     * Does not emit a state change.
     */
    applyState(state) {
        let results = [...this.data];
        const newState = {
            filters: null,
            sort: null,
            page: null
        };
        if (state.filters) {
            const requestedFilters = state.filters.map(entry => ({ ...entry, ...this.fnService.filterFns[entry.key] }));
            const filtered = doFilter(requestedFilters, results);
            results = filtered.data;
            newState.filters = filtered.state;
        }
        if (state.sort) {
            const requestedSort = { ...state.sort, comparatorFn: this.fnService.comparatorFns[state.sort.key] };
            const sorted = doSort(requestedSort, results);
            results = sorted.data;
            newState.sort = sorted.state;
        }
        if (state.page) {
            const requestedPage = { ...state.page, paginationFn: this.fnService.paginationFn };
            const paged = doPage(requestedPage, results);
            results = paged.data;
            newState.page = paged.state;
        }
        // Set the state without notifying the parent
        this.stateService.setState(newState, false);
        const page = this.stateService.getState().page
            || { offset: 0, totalResults: results.length, pageSize: results.length };
        this.updateRows(page, results);
    }
    /**
     * @internal
     */
    updateRows(page, data) {
        const pagedData = new Map(data.map((entry, index) => ([index + page.offset, entry])));
        this.iterator.component.update(page, pagedData, this.rowTemplate, this.viewContainerRef.parentInjector.get(ChangeDetectorRef));
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.selectService.destroy();
        this.stateService.destroy();
        this.cancelSubscriptions.destroy();
        this.iterator.destroy();
    }
}
CdsSmartDataGridDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridDirective, deps: [{ token: CdsDataGridStateService }, { token: DataGridFnService }, { token: SelectableItemPresenter }, { token: SmartRowIteratorService }, { token: SubscriptionManager }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsSmartDataGridDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSmartDataGridDirective, selector: "cds-datagrid[data]", inputs: { initialSelection: "initialSelection", data: "data" }, outputs: { stateChange: "stateChange", selectionChange: "selectionChange" }, providers: [CdsDataGridStateService, DataGridFnService, SmartRowIteratorService, SelectableItemPresenter, SubscriptionManager], queries: [{ propertyName: "rowTemplate", first: true, predicate: CdsSmartRowTemplateDirective, descendants: true }, { propertyName: "smartRows", predicate: CdsSmartRowDirective, descendants: true }, { propertyName: "smartCheckboxes", predicate: CdsSmartDataGridCheckboxCellComponent, descendants: true }], exportAs: ["cdsSmartDataGrid"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'cds-datagrid[data]',
                    exportAs: 'cdsSmartDataGrid',
                    providers: [CdsDataGridStateService, DataGridFnService, SmartRowIteratorService, SelectableItemPresenter, SubscriptionManager]
                }]
        }], ctorParameters: function () { return [{ type: CdsDataGridStateService }, { type: DataGridFnService }, { type: SelectableItemPresenter }, { type: SmartRowIteratorService }, { type: SubscriptionManager }, { type: i0.ViewContainerRef }]; }, propDecorators: { initialSelection: [{
                type: Input
            }], data: [{
                type: Input
            }], stateChange: [{
                type: Output
            }], selectionChange: [{
                type: Output
            }], rowTemplate: [{
                type: ContentChild,
                args: [CdsSmartRowTemplateDirective]
            }], smartRows: [{
                type: ContentChildren,
                args: [CdsSmartRowDirective, { descendants: true }]
            }], smartCheckboxes: [{
                type: ContentChildren,
                args: [CdsSmartDataGridCheckboxCellComponent, { descendants: true }]
            }] } });

const indexComparatorFn = (item1, item2, sortDirection, collection) => {
    const index1 = collection.indexOf(item1);
    const index2 = collection.indexOf(item2);
    if (index1 < index2) {
        return -1 * sortDirection;
    }
    else if (index1 > index2) {
        return 1 * sortDirection;
    }
};

const stringComparatorFn = (value1, value2, sortDirection) => {
    if (value1 && !value2) {
        return -1;
    }
    else if (!value1 && value2) {
        return 1;
    }
    else if (value1 < value2) {
        return -1 * sortDirection;
    }
    else if (value1 > value2) {
        return 1 * sortDirection;
    }
    return 0;
};

function pageOf(offset, pageSize, collection) {
    const start = offset;
    const end = offset + pageSize;
    return collection.slice(start, end);
}
function extractFn(dataStore, selector, defaultValue = null) {
    if (dataStore && selector) {
        const jumps = selector.split('.');
        while (dataStore && jumps.length) {
            dataStore = dataStore[jumps.shift()];
        }
        return dataStore;
    }
    return defaultValue;
}
function lookupString(dataStore, key) {
    const value = extractFn(dataStore, key);
    if (value === 0) {
        return '0';
    }
    return (value || '').toString().toLowerCase().trim();
}
function getDefaultComparatorFn(key) {
    return (item1, item2, direction, collection) => {
        const value1 = lookupString(item1, key);
        const value2 = lookupString(item2, key);
        return (stringComparatorFn(value1, value2, direction) ||
            indexComparatorFn(item1, item2, direction, collection));
    };
}
const getMatchFn = (key, multi) => {
    if (multi) {
        return (value, item) => Object.entries(value).filter(([_, v]) => !!v).map(([k]) => k).includes(item[key]);
    }
    return (value, item) => !value || item[key] === value;
};

class CdsPagedDataGridDirective {
    /**
     * @internal
     */
    constructor(component, stateService, fnService, appRef, injector, cancelSubscriptions) {
        this.component = component;
        this.stateService = stateService;
        this.fnService = fnService;
        this.appRef = appRef;
        this.injector = injector;
        this.cancelSubscriptions = cancelSubscriptions;
        console.warn(`
      Use of the '[cdsPaged]' attribute is deprecated and will be removed in a future version.
      Prefer to use a CdsPaginator component with the \`cdsSmartDatagridPaginator\` attribute.
    `);
        this.createPaginator();
        this.stateService.change
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions))
            .subscribe(state => this.onPageChanged(state.state.page));
    }
    /**
     * Provide a custom pagination function to be used for paging.
     */
    set paginationFn(paginationFn) {
        this._paginationFn = paginationFn;
    }
    get paginationFn() {
        // when paginationFn === null, skip pagination
        // when paginationFn === undefined, use this.pageOf
        // when paginationFn is defined, use paginationFn
        return this._paginationFn === null ? this._paginationFn : this._paginationFn || pageOf;
    }
    /**
     * @internal
     */
    ngOnInit() {
        if (!this.page) {
            this.page = {
                offset: 0,
                pageSize: 5,
                totalResults: this.data.length
            };
        }
        this.fnService.paginationFn = this.paginationFn;
        this.stateService.setState({ ...this.stateService.getState(), page: this.page }, false);
        this.paginatorRef.instance.name = this.component.name;
        this.paginatorRef.instance.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    createPaginator() {
        this.paginatorRef = createComponent(CdsPaginatorComponent, this.appRef.injector, this.injector);
        this.paginatorRef.instance.type = 'standard';
        const $offsetChange = this.paginatorRef.instance.offsetChange
            .pipe(tap(offset => {
            this.fnService.paginationFn = this.paginationFn;
            this.stateService.requestPage({
                ...this.page,
                offset
            });
        }));
        const $pageSizeChange = this.paginatorRef.instance.pageSizeChange
            .pipe(tap(pageSize => {
            this.fnService.paginationFn = this.paginationFn;
            this.stateService.requestPage({
                ...this.page,
                offset: 0,
                pageSize
            });
        }));
        merge($offsetChange, $pageSizeChange)
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions))
            .subscribe();
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.component.paginationElement.insert(this.paginatorRef.hostView);
    }
    /**
     * @internal
     */
    onPageChanged(page) {
        if (!page || !this.paginatorRef) {
            return;
        }
        this.page = { ...page };
        if (this.paginatorRef) {
            const paginatorComponent = this.paginatorRef.instance;
            paginatorComponent.offset = page.offset;
            paginatorComponent.pageSize = page.pageSize;
            paginatorComponent.totalResults = page.totalResults;
            paginatorComponent.recordLabel = this.recordLabel || 'record';
            paginatorComponent.changeDetector.detectChanges();
        }
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.paginatorRef.destroy();
        this.cancelSubscriptions.destroy();
    }
}
CdsPagedDataGridDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPagedDataGridDirective, deps: [{ token: CdsDataGridComponent }, { token: CdsDataGridStateService }, { token: DataGridFnService }, { token: i0.ApplicationRef }, { token: i0.Injector }, { token: SubscriptionManager }], target: i0.ɵɵFactoryTarget.Directive });
CdsPagedDataGridDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsPagedDataGridDirective, selector: "cds-datagrid[cdsPaged]", inputs: { data: "data", paginationFn: "paginationFn", page: "page", recordLabel: "recordLabel" }, providers: [SubscriptionManager], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPagedDataGridDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'cds-datagrid[cdsPaged]',
                    providers: [SubscriptionManager]
                }]
        }], ctorParameters: function () { return [{ type: CdsDataGridComponent }, { type: CdsDataGridStateService }, { type: DataGridFnService }, { type: i0.ApplicationRef }, { type: i0.Injector }, { type: SubscriptionManager }]; }, propDecorators: { data: [{
                type: Input
            }], paginationFn: [{
                type: Input
            }], page: [{
                type: Input
            }], recordLabel: [{
                type: Input
            }] } });

class CdsDataGridFilterComponent {
    constructor() {
        this.change = new EventEmitter();
    }
    ngOnInit() {
        this.formControl = new UntypedFormControl(this.value);
    }
    onChange(value) {
        this.change.emit(value);
    }
}
CdsDataGridFilterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridFilterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsDataGridFilterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsDataGridFilterComponent, selector: "cds-datagrid-filter", outputs: { change: "change" }, queries: [{ propertyName: "select", first: true, predicate: CdsSelectComponent, descendants: true }], ngImport: i0, template: `
    <cds-select
      [options]="options"
      [formControl]="formControl"
      (change)="onChange($event)"
    ></cds-select>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: CdsSelectComponent, selector: "cds-select", inputs: ["name", "options", "inline", "placeholder", "iconName", "hideLabel", "leftAligned", "readout"], outputs: ["change"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridFilterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-datagrid-filter',
                    template: `
    <cds-select
      [options]="options"
      [formControl]="formControl"
      (change)="onChange($event)"
    ></cds-select>
  `,
                }]
        }], propDecorators: { select: [{
                type: ContentChild,
                args: [CdsSelectComponent]
            }], change: [{
                type: Output
            }] } });

/**
 * This directive exposes an API for performing client-side filtering on the rows of a Smart DataGrid. To enable client-side filtering,
 * you must add the `cdsFiltered` attribute to the `<cds-datagrid>` element.
 *
 * You may provide an optional FilterBy array that is a list of keys where each key matches a property in each row object.
 *
 * You may use the `filterOptions` input to provide a list of options for each key in the `filterBy` array, or allow the Filtered Datagrid
 * to auto-generate the options using the values of the objects in the table (beta). Each property
 *
 * Alternatively, you can explicitly specify the filters to be used by passing in a `CdsDataGridFilterConfig` array
 * where each object contains a `key`, a `matchFunction`, a list of options, and an optional `value`. If a `value` is provided,
 * this filter will be applied to the Datagrid's contents on render.
 *
 * The DataGrid will emit a CdsDataGridStateChange event after filtering is completed.
 */
class CdsFilteredDataGridDirective {
    /**
     * @internal
     */
    constructor(component, fnService, stateService, appRef, injector) {
        this.component = component;
        this.fnService = fnService;
        this.stateService = stateService;
        this.appRef = appRef;
        this.injector = injector;
        /**
         * @internal
         */
        this.filterComponents = {};
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        console.warn(`
      Use of the '[cdsFiltered]' attribute is deprecated and will be removed in a future version.
      Prefer to use a CdsSelect or CdsMultiSelect component with the \`cdsSmartDatagridFilter\` attribute.
    `);
    }
    /**
     * @internal
     */
    ngOnInit() {
        if (this.filters) {
            this.filters.forEach(filter => {
                if (!filter.key) {
                    throw Error('Missing key for DataGrid filter. Please check `filters` array.');
                }
                filter.matchFn = filter.matchFn === null ? filter.matchFn : (filter.matchFn || getMatchFn(filter.key, false));
            });
            this.updateFilters(this.filters);
            return;
        }
        const filterByFilters = this.filterBy.map(key => {
            const options = this.filterOptions ? this.filterOptions[key] : getOptionsFromColumn(key, this.data);
            return {
                key,
                value: null,
                options,
                matchFn: getMatchFn(key, false)
            };
        });
        this.updateFilters(filterByFilters);
    }
    /**
     * @internal
     */
    updateFilters(filters) {
        filters.map(filter => {
            this.fnService.filterFns[filter.key] = { options: filter.options, matchFn: filter.matchFn };
        });
        const filtersWithValues = filters.filter(filter => !!filter.value);
        if (filtersWithValues.length > 0) {
            this.stateService.setState({ ...this.stateService.getState(), filters: filtersWithValues }, false);
        }
        this.createAndUpdateFilterComponents(filters);
    }
    /**
     * @internal
     */
    createAndUpdateFilterComponents(filters) {
        filters.forEach(filter => {
            if (!this.filterComponents[filter.key]) {
                this.filterComponents[filter.key] = this.createFilterComponent(filter);
            }
            const filterComponent = this.filterComponents[filter.key];
            filterComponent.instance.options = filter.options;
            filterComponent.instance.value = filter.value;
            filterComponent.changeDetectorRef.detectChanges();
        });
    }
    /**
     * @internal
     */
    createFilterComponent(filter) {
        const filterComponent = createComponent(CdsDataGridFilterComponent, this.appRef.injector, this.injector);
        filterComponent.instance.change.pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(value => this.stateService.requestFilters([{ ...filter, value }]));
        return filterComponent;
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        setTimeout(() => {
            Object.values(this.filterComponents).forEach(dropdown => this.component.toolbarActionContainer.insert(dropdown.hostView));
            this.component.changeDetector.detectChanges();
        });
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        Object.values(this.filterComponents).forEach(dropdown => {
            dropdown.destroy();
        });
    }
}
CdsFilteredDataGridDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFilteredDataGridDirective, deps: [{ token: CdsDataGridComponent }, { token: DataGridFnService }, { token: CdsDataGridStateService }, { token: i0.ApplicationRef }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Directive });
CdsFilteredDataGridDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsFilteredDataGridDirective, selector: "cds-datagrid[cdsFiltered]", inputs: { data: "data", filters: "filters", filterBy: "filterBy", filterOptions: "filterOptions" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFilteredDataGridDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'cds-datagrid[cdsFiltered]'
                }]
        }], ctorParameters: function () { return [{ type: CdsDataGridComponent }, { type: DataGridFnService }, { type: CdsDataGridStateService }, { type: i0.ApplicationRef }, { type: i0.Injector }]; }, propDecorators: { data: [{
                type: Input
            }], filters: [{
                type: Input
            }], filterBy: [{
                type: Input
            }], filterOptions: [{
                type: Input
            }] } });

class CdsSortedHeaderDirective {
    /**
     * @internal
     */
    constructor(component, fnService) {
        this.component = component;
        this.fnService = fnService;
        /**
         * @internal
         */
        this.sort = this.component.sort
            .pipe(map(sort => ({
            ...sort,
            key: this.key,
        })));
    }
    /**
     * Provide a custom comparator function to apply when sorting the dataset.
     */
    set comparatorFn(comparatorFn) {
        this._comparatorFn = comparatorFn;
    }
    // when comparatorFn === null, use the sorted header but skip sorting
    // when comparatorFn === undefined, use defaultCompoaratorFn
    // when comparatorFn is defined, use comparatorFn
    get comparatorFn() {
        return this._comparatorFn === null ? this._comparatorFn : this._comparatorFn || getDefaultComparatorFn(this.key);
    }
    /**
     * @internal
     */
    ngOnInit() {
        this.component.sortable = true;
        this.fnService.comparatorFns[this.key] = this.comparatorFn;
    }
    /**
     * @internal
     */
    setSort(sort) {
        this.component.sortActive = true;
        this.component.sortDirection = sort.direction;
    }
    /**
     * @internal
     */
    clearSort() {
        this.component.sortActive = false;
        this.component.sortDirection = CdsSortDirection.None;
    }
}
CdsSortedHeaderDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSortedHeaderDirective, deps: [{ token: CdsDataGridHeaderComponent }, { token: DataGridFnService }], target: i0.ɵɵFactoryTarget.Directive });
CdsSortedHeaderDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSortedHeaderDirective, selector: "cds-datagrid-header[key]:not([cdsSmartDatagridSort])", inputs: { key: "key", comparatorFn: "comparatorFn" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSortedHeaderDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'cds-datagrid-header[key]:not([cdsSmartDatagridSort])'
                }]
        }], ctorParameters: function () { return [{ type: CdsDataGridHeaderComponent }, { type: DataGridFnService }]; }, propDecorators: { key: [{
                type: Input
            }], comparatorFn: [{
                type: Input
            }] } });

/**
 * This directive exposes an API for performing client-side sorting on the rows of a Smart DataGrid. To enable client-side sorting,
 * you must add the `cdsSorted` attribute to the `<cds-datagrid>` element.
 *
 * You may provide an optional CdsDataSort object that tells the Sorted DataGrid which column to sort by when it is rendered for the
 * first time.
 *
 * Additionally, each header present in the Sorted Datagrid should have its `key` attribute set to a unique value.
 * If a comparator is not provided, the default comparator is used, which assumes that the `key` attribute is a dot-delimited string
 * pointing to a property on the top-level object or nested objects represented by each row.
 *
 * The DataGrid will emit a CdsDataGridStateChange event after sorting is completed.
 */
class CdsSortedDataGridDirective {
    /**
     * @internal
     */
    constructor(stateService) {
        this.stateService = stateService;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        console.warn(`
      Use of the '[cdsSorted]' attribute is deprecated and will be removed in a future version.
      Prefer to add a \`cdsSmartDatagridSort\` attribute to sortable headers.
    `);
        this.stateService.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(state => {
            setTimeout(() => this.onSortChanged(state.state.sort));
        });
    }
    /**
     * @internal
     */
    ngOnInit() {
        this.stateService.setState({ ...this.stateService.getState(), sort: this.sort }, false);
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        merge(...this.headers.map(header => header.sort))
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(sort => this.stateService.requestSort(sort));
    }
    /**
     * @internal
     */
    onSortChanged(sort) {
        if (!sort || !this.headers) {
            return;
        }
        this.headers.forEach(header => {
            if (header.key === sort.key) {
                header.setSort(sort);
            }
            else {
                header.clearSort();
            }
        });
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
    }
}
CdsSortedDataGridDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSortedDataGridDirective, deps: [{ token: CdsDataGridStateService }], target: i0.ɵɵFactoryTarget.Directive });
CdsSortedDataGridDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSortedDataGridDirective, selector: "cds-datagrid[cdsSorted]", inputs: { sort: "sort" }, queries: [{ propertyName: "headers", predicate: CdsSortedHeaderDirective, descendants: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSortedDataGridDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'cds-datagrid[cdsSorted]'
                }]
        }], ctorParameters: function () { return [{ type: CdsDataGridStateService }]; }, propDecorators: { sort: [{
                type: Input
            }], headers: [{
                type: ContentChildren,
                args: [CdsSortedHeaderDirective, { descendants: true }]
            }] } });

/* eslint-disable @angular-eslint/no-output-native */
/* eslint-disable @angular-eslint/component-selector */
class CdsSmartDataGridCheckboxHeaderComponent {
    constructor(selectService, renderer, header, datagrid, smartDatagrid) {
        this.selectService = selectService;
        this.renderer = renderer;
        this.header = header;
        this.datagrid = datagrid;
        this.smartDatagrid = smartDatagrid;
        /**
         * @internal
         */
        this.formControl = new UntypedFormControl(false);
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        this.header.align = CdsDataGridCellAlignment.Center;
        this.header.frozen = true;
        this.header.width = '3rem';
        this.header.interactive = true;
    }
    /**
     * @internal
     */
    get name() {
        return `cds-dg-checkbox-header-${this.header.name}`;
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.renderer.setAttribute(this.datagrid.tableElement.element.nativeElement, 'aria-multiselectable', 'true');
        this.selectService.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => {
            this.updateHeaderCheckboxState();
        });
        setTimeout(() => {
            this.smartDatagrid.smartCheckboxes
                .changes
                .pipe(takeUntil(this.$cancelSubscriptions))
                .subscribe(() => {
                this.updateHeaderCheckboxState();
            });
            // update the checkbox state after creation
            this.updateHeaderCheckboxState();
        });
    }
    /**
     * @internal
     */
    onSelect(value) {
        this.selectService.select(value, ...this.smartDatagrid.data.map(entry => entry[this.smartDatagrid.keyProp]));
    }
    /**
     * @internal
     */
    updateHeaderCheckboxState() {
        const selection = this.selectService.getSelection();
        const allChecked = this.smartDatagrid.data.length > 0
            && this.smartDatagrid.data.every(entry => selection.includes(entry[this.smartDatagrid.keyProp]));
        const allUnchecked = this.smartDatagrid.data.every(entry => !selection.includes(entry[this.smartDatagrid.keyProp]));
        const indeterminate = !allChecked && !allUnchecked;
        this.checkbox.control.control.setValue(indeterminate ? null : allChecked);
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsSmartDataGridCheckboxHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridCheckboxHeaderComponent, deps: [{ token: SelectableItemPresenter }, { token: i0.Renderer2 }, { token: CdsDataGridHeaderComponent }, { token: CdsDataGridComponent }, { token: CdsSmartDataGridDirective }], target: i0.ɵɵFactoryTarget.Component });
CdsSmartDataGridCheckboxHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSmartDataGridCheckboxHeaderComponent, selector: "cds-smart-datagrid-checkbox-header", viewQueries: [{ propertyName: "checkbox", first: true, predicate: CdsCheckboxComponent, descendants: true }], ngImport: i0, template: `
    <cds-checkbox
      [name]="name"
      [formControl]="formControl"
      (change)="onSelect($event)"
      label="Select all rows"
      [hideLabel]="true"
    >
    </cds-checkbox>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: CdsCheckboxComponent, selector: "cds-checkbox", inputs: ["isDisabled", "label", "hideLabel", "controlLabels", "name", "leftAlignedLabel"], outputs: ["change"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridCheckboxHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-smart-datagrid-checkbox-header',
                    template: `
    <cds-checkbox
      [name]="name"
      [formControl]="formControl"
      (change)="onSelect($event)"
      label="Select all rows"
      [hideLabel]="true"
    >
    </cds-checkbox>
  `
                }]
        }], ctorParameters: function () { return [{ type: SelectableItemPresenter }, { type: i0.Renderer2 }, { type: CdsDataGridHeaderComponent }, { type: CdsDataGridComponent }, { type: CdsSmartDataGridDirective }]; }, propDecorators: { checkbox: [{
                type: ViewChild,
                args: [CdsCheckboxComponent]
            }] } });

const getDefaultRowHeightArray = (itemCount) => {
    const cache = new Map();
    for (let index = 0; index < itemCount; index++) {
        cache.set(index, 48);
    }
    return cache;
};
class RowHeightCache {
    constructor() {
        this.$cancelSubscriptions = new Subject();
        // will create an object with 5 entries as default, until we know the actual total record count
        this._rowHeightCache = getDefaultRowHeightArray(5);
        this.$rowHeightCache = new Subject();
        this.change = this.$rowHeightCache.asObservable();
    }
    getRowHeights() {
        return this._rowHeightCache;
    }
    sumHeights(endOffset = this._rowHeightCache.size) {
        let sum = 0; // the distance of each subsequent row from the top of the container
        for (const [index, height] of this._rowHeightCache) {
            if (+index > endOffset) {
                break;
            }
            sum += height;
        }
        return sum;
    }
    ;
    updateTotalRecordCount(totalCount) {
        const newHeights = new Map();
        for (let index = 0; index < totalCount; index++) {
            newHeights.set(index, this._rowHeightCache.get(index) || 48);
        }
        this._rowHeightCache = newHeights;
        this.$rowHeightCache.next(this._rowHeightCache);
    }
    unwatch() {
        this.$cancelSubscriptions.next();
    }
    watch(rows) {
        this.$cancelSubscriptions.next();
        for (const [index, element] of rows) {
            this._rowHeightCache.set(index, element.clientHeight);
            this.createRowHeightSub(index, element);
        }
    }
    createRowHeightSub(index, element) {
        // update row height after changes
        ResizeObserverSubject(element)
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => {
            const newHeight = element.clientHeight;
            const cachedHeight = this._rowHeightCache.get(index);
            if (cachedHeight === newHeight) {
                return;
            }
            this._rowHeightCache.set(index, element.clientHeight);
            this.$rowHeightCache.next(this._rowHeightCache);
        });
    }
    reset() {
        this._rowHeightCache = new Map();
    }
    destroy() {
        this.reset();
        this.$rowHeightCache.complete();
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
RowHeightCache.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RowHeightCache, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
RowHeightCache.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RowHeightCache });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RowHeightCache, decorators: [{
            type: Injectable
        }] });

class CdsIntersectionObserverPresenter {
    constructor() {
        this.change = new Subject();
    }
    watch(container, displayed) {
        const options = {
            root: container,
            threshold: 0,
        };
        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (this.isNotVisible(entry)) {
                    this.change.next(entry);
                }
            });
        }, options);
        displayed.forEach((target) => this.intersectionObserver.observe(target));
    }
    isNotVisible(entry) {
        return !entry.isIntersecting || entry.intersectionRatio < 0;
    }
    disconnect() {
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    }
    /**
     * @internal
     */
    destroy() {
        this.change.complete();
        this.disconnect();
    }
}
CdsIntersectionObserverPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsIntersectionObserverPresenter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsIntersectionObserverPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsIntersectionObserverPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsIntersectionObserverPresenter, decorators: [{
            type: Injectable
        }] });

class VirtualScrollDataCache {
    constructor() {
        this.cache = new Map();
        this.reset();
    }
    cacheData(data, page) {
        data.forEach((entry, index) => {
            this.cache.set(index + page.offset, entry);
        });
    }
    hasMissingEntries(page) {
        for (let i = 0; i < page.pageSize; i++) {
            if (!this.cache.get(page.offset + i)) {
                return true;
            }
        }
        return false;
    }
    getCachedData() {
        return new Map(this.cache);
    }
    reset() {
        this.cache = new Map();
    }
    destroy() {
        this.reset();
    }
}
VirtualScrollDataCache.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VirtualScrollDataCache, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
VirtualScrollDataCache.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VirtualScrollDataCache });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VirtualScrollDataCache, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class VirtualScrollerPagePresenter {
    constructor(zone) {
        this.zone = zone;
        /**
         * @internal
         */
        this.$scrollPause = new Subject();
        /**
         * @internal
         */
        this.$scrollResume = new Subject();
        this.$page = new Subject();
        this.$cancelSubscriptions = new Subject();
        this.change = this.$page.asObservable();
    }
    get page() {
        return this._page;
    }
    init(initialHeight, bufferSize, scrollContainer, rowHeightCache) {
        this._rowHeightCache = rowHeightCache;
        this._scrollContainer = scrollContainer;
        this._bufferSize = bufferSize;
        this.zone.runOutsideAngular(() => fromEvent(scrollContainer, 'scroll')
            .pipe(takeUntil(this.$cancelSubscriptions), map(() => this.calculatePage(this._bufferSize)), filter(page => page.offset !== this._page.offset)).subscribe(page => {
            this._page = page;
            this.$page.next(this._page);
        }));
        // Create as many loading/data rows as is needed to fill the given scroll space initially
        const pageSize = Math.ceil(initialHeight / 48) * ((this._bufferSize * 2) + 1);
        this._page = {
            offset: 0,
            pageSize,
            totalResults: pageSize
        };
        this.$page.next(this._page);
    }
    pause() {
        this.$scrollPause.next();
    }
    resume() {
        this.$scrollResume.next();
    }
    calculatePage(bufferSize) {
        const clientHeight = this._scrollContainer.clientHeight;
        const rowHeights = this._rowHeightCache.getRowHeights();
        const totalResults = rowHeights.size;
        const offset = getOffset(this._scrollContainer, bufferSize, rowHeights);
        const pageSize = Math.min(Math.ceil(clientHeight / 48) * ((bufferSize * 2) + 1), rowHeights.size);
        return {
            // handle scenarios when we've scrolled down to the end of the container
            offset: Math.min(offset, totalResults - pageSize),
            pageSize,
            totalResults
        };
    }
    setPage(page) {
        this._page = page;
    }
    getFirstVisibleElement(container, elements) {
        let hiddenPixels = 0;
        let index = 0;
        while (hiddenPixels === 0 && index < elements.length) {
            const row = elements.item(index);
            const nextRow = elements.item(index + 1);
            if (row.offsetTop <= container.scrollTop && nextRow?.offsetTop > container.scrollTop) {
                const tableTop = container.getBoundingClientRect().top;
                const rowTop = row.getBoundingClientRect().top;
                hiddenPixels = tableTop - rowTop;
                break;
            }
            index += 1;
        }
        return { hiddenPixels, index };
    }
    destroy() {
        this.$page.complete();
        this.$scrollPause.complete();
        this.$scrollResume.complete();
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
VirtualScrollerPagePresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VirtualScrollerPagePresenter, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable });
VirtualScrollerPagePresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VirtualScrollerPagePresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VirtualScrollerPagePresenter, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.NgZone }]; } });
function calculateRelativeScrollTop(previousScrollTop, previousRowHeights, newRowHeights) {
    const getHeightsSum = (heights) => {
        let sum = 0;
        for (const [_, value] of heights) {
            sum += value;
        }
        return sum;
    };
    const scrollHeight = getHeightsSum(newRowHeights);
    const oldScrollHeight = getHeightsSum(previousRowHeights);
    return (previousScrollTop / oldScrollHeight) * scrollHeight;
}
function getOffset(container, bufferSize, rowHeightCache) {
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const scrollTop = container.scrollTop;
    if (scrollTop <= (bufferSize * clientHeight)) {
        return 0;
    }
    // scrollHeight - clientHeight - (2 * bufferSize) handles the case when we're approaching the bottom
    // of the container
    const upperBoundary = scrollHeight - (scrollTop + clientHeight) < (2 * bufferSize * clientHeight)
        ? scrollHeight - clientHeight - (2 * bufferSize * clientHeight)
        : scrollTop - (bufferSize * clientHeight);
    let sum = 0; // the distance of each subsequent row from the top of the container
    let offset = 0;
    for (const [index, height] of rowHeightCache) {
        sum += height;
        if (sum > upperBoundary) {
            offset = +index;
            break;
        }
    }
    return offset;
}
;

/* eslint-disable @typescript-eslint/consistent-type-definitions */
/* eslint-disable @typescript-eslint/naming-convention */
/**
 * Provides Virtual Scrolling functionality for the Datagrid, where rows are dynamically
 * added and removed from the DOM based on a user's scroll position.
 */
class CdsVirtualScrollDataGridDirective {
    /**
     * @internal
     */
    constructor(component, elementRef, renderer, presenter, dataCache, rowHeightCache, iterator, subscriptionManager, viewContainerRef, zone) {
        this.component = component;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.presenter = presenter;
        this.dataCache = dataCache;
        this.rowHeightCache = rowHeightCache;
        this.iterator = iterator;
        this.subscriptionManager = subscriptionManager;
        this.viewContainerRef = viewContainerRef;
        this.zone = zone;
        /**
         * @internal
         */
        this.$processUpdates = new Subject();
        /**
         * Emits the new page when the Data Grid is scrolled.
         */
        this.scrollPageChange = new EventEmitter();
        this.component.displayService.setDisplayedRows = this.setDisplayedRows(this.component.displayService.setDisplayedRows);
    }
    /**
     * @internal
     */
    // @todo: angular does not order QueryList items properly, see:
    // https://github.com/angular/angular/issues/57891
    // https://github.com/angular/angular/issues/42519
    setDisplayedRows(originalFn) {
        return (rows) => {
            const indices = [...this.rows]
                .sort((a, b) => b.rowPosition - a.rowPosition)
                .map(row => row.component);
            originalFn.call(this.component.displayService, rows.sort((a, b) => indices.indexOf(b) - indices.indexOf(a)));
        };
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.table = this.elementRef.nativeElement.querySelector('table');
        this.tableBody = this.elementRef.nativeElement.querySelector('tbody');
        const spacer = document.createElement('tr');
        spacer.classList.add('virtual-scroll-spacer');
        this.tableBody.insertAdjacentElement('beforeend', spacer);
        this.renderer.addClass(this.table, '-virtual-scroll');
        this.renderer.setStyle(this.tableBody, 'pointer-events', 'none');
        this.component.stickyHeaders = true;
        this.component.changeDetector.detectChanges();
        this.createSubscriptions();
    }
    /**
     * @internal
     */
    createSubscriptions() {
        this.rowHeightCache.change
            .pipe(takeUntil(this.subscriptionManager.$cancelSubscriptions), debounceTime(50, animationFrameScheduler))
            .subscribe(() => {
            this.updateScrollHeight();
        });
        // @todo: run outside angular
        const $windowClosed = fromEvent(this.tableBody, 'scroll')
            .pipe(takeUntil(this.subscriptionManager.$cancelSubscriptions));
        const $windowOpen = merge(of([]), $windowClosed.pipe(debounceTime(200)));
        this.$processUpdates
            .pipe(takeUntil(this.subscriptionManager.$cancelSubscriptions))
            .pipe(windowToggle($windowOpen, () => $windowClosed), mergeAll()).subscribe(() => {
            this.zone.run(() => this.processUpdates());
        });
        // emit first page
        this.presenter.change
            .pipe(take(1), filter(page => this.dataCache.hasMissingEntries(page)), debounceTime(750))
            .subscribe(page => {
            this.zone.run(() => this.scrollPageChange.emit(page));
        });
        // emit page objects on scroll
        $windowClosed
            .pipe(switchMap(() => $windowOpen), debounceTime(750), filter(() => this.dataCache.hasMissingEntries(this.presenter.page)))
            .subscribe(() => {
            this.zone.run(() => this.scrollPageChange.emit(this.presenter.page));
        });
        this.presenter.change
            .subscribe(() => {
            this.zone.run(() => this.createLoadingRows());
            setTimeout(() => this.updateSpacer());
        });
        const initialHeight = this.table.clientHeight - this.table.querySelector('thead').clientHeight;
        this.presenter.init(initialHeight, 1.5, this.tableBody, this.rowHeightCache);
        this.component.renderedRowService.$finishedRenderingRows
            .pipe(takeUntil(this.subscriptionManager.$cancelSubscriptions))
            .subscribe(() => {
            this.updateSpacer();
        });
    }
    /**
     * Use this method to notify the Virtual Scroll Datagrid of the data that corresponds with a certain page.
     */
    update(data, page) {
        this.dataCache.cacheData(data, page);
        this.presenter.setPage({
            ...this.presenter.page,
            totalResults: page.totalResults
        });
        this.$processUpdates.next();
    }
    /**
     * @internal
     * Wait until scrolling has stopped before updating the DOM with data
     */
    processUpdates() {
        const lastPage = this.tableBody.scrollTop > 0
            && this.tableBody.scrollHeight - this.tableBody.scrollTop <= (this.tableBody.clientHeight * 1);
        // scrollBottom = opposite of scrollTop (i.e. distance from bottom of client)
        const scrollBottom = this.tableBody.scrollHeight - (this.tableBody.scrollTop + this.tableBody.clientHeight);
        const onRender = () => {
            this.renderer.setStyle(this.tableBody, 'pointer-events', 'auto');
            this.rowHeightCache.updateTotalRecordCount(this.presenter.page.totalResults);
            this.renderer.setAttribute(this.table, 'aria-rowcount', `${this.presenter.page.totalResults}`);
            if (lastPage) {
                this.tableBody.scrollTop = this.tableBody.scrollHeight - this.tableBody.clientHeight - scrollBottom;
            }
            const renderedRows = this.component.renderedRowService.getRenderedRows();
            const elements = this.rows.map((row, index) => {
                this.renderer.setAttribute(renderedRows.get(row.component), 'aria-rowindex', `${this.presenter.page.offset + index + 1}`);
                return [
                    row.rowPosition,
                    renderedRows.get(row.component).querySelector('td')
                ];
            });
            this.rowHeightCache.watch(elements);
        };
        this.component.renderedRowService.$finishedRenderingRows
            .pipe(take(1))
            .subscribe(() => {
            onRender();
        });
        this.render(this.dataCache.getCachedData());
    }
    /**
     * Use this method to scroll to the top of the Datagrid, in situations where a new filter or sort has been applied
     * externally and you wish to return the Datagrid scroll position to it's initial state.
     */
    scrollToTop() {
        this.scrollTo(0);
    }
    /**
     * Use this method to scroll to a particular row.
     */
    scrollTo(offset) {
        const offsetHeight = this.rowHeightCache.sumHeights(offset - 1);
        const totalHeight = this.rowHeightCache.sumHeights();
        const tableHeight = this.tableBody.clientHeight;
        const scrollTop = Math.min(totalHeight - tableHeight, offsetHeight);
        this.tableBody.scrollTop = scrollTop;
    }
    /**
     * The Virtual Scroll Datagrid caches data so that duplicate requests for data don't occur e.g. when a user scrolls back to a page
     * for which data has already been requested.
     * Use this method to clear the data cache and restart caching. The Datagrid will request data for each new page unless that data
     * has already been requested subsequent to the call to `reset()`.
     */
    reset() {
        this.dataCache.reset();
    }
    /**
     * @internal
     * Use this method to preserve the current scrollheight of the datagrid while updating the data within.
     * This prevents triggering a new page request due to the change in the scroll position, which may happen
     * as a result of the data changing (and scroll height increasing/decreasing)
     */
    updateScrollHeight() {
        const scrollHeight = this.rowHeightCache.sumHeights();
        // @todo: HACK :-
        // renderer.setStyle doesn't update the cdsVirtualScrollHeight CSS var immediately in
        // downstream apps (CCP) so we're setting it directly using low-level DOM apis
        // this.tableBody.style.setProperty('--cdsVirtualScrollHeight', `${ scrollHeight }px`);
        this.renderer.setStyle(this.tableBody, '--cdsVirtualScrollHeight', `${scrollHeight}px`, 2);
    }
    /**
     * @internal
     */
    updateSpacer() {
        const { offset } = this.presenter.page;
        const spacer = this.rowHeightCache.sumHeights(offset - 1);
        this.tableBody.querySelectorAll('td').forEach(element => {
            element.style.transform = `translateY(${spacer}px)`;
        });
    }
    /**
     * @internal
     */
    createLoadingRows() {
        const cachedData = this.dataCache.getCachedData();
        const data = new Map();
        // use cached data for the previous page. Real data will be loaded afterwards
        for (let i = 0; i < this.presenter.page.pageSize; i++) {
            data.set(this.presenter.page.offset + i, cachedData.get(this.presenter.page.offset + i));
        }
        this.render(data);
    }
    /**
     * @internal
     */
    render(data) {
        this.rowHeightCache.unwatch();
        // This generates a new row for each record, triggering a change in the QueryList of the CdsDatagridComponent
        this.iterator.component.update(this.presenter.page, data, this.rowTemplate, this.viewContainerRef.parentInjector.get(ChangeDetectorRef));
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.dataCache.destroy();
        this.rowHeightCache.destroy();
        this.presenter.destroy();
        this.subscriptionManager.destroy();
        this.iterator.destroy();
        this.$processUpdates.complete();
    }
}
CdsVirtualScrollDataGridDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsVirtualScrollDataGridDirective, deps: [{ token: CdsDataGridComponent }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: VirtualScrollerPagePresenter }, { token: VirtualScrollDataCache }, { token: RowHeightCache }, { token: SmartRowIteratorService }, { token: SubscriptionManager }, { token: i0.ViewContainerRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive });
CdsVirtualScrollDataGridDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsVirtualScrollDataGridDirective, selector: "cds-datagrid[cdsVirtualScroll]", outputs: { scrollPageChange: "scrollPageChange" }, providers: [
        CdsIntersectionObserverPresenter,
        VirtualScrollerPagePresenter,
        VirtualScrollDataCache,
        RowHeightCache,
        SmartRowIteratorService,
        SubscriptionManager
    ], queries: [{ propertyName: "rowTemplate", first: true, predicate: CdsSmartRowTemplateDirective, descendants: true }, { propertyName: "rows", predicate: CdsSmartRowDirective, descendants: true }], exportAs: ["cdsVirtualScroll"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsVirtualScrollDataGridDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'cds-datagrid[cdsVirtualScroll]',
                    exportAs: 'cdsVirtualScroll',
                    providers: [
                        CdsIntersectionObserverPresenter,
                        VirtualScrollerPagePresenter,
                        VirtualScrollDataCache,
                        RowHeightCache,
                        SmartRowIteratorService,
                        SubscriptionManager
                    ],
                }]
        }], ctorParameters: function () { return [{ type: CdsDataGridComponent }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: VirtualScrollerPagePresenter }, { type: VirtualScrollDataCache }, { type: RowHeightCache }, { type: SmartRowIteratorService }, { type: SubscriptionManager }, { type: i0.ViewContainerRef }, { type: i0.NgZone }]; }, propDecorators: { scrollPageChange: [{
                type: Output
            }], rowTemplate: [{
                type: ContentChild,
                args: [CdsSmartRowTemplateDirective]
            }], rows: [{
                type: ContentChildren,
                args: [CdsSmartRowDirective, { descendants: true }]
            }] } });

class CdsVirtualScrollSmartDataGridDirective {
    /**
     * @internal
     */
    constructor(component, stateService, smartDatagrid, fnService, virtualScrollDataGrid, cancelSubscriptions) {
        this.component = component;
        this.stateService = stateService;
        this.smartDatagrid = smartDatagrid;
        this.fnService = fnService;
        this.virtualScrollDataGrid = virtualScrollDataGrid;
        this.cancelSubscriptions = cancelSubscriptions;
        this.onDataProcessed = (page, data) => {
            if (page) {
                this.virtualScrollDataGrid.update(data, page);
            }
        };
        this.smartDatagrid.updateRows = () => { };
    }
    /**
     * Provide a custom pagination function to be used for paging.
     */
    set paginationFn(paginationFn) {
        this._paginationFn = paginationFn;
    }
    get paginationFn() {
        // when paginationFn === null, skip pagination
        // when paginationFn === undefined, use this.pageOf
        // when paginationFn is defined, use paginationFn
        return this._paginationFn === null ? this._paginationFn : this._paginationFn || pageOf;
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        this.smartDatagrid.updateRows = this.onDataProcessed;
    }
    /**
     * @internal
     */
    ngOnInit() {
        // gets called every time scroll changes
        this.virtualScrollDataGrid.scrollPageChange
            .pipe(skip(1), takeUntil(this.cancelSubscriptions.$cancelSubscriptions), filter(() => this.smartDatagrid.data.length > 0))
            .subscribe(page => {
            this.stateService.requestPage(page);
        });
        // gets called when the virtual scroll datagrid is initialized
        this.virtualScrollDataGrid.scrollPageChange
            .pipe(take(1))
            .subscribe(page => {
            // perform initial data processing if data exists
            if (this.smartDatagrid.data.length > 0) {
                this.stateService.requestPage(page);
                return;
            }
            this.stateService.setState({ ...this.stateService.getState(), page }, false);
        });
        merge(this.stateService.requestedFilters, this.stateService.requestedSort)
            .subscribe(() => {
            this.virtualScrollDataGrid.scrollToTop();
            this.virtualScrollDataGrid.reset();
        });
        this.fnService.paginationFn = this.paginationFn;
    }
    ngOnDestroy() {
        this.cancelSubscriptions.destroy();
    }
}
CdsVirtualScrollSmartDataGridDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsVirtualScrollSmartDataGridDirective, deps: [{ token: CdsDataGridComponent }, { token: CdsDataGridStateService }, { token: CdsSmartDataGridDirective }, { token: DataGridFnService }, { token: CdsVirtualScrollDataGridDirective }, { token: SubscriptionManager }], target: i0.ɵɵFactoryTarget.Directive });
CdsVirtualScrollSmartDataGridDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsVirtualScrollSmartDataGridDirective, selector: "cds-datagrid[cdsVirtualScroll][data]", inputs: { paginationFn: "paginationFn" }, providers: [SubscriptionManager], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsVirtualScrollSmartDataGridDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'cds-datagrid[cdsVirtualScroll][data]',
                    providers: [SubscriptionManager]
                }]
        }], ctorParameters: function () { return [{ type: CdsDataGridComponent }, { type: CdsDataGridStateService }, { type: CdsSmartDataGridDirective }, { type: DataGridFnService }, { type: CdsVirtualScrollDataGridDirective }, { type: SubscriptionManager }]; }, propDecorators: { paginationFn: [{
                type: Input
            }] } });

/**
 * @internal
 */
class CdsListItemComponent {
    constructor(renderer) {
        this.renderer = renderer;
    }
    focus() {
        if (this.anchor) {
            setTimeout(() => this.anchor.nativeElement.focus());
        }
    }
    onFocusIn() {
        this.renderer.addClass(this.atom.nativeElement, '-ie-focus-within');
    }
    onFocusOut() {
        this.renderer.removeClass(this.atom.nativeElement, '-ie-focus-within');
    }
    getLabel() {
        return String(this.label);
    }
}
CdsListItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsListItemComponent, deps: [{ token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
CdsListItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsListItemComponent, selector: "cds-list-item", inputs: { name: "name", label: "label", value: "value", selected: "selected" }, providers: [
        { provide: CdsTraversableComponent, useExisting: forwardRef(() => CdsListItemComponent) },
    ], viewQueries: [{ propertyName: "atom", first: true, predicate: ["atom"], descendants: true, static: true }, { propertyName: "anchor", first: true, predicate: ["anchor"], descendants: true }], ngImport: i0, template: "<li\n  [id]=\"'cds-list-item-' + name + '-' + label | kebabCase\"\n  class=\"cds-list-item\"\n  [class.-selected]=\"selected\"\n  role=\"presentation\"\n  (focusin)=\"onFocusIn()\"\n  (focusout)=\"onFocusOut()\"\n  #atom\n>\n  <ng-content></ng-content>\n  <a\n    *ngIf=\"label\"\n    class=\"cds-list-item-link\"\n    role=\"menuitem\"\n    tabindex=\"-1\"\n    #anchor\n  >\n    {{ label }}\n  </a>\n</li>\n", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsListItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-list-item', providers: [
                        { provide: CdsTraversableComponent, useExisting: forwardRef(() => CdsListItemComponent) },
                    ], template: "<li\n  [id]=\"'cds-list-item-' + name + '-' + label | kebabCase\"\n  class=\"cds-list-item\"\n  [class.-selected]=\"selected\"\n  role=\"presentation\"\n  (focusin)=\"onFocusIn()\"\n  (focusout)=\"onFocusOut()\"\n  #atom\n>\n  <ng-content></ng-content>\n  <a\n    *ngIf=\"label\"\n    class=\"cds-list-item-link\"\n    role=\"menuitem\"\n    tabindex=\"-1\"\n    #anchor\n  >\n    {{ label }}\n  </a>\n</li>\n" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }]; }, propDecorators: { name: [{
                type: Input
            }], label: [{
                type: Input
            }], value: [{
                type: Input
            }], selected: [{
                type: Input
            }], atom: [{
                type: ViewChild,
                args: ['atom', { static: true }]
            }], anchor: [{
                type: ViewChild,
                args: ['anchor']
            }] } });

class CdsListComponent {
}
CdsListComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsListComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsListComponent, selector: "cds-list", inputs: { name: "name", items: "items" }, ngImport: i0, template: "<ul [id]=\"'cds-list-' + name | kebabCase\" class=\"cds-list\" role=\"menu\">\n  <ng-content select=\"cds-list-item\"></ng-content>\n</ul>\n", dependencies: [{ kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-list', template: "<ul [id]=\"'cds-list-' + name | kebabCase\" class=\"cds-list\" role=\"menu\">\n  <ng-content select=\"cds-list-item\"></ng-content>\n</ul>\n" }]
        }], propDecorators: { name: [{
                type: Input
            }], items: [{
                type: Input
            }] } });

class CdsListModule {
}
CdsListModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsListModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsListModule, declarations: [CdsListComponent, CdsListItemComponent], imports: [A11yModule, CdsPipesModule, CommonModule], exports: [CdsListComponent, CdsListItemComponent] });
CdsListModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsListModule, imports: [A11yModule, CdsPipesModule, CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsListModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsListComponent, CdsListItemComponent],
                    exports: [CdsListComponent, CdsListItemComponent],
                    imports: [A11yModule, CdsPipesModule, CommonModule],
                }]
        }] });

/**
 * ### Description
 * The Multi-Select Component displays an array of selectable CdsListItems  in a dropdown menu.
 *
 * You may change the value of the Multi-Select component by setting the value of the FormControl to an object that matches the
 * structure of the array CdsListItem array. This object should map the label of each CdsListItem to
 * a boolean value.
 */
class CdsMultiSelectComponent {
    /**
     * @internal
     */
    constructor(control, focusableListPresenter, typeaheadPresenter, controlValueAccessor, renderer) {
        this.control = control;
        this.focusableListPresenter = focusableListPresenter;
        this.typeaheadPresenter = typeaheadPresenter;
        this.controlValueAccessor = controlValueAccessor;
        this.renderer = renderer;
        /**
         * An event containing the new state of this control.
         */
        this.change = this.controlValueAccessor.change;
        /**
         * @internal
         */
        this.registerOnChange = this.controlValueAccessor.registerOnChange;
        /**
         * @internal
         */
        this.registerOnTouched = this.controlValueAccessor.registerOnTouched;
        /**
         * @internal
         */
        this.setDisabledState = this.controlValueAccessor.setDisabledState;
        /**
         * @internal
         */
        this.focusButton = () => {
            this.button.focus({ preventScroll: true });
        };
        /**
         * @internal
         */
        this.focusFirstItem = () => {
            if (this.expanded) {
                this.focusableListPresenter.setAndFocusActiveItem();
            }
        };
        /**
         * @internal
         */
        this.keyValueOrder = (a, b) => 0;
        if (this.control) {
            this.control.valueAccessor = this;
        }
        else {
            throw new Error('FormControl is missing from Multi-Select Component. ');
        }
        this.typeaheadPresenter.change
            .subscribe(event => this.onTypehead(event));
    }
    /**
     * @internal
     */
    get replacementValues() {
        return {
            count: this.selectedLabels.length
        };
    }
    /**
     * @internal
     */
    get text() {
        if (this.hideLabel) {
            return '';
        }
        if (this.selectedLabels.length === 0) {
            return this.placeholder || 'Choose an option';
        }
        if (this.readout && this.selectedLabels.length > 0) {
            return stringInterpolatorTransform(this.readout, this.replacementValues);
        }
        return this.selectedLabels.length === 1 ? this.selectedLabels[0] : `(${this.selectedLabels.length}) selected`;
    }
    /**
     * @internal
     */
    get disabled() {
        return this.controlValueAccessor.disabled;
    }
    /**
     * @internal
     */
    get dropdownIcon() {
        return this.iconName
            ? this.iconName
            : (this.expanded ? 'arrow_drop_up' : 'arrow_drop_down');
    }
    /**
     * An array of [List Items](?path=/story/types-list-item--page#cdslistitem) to choose from.
     */
    set options(options) {
        if (this.checkboxes) {
            this.createFormGroup(options);
        }
        this._options = options;
    }
    /**
     * @internal
     */
    get options() {
        return this._options;
    }
    /**
     * @internal
     */
    ngOnInit() {
        this.createFormGroup(this.options);
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        merge(this.checkboxGroups.changes, this.checkboxes.changes)
            .subscribe(() => this.setupTraversableItems());
        this.setupTraversableItems();
    }
    /**
     * @internal
     */
    closeDropdown(event) {
        if (this.expanded) {
            if (event) {
                event.stopPropagation();
            }
            this.dropdownToggle.collapse();
        }
    }
    /**
     * @internal
     */
    openDropdown() {
        if (!this.expanded) {
            this.dropdownToggle.expand();
        }
    }
    /**
     * @internal
     */
    createFormGroup(options) {
        this.formGroup = this.createControlsForOptions(options);
        if (this.value) {
            this.formGroup.patchValue(this.value);
        }
        this.updateText();
    }
    /**
     * @internal
     */
    createControlsForOptions(options) {
        const optionGroups = options.reduce((acc, childOption) => {
            const control = childOption.children
                ? this.createControlsForOptions(childOption.children)
                : new UntypedFormControl({ value: childOption.value || false, disabled: childOption.disabled });
            return { ...acc, [childOption.label]: control };
        }, {});
        return new UntypedFormGroup(optionGroups);
    }
    /**
     * @internal
     */
    setupTraversableItems() {
        const extractGroupStateCheckboxFn = (group) => group.groupStateCheckbox;
        const extractChildrenFn = (entry) => entry.children.reduce((acc, child) => child.component instanceof CdsCheckboxComponent ? acc.concat(child.component) : acc, []);
        const groupedCheckboxes = this.checkboxGroups.reduce(flatten(extractGroupStateCheckboxFn, extractChildrenFn), []);
        const standaloneCheckboxes = this.checkboxes.filter(checkbox => !groupedCheckboxes.includes(checkbox));
        const registry = [...groupedCheckboxes, ...standaloneCheckboxes].map(checkbox => ({
            component: checkbox,
            element: checkbox.atom.nativeElement,
        }));
        this.traversableItems = registry.map((entry) => entry.component);
        this.focusableListPresenter.watch(registry);
    }
    /**
     * @internal
     */
    updateText() {
        const extractSelf = ([key, value]) => typeof value === 'object' ? [] : { [key]: value };
        const extractChildren = ([key, value]) => typeof value === 'object' ? Object.entries(value).reduce(flatten(extractSelf, extractChildren), []) : [];
        const flattenedValues = Object.entries(this.formGroup.value).reduce(flatten(extractSelf, extractChildren), []);
        const selectedValues = flattenedValues.filter(entry => Object.values(entry).every(value => !!value));
        this.selectedLabels = [].concat(...selectedValues.map(option => [].concat(...Object.keys(option))));
    }
    /**
     * @internal
     */
    onExpandedStateChanged({ expanded, source }) {
        this.expanded = expanded;
        this.renderer.setAttribute(this.button.elementRef.nativeElement, 'aria-expanded', `${this.expanded}`);
        if (expanded && source === 'keydown') {
            this.focusableListPresenter.setAndFocusActiveItem();
        }
        if (!expanded) {
            this.controlValueAccessor.onTouch();
        }
    }
    /**
     * @internal
     */
    handleTypeahead(event) {
        this.typeaheadPresenter.onType(event);
    }
    /**
     * @internal
     */
    onTypehead({ value }) {
        const options = this.traversableItems.map((component) => component instanceof CdsCheckboxGroupComponent
            ? { label: component.groupStateCheckbox.getLabel(), key: '' }
            : { label: component.getLabel(), key: '' });
        const matches = DropdownTypeaheadMatchAllFn(value, options);
        const activeItem = this.focusableListPresenter.getActiveItemIndex();
        const typeaheadTarget = findNextMatchingElement(this.traversableItems, matches, activeItem);
        if (typeaheadTarget) {
            this.openDropdown();
            this.focusableListPresenter.setAndFocusActiveItem(typeaheadTarget);
        }
    }
    /**
     * @internal
     * Updates the value of this control after downstream changes
     */
    onValueChanged() {
        this.value = this.formGroup.getRawValue();
        this.controlValueAccessor.onChange(this.value);
        this.updateText();
    }
    /**
     * @internal
     * Updates the value of this control after upstream/parent changes
     */
    writeValue(value) {
        this.value = value || {};
        this.createFormGroup(this.options);
    }
    /**
     * @internal
     */
    getComponentIdHash(groupId, optionKey) {
        return `${this.name}-${groupId}-${optionKey || ''}`;
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.focusableListPresenter.destroy();
        this.typeaheadPresenter.destroy();
    }
}
CdsMultiSelectComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMultiSelectComponent, deps: [{ token: i1$2.NgControl, optional: true, self: true }, { token: CdsFocusableListPresenter }, { token: TypeaheadPresenter }, { token: CdsControlValueAccessorComponent }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
CdsMultiSelectComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsMultiSelectComponent, selector: "cds-multi-select", inputs: { options: "options", name: "name", inline: "inline", placeholder: "placeholder", readout: "readout", iconName: "iconName", hideLabel: "hideLabel", leftAligned: "leftAligned" }, outputs: { change: "change" }, providers: [
        { provide: SelectComponentType, useExisting: forwardRef(() => CdsMultiSelectComponent) },
        CdsFocusableListPresenter,
        TypeaheadPresenter,
        CdsControlValueAccessorComponent,
    ], viewQueries: [{ propertyName: "dropdownToggle", first: true, predicate: CdsDropdownDirective, descendants: true, static: true }, { propertyName: "button", first: true, predicate: CdsButtonComponent, descendants: true, static: true }, { propertyName: "checkboxGroups", predicate: CdsCheckboxGroupComponent, descendants: true }, { propertyName: "checkboxes", predicate: CdsCheckboxComponent, descendants: true }], ngImport: i0, template: "<div\n  class=\"cds-multi-select cds-dropdown\"\n  [class.-open]=\"expanded\"\n  [class.-invalid]=\"!control.disabled && !control.valid && control.touched\"\n  (keydown)=\"handleTypeahead($event)\"\n  (keydown.tab)=\"closeDropdown($event)\"\n  (keydown.shift.tab)=\"closeDropdown($event)\"\n>\n  <cds-button\n    #button\n    cdsElementRef\n    #toggle=\"cdsElementRef\"\n    [name]=\"name ? ('multi-select-toggle-' + name | kebabCase) : ''\"\n    class=\"cds-dropdown-toggle\"\n    [class.-inline]=\"inline\"\n    [class.-icon-only]=\"hideLabel\"\n    [class.-left-aligned-icon]=\"!hideLabel && leftAligned\"\n    [class.-right-aligned-icon]=\"!hideLabel && !leftAligned\"\n    [disabled]=\"control.disabled\"\n    [text]=\"text\"\n    size=\"small\"\n    [type]=\"inline ? 'tertiary' : 'secondary'\"\n    ariaLabel=\"Open menu\"\n    (click)=\"focusButton()\"\n    (keydown.arrowdown)=\"focusFirstItem()\"\n  >\n    <i class=\"cds-dropdown-icon material-icons\" cdsButtonSuffix *ngIf=\"!leftAligned\">{{ dropdownIcon }}</i>\n    <i class=\"cds-dropdown-icon material-icons\" cdsButtonPrefix *ngIf=\"leftAligned\">{{ dropdownIcon }}</i>\n  </cds-button>\n\n  <div\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n    cdsDropdownPanel\n    #multiSelectPanel\n  >\n\n    <div class=\"cds-multi-select-panel\">\n      <ng-container *ngFor=\"let option of options\">\n        <ng-container\n          *ngTemplateOutlet=\"recursiveTemplate;\n            context:{\n              group: formGroup,\n              option: option,\n              name: name + '-' + option.key,\n              groupId: getComponentIdHash(option.key)\n            }\"\n        >\n        </ng-container>\n      </ng-container>\n\n      <ng-template #recursiveTemplate let-option=\"option\" let-group=\"group\" let-parentId=\"parentId\" let-groupId=\"groupId\" let-name=\"name\">\n        <!-- Single -->\n        <cds-checkbox\n          [name]=\"name\"\n          [label]=\"option.label\"\n          [formControl]=\"group.controls[option.label]\"\n          [parentId]=\"parentId\"\n          class=\"cds-select-option\"\n          [class.-wrap]=\"option.wrap\"\n          (change)=\"!parentId ? onValueChanged() : null\"\n          cdsFocusableItem\n          *ngIf=\"!option.children\"\n        >\n        </cds-checkbox>\n\n        <!-- Group -->\n        <cds-checkbox-group\n          [collapsible]=\"option.collapsible\"\n          [label]=\"option.label\"\n          [name]=\"name\"\n          (change)=\"onValueChanged()\"\n          [id]=\"groupId\"\n          [parentId]=\"parentId\"\n          [disabled]=\"option.disabled\"\n          [class.-wrap-label]=\"option.wrap\"\n          *ngIf=\"option.children\"\n        >\n          <ng-container *ngFor=\"let childOption of option.children\">\n            <ng-container *ngTemplateOutlet=\"recursiveTemplate;\n              context:{\n                group: group.controls[option.label],\n                option: childOption,\n                parentId: groupId,\n                groupId: getComponentIdHash(groupId, childOption.key),\n                name: name + '-' + option.key + '-' + childOption.key\n              }\"\n            >\n            </ng-container>\n          </ng-container>\n        </cds-checkbox-group>\n      </ng-template>\n    </div>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  buttonTrigger\n  [anchor]=\"toggle.nativeElement\"\n  [wasFocusedFn]=\"focusButton\"\n  [panel]=\"multiSelectPanel\"\n  (removeFocus)=\"closeDropdown()\"\n  (toggleChange)=\"onExpandedStateChanged($event)\"\n>\n</ng-container>\n", dependencies: [{ kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: CdsDropdownDirective, selector: "[cdsDropdown]", inputs: ["anchor", "panel", "options", "strategy", "wasFocusedFn"], outputs: ["removeFocus", "change"] }, { kind: "directive", type: CdsDropdownPanelDirective, selector: "[cdsDropdownPanel]", exportAs: ["dropdownPanel"] }, { kind: "directive", type: CdsFocusableItemDirective, selector: "[cdsFocusableItem]" }, { kind: "directive", type: CdsDropdownToggleButtonDirective, selector: "[buttonTrigger]", outputs: ["toggleChange"] }, { kind: "directive", type: CdsElementRefDirective, selector: "[cdsElementRef]", exportAs: ["cdsElementRef"] }, { kind: "component", type: CdsButtonComponent, selector: "cds-button", inputs: ["name", "text", "type", "size", "disabled", "underline", "iconName", "alignIcon", "designation", "ariaLabel", "link"] }, { kind: "component", type: CdsCheckboxComponent, selector: "cds-checkbox", inputs: ["isDisabled", "label", "hideLabel", "controlLabels", "name", "leftAlignedLabel"], outputs: ["change"] }, { kind: "component", type: CdsCheckboxGroupComponent, selector: "cds-checkbox-group", inputs: ["id", "name", "labelOnly", "label", "inline", "disabled", "collapsible"], outputs: ["change"] }, { kind: "directive", type: CdsCheckboxGroupCheckboxDirective, selector: "cds-checkbox:not([cdsCheckboxGroupMaster])", inputs: ["parentId"] }, { kind: "directive", type: CdsCheckboxGroupSubGroupDirective, selector: "cds-checkbox-group", inputs: ["parentId"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMultiSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-multi-select', providers: [
                        { provide: SelectComponentType, useExisting: forwardRef(() => CdsMultiSelectComponent) },
                        CdsFocusableListPresenter,
                        TypeaheadPresenter,
                        CdsControlValueAccessorComponent,
                    ], template: "<div\n  class=\"cds-multi-select cds-dropdown\"\n  [class.-open]=\"expanded\"\n  [class.-invalid]=\"!control.disabled && !control.valid && control.touched\"\n  (keydown)=\"handleTypeahead($event)\"\n  (keydown.tab)=\"closeDropdown($event)\"\n  (keydown.shift.tab)=\"closeDropdown($event)\"\n>\n  <cds-button\n    #button\n    cdsElementRef\n    #toggle=\"cdsElementRef\"\n    [name]=\"name ? ('multi-select-toggle-' + name | kebabCase) : ''\"\n    class=\"cds-dropdown-toggle\"\n    [class.-inline]=\"inline\"\n    [class.-icon-only]=\"hideLabel\"\n    [class.-left-aligned-icon]=\"!hideLabel && leftAligned\"\n    [class.-right-aligned-icon]=\"!hideLabel && !leftAligned\"\n    [disabled]=\"control.disabled\"\n    [text]=\"text\"\n    size=\"small\"\n    [type]=\"inline ? 'tertiary' : 'secondary'\"\n    ariaLabel=\"Open menu\"\n    (click)=\"focusButton()\"\n    (keydown.arrowdown)=\"focusFirstItem()\"\n  >\n    <i class=\"cds-dropdown-icon material-icons\" cdsButtonSuffix *ngIf=\"!leftAligned\">{{ dropdownIcon }}</i>\n    <i class=\"cds-dropdown-icon material-icons\" cdsButtonPrefix *ngIf=\"leftAligned\">{{ dropdownIcon }}</i>\n  </cds-button>\n\n  <div\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n    cdsDropdownPanel\n    #multiSelectPanel\n  >\n\n    <div class=\"cds-multi-select-panel\">\n      <ng-container *ngFor=\"let option of options\">\n        <ng-container\n          *ngTemplateOutlet=\"recursiveTemplate;\n            context:{\n              group: formGroup,\n              option: option,\n              name: name + '-' + option.key,\n              groupId: getComponentIdHash(option.key)\n            }\"\n        >\n        </ng-container>\n      </ng-container>\n\n      <ng-template #recursiveTemplate let-option=\"option\" let-group=\"group\" let-parentId=\"parentId\" let-groupId=\"groupId\" let-name=\"name\">\n        <!-- Single -->\n        <cds-checkbox\n          [name]=\"name\"\n          [label]=\"option.label\"\n          [formControl]=\"group.controls[option.label]\"\n          [parentId]=\"parentId\"\n          class=\"cds-select-option\"\n          [class.-wrap]=\"option.wrap\"\n          (change)=\"!parentId ? onValueChanged() : null\"\n          cdsFocusableItem\n          *ngIf=\"!option.children\"\n        >\n        </cds-checkbox>\n\n        <!-- Group -->\n        <cds-checkbox-group\n          [collapsible]=\"option.collapsible\"\n          [label]=\"option.label\"\n          [name]=\"name\"\n          (change)=\"onValueChanged()\"\n          [id]=\"groupId\"\n          [parentId]=\"parentId\"\n          [disabled]=\"option.disabled\"\n          [class.-wrap-label]=\"option.wrap\"\n          *ngIf=\"option.children\"\n        >\n          <ng-container *ngFor=\"let childOption of option.children\">\n            <ng-container *ngTemplateOutlet=\"recursiveTemplate;\n              context:{\n                group: group.controls[option.label],\n                option: childOption,\n                parentId: groupId,\n                groupId: getComponentIdHash(groupId, childOption.key),\n                name: name + '-' + option.key + '-' + childOption.key\n              }\"\n            >\n            </ng-container>\n          </ng-container>\n        </cds-checkbox-group>\n      </ng-template>\n    </div>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  buttonTrigger\n  [anchor]=\"toggle.nativeElement\"\n  [wasFocusedFn]=\"focusButton\"\n  [panel]=\"multiSelectPanel\"\n  (removeFocus)=\"closeDropdown()\"\n  (toggleChange)=\"onExpandedStateChanged($event)\"\n>\n</ng-container>\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: CdsFocusableListPresenter }, { type: TypeaheadPresenter }, { type: CdsControlValueAccessorComponent }, { type: i0.Renderer2 }]; }, propDecorators: { options: [{
                type: Input
            }], name: [{
                type: Input
            }], inline: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], readout: [{
                type: Input
            }], iconName: [{
                type: Input
            }], hideLabel: [{
                type: Input
            }], leftAligned: [{
                type: Input
            }], change: [{
                type: Output
            }], dropdownToggle: [{
                type: ViewChild,
                args: [CdsDropdownDirective, { static: true }]
            }], button: [{
                type: ViewChild,
                args: [CdsButtonComponent, { static: true }]
            }], checkboxGroups: [{
                type: ViewChildren,
                args: [CdsCheckboxGroupComponent]
            }], checkboxes: [{
                type: ViewChildren,
                args: [CdsCheckboxComponent]
            }] } });

class CdsMultiSelectModule {
}
CdsMultiSelectModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMultiSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsMultiSelectModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsMultiSelectModule, declarations: [CdsMultiSelectComponent], imports: [FormsModule,
        ReactiveFormsModule,
        CommonModule,
        CdsDirectivesModule,
        CdsListModule,
        CdsButtonModule,
        CdsCheckboxModule,
        CdsCheckboxGroupModule,
        CdsPipesModule], exports: [CdsMultiSelectComponent] });
CdsMultiSelectModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMultiSelectModule, imports: [FormsModule,
        ReactiveFormsModule,
        CommonModule,
        CdsDirectivesModule,
        CdsListModule,
        CdsButtonModule,
        CdsCheckboxModule,
        CdsCheckboxGroupModule,
        CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMultiSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsMultiSelectComponent],
                    exports: [CdsMultiSelectComponent],
                    imports: [
                        FormsModule,
                        ReactiveFormsModule,
                        CommonModule,
                        CdsDirectivesModule,
                        CdsListModule,
                        CdsButtonModule,
                        CdsCheckboxModule,
                        CdsCheckboxGroupModule,
                        CdsPipesModule,
                    ],
                }]
        }] });

/**
 * This directive exposes an API for performing client-side filtering on the rows of a Smart DataGrid. To enable client-side filtering,
 * you must add the `cdsSmartDatagridFilter` attribute to a select or multi-select element.
 *
 * You may use the `autoOptions` input to instruct this directive to automatically generate options for the select/multi-select
 * compmonent using the values of the objects in the table.
 *
 * Alternatively, you can explicitly specify options by passing them to the select/multi-select as usual.
 *
 * Lastly, using the `matchFn` input, you can provide a custom comparator function that matches values in
 * the dataset to the selected option. If this input is `null`, internal filtering is skipped.
 *
 * The DataGrid will emit a CdsDataGridStateChange event after filtering is completed.
 */
class CdsSmartDataGridFilterDirective {
    /**
     * @internal
     */
    constructor(datagrid, select, fnService, stateService) {
        this.datagrid = datagrid;
        this.select = select;
        this.fnService = fnService;
        this.stateService = stateService;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        this.select.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(value => this.stateService.requestFilters([{ key: this.key, value }]));
        this.stateService.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(({ state: { filters } }) => {
            if (filters) {
                const filter = filters[this.key];
                if (filter) {
                    this.select.writeValue(filter.value);
                }
            }
        });
    }
    /**
     * Automatically generate filter options using the contents of the column whose key is the same as this value.
     */
    set autoOptions(value) {
        if (value === true) {
            this.select.options = getOptionsFromColumn(this.key, this.datagrid.data, this.select instanceof CdsMultiSelectComponent);
        }
    }
    ;
    /**
     * @internal
     */
    ngOnInit() {
        if (!this.key) {
            throw Error('Missing key for DataGrid filter.');
        }
        this.fnService.filterFns[this.key] = {
            options: this.select.options,
            matchFn: this.matchFn === null ? this.matchFn : (this.matchFn || getMatchFn(this.key, this.select instanceof CdsMultiSelectComponent))
        };
        const value = this.select.control.control.value;
        if (value) {
            const state = this.stateService.getState();
            const filters = state.filters || [];
            this.stateService.setState({ ...state, filters: [...filters, { key: this.key, value }] }, false);
        }
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsSmartDataGridFilterDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridFilterDirective, deps: [{ token: CdsSmartDataGridDirective }, { token: SelectComponentType, optional: true, self: true }, { token: DataGridFnService }, { token: CdsDataGridStateService }], target: i0.ɵɵFactoryTarget.Directive });
CdsSmartDataGridFilterDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSmartDataGridFilterDirective, selector: "[cdsSmartDatagridFilter]", inputs: { key: "key", matchFn: "matchFn", autoOptions: "autoOptions" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridFilterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsSmartDatagridFilter]'
                }]
        }], ctorParameters: function () { return [{ type: CdsSmartDataGridDirective }, { type: SelectComponentType, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: DataGridFnService }, { type: CdsDataGridStateService }]; }, propDecorators: { key: [{
                type: Input
            }], matchFn: [{
                type: Input
            }], autoOptions: [{
                type: Input
            }] } });

/**
 * This directive exposes an API for performing client-side sorting on the rows of a Smart DataGrid.
 * To enable client-side sorting, you must add the `cdsSmartDatagridSort`
 * attribute to a `<cds-datagrid-header>` element.
 *
 * Using the `active` property, you may notify the DataGrid of which column to sort by when rendered for the
 * first time.
 *
 * Each Sort header present in the Datagrid should have its `key` attribute set to a unique value.
 *
 * If a comparator is not provided, the default comparator is used, which assumes that the `key` attribute is a dot-delimited string
 * pointing to a property on the top-level object or nested objects represented by each row.
 *
 * The DataGrid will emit a CdsDataGridStateChange event after sorting is completed.
 */
class CdsSmartDataGridSortDirective {
    /**
     * @internal
     */
    constructor(stateService, component, fnService) {
        this.stateService = stateService;
        this.component = component;
        this.fnService = fnService;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        /**
         * @internal
         */
        this.sort = this.component.sort
            .pipe(map(sort => ({
            ...sort,
            key: this.key,
        })));
        this.stateService.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(state => {
            setTimeout(() => this.onSortChanged(state.state.sort));
        });
    }
    /**
     * Provide a custom comparator function to apply when sorting the dataset.
     */
    set comparatorFn(comparatorFn) {
        this._comparatorFn = comparatorFn;
    }
    // when comparatorFn === null, use the sorted header but skip sorting
    // when comparatorFn === undefined, use defaultCompoaratorFn
    // when comparatorFn is defined, use comparatorFn
    get comparatorFn() {
        return this._comparatorFn === null ? this._comparatorFn : this._comparatorFn || getDefaultComparatorFn(this.key);
    }
    /**
     * @internal
     */
    ngOnInit() {
        this.component.sortable = true;
        this.fnService.comparatorFns[this.key] = this.comparatorFn;
        if (this.active) {
            this.stateService.setState({ ...this.stateService.getState(), sort: { key: this.key, direction: this.direction } }, false);
        }
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        this.sort
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(sort => this.stateService.requestSort(sort));
    }
    /**
     * @internal
     */
    onSortChanged(sort) {
        if (!sort) {
            return;
        }
        if (sort.key !== this.key) {
            this.clearSort();
            return;
        }
        this.setSort(sort);
    }
    /**
     * @internal
     */
    setSort(sort) {
        this.component.sortActive = true;
        this.component.sortDirection = sort.direction;
    }
    /**
     * @internal
     */
    clearSort() {
        this.component.sortActive = false;
        this.component.sortDirection = CdsSortDirection.None;
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsSmartDataGridSortDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridSortDirective, deps: [{ token: CdsDataGridStateService }, { token: CdsDataGridHeaderComponent }, { token: DataGridFnService }], target: i0.ɵɵFactoryTarget.Directive });
CdsSmartDataGridSortDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSmartDataGridSortDirective, selector: "[cdsSmartDatagridSort]", inputs: { key: "key", comparatorFn: "comparatorFn", active: "active", direction: "direction" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridSortDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[cdsSmartDatagridSort]'
                }]
        }], ctorParameters: function () { return [{ type: CdsDataGridStateService }, { type: CdsDataGridHeaderComponent }, { type: DataGridFnService }]; }, propDecorators: { key: [{
                type: Input
            }], comparatorFn: [{
                type: Input
            }], active: [{
                type: Input
            }], direction: [{
                type: Input
            }] } });

/**
 * This directive exposes an API for performing client-side paging on the rows of a Smart DataGrid. To enable client-side paging,
 * you must add the `cdsSmartDatagridPaginator` attribute to a `<cds-paginator>` element inside your datagrid markup.
 *
 * The dataset page that is rendered on initialization is dependent on the value of the paginator inputs.
 *
 * Additionally, you can provide custom a `CdsPaginationFn` to the `paginationFn` input, which returns an array
 * of paged rows to be displayed. If a paginationFn is not provided, the default pagination function is used.
 *
 * The DataGrid will emit a `CdsDataGridStateChange` event after paging is completed.
 */
class CdsSmartDataGridPaginatorDirective {
    /**
     * @internal
     */
    constructor(datagrid, pagination, stateService, fnService) {
        this.datagrid = datagrid;
        this.pagination = pagination;
        this.stateService = stateService;
        this.fnService = fnService;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        this.createPaginator();
        this.stateService.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(state => this.onPageChanged(state.state.page));
    }
    /**
     * Provide a custom pagination function to be used for paging.
     */
    set paginationFn(paginationFn) {
        this._paginationFn = paginationFn;
    }
    get paginationFn() {
        // when paginationFn === null, skip pagination
        // when paginationFn === undefined, use this.pageOf
        // when paginationFn is defined, use paginationFn
        return this._paginationFn === null ? this._paginationFn : this._paginationFn || pageOf;
    }
    /**
     * @internal
     */
    ngOnInit() {
        this.page = {
            offset: this.pagination.offset,
            pageSize: this.pagination.pageSize,
            totalResults: this.pagination.totalResults
        };
        this.fnService.paginationFn = this.paginationFn;
        this.stateService.setState({ ...this.stateService.getState(), page: this.page }, false);
    }
    /**
     * @internal
     */
    createPaginator() {
        const $offsetChange = this.pagination.offsetChange
            .pipe(tap(offset => {
            this.fnService.paginationFn = this.paginationFn;
            this.stateService.requestPage({
                ...this.page,
                offset
            });
        }));
        const $pageSizeChange = this.pagination.pageSizeChange
            .pipe(tap(pageSize => {
            this.fnService.paginationFn = this.paginationFn;
            this.stateService.requestPage({
                ...this.page,
                offset: 0,
                pageSize
            });
        }));
        merge($offsetChange, $pageSizeChange)
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe();
    }
    /**
     * @internal
     */
    onPageChanged(page) {
        if (!page) {
            return;
        }
        this.page = { ...page };
        this.pagination.offset = page.offset;
        this.pagination.pageSize = page.pageSize;
        this.pagination.totalResults = page.totalResults;
        this.pagination.recordLabel = this.recordLabel || 'record';
        this.pagination.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsSmartDataGridPaginatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridPaginatorDirective, deps: [{ token: CdsSmartDataGridDirective }, { token: CdsPaginatorComponent }, { token: CdsDataGridStateService }, { token: DataGridFnService }], target: i0.ɵɵFactoryTarget.Directive });
CdsSmartDataGridPaginatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSmartDataGridPaginatorDirective, selector: "[cdsSmartDataGridPaginator]", inputs: { paginationFn: "paginationFn", page: "page", recordLabel: "recordLabel" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSmartDataGridPaginatorDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[cdsSmartDataGridPaginator]'
                }]
        }], ctorParameters: function () { return [{ type: CdsSmartDataGridDirective }, { type: CdsPaginatorComponent }, { type: CdsDataGridStateService }, { type: DataGridFnService }]; }, propDecorators: { paginationFn: [{
                type: Input
            }], page: [{
                type: Input
            }], recordLabel: [{
                type: Input
            }] } });

class CdsDatagridPanelDirective {
    constructor(resizeService, resizablePresenter, cancelSubscriptions, datagrid) {
        this.resizeService = resizeService;
        this.resizablePresenter = resizablePresenter;
        this.cancelSubscriptions = cancelSubscriptions;
        this.datagrid = datagrid;
    }
    ngAfterViewInit() {
        const tableElement = this.datagrid.tableElement.element.nativeElement;
        this.resizablePresenter.watch([tableElement], this.resizeService, {});
        this.resizablePresenter.change
            .pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions))
            .subscribe((event) => {
            const width = event.get(tableElement).width;
            this.datagrid.width = width;
            this.datagrid.changeDetector.detectChanges();
        });
    }
    ngOnDestroy() {
        this.cancelSubscriptions.destroy();
        this.resizablePresenter.destroy();
    }
}
CdsDatagridPanelDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatagridPanelDirective, deps: [{ token: CdsResizeService }, { token: CdsResizableContainerPresenter }, { token: SubscriptionManager }, { token: CdsDataGridComponent }], target: i0.ɵɵFactoryTarget.Directive });
CdsDatagridPanelDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsDatagridPanelDirective, selector: "[cdsDgPanel]", providers: [CdsResizableContainerPresenter, SubscriptionManager], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatagridPanelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsDgPanel]',
                    providers: [CdsResizableContainerPresenter, SubscriptionManager]
                }]
        }], ctorParameters: function () { return [{ type: CdsResizeService }, { type: CdsResizableContainerPresenter }, { type: SubscriptionManager }, { type: CdsDataGridComponent }]; } });

class CdsDataGridPanelComponent {
    constructor(
    /** @internal */ displayService, 
    /** @internal */ datagrid) {
        this.displayService = displayService;
        this.datagrid = datagrid;
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.displayService.setPanel(this);
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.displayService.setPanel(null);
    }
}
CdsDataGridPanelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridPanelComponent, deps: [{ token: CdsDataGridDisplayService }, { token: CdsDataGridComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsDataGridPanelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsDataGridPanelComponent, selector: "cds-datagrid-panel", inputs: { name: "name", width: "width" }, providers: [SubscriptionManager, CdsResizeService], viewQueries: [{ propertyName: "template", first: true, predicate: ["template"], descendants: true, static: true }], ngImport: i0, template: "<ng-template #template>\n  <div\n    class=\"cds-data-table-panel\"\n    [style.width.px]=\"width\"\n    cdsDgPanel\n  >\n    <span\n      class=\"cds-resize-handle\"\n      cdsResizeHandler\n      [target]=\"datagrid.tableElement.element\"\n      aria-hidden=\"true\"\n      (click)=\"$event.stopPropagation()\"\n    ></span>\n\n    <div class=\"cds-data-table-panel-header\"></div>\n\n    <div class=\"cds-data-table-panel-content\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</ng-template>\n", dependencies: [{ kind: "directive", type: CdsResizeHandlerDirective, selector: "[cdsResizeHandler]", inputs: ["target", "minWidth", "maxWidth"] }, { kind: "directive", type: CdsDatagridPanelDirective, selector: "[cdsDgPanel]" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridPanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-datagrid-panel', providers: [SubscriptionManager, CdsResizeService], template: "<ng-template #template>\n  <div\n    class=\"cds-data-table-panel\"\n    [style.width.px]=\"width\"\n    cdsDgPanel\n  >\n    <span\n      class=\"cds-resize-handle\"\n      cdsResizeHandler\n      [target]=\"datagrid.tableElement.element\"\n      aria-hidden=\"true\"\n      (click)=\"$event.stopPropagation()\"\n    ></span>\n\n    <div class=\"cds-data-table-panel-header\"></div>\n\n    <div class=\"cds-data-table-panel-content\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: CdsDataGridDisplayService }, { type: CdsDataGridComponent }]; }, propDecorators: { name: [{
                type: Input
            }], width: [{
                type: Input
            }], template: [{
                type: ViewChild,
                args: ['template', { static: true }]
            }] } });

class CdsDataGridModule {
}
CdsDataGridModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsDataGridModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridModule, declarations: [CdsDataGridCellComponent,
        CdsDatagridCellDirective,
        CdsDataGridHeaderComponent,
        CdsDatagridHeaderDirective,
        CdsSortedHeaderDirective,
        CdsDataGridComponent,
        CdsPagedDataGridDirective,
        CdsSortedDataGridDirective,
        CdsSmartDataGridDirective,
        CdsFilteredDataGridDirective,
        CdsResizableTableDirective,
        CdsVirtualScrollDataGridDirective,
        CdsVirtualScrollSmartDataGridDirective,
        CdsDataGridRowComponent,
        CdsDataGridRowLinkDirective,
        CdsSmartRowDirective,
        CdsDatagridRowDirective,
        CdsSmartDataGridSelectDirective,
        CdsDataGridFilterComponent,
        CdsSmartDataGridFilterDirective,
        CdsSmartDataGridSortDirective,
        CdsSmartDataGridPaginatorDirective,
        CdsSmartDataGridCheckboxCellComponent,
        CdsSmartDataGridCheckboxHeaderComponent,
        CdsDataGridToolbarContentDirective,
        CdsDataGridPanelComponent,
        CdsDatagridPanelDirective], imports: [CommonModule,
        RouterModule,
        ReactiveFormsModule,
        CdsDirectivesModule,
        CdsPipesModule,
        CdsPaginatorModule,
        CdsSelectModule,
        CdsCheckboxModule,
        CdsVirtualScrollerModule], exports: [CdsDataGridCellComponent,
        CdsDataGridHeaderComponent,
        CdsSortedHeaderDirective,
        CdsDataGridComponent,
        CdsPagedDataGridDirective,
        CdsSortedDataGridDirective,
        CdsSmartDataGridDirective,
        CdsFilteredDataGridDirective,
        CdsVirtualScrollDataGridDirective,
        CdsVirtualScrollSmartDataGridDirective,
        CdsDataGridRowComponent,
        CdsDataGridRowLinkDirective,
        CdsSmartRowDirective,
        CdsSmartDataGridSelectDirective,
        CdsSmartDataGridFilterDirective,
        CdsSmartDataGridSortDirective,
        CdsSmartDataGridPaginatorDirective,
        CdsSmartDataGridCheckboxCellComponent,
        CdsSmartDataGridCheckboxHeaderComponent,
        CdsDataGridToolbarContentDirective,
        CdsDataGridPanelComponent,
        CdsVirtualScrollerModule] });
CdsDataGridModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridModule, imports: [CommonModule,
        RouterModule,
        ReactiveFormsModule,
        CdsDirectivesModule,
        CdsPipesModule,
        CdsPaginatorModule,
        CdsSelectModule,
        CdsCheckboxModule,
        CdsVirtualScrollerModule, CdsVirtualScrollerModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDataGridModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsDataGridCellComponent,
                        CdsDatagridCellDirective,
                        CdsDataGridHeaderComponent,
                        CdsDatagridHeaderDirective,
                        CdsSortedHeaderDirective,
                        CdsDataGridComponent,
                        CdsPagedDataGridDirective,
                        CdsSortedDataGridDirective,
                        CdsSmartDataGridDirective,
                        CdsFilteredDataGridDirective,
                        CdsResizableTableDirective,
                        CdsVirtualScrollDataGridDirective,
                        CdsVirtualScrollSmartDataGridDirective,
                        CdsDataGridRowComponent,
                        CdsDataGridRowLinkDirective,
                        CdsSmartRowDirective,
                        CdsDatagridRowDirective,
                        CdsSmartDataGridSelectDirective,
                        CdsDataGridFilterComponent,
                        CdsSmartDataGridFilterDirective,
                        CdsSmartDataGridSortDirective,
                        CdsSmartDataGridPaginatorDirective,
                        CdsSmartDataGridCheckboxCellComponent,
                        CdsSmartDataGridCheckboxHeaderComponent,
                        CdsDataGridToolbarContentDirective,
                        CdsDataGridPanelComponent,
                        CdsDatagridPanelDirective
                    ],
                    exports: [
                        CdsDataGridCellComponent,
                        CdsDataGridHeaderComponent,
                        CdsSortedHeaderDirective,
                        CdsDataGridComponent,
                        CdsPagedDataGridDirective,
                        CdsSortedDataGridDirective,
                        CdsSmartDataGridDirective,
                        CdsFilteredDataGridDirective,
                        CdsVirtualScrollDataGridDirective,
                        CdsVirtualScrollSmartDataGridDirective,
                        CdsDataGridRowComponent,
                        CdsDataGridRowLinkDirective,
                        CdsSmartRowDirective,
                        CdsSmartDataGridSelectDirective,
                        CdsSmartDataGridFilterDirective,
                        CdsSmartDataGridSortDirective,
                        CdsSmartDataGridPaginatorDirective,
                        CdsSmartDataGridCheckboxCellComponent,
                        CdsSmartDataGridCheckboxHeaderComponent,
                        CdsDataGridToolbarContentDirective,
                        CdsDataGridPanelComponent,
                        CdsVirtualScrollerModule
                    ],
                    imports: [
                        CommonModule,
                        RouterModule,
                        ReactiveFormsModule,
                        CdsDirectivesModule,
                        CdsPipesModule,
                        CdsPaginatorModule,
                        CdsSelectModule,
                        CdsCheckboxModule,
                        CdsVirtualScrollerModule
                    ]
                }]
        }] });

/**
 * An object representing the data state of the Datagrid at any given point in time.
 */
class ICdsDataGridState {
}

/**
 * Traps focus within an element.
 */
class FocusTrapPresenter {
    constructor() {
        this.$cancelSubscriptions = new Subject();
        this.onKeydown = (event, element) => {
            const focusableElements = getFocusableElements(element);
            const firstFocusableEl = focusableElements[0];
            const lastFocusableEl = focusableElements[focusableElements.length - 1];
            if (event.key === 'Tab' &&
                !event.shiftKey &&
                event.target === lastFocusableEl) {
                event.preventDefault();
                this.shiftFocusToStart(element);
            }
            if (event.key === 'Tab' &&
                event.shiftKey &&
                event.target === firstFocusableEl) {
                event.preventDefault();
                this.shiftFocusToEnd(element);
            }
        };
    }
    watch(element) {
        fromEvent(element, 'keydown')
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe((event) => this.onKeydown(event, element));
    }
    setFocus(element, parent) {
        const focusableElements = getFocusableElements(parent);
        const index = Array.from(focusableElements).findIndex((entry) => element.contains(entry) || element === entry);
        if (index > -1) {
            // @TODO: HACK
            // - Angular doesn't play nice with setting focus from within services and components (see LinkComponent, ListItemComponent)
            // Dave O, 9-22-2020
            timer(100)
                .pipe(take(1))
                .subscribe(() => {
                focusableElements.item(index).focus();
            });
        }
    }
    shiftFocusToStart(element) {
        timer(100)
            .pipe(take(1))
            .subscribe((count) => {
            getFocusableElements(element)[0].focus();
        });
    }
    shiftFocusToEnd(element) {
        const focusableElements = getFocusableElements(element);
        timer(100)
            .pipe(take(1))
            .subscribe((count) => {
            focusableElements[focusableElements.length - 1].focus();
        });
    }
    destroy() {
        this.$cancelSubscriptions.next();
    }
}
FocusTrapPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FocusTrapPresenter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FocusTrapPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FocusTrapPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FocusTrapPresenter, decorators: [{
            type: Injectable
        }] });
function getFocusableElements(element) {
    return element.querySelectorAll(`a[href]:not([disabled]),
    button:not([disabled]),
    textarea:not([disabled]),
    input:not([disabled]),
    select:not([disabled]),
    [tabindex]:not([tabindex="-1"])`);
}

/**
 * Uses keyboard arrow events to manage the focus state of DOM elements in a grid.
 */
class CdsGridKeyManager {
    constructor(items, rows, columns, rangeStart, rangeEnd) {
        this.items = items;
        this.rows = rows;
        this.columns = columns;
        this.rangeStart = rangeStart;
        this.rangeEnd = rangeEnd;
        this.$focusedItem = new BehaviorSubject(null);
        this.change = this.$focusedItem.asObservable().pipe(pairwise());
        this.outOfRangeNavigationException = new Subject();
        if (!this.rangeStart) {
            this.rangeStart = 0;
        }
        if (!this.rangeEnd) {
            this.rangeEnd = this.rows * this.columns - 1;
        }
    }
    updateRange(rangeStart, rangeEnd) {
        this.rangeStart = rangeStart;
        this.rangeEnd = rangeEnd;
    }
    onKeydown(event) {
        const focusedItemIndex = this.items
            .toArray()
            .indexOf(this.$focusedItem.value);
        let newfocusedItemIndex;
        event.preventDefault();
        if (event.key === 'ArrowUp') {
            newfocusedItemIndex = this.moveUpByIndex(focusedItemIndex);
        }
        else if (event.key === 'ArrowDown') {
            newfocusedItemIndex = this.moveDownByIndex(focusedItemIndex);
        }
        else if (event.key === 'ArrowLeft') {
            newfocusedItemIndex = this.moveLeftByIndex(focusedItemIndex);
        }
        else if (event.key === 'ArrowRight') {
            newfocusedItemIndex = this.moveRightByIndex(focusedItemIndex);
        }
        if (newfocusedItemIndex < 0 || newfocusedItemIndex < this.rangeStart) {
            this.outOfRangeNavigationException.next({
                thresholdExceeded: 'top',
                index: newfocusedItemIndex,
            });
        }
        else if (newfocusedItemIndex > this.rangeEnd) {
            this.outOfRangeNavigationException.next({
                thresholdExceeded: 'bottom',
                index: newfocusedItemIndex,
            });
        }
        else {
            this.setFocusedItemByIndex(newfocusedItemIndex);
        }
    }
    setFocusedItemByIndex(index) {
        this.$focusedItem.next(this.items.toArray()[index]);
    }
    getFocusedItem() {
        return this.$focusedItem;
    }
    getFocusedItemIndex() {
        return this.items.toArray().indexOf(this.$focusedItem.value);
    }
    getItemPosition(index) {
        const xAxis = index % this.columns;
        const yAxis = Math.floor(index / this.columns);
        return { x: xAxis, y: yAxis };
    }
    getItemIndex(x, y) {
        return y * this.columns + x;
    }
    moveUpByIndex(index) {
        const position = this.getItemPosition(index);
        const y = position.y - 1;
        return this.getItemIndex(position.x, y);
    }
    moveDownByIndex(index) {
        const position = this.getItemPosition(index);
        const y = position.y + 1;
        return this.getItemIndex(position.x, y);
    }
    moveLeftByIndex(index) {
        const position = this.getItemPosition(index);
        const x = position.x - 1;
        return this.getItemIndex(x, position.y);
    }
    moveRightByIndex(index) {
        const position = this.getItemPosition(index);
        const x = position.x + 1;
        return this.getItemIndex(x, position.y);
    }
    destroy() {
        this.$focusedItem.complete();
    }
}

/**
 * @internal
 *
 * This presenter is responsible for keeping track of the calendar's state (active date, selected date, displayed date etc).
 */
class CdsCalendarPresenter {
    constructor() {
        this.$date = new Subject();
        this.change = this.$date.asObservable();
    }
    getActiveDateIndex(dates) {
        return dates.findIndex(date => isSameDay(date.dateObject, this.getValue()));
    }
    setValue(date) {
        const dateParts = getDateParts(date);
        this._date = combineDateparts(dateParts.day, dateParts.month, dateParts.year);
        this.$date.next(this._date);
    }
    resetValue() {
        this._date = null;
        this.$date.next(this._date);
    }
    getValue() {
        return this._date;
    }
    updateValue(date) {
        this._date = date;
    }
    destroy() {
        this.$date.complete();
    }
}
CdsCalendarPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCalendarPresenter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsCalendarPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCalendarPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCalendarPresenter, decorators: [{
            type: Injectable
        }] });

/**
 * @internal
 *
 * This service is responsible for keeping track of the currently displayed calendar month.
 */
class CalendarDisplayService {
    constructor() {
        this._date = new Date();
        this.$date = new BehaviorSubject(this._date);
        this.change = this.$date.asObservable();
    }
    getCurrentMonth() {
        return this._date;
    }
    goToPreviousMonth() {
        const priorMonth = subMonths(this._date, 1);
        this.goToMonth(priorMonth);
    }
    goToNextMonth() {
        const nextMonth = addMonths(this._date, 1);
        this.goToMonth(nextMonth);
    }
    goToToday() {
        this.goToMonth(new Date());
    }
    goToMonth(date) {
        const dateParts = getDateParts(date);
        this._date = startOfMonth(combineDateparts(dateParts.day, dateParts.month, dateParts.year));
        this.$date.next(this._date);
    }
    destroy() {
        this.$date.complete();
    }
}
CalendarDisplayService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CalendarDisplayService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CalendarDisplayService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CalendarDisplayService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CalendarDisplayService, decorators: [{
            type: Injectable
        }] });

/**
 * @internal
 */
class CdsCalendarComponent {
    // Interesting and noteworthy:
    // if ngControl is injected into the component, writeValue() is called before lifecycle hooks.
    // if NG_VALUE_ACCESSOR is provided in the list of providers, lifecycle hooks are called before writeValue().
    constructor(control, changeDetector, displayService, presenter, focusTrapPresenter, controlValueAccessor, element) {
        this.control = control;
        this.changeDetector = changeDetector;
        this.displayService = displayService;
        this.presenter = presenter;
        this.focusTrapPresenter = focusTrapPresenter;
        this.controlValueAccessor = controlValueAccessor;
        this.element = element;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        /**
         * @internal
         */
        this.today = getDateObject(new Date(), 'current');
        /**
         * @internal
         */
        this.month = new UntypedFormControl(null);
        /**
         * @internal
         */
        this.year = new UntypedFormControl(null);
        /**
         * @internal
         */
        this.day = new UntypedFormControl(null);
        /**
         * @internal
         */
        this.weekdays = [];
        /**
         * @internal
         */
        this.months = getMonths();
        /**
         * @internal
         */
        this.years = [];
        /**
         * @internal
         */
        this.dates = [];
        this.clear = new EventEmitter();
        this.close = new EventEmitter();
        this.change = this.controlValueAccessor.change;
        this.registerOnChange = this.controlValueAccessor.registerOnChange;
        this.registerOnTouched = this.controlValueAccessor.registerOnTouched;
        this.onTouch = this.controlValueAccessor.onTouch;
        this.setDisabledState = this.controlValueAccessor.setDisabledState;
        if (this.control) {
            this.control.valueAccessor = this;
        }
        else {
            throw new Error('FormControl is missing from Calendar.');
        }
        this.presenter.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(date => {
            this.controlValueAccessor.onChange(date);
            // this.updateCalendarUI();
        });
    }
    /**
     * @internal
     */
    get disabled() {
        return this.controlValueAccessor.disabled;
    }
    ngOnInit() {
        this.weekdays = getWeekDays(this.locale).map(weekday => weekday.abbr);
        this.years = getYears(this.minYear, this.maxYear);
    }
    ngAfterViewInit() {
        this.displayService.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => {
            this.updateCalendarUI();
        });
        this.calendarItems.changes
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => {
            this.setupTraversableItems();
        });
        this.setupTraversableItems();
        this.focusTrapPresenter.watch(this.element.nativeElement);
    }
    setupTraversableItems() {
        this.gridKeyManager = new CdsGridKeyManager(this.calendarItems, 6, 7);
        this.gridKeyManager.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(([previouslyFocused, currentlyFocused]) => {
            if (previouslyFocused) {
                previouslyFocused.removeHighlight();
            }
            if (currentlyFocused) {
                currentlyFocused.addHighlight();
                currentlyFocused.focus();
            }
        });
    }
    // Enter triggers the primary button in the context of the Calendar, which is the `close` button.
    onEnterKeydown(event) {
        event.stopPropagation();
        event.preventDefault();
        this.closeClicked();
    }
    // Click/Keyboard Interaction Methods
    onKeydown(event, date) {
        if (event.key === ' ') {
            event.preventDefault();
            this.presenter.setValue(date.dateObject);
            this.focusActiveItem(date.dateObject);
            return;
        }
        if (event.key === 'Enter') {
            event.stopPropagation();
            event.preventDefault();
            this.presenter.setValue(date.dateObject);
            this.focusActiveItem(date.dateObject);
            // Enter triggers the primary button in the context of the Calendar, which is the `close` button.
            this.closeClicked();
            return;
        }
        if (event.key === 'ArrowLeft' && this.gridKeyManager.getFocusedItemIndex() === 0) {
            event.stopImmediatePropagation();
            this.tryNavigateToPreviousMonth();
            return;
        }
        if (event.key === 'ArrowRight' && this.gridKeyManager.getFocusedItemIndex() === this.dates.length - 1) {
            event.stopImmediatePropagation();
            this.tryNavigateToNextMonth();
            return;
        }
        if (!isNavigationKey(event.key)) {
            return;
        }
        this.gridKeyManager.onKeydown(event);
    }
    isActive(date) {
        return isSameDay(date.dateObject, this.presenter.getValue());
    }
    focusActiveItem(date) {
        this.displayService.goToMonth(date);
        // @todo: will not be the same day for ranged pickers
        const activeItemIndex = this.dates.findIndex(entry => isSameDay(date, entry.dateObject));
        this.gridKeyManager.setFocusedItemByIndex(activeItemIndex);
        // this.changeDetector.detectChanges();
    }
    onBlur(calendarItem) {
        calendarItem.highlightable.removeHighlightedClass();
    }
    tryNavigateToPreviousMonth() {
        const date = this.dates[this.gridKeyManager.getFocusedItemIndex()];
        this.displayService.goToPreviousMonth();
        const activeItemIndex = this.dates.findIndex(entry => isSameDay(date.dateObject, entry.dateObject));
        this.gridKeyManager.setFocusedItemByIndex(activeItemIndex - 1);
    }
    tryNavigateToNextMonth() {
        const date = this.dates[this.gridKeyManager.getFocusedItemIndex()];
        this.displayService.goToNextMonth();
        const activeItemIndex = this.dates.findIndex(entry => isSameDay(date.dateObject, entry.dateObject));
        this.gridKeyManager.setFocusedItemByIndex(activeItemIndex + 1);
    }
    // Buttons
    closeClicked() {
        this.close.emit();
    }
    previousMonthClicked() {
        this.displayService.goToPreviousMonth();
    }
    nextMonthClicked() {
        this.displayService.goToNextMonth();
    }
    clearClicked() {
        this.presenter.resetValue();
    }
    dateClicked(event, date) {
        event.preventDefault();
        event.stopPropagation();
        this.presenter.setValue(date.dateObject);
        this.focusActiveItem(date.dateObject);
    }
    // Dropdowns
    setMonth(month) {
        const date = setMonth(this.displayService.getCurrentMonth(), month);
        this.focusActiveItem(date);
    }
    setYear(year) {
        const date = setYear(this.displayService.getCurrentMonth(), year);
        this.focusActiveItem(date);
    }
    // Utils
    goToDate(date) {
        this.displayService.goToMonth(date);
    }
    focusCalendarBody() {
        const selectedDateIndex = Math.max(this.presenter.getActiveDateIndex(this.dates), 0);
        const todayIndex = Math.max(this.dates.findIndex(date => isSameDay(date.dateObject, this.today.dateObject)), 0);
        this.gridKeyManager.setFocusedItemByIndex(selectedDateIndex || todayIndex);
    }
    updateCalendarUI() {
        const date = this.displayService.getCurrentMonth();
        this.setControlValues(date);
        this.dates = calculateCalendarDates(date, 1, this.locale);
        this.changeDetector.detectChanges();
    }
    setControlValues(date) {
        if (!date) {
            date = new Date();
        }
        const dateParts = getDateParts(date);
        this.day.setValue(dateParts.day);
        this.month.setValue(dateParts.month);
        this.year.setValue(dateParts.year);
    }
    // Control Value Acessor Methods
    writeValue(value) {
        value = value || new Date();
        this.presenter.updateValue(value);
        this.displayService.goToMonth(value);
    }
    ngOnDestroy() {
        this.focusTrapPresenter.destroy();
        this.gridKeyManager.destroy();
        this.displayService.destroy();
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsCalendarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCalendarComponent, deps: [{ token: i1$2.NgControl, optional: true, self: true }, { token: i0.ChangeDetectorRef }, { token: CalendarDisplayService }, { token: CdsCalendarPresenter }, { token: FocusTrapPresenter }, { token: CdsControlValueAccessorComponent }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
CdsCalendarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsCalendarComponent, selector: "cds-calendar", inputs: { name: "name", locale: "locale", minYear: "minYear", maxYear: "maxYear" }, outputs: { clear: "clear", close: "close", change: "change" }, providers: [FocusTrapPresenter, CalendarDisplayService, CdsCalendarPresenter, CdsControlValueAccessorComponent], viewQueries: [{ propertyName: "calendarItems", predicate: ["calendarDate"], descendants: true }], ngImport: i0, template: "<div class=\"cds-calendar\" (keydown.enter)=\"onEnterKeydown($event)\">\n  <div class=\"cds-calendar-header\">\n    <cds-select\n      [name]=\"name + '-month-dropdown'\"\n      id=\"month-dropdown\"\n      placeholder=\"Month\"\n      [formControl]=\"month\"\n      [options]=\"months\"\n      (change)=\"setMonth($event)\"\n    ></cds-select>\n\n    <cds-select\n      [name]=\"name + '-year-dropdown'\"\n      id=\"year-dropdown\"\n      placeholder=\"Year\"\n      [formControl]=\"year\"\n      [options]=\"years\"\n      (change)=\"setYear($event)\"\n    ></cds-select>\n\n    <div id=\"month-navigation\">\n      <cds-control-block\n        id=\"previous-month\"\n        [name]=\"name + '-previous-month'\"\n        size=\"small\"\n        (click)=\"previousMonthClicked()\"\n      >\n        <i class=\"material-icons\">keyboard_arrow_left</i>\n      </cds-control-block>\n\n      <cds-control-block\n        id=\"next-month\"\n        [name]=\"name + '-next-month'\"\n        size=\"small\"\n        (click)=\"nextMonthClicked()\"\n        (keydown.tab)=\"$event.preventDefault();focusCalendarBody()\"\n      >\n        <i class=\"material-icons\">keyboard_arrow_right</i>\n      </cds-control-block>\n    </div>\n  </div>\n\n  <div #calendarBody class=\"cds-calendar-body\">\n    <div class=\"date-weekday\">\n      <cds-control-block\n        tabindex=\"-1\"\n        size=\"small\"\n        *ngFor=\"let weekday of weekdays\"\n        >\n        <span>{{weekday}}</span>\n      </cds-control-block>\n    </div>\n\n    <cds-control-block\n      #calendarDate\n      size=\"small\"\n      *ngFor=\"let date of dates\"\n      [name]=\"name + '-' + date.month.name + '-' + date.value\"\n      [class.prev-month]=\"date.designation === 'previous'\"\n      [class.next-month]=\"date.designation === 'next'\"\n      tabindex=\"-1\"\n      [active]=\"isActive(date)\"\n      [class.today]=\"date.today\"\n      (click)=\"dateClicked($event, date)\"\n      (focusout)=\"onBlur(calendarDate)\"\n      (keydown)=\"onKeydown($event, date)\"\n      >\n      <span>{{ date.value }}</span>\n    </cds-control-block>\n  </div>\n\n\n  <div class=\"cds-calendar-footer\">\n    <cds-button\n      [name]=\"name + '-clear'\"\n      type=\"tertiary\"\n      size=\"small\"\n      designation=\"button\"\n      text=\"Clear\"\n      (click)=\"clearClicked()\"\n      (keydown.shift.tab)=\"$event.preventDefault();focusCalendarBody()\"\n    ></cds-button>\n\n    <cds-button\n      [name]=\"name + '-' + 'today-button'\"\n      type=\"secondary\"\n      size=\"small\"\n      (click)=\"dateClicked($event, today)\"\n      designation=\"button\"\n      text=\"Today\"\n    ></cds-button>\n\n    <cds-button\n      [name]=\"name + '-close'\"\n      type=\"primary\"\n      size=\"small\"\n      (click)=\"closeClicked()\"\n      designation=\"button\"\n      text=\"Close\"\n    ></cds-button>\n  </div>\n</div>", dependencies: [{ kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: CdsSelectComponent, selector: "cds-select", inputs: ["name", "options", "inline", "placeholder", "iconName", "hideLabel", "leftAligned", "readout"], outputs: ["change"] }, { kind: "component", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: ["name", "size", "disabled", "tabindex", "active", "attributes"] }, { kind: "component", type: CdsButtonComponent, selector: "cds-button", inputs: ["name", "text", "type", "size", "disabled", "underline", "iconName", "alignIcon", "designation", "ariaLabel", "link"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCalendarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-calendar', providers: [FocusTrapPresenter, CalendarDisplayService, CdsCalendarPresenter, CdsControlValueAccessorComponent], template: "<div class=\"cds-calendar\" (keydown.enter)=\"onEnterKeydown($event)\">\n  <div class=\"cds-calendar-header\">\n    <cds-select\n      [name]=\"name + '-month-dropdown'\"\n      id=\"month-dropdown\"\n      placeholder=\"Month\"\n      [formControl]=\"month\"\n      [options]=\"months\"\n      (change)=\"setMonth($event)\"\n    ></cds-select>\n\n    <cds-select\n      [name]=\"name + '-year-dropdown'\"\n      id=\"year-dropdown\"\n      placeholder=\"Year\"\n      [formControl]=\"year\"\n      [options]=\"years\"\n      (change)=\"setYear($event)\"\n    ></cds-select>\n\n    <div id=\"month-navigation\">\n      <cds-control-block\n        id=\"previous-month\"\n        [name]=\"name + '-previous-month'\"\n        size=\"small\"\n        (click)=\"previousMonthClicked()\"\n      >\n        <i class=\"material-icons\">keyboard_arrow_left</i>\n      </cds-control-block>\n\n      <cds-control-block\n        id=\"next-month\"\n        [name]=\"name + '-next-month'\"\n        size=\"small\"\n        (click)=\"nextMonthClicked()\"\n        (keydown.tab)=\"$event.preventDefault();focusCalendarBody()\"\n      >\n        <i class=\"material-icons\">keyboard_arrow_right</i>\n      </cds-control-block>\n    </div>\n  </div>\n\n  <div #calendarBody class=\"cds-calendar-body\">\n    <div class=\"date-weekday\">\n      <cds-control-block\n        tabindex=\"-1\"\n        size=\"small\"\n        *ngFor=\"let weekday of weekdays\"\n        >\n        <span>{{weekday}}</span>\n      </cds-control-block>\n    </div>\n\n    <cds-control-block\n      #calendarDate\n      size=\"small\"\n      *ngFor=\"let date of dates\"\n      [name]=\"name + '-' + date.month.name + '-' + date.value\"\n      [class.prev-month]=\"date.designation === 'previous'\"\n      [class.next-month]=\"date.designation === 'next'\"\n      tabindex=\"-1\"\n      [active]=\"isActive(date)\"\n      [class.today]=\"date.today\"\n      (click)=\"dateClicked($event, date)\"\n      (focusout)=\"onBlur(calendarDate)\"\n      (keydown)=\"onKeydown($event, date)\"\n      >\n      <span>{{ date.value }}</span>\n    </cds-control-block>\n  </div>\n\n\n  <div class=\"cds-calendar-footer\">\n    <cds-button\n      [name]=\"name + '-clear'\"\n      type=\"tertiary\"\n      size=\"small\"\n      designation=\"button\"\n      text=\"Clear\"\n      (click)=\"clearClicked()\"\n      (keydown.shift.tab)=\"$event.preventDefault();focusCalendarBody()\"\n    ></cds-button>\n\n    <cds-button\n      [name]=\"name + '-' + 'today-button'\"\n      type=\"secondary\"\n      size=\"small\"\n      (click)=\"dateClicked($event, today)\"\n      designation=\"button\"\n      text=\"Today\"\n    ></cds-button>\n\n    <cds-button\n      [name]=\"name + '-close'\"\n      type=\"primary\"\n      size=\"small\"\n      (click)=\"closeClicked()\"\n      designation=\"button\"\n      text=\"Close\"\n    ></cds-button>\n  </div>\n</div>" }]
        }], ctorParameters: function () { return [{ type: i1$2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: i0.ChangeDetectorRef }, { type: CalendarDisplayService }, { type: CdsCalendarPresenter }, { type: FocusTrapPresenter }, { type: CdsControlValueAccessorComponent }, { type: i0.ElementRef }]; }, propDecorators: { name: [{
                type: Input
            }], locale: [{
                type: Input
            }], minYear: [{
                type: Input
            }], maxYear: [{
                type: Input
            }], clear: [{
                type: Output
            }], close: [{
                type: Output
            }], change: [{
                type: Output
            }], calendarItems: [{
                type: ViewChildren,
                args: ['calendarDate']
            }] } });

class CdsCalendarModule {
}
CdsCalendarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCalendarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsCalendarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsCalendarModule, declarations: [CdsCalendarComponent], imports: [FormsModule,
        ReactiveFormsModule,
        CommonModule,
        CdsSelectModule,
        CdsControlBlockModule,
        CdsButtonModule,
        CdsDirectivesModule], exports: [CdsCalendarComponent] });
CdsCalendarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCalendarModule, imports: [FormsModule,
        ReactiveFormsModule,
        CommonModule,
        CdsSelectModule,
        CdsControlBlockModule,
        CdsButtonModule,
        CdsDirectivesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsCalendarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsCalendarComponent],
                    exports: [CdsCalendarComponent],
                    imports: [
                        FormsModule,
                        ReactiveFormsModule,
                        CommonModule,
                        CdsSelectModule,
                        CdsControlBlockModule,
                        CdsButtonModule,
                        CdsDirectivesModule,
                    ],
                }]
        }] });

/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable max-classes-per-file */
class AbstractSuggestionDirective {
}
class CdsSuggestionDirective {
    constructor(
    /** @internal */ elementRef, 
    /** @internal */ wrappedComponent) {
        this.elementRef = elementRef;
        this.wrappedComponent = wrappedComponent;
        /**
         * @internal
         */
        this.highlightable = new CdsHighlightableComponent();
        /**
         * @internal
         */
        this.tabIndex = '-1';
        /**
         * @internal
         */
        this.klass = true;
        /**
         * @internal
         */
        this.select = new EventEmitter();
    }
    /**
     * @internal
     */
    get element() {
        return this.elementRef.nativeElement;
    }
    /**
     * @internal
     */
    get template() {
        return this.wrappedComponent.template;
    }
    /**
     * @internal
     */
    onSelect() {
        this.select.emit();
    }
}
CdsSuggestionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionDirective, deps: [{ token: i0.ElementRef }, { token: CdsWrappedComponent }], target: i0.ɵɵFactoryTarget.Directive });
CdsSuggestionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSuggestionDirective, selector: "[cdsSuggestion]", inputs: { name: "name", iconName: "iconName" }, outputs: { select: "select" }, host: { listeners: { "click": "onSelect()" }, properties: { "attr.icon-name": "this.iconName", "attr.tabindex": "this.tabIndex", "class.cds-suggestion-item": "this.klass" } }, providers: [
        CdsWrappedComponent,
        {
            provide: AbstractSuggestionDirective,
            useExisting: forwardRef(() => CdsSuggestionDirective),
        },
    ], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/component-selector
                    selector: '[cdsSuggestion]',
                    providers: [
                        CdsWrappedComponent,
                        {
                            provide: AbstractSuggestionDirective,
                            useExisting: forwardRef(() => CdsSuggestionDirective),
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: CdsWrappedComponent }]; }, propDecorators: { name: [{
                type: Input
            }], iconName: [{
                type: HostBinding,
                args: ['attr.icon-name']
            }, {
                type: Input
            }], tabIndex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], klass: [{
                type: HostBinding,
                args: ['class.cds-suggestion-item']
            }], select: [{
                type: Output
            }], onSelect: [{
                type: HostListener,
                args: ['click']
            }] } });
class CdsSuggestionHeaderDirective {
    constructor(wrappedComponent) {
        this.wrappedComponent = wrappedComponent;
        /**
         * @internal
         */
        this.klass = true;
    }
    get template() {
        return this.wrappedComponent.template;
    }
}
CdsSuggestionHeaderDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionHeaderDirective, deps: [{ token: CdsWrappedComponent }], target: i0.ɵɵFactoryTarget.Directive });
CdsSuggestionHeaderDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSuggestionHeaderDirective, selector: "[cdsSuggestionHeader]", host: { properties: { "class.cds-suggestion-header": "this.klass" } }, providers: [
        CdsWrappedComponent,
        {
            provide: AbstractSuggestionDirective,
            useExisting: forwardRef(() => CdsSuggestionHeaderDirective),
        }
    ], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionHeaderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsSuggestionHeader]',
                    providers: [
                        CdsWrappedComponent,
                        {
                            provide: AbstractSuggestionDirective,
                            useExisting: forwardRef(() => CdsSuggestionHeaderDirective),
                        }
                    ]
                }]
        }], ctorParameters: function () { return [{ type: CdsWrappedComponent }]; }, propDecorators: { klass: [{
                type: HostBinding,
                args: ['class.cds-suggestion-header']
            }] } });

/**
 * Uses keyboard arrow events to manage the active state of DOM elements in a list.
 */
class CdsListKeyManager {
    constructor(items) {
        this.items = items;
        this.$activeItem = new BehaviorSubject(null);
        this.activeItem = this.$activeItem.asObservable().pipe(pairwise());
        this.rangeStart = 0;
    }
    get rangeEnd() {
        return this.items ? this.items.length - 1 : 0;
    }
    withWrap() {
        this.enableWrapping = true;
        return this;
    }
    withTypeAhead() {
        // @TODO: implementation
        return this;
    }
    onKeydown(event) {
        const activeItemIndex = this.items.indexOf(this.$activeItem.value);
        let newactiveItemIndex;
        event.preventDefault();
        if (event.key === 'ArrowUp') {
            newactiveItemIndex = activeItemIndex - 1;
        }
        else if (event.key === 'ArrowDown') {
            newactiveItemIndex = activeItemIndex + 1;
        }
        if (newactiveItemIndex < this.rangeStart) {
            newactiveItemIndex = this.enableWrapping
                ? this.items.length - Math.abs(newactiveItemIndex)
                : this.rangeStart;
        }
        if (newactiveItemIndex > this.rangeEnd) {
            newactiveItemIndex = this.enableWrapping
                ? this.rangeStart + (newactiveItemIndex - this.items.length)
                : this.rangeEnd;
        }
        this.setActiveItem(this.items[newactiveItemIndex]);
    }
    setActiveItem(item) {
        const activeItemIndex = this.items.indexOf(item);
        if (activeItemIndex >= 0) {
            this.$activeItem.next(item);
            return;
        }
        this.$activeItem.next(this.items[0]);
    }
    clearActiveItem() {
        this.$activeItem.next(null);
    }
    getActiveItem() {
        return this.$activeItem.value;
    }
    getActiveItemIndex() {
        return this.items.indexOf(this.$activeItem.value);
    }
    destroy() {
        this.$activeItem.complete();
    }
}

/**
 * Abstract class for a directive that manages a list of focusable items,
 * where each item is a tuple containing an HTMLElement paired with
 * a traversable component (CdsHighlightableComponent).
 */
class CdsHighlightableListPresenter {
    constructor() {
        this.$cancelSubscriptions = new Subject();
    }
    /**
     * @param items: Array<{ element: HTMLElement, component: CdsHighlightableComponent }>
     * Detects UI events on a CdsHighlightableComponent by watching its containing element, and sets the active state of an element
     * within the CdsHighlightableComponent.
     */
    watch(items, wrap = true, typeahead = true, horizontalOrientation) {
        this.$cancelSubscriptions.complete();
        this.$cancelSubscriptions = new Subject();
        const targets = items.map((item) => item.component);
        this.keyManager = new CdsListKeyManager(targets);
        if (wrap) {
            this.keyManager = this.keyManager.withWrap();
        }
        if (typeahead) {
            this.keyManager = this.keyManager.withTypeAhead();
        }
        const clicks = items.map((item) => fromEvent(item.element, 'click').pipe(map(() => item.component), tap((component) => this.setAndHighlightActiveItem(component))));
        const activeItemChanges = this.keyManager.activeItem.pipe(tap(([previous, current]) => {
            if (previous) {
                previous.highlightable.removeHighlightedClass(previous.element);
            }
            if (current) {
                current.highlightable.addHighlightedClass(current.element);
                current.highlightable.scrollIntoView(current.element);
            }
        }));
        merge(...clicks, activeItemChanges)
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe();
    }
    isNavigationKey(key) {
        return (key === 'Tab' ||
            key === 'Escape' ||
            key === 'Up' ||
            key === 'ArrowUp' ||
            key === 'Down' ||
            key === 'ArrowDown');
    }
    onKeydown(event) {
        if (event.key === 'Tab' || event.key === 'Escape') {
            return;
        }
        event.stopPropagation();
        this.keyManager.onKeydown(event);
    }
    setActiveItem(item) {
        if (item) {
            this.keyManager.setActiveItem(item);
        }
        else {
            this.keyManager.clearActiveItem();
        }
    }
    setAndHighlightActiveItem(item) {
        this.keyManager.setActiveItem(item);
    }
    getActiveItem() {
        return this.keyManager.getActiveItem();
    }
    getActiveItemIndex() {
        return this.keyManager.getActiveItemIndex();
    }
    destroy() {
        if (this.keyManager) {
            this.keyManager.destroy();
        }
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}

/**
 * Handles traversal and selection of Suggestion Items.
 */
class CdsSuggestionsPresenter {
    constructor() {
        this.$cancelSubscriptions = new Subject();
        this.highlightableListPresenter = new CdsHighlightableListPresenter();
    }
    /**
     * @internal
     */
    createKeyBindings(input, items) {
        this.$cancelSubscriptions.next();
        // navigation
        fromEvent(input, 'keydown')
            .pipe(takeUntil(this.$cancelSubscriptions), filter((event) => this.highlightableListPresenter.isNavigationKey(event.key)), tap((event) => this.highlightableListPresenter.onKeydown(event)))
            .subscribe();
        // selection
        fromEvent(input, 'keydown')
            .pipe(takeUntil(this.$cancelSubscriptions), filter((event) => event.key === 'Enter'), tap((event) => {
            // select user-provided suggestions
            const active = this.highlightableListPresenter.getActiveItem();
            if (active) {
                event.preventDefault();
                active.onSelect();
            }
        }))
            .subscribe();
        // selection
        const clicks = items.map((item) => fromEvent(item, 'click').pipe(takeUntil(this.$cancelSubscriptions)));
        merge(...clicks).subscribe((event) => input.focus());
    }
    /**
     * @internal
     */
    setupHighlightableItems(items) {
        this.highlightableListPresenter.destroy();
        const targets = items.map((suggestion) => ({
            element: suggestion.elementRef.nativeElement,
            component: suggestion,
        }));
        this.highlightableListPresenter.watch(targets);
        this.highlightableListPresenter.setAndHighlightActiveItem();
    }
    /**
     * @internal
     */
    destroy() {
        this.highlightableListPresenter.destroy();
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsSuggestionsPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionsPresenter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsSuggestionsPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionsPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionsPresenter, decorators: [{
            type: Injectable
        }] });

/**
 * ### Description
 * Provides a field for capturing a search query entered by a user.
 */
class CdsSuggestionsComponent {
    /**
     * @internal
     */
    constructor(suggestionsPresenter) {
        this.suggestionsPresenter = suggestionsPresenter;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
    }
    /**
     * @internal
     */
    get expanded() {
        return this.dropdown && this.dropdown.expanded;
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.children.changes
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => {
            this.container.clear();
            this.children.map(child => this.container.createEmbeddedView(child.template));
            this.suggestionsPresenter.setupHighlightableItems(this.children.filter(component => component instanceof CdsSuggestionDirective));
        });
        this.children.forEach(child => this.container.createEmbeddedView(child.template));
        this.suggestionsPresenter.setupHighlightableItems(this.children.filter(component => component instanceof CdsSuggestionDirective));
    }
    /**
     * @internal
     */
    init(input) {
        this.input = input;
    }
    /**
     * Hides the suggestions dropdown.
     */
    hide() {
        if (!this.dropdown.expanded) {
            return;
        }
        this.dropdown.collapse();
    }
    /**
     * Shows the suggestions dropdown.
     */
    show() {
        if (this.dropdown.expanded) {
            return;
        }
        // this.input = input;
        this.strategy = new DirectionalPositionStrategy('bottom', {});
        this.suggestionsPresenter.createKeyBindings(this.input, this.children.filter(component => component instanceof CdsSuggestionDirective)
            .map((item) => item.elementRef.nativeElement));
        this.dropdown.expand(this.input);
    }
    /**
     * @internal
     */
    focusInput() {
        this.input?.focus();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
        this.suggestionsPresenter.destroy();
    }
}
CdsSuggestionsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionsComponent, deps: [{ token: CdsSuggestionsPresenter }], target: i0.ɵɵFactoryTarget.Component });
CdsSuggestionsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSuggestionsComponent, selector: "cds-suggestions", inputs: { name: "name" }, providers: [CdsSuggestionsPresenter], queries: [{ propertyName: "children", predicate: AbstractSuggestionDirective, descendants: true }], viewQueries: [{ propertyName: "dropdown", first: true, predicate: CdsDropdownDirective, descendants: true, static: true }, { propertyName: "container", first: true, predicate: ["container"], descendants: true, read: ViewContainerRef }], ngImport: i0, template: "<div class=\"cds-dropdown\" [class.-open]=\"expanded\">\n  <div\n    #panel\n    cdsDropdownPanel\n    [id]=\"('cds-search-suggestions-panel-' + name) | kebabCase\" \n  >\n    <ul class=\"cds-suggestions\">\n      <ng-container #container></ng-container>\n    </ul>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  [panel]=\"panel\"\n  [wasFocusedFn]=\"focusInput\"\n  (removeFocus)=\"hide()\"\n>\n</ng-container>\n", dependencies: [{ kind: "directive", type: CdsDropdownDirective, selector: "[cdsDropdown]", inputs: ["anchor", "panel", "options", "strategy", "wasFocusedFn"], outputs: ["removeFocus", "change"] }, { kind: "directive", type: CdsDropdownPanelDirective, selector: "[cdsDropdownPanel]", exportAs: ["dropdownPanel"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-suggestions', providers: [CdsSuggestionsPresenter], template: "<div class=\"cds-dropdown\" [class.-open]=\"expanded\">\n  <div\n    #panel\n    cdsDropdownPanel\n    [id]=\"('cds-search-suggestions-panel-' + name) | kebabCase\" \n  >\n    <ul class=\"cds-suggestions\">\n      <ng-container #container></ng-container>\n    </ul>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  [panel]=\"panel\"\n  [wasFocusedFn]=\"focusInput\"\n  (removeFocus)=\"hide()\"\n>\n</ng-container>\n" }]
        }], ctorParameters: function () { return [{ type: CdsSuggestionsPresenter }]; }, propDecorators: { name: [{
                type: Input
            }], children: [{
                type: ContentChildren,
                args: [AbstractSuggestionDirective, { descendants: true }]
            }], dropdown: [{
                type: ViewChild,
                args: [CdsDropdownDirective, { static: true }]
            }], container: [{
                type: ViewChild,
                args: ['container', { read: ViewContainerRef }]
            }] } });

class CdsSuggestionsModule {
}
CdsSuggestionsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsSuggestionsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionsModule, declarations: [CdsSuggestionsComponent,
        CdsSuggestionHeaderDirective,
        CdsSuggestionDirective], imports: [CdsPipesModule,
        CdsDirectivesModule,
        CdsButtonModule,
        CommonModule,
        CdsHostWrapperModule], exports: [CdsSuggestionsComponent,
        CdsSuggestionHeaderDirective,
        CdsSuggestionDirective] });
CdsSuggestionsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionsModule, imports: [CdsPipesModule,
        CdsDirectivesModule,
        CdsButtonModule,
        CommonModule,
        CdsHostWrapperModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSuggestionsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsSuggestionsComponent,
                        CdsSuggestionHeaderDirective,
                        CdsSuggestionDirective
                    ],
                    exports: [
                        CdsSuggestionsComponent,
                        CdsSuggestionHeaderDirective,
                        CdsSuggestionDirective
                    ],
                    imports: [
                        CdsPipesModule,
                        CdsDirectivesModule,
                        CdsButtonModule,
                        CommonModule,
                        CdsHostWrapperModule,
                    ],
                }]
        }] });

const undefinedCheck = (validator, callback) => {
    if (validator === undefined || validator === null) {
        return validator;
    }
    return callback();
};
const copyErrors = (errors, control) => {
    if (!control || !errors) {
        return;
    }
    if (Object.keys(errors).length === 0) {
        return;
    }
    control.setErrors({ ...control.errors, ...errors }, { emitEvent: true });
};
/**
 * @internal
 */
const removeErrors = (keys, control) => {
    if (!control || !keys || keys.length === 0) {
        return;
    }
    const remainingErrors = keys.reduce((errors, key) => {
        delete errors[key];
        return errors;
    }, { ...control.errors });
    control.setErrors(remainingErrors);
    if (Object.keys(control.errors || {}).length === 0) {
        control.setErrors(null);
    }
};
/**
 * @internal
 */
const setValidators = (control, validators) => {
    const validatorBackup = control.validator;
    if (validatorBackup) {
        validators.push(validatorBackup);
    }
    if (validators.length > 0) {
        control.setValidators(validators);
        control.updateValueAndValidity({ emitEvent: false });
    }
    return validatorBackup;
};
/**
 * @internal
 */
const resetValidators = (control, originalValidators) => {
    control.clearValidators();
    control.setValidators(originalValidators);
};
/**
 * @internal
 */
const getValidatorFunctions = (validation, customValidators = []) => {
    const validatorFns = [
        undefinedCheck(validation.minlength, () => Validators.minLength(validation.minlength)),
        undefinedCheck(validation.maxlength, () => Validators.maxLength(validation.maxlength)),
        undefinedCheck(validation.max, () => Validators.max(validation.max)),
        undefinedCheck(validation.min, () => Validators.min(validation.min)),
        undefinedCheck(validation.required, () => Validators.required),
        undefinedCheck(validation.email, () => Validators.email),
        undefinedCheck(validation.pattern, () => Validators.pattern(validation.pattern)),
        ...customValidators
    ];
    return validatorFns.filter(fn => !!fn);
};
const typeaheadExactMatch = (options = []) => (control) => {
    if (!control.value || options.includes(control.value)) {
        return null;
    }
    return {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        CDS_INPUT_INVALID_TYPEAHEAD_OPTION: true
    };
};
const getValidationErrors = (validation, control, ...customValidators) => {
    const validationErrors = [
        undefinedCheck(validation.minlength, () => Validators.minLength(validation.minlength)(control)),
        undefinedCheck(validation.maxlength, () => Validators.maxLength(validation.maxlength)(control)),
        undefinedCheck(validation.max, () => Validators.max(validation.max)(control)),
        undefinedCheck(validation.min, () => Validators.min(validation.min)(control)),
        undefinedCheck(validation.required, () => Validators.required(control)),
        undefinedCheck(validation.email, () => Validators.email(control)),
        undefinedCheck(validation.pattern, () => Validators.pattern(validation.pattern)(control)),
        ...customValidators.map(validationFn => validationFn(control))
    ].filter(error => error);
    return validationErrors.length > 0 ? Object.assign({}, ...validationErrors) : null;
};
const getAsyncValidatorFunctions = (validation, customValidators = []) => (control) => of(getValidationErrors(validation, control, ...customValidators));

class CdsInputPresenter {
}

/**
 * ### Description
 * Provides a field for capturing numbers entered by a user.
 * The Number Input component accepts content to be displayed to the left or right (or both) of the input element.
 * This input prefix/suffix content should be marked with the attributes `[cdsInputPrefix]` or `[cdsInputSuffix]` for left
 * and right display respectively of the input element as needed.
 */
class CdsNumberInputPresenter extends CdsInputPresenter {
    constructor() {
        super(...arguments);
        this.defaultAlignment = 'right';
        this.defaultType = 'number';
        this.defaultPlaceholder = '0';
    }
    /**
     * Set the supported number of decimal places.
     */
    getStep(precision) {
        const decimalPlaces = Array.from('0'.repeat(precision - 1)).concat('1').join('');
        return `.${decimalPlaces}`;
    }
    getId(name) {
        return kebabCaseTransform('cds-number-input-' + name);
    }
    /**
     * @internal
     */
    prepValue(value, precision) {
        if (precision) {
            value = parseFloat(Number(value).toFixed(precision));
        }
        else {
            value = parseInt(value, 10);
        }
        return value;
    }
}

/**
 * ### Description
 * Provides a field for capturing the text entered by a user.
 * The Text Input component accepts content to be displayed to the left or right (or both) of the input element.
 * This input prefix/suffix content should be marked with the attributes `[cdsInputPrefix]` or `[cdsInputSuffix]` for left
 * and right display respectively of the input element as needed.
 */
class CdsTextInputPresenter extends CdsInputPresenter {
    constructor() {
        super(...arguments);
        this.defaultAlignment = 'left';
        this.defaultType = 'text';
        this.defaultPlaceholder = 'Example text';
    }
    getId(name) {
        return kebabCaseTransform('cds-text-input-' + name);
    }
    prepValue(value) {
        return value;
    }
}

class CdsInputTypeaheadConfig {
}

/**
 * A component that helps with text highlighting.
 *
 * It splits the `result` text into parts that contain the searched `term` and generates the HTML markup to simplify
 * highlighting:
 *
 * Ex. `result="Alaska"` and `term="as"` will produce `Al<span class="ngb-highlight">as</span>ka`.
 */
class CdsStringHighlightComponent {
    constructor() {
        /**
         * @internal
         */
        this.highlightClass = 'cds-string-highlight';
        /**
         * @internal
         */
        this.accentSensitive = true;
    }
    /**
     * @internal
     */
    ngOnChanges(changes) {
        const result = this.toString(this.result);
        const terms = [...Array.isArray(this.term) ? this.term : [this.term]];
        const prepareTerm = (term) => (this.accentSensitive ? term : this.removeAccents(term));
        const escapedTerms = terms.map((term) => this.regExpEscape(prepareTerm(this.toString(term)))).filter((term) => term);
        const toSplit = result;
        const parts = escapedTerms.length ? toSplit.split(new RegExp(`(${escapedTerms.join('|')})`, 'gmi')) : [result];
        if (this.accentSensitive) {
            this.parts = parts;
        }
        else {
            let offset = 0;
            this.parts = parts.map((part) => result.substring(offset, (offset += part.length)));
        }
    }
    /**
     * @internal
     */
    regExpEscape(text) {
        return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }
    /**
     * @internal
     */
    removeAccents(str) {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    /**
     * @internal
     */
    toString(value) {
        return value !== undefined && value !== null ? `${value}` : '';
    }
}
CdsStringHighlightComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsStringHighlightComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsStringHighlightComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsStringHighlightComponent, selector: "cds-string-highlight", inputs: { highlightClass: "highlightClass", result: "result", term: "term", accentSensitive: "accentSensitive" }, usesOnChanges: true, ngImport: i0, template: "\n<ng-container *ngFor=\"let part of parts; let odd = odd\">\n    <span *ngIf=\"odd\" class=\"{{ highlightClass }}\">{{ part }}</span>\n    <span *ngIf=\"!odd\">{{ part }}</span>\n</ng-container>\n\n", styles: [".cds-string-highlight{font-weight:700}\n"], dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsStringHighlightComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-string-highlight', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "\n<ng-container *ngFor=\"let part of parts; let odd = odd\">\n    <span *ngIf=\"odd\" class=\"{{ highlightClass }}\">{{ part }}</span>\n    <span *ngIf=\"!odd\">{{ part }}</span>\n</ng-container>\n\n", styles: [".cds-string-highlight{font-weight:700}\n"] }]
        }], propDecorators: { highlightClass: [{
                type: Input
            }], result: [{
                type: Input
            }], term: [{
                type: Input
            }], accentSensitive: [{
                type: Input
            }] } });

/* eslint-disable @angular-eslint/no-output-native */
/* eslint-disable @angular-eslint/no-input-rename */
/**
 * ### Description
 * Provides a field for capturing the text entered by a user.
 * The Text Input component accepts content to be displayed to the left or right (or both) of the input element.
 * This input prefix/suffix content should be marked with the attributes `[cdsInputPrefix]` or `[cdsInputSuffix]` for left
 * and right display respectively of the input element as needed.
 */
class CdsInputComponent extends CdsAbstractTextInputComponent {
    constructor(
    /** @internal */ control, 
    /** @internal */ controlValueAccessor, 
    /** @internal */ changeDetector) {
        super(controlValueAccessor);
        this.control = control;
        this.changeDetector = changeDetector;
        /**
         * @internal
         */
        this.textPresenter = new CdsTextInputPresenter();
        /**
         * @internal
         */
        this.numberPresenter = new CdsNumberInputPresenter();
        /**
         * @internal
         */
        this.typeaheadResults = null;
        /**
         * @internal
         * An event containing the new state of this control.
         */
        this.change = this.controlValueAccessor.change;
        if (this.control) {
            this.control.valueAccessor = this;
        }
        else {
            throw new Error(`FormControl is missing from Text Input.`);
        }
    }
    /**
     * @internal
     */
    get email() {
        return this.type === 'email' || undefined;
    }
    /**
     * @internal
     */
    get presenter() {
        return this.type === 'number' ? this.numberPresenter : this.textPresenter;
    }
    /**
     * @internal
     */
    get _align() {
        return `-${this.align || this.presenter.defaultAlignment}-aligned`;
    }
    /**
     * @internal
     */
    get _id() {
        return this.presenter.getId(this.name);
    }
    /**
     * @internal
     */
    get _placeholder() {
        return this.placeholder || this.presenter.defaultPlaceholder;
    }
    /**
     * @internal
     */
    get _step() {
        return this.presenter instanceof CdsNumberInputPresenter ? this.presenter.getStep(this.precision) : null;
    }
    /**
     * @internal
     */
    get _type() {
        return this.type || this.presenter.defaultType;
    }
    /**
     * @internal
     */
    get _ngClass() {
        return {
            [this._align]: true
        };
    }
    /**
     * The minimum value allowed. Enables @angular/forms' Validators.min
     */
    set _min(value) {
        this.min = value;
        this.control.control.updateValueAndValidity({ emitEvent: false });
    }
    /**
     * The maximum value allowed. Enables @angular/forms' Validators.max
     */
    set _max(value) {
        this.max = value;
        this.control.control.updateValueAndValidity({ emitEvent: false });
    }
    /**
     * The maximum amount of characters allowed by this input. Enables @angular/forms' Validators.maxLength
     */
    set _maxlength(value) {
        this.maxlength = value;
        this.control.control.updateValueAndValidity({ emitEvent: false });
    }
    /**
     * The minimum amount of characters required by this input. Enables @angular/forms' Validators.minLength
     */
    set _minlength(value) {
        this.minlength = value;
        this.control.control.updateValueAndValidity({ emitEvent: false });
    }
    /**
     * A RegEx pattern for validating the input value. Enables @angular/forms' Validators.pattern
     */
    set _pattern(value) {
        this.pattern = value;
        this.control.control.updateValueAndValidity({ emitEvent: false });
    }
    /**
     * Is the input required? Enables @angular/forms' Validators.required
     */
    set _required(value) {
        this.required = value || undefined;
        this.control.control.updateValueAndValidity({ emitEvent: false });
    }
    /**
     * @internal
     */
    ngOnInit() {
        if (this.autoTypeahead && !this.typeahead) {
            const validators = getAsyncValidatorFunctions(this, [typeaheadExactMatch(this.autoTypeahead.options)]);
            this.control.control.setAsyncValidators(validators);
            return;
        }
        this.control.control.setAsyncValidators(getAsyncValidatorFunctions(this));
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.defaultSuggestions?.init(this.inputElement.nativeElement);
        this.typeahead?.init(this.inputElement.nativeElement);
    }
    /**
     * @internal
     */
    updateValue() {
        this.value = this.presenter.prepValue(this.value, this.precision);
        onTypeaheadChange(this);
        this.controlValueAccessor.onChange(this.value);
    }
    /**
     * @internal
     */
    onFocus() {
        this.active = true;
    }
    /**
     * @internal
     */
    onBlur() {
        this.active = false;
        this.controlValueAccessor.onTouch();
        this.updateValue();
    }
    /**
     * @internal
     */
    writeValue(value) {
        this.value = value;
    }
    /**
     * @internal
     */
    showTypeahead() {
        (this.typeahead || this.defaultSuggestions)?.show();
    }
    /**
     * @internal
     */
    hideTypeahead() {
        (this.typeahead || this.defaultSuggestions)?.hide();
    }
    /**
     * @internal
     */
    selectSuggestedItem(value) {
        this.value = value;
        this.updateValue();
        this.hideTypeahead();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.control.control.clearAsyncValidators();
    }
}
CdsInputComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsInputComponent, deps: [{ token: i1$2.NgControl, optional: true, self: true }, { token: CdsControlValueAccessorComponent }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsInputComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsInputComponent, selector: "cds-input", inputs: { type: "type", align: "align", inline: "inline", _min: ["min", "_min"], _max: ["max", "_max"], _maxlength: ["maxlength", "_maxlength"], _minlength: ["minlength", "_minlength"], _pattern: ["pattern", "_pattern"], _required: ["required", "_required"], precision: "precision", autoTypeahead: "autoTypeahead", typeahead: "typeahead" }, outputs: { change: "change" }, providers: [CdsControlValueAccessorComponent], viewQueries: [{ propertyName: "defaultSuggestions", first: true, predicate: CdsSuggestionsComponent, descendants: true }], usesInheritance: true, ngImport: i0, template: "<div\n  class=\"cds-input-container\"\n  [class.-with-prefix]=\"prefixes.length > 0\"\n  [class.-with-suffix]=\"suffixes.length > 0\"\n  [class.-invalid]=\"!control.disabled && !control.valid && control.touched && !active\"\n  [class.-inline]=\"inline\"\n  [class.-disabled]=\"control.disabled || null\"\n  [class.-active]=\"active\"\n>\n\n  <div *ngIf=\"prefixes.length > 0\" class=\"cds-input-addon -prefix\">\n    <ng-content select=\"[cdsInputPrefix]\"></ng-content>\n  </div>\n \n  <input #input\n    [(ngModel)]=\"value\"\n    class=\"cds-input\"\n    [ngClass]=\"_ngClass\"\n    [id]=\"_id\"\n    [placeholder]=\"_placeholder\"\n    [name]=\"name\"\n    [type]=\"_type\"\n    [autocomplete]=\"autocomplete || 'off'\"\n    [attr.disabled]=\"control.disabled || null\"\n    [attr.max]=\"max\"\n    [attr.min]=\"min\"\n    [attr.maxlength]=\"'' + maxlength\"\n    [attr.minlength]=\"'' + minlength\"\n    [attr.step]=\"_step\"\n    (input)=\"updateValue(); this.showTypeahead();\"\n    (focus)=\"onFocus()\"\n    (blur)=\"onBlur()\"\n    (change)=\"$event.stopPropagation()\"\n  />\n  <div *ngIf=\"suffixes.length > 0\" class=\"cds-input-addon -suffix\">\n    <ng-content select=\"[cdsInputSuffix]\"></ng-content>\n  </div>\n  \n  <cds-suggestions *ngIf=\"autoTypeahead\">\n    <li\n      *ngFor=\"let item of typeaheadResults\"\n      cdsSuggestion\n      (select)=\"selectSuggestedItem(item)\">\n\n      <cds-string-highlight [result]=\"item\" [term]=\"value\"></cds-string-highlight>\n    </li>\n  </cds-suggestions>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.MinLengthValidator, selector: "[minlength][formControlName],[minlength][formControl],[minlength][ngModel]", inputs: ["minlength"] }, { kind: "directive", type: i1$2.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i1$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsSuggestionsComponent, selector: "cds-suggestions", inputs: ["name"] }, { kind: "directive", type: CdsSuggestionDirective, selector: "[cdsSuggestion]", inputs: ["name", "iconName"], outputs: ["select"] }, { kind: "component", type: CdsStringHighlightComponent, selector: "cds-string-highlight", inputs: ["highlightClass", "result", "term", "accentSensitive"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-input', providers: [CdsControlValueAccessorComponent], template: "<div\n  class=\"cds-input-container\"\n  [class.-with-prefix]=\"prefixes.length > 0\"\n  [class.-with-suffix]=\"suffixes.length > 0\"\n  [class.-invalid]=\"!control.disabled && !control.valid && control.touched && !active\"\n  [class.-inline]=\"inline\"\n  [class.-disabled]=\"control.disabled || null\"\n  [class.-active]=\"active\"\n>\n\n  <div *ngIf=\"prefixes.length > 0\" class=\"cds-input-addon -prefix\">\n    <ng-content select=\"[cdsInputPrefix]\"></ng-content>\n  </div>\n \n  <input #input\n    [(ngModel)]=\"value\"\n    class=\"cds-input\"\n    [ngClass]=\"_ngClass\"\n    [id]=\"_id\"\n    [placeholder]=\"_placeholder\"\n    [name]=\"name\"\n    [type]=\"_type\"\n    [autocomplete]=\"autocomplete || 'off'\"\n    [attr.disabled]=\"control.disabled || null\"\n    [attr.max]=\"max\"\n    [attr.min]=\"min\"\n    [attr.maxlength]=\"'' + maxlength\"\n    [attr.minlength]=\"'' + minlength\"\n    [attr.step]=\"_step\"\n    (input)=\"updateValue(); this.showTypeahead();\"\n    (focus)=\"onFocus()\"\n    (blur)=\"onBlur()\"\n    (change)=\"$event.stopPropagation()\"\n  />\n  <div *ngIf=\"suffixes.length > 0\" class=\"cds-input-addon -suffix\">\n    <ng-content select=\"[cdsInputSuffix]\"></ng-content>\n  </div>\n  \n  <cds-suggestions *ngIf=\"autoTypeahead\">\n    <li\n      *ngFor=\"let item of typeaheadResults\"\n      cdsSuggestion\n      (select)=\"selectSuggestedItem(item)\">\n\n      <cds-string-highlight [result]=\"item\" [term]=\"value\"></cds-string-highlight>\n    </li>\n  </cds-suggestions>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: CdsControlValueAccessorComponent }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { type: [{
                type: Input
            }], align: [{
                type: Input
            }], inline: [{
                type: Input
            }], _min: [{
                type: Input,
                args: ['min']
            }], _max: [{
                type: Input,
                args: ['max']
            }], _maxlength: [{
                type: Input,
                args: ['maxlength']
            }], _minlength: [{
                type: Input,
                args: ['minlength']
            }], _pattern: [{
                type: Input,
                args: ['pattern']
            }], _required: [{
                type: Input,
                args: ['required']
            }], precision: [{
                type: Input
            }], autoTypeahead: [{
                type: Input
            }], typeahead: [{
                type: Input
            }], change: [{
                type: Output
            }], defaultSuggestions: [{
                type: ViewChild,
                args: [CdsSuggestionsComponent]
            }] } });
function onTypeaheadChange(input) {
    if (!input.autoTypeahead) {
        return;
    }
    const defaultFilterFn = (term = '', opts) => opts && term.length > 1
        ? opts.filter((v) => v.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, 10)
        : [];
    const { filterFn = defaultFilterFn, debounceTime = 0, options } = input.autoTypeahead;
    setTimeout(() => {
        input.typeaheadResults = filterFn(input.value || '', options || []);
    }, debounceTime);
}

/**
 * ### Description
 * Provides a field for capturing the text entered by a user.
 */
class CdsTextareaComponent extends CdsAbstractTextInputComponent {
    /** @internal */
    constructor(control, controlValueAccessor) {
        super(controlValueAccessor);
        this.control = control;
        /**
         * @internal
         * An event containing the new state of this control.
         */
        this.change = this.controlValueAccessor.change;
        if (this.control) {
            this.control.valueAccessor = this;
        }
        else {
            throw new Error(`FormControl is missing from Text Input.`);
        }
    }
    /**
     * @internal
     */
    get _resizable() {
        return this.resizable === false;
    }
    /**
     * Is the input required? Enables @angular/forms' Validators.required
     */
    set _required(value) {
        this.required = value || undefined;
        this.control.control.updateValueAndValidity({ emitEvent: false });
    }
    /**
     * The maximum amount of characters allowed by this input. Enables @angular/forms' Validators.maxLength
     */
    set _maxlength(value) {
        this.maxlength = value;
        this.control.control.updateValueAndValidity({ emitEvent: false });
    }
    /**
     * @internal
     */
    ngOnInit() {
        this.control.control.setAsyncValidators(getAsyncValidatorFunctions(this));
    }
    /**
     * @internal
     */
    updateValue() {
        this.controlValueAccessor.onChange(this.value);
    }
    /**
     * @internal
     */
    onFocus() {
        this.active = true;
    }
    /**
     * @internal
     */
    onBlur() {
        this.active = false;
        this.controlValueAccessor.onTouch();
        this.updateValue();
    }
    /**
     * @internal
     */
    writeValue(value) {
        this.value = value;
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.control.control.clearAsyncValidators();
    }
}
CdsTextareaComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTextareaComponent, deps: [{ token: i1$2.NgControl, optional: true, self: true }, { token: CdsControlValueAccessorComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsTextareaComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsTextareaComponent, selector: "cds-textarea", inputs: { _required: ["required", "_required"], _maxlength: ["maxlength", "_maxlength"], resizable: "resizable" }, outputs: { change: "change" }, providers: [CdsControlValueAccessorComponent], usesInheritance: true, ngImport: i0, template: "<div class=\"cds-textarea\">\n  <div\n    class=\"cds-input-container\"\n    [class.-invalid]=\"!control.disabled && !control.valid && control.touched && !active\"\n    [class.-disabled]=\"control.disabled || null\"\n    [class.-active]=\"active\"\n  >\n    <textarea #atom\n      [(ngModel)]=\"value\"\n      class=\"cds-input\"\n      [class.-no-resize]=\"resizable===false\"\n      [id]=\"'cds-textarea-' + name\"\n      [placeholder]=\"placeholder || 'Example text'\"\n      [name]=\"name\"\n      [attr.disabled]=\"control.disabled || null\"\n      (input)=\"updateValue()\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur()\"\n      (change)=\"$event.stopPropagation()\"></textarea>\n  </div>\n</div>", dependencies: [{ kind: "directive", type: i1$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTextareaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-textarea', providers: [CdsControlValueAccessorComponent], template: "<div class=\"cds-textarea\">\n  <div\n    class=\"cds-input-container\"\n    [class.-invalid]=\"!control.disabled && !control.valid && control.touched && !active\"\n    [class.-disabled]=\"control.disabled || null\"\n    [class.-active]=\"active\"\n  >\n    <textarea #atom\n      [(ngModel)]=\"value\"\n      class=\"cds-input\"\n      [class.-no-resize]=\"resizable===false\"\n      [id]=\"'cds-textarea-' + name\"\n      [placeholder]=\"placeholder || 'Example text'\"\n      [name]=\"name\"\n      [attr.disabled]=\"control.disabled || null\"\n      (input)=\"updateValue()\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur()\"\n      (change)=\"$event.stopPropagation()\"></textarea>\n  </div>\n</div>" }]
        }], ctorParameters: function () { return [{ type: i1$2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: CdsControlValueAccessorComponent }]; }, propDecorators: { _required: [{
                type: Input,
                args: ['required']
            }], _maxlength: [{
                type: Input,
                args: ['maxlength']
            }], resizable: [{
                type: Input
            }], change: [{
                type: Output
            }] } });

class CdsInputModule {
}
CdsInputModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsInputModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsInputModule, declarations: [CdsInputComponent,
        CdsTextareaComponent,
        CdsInputPrefixDirective,
        CdsInputSuffixDirective,
        CdsStringHighlightComponent], imports: [FormsModule, ReactiveFormsModule, CdsPipesModule, CommonModule, CdsSuggestionsModule], exports: [CdsInputComponent,
        CdsTextareaComponent,
        CdsInputPrefixDirective,
        CdsInputSuffixDirective,
        CdsSuggestionsModule,
        CdsStringHighlightComponent] });
CdsInputModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsInputModule, imports: [FormsModule, ReactiveFormsModule, CdsPipesModule, CommonModule, CdsSuggestionsModule, CdsSuggestionsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsInputModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsInputComponent,
                        CdsTextareaComponent,
                        CdsInputPrefixDirective,
                        CdsInputSuffixDirective,
                        CdsStringHighlightComponent
                    ],
                    exports: [
                        CdsInputComponent,
                        CdsTextareaComponent,
                        CdsInputPrefixDirective,
                        CdsInputSuffixDirective,
                        CdsSuggestionsModule,
                        CdsStringHighlightComponent
                    ],
                    imports: [FormsModule, ReactiveFormsModule, CdsPipesModule, CommonModule, CdsSuggestionsModule],
                }]
        }] });

class DateInputConfigService {
    static getLocaleDateConfig(locale) {
        const dateConfig = {
            name: 'day',
            placeholder: 'dd',
            maxlength: 2,
        };
        const monthConfig = {
            name: 'month',
            placeholder: 'mm',
            maxlength: 2,
        };
        const yearConfig = {
            name: 'year',
            placeholder: 'yyyy',
            maxlength: 4,
        };
        // @TODO: match locale with date-fns and arrange date parts accordingly.
        return [monthConfig, dateConfig, yearConfig];
    }
}

class ConditionalFocusTransferPresenter {
    constructor() {
        this.cancelSubscriptions$ = new Subject();
        this.elementRegistry = [];
        this.change$ = new Subject();
        this.change = this.change$.asObservable();
    }
    watch(targets) {
        targets.forEach((target) => {
            this.elementRegistry.push(target.element);
            target.forwardCondition
                .pipe(takeUntil(this.cancelSubscriptions$))
                .subscribe(() => {
                this.navigateForward(target.element);
            });
            target.backwardCondition
                .pipe(takeUntil(this.cancelSubscriptions$))
                .subscribe(() => {
                this.navigateBackward(target.element);
            });
        });
    }
    navigateForward(element) {
        const targetIndex = this.elementRegistry.indexOf(element) + 1;
        if (targetIndex <= this.elementRegistry.length - 1) {
            this.change$.next(this.elementRegistry[targetIndex]);
        }
    }
    navigateBackward(element) {
        const targetIndex = this.elementRegistry.indexOf(element) - 1;
        if (targetIndex > -1) {
            this.change$.next(this.elementRegistry[targetIndex]);
        }
    }
    destroy() {
        this.change$.complete();
        this.cancelSubscriptions$.next();
        this.cancelSubscriptions$.complete();
    }
}

class DateInputPresenter extends ConditionalFocusTransferPresenter {
    createWatcher(targets) {
        const targetsAndConditions = targets.map((target) => {
            const forwardCondition = target.keyup.pipe(takeUntil(this.cancelSubscriptions$), filter((event) => this.forwardCondition(event, target.dateInputConfig)), tap((event) => event.preventDefault()), map(() => true));
            const backwardCondition = target.keyup.pipe(takeUntil(this.cancelSubscriptions$), filter((event) => this.backwardCondition(event)), tap((event) => event.preventDefault()), map(() => true));
            return { element: target.element, forwardCondition, backwardCondition };
        });
        super.watch(targetsAndConditions);
    }
    forwardCondition(event, config) {
        const input = event.target;
        const isForwardSlash = event.key === '/';
        return ((input.value.length > 0 && isForwardSlash) ||
            (event.key.length === 1 &&
                input.value.length === config.maxlength &&
                input.selectionStart === config.maxlength));
    }
    backwardCondition(event) {
        const input = event.target;
        const isBackspace = event.key === 'Backspace';
        return input.value.length === 0 && isBackspace;
    }
}
DateInputPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DateInputPresenter, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
DateInputPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DateInputPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DateInputPresenter, decorators: [{
            type: Injectable
        }] });

function validateCharacters(key, value) {
    const regexp = /^\d+$/;
    if (value && !regexp.test(value)) {
        return { [`CDS_DATE_${key.toUpperCase()}_NOT_NUMBER`]: value };
    }
    return null;
}
function validateDay(value) {
    if (value.length < 2) {
        return null;
    }
    const day = +value;
    if (isNaN(day) || day <= 0 || day > 31) {
        return { CDS_DATE_INVALID_DAY: true };
    }
    return null;
}
function validateDaysInMonth(dayString, month, year) {
    const day = Number(dayString);
    const date = new Date(Number(year), Number(month) - 1, 1);
    if (day < 0 || day > getDaysInMonth(date)) {
        return { CDS_DATE_EXCEEDS_DAYS_IN_MONTH: dayString };
    }
    return null;
}
function validateMonth(value) {
    if (!value) {
        return null;
    }
    if (value.length < 2) {
        return null;
    }
    const month = +value - 1;
    if (isNaN(month)) {
        return null;
    }
    const date = setMonth(setDate(new Date(), 1), month);
    if (getMonth(date) !== month) {
        // i.e. setMonth('13') results in February
        return { CDS_DATE_INVALID_MONTH: true };
    }
    return null;
}
function validateDate(day, month, year) {
    const date = setDate(setMonth(setYear(new Date(), +year), +month - 1), +day);
    if (!isValid(date)) {
        return { CDS_DATE_NOT_A_DATE: true };
    }
    return null;
}
function isInvalidDay(control) {
    return validateCharacters('day', control.value) || validateDay(control.value);
}
function isInvalidMonth(control) {
    return validateCharacters('month', control.value) || validateMonth(control.value);
}
function isInvalidYear(control) {
    return validateCharacters('year', control.value);
}
function isInvalidDate(control) {
    const { day, month, year } = control.value;
    if (!day || !month || !year) {
        return null;
    }
    if (control.get('day').errors || control.get('month').errors || control.get('year').errors) {
        return null;
    }
    return validateDate(day, month, year) || validateDaysInMonth(day, month, year);
}
function isWithinRange(minYear, maxYear) {
    return (control) => {
        if (!control.value || !control.dirty) {
            return;
        }
        const isGreaterThanMaxYear = { CDS_DATE_MAX: maxYear };
        const isLessThanMinYear = { CDS_DATE_MIN: minYear };
        if (+control.value > maxYear) {
            return isGreaterThanMaxYear;
        }
        else if (+control.value < minYear) {
            return isLessThanMinYear;
        }
        return null;
    };
}
function isMissingDatePart(control) {
    let errors = {};
    if (!control.dirty) {
        return;
    }
    if (!control.value.month && !control.value.day && !control.value.year) {
        return;
    }
    if (!control.value.month) {
        errors = { ...errors, CDS_DATE_MISSING_MONTH: true };
    }
    if (!control.value.day) {
        errors = { ...errors, CDS_DATE_MISSING_DAY: true };
    }
    if (!control.value.year) {
        errors = { ...errors, CDS_DATE_MISSING_YEAR: true };
    }
    if (Object.keys(errors).length > 0) {
        return errors;
    }
    return null;
}
// Copies errors from one control to another
function hasNoErrors(...sources) {
    return () => {
        const errors = Object.assign({}, ...sources.map(source => !source.valid ? source.errors : {}));
        return Object.keys(errors).length > 0 ? { ...errors } : null;
    };
}

/**
 * @internal
 */
class CdsDateInputComponent extends CdsAbstractTextInputComponent {
    constructor(control, element, presenter, controlValueAccessor) {
        super(controlValueAccessor);
        this.control = control;
        this.element = element;
        this.presenter = presenter;
        this.$cancelSubscriptions = new Subject();
        this.change = this.controlValueAccessor.change;
        if (this.control) {
            this.control.valueAccessor = this;
        }
        else {
            throw new Error(`FormControl is missing from Date Input.`);
        }
        this.formGroup = new UntypedFormGroup({
            day: new UntypedFormControl('', [isInvalidDay]),
            month: new UntypedFormControl('', [isInvalidMonth]),
            year: new UntypedFormControl('', [isInvalidYear])
        });
        const hasNoErrorsValidator = hasNoErrors(this.formGroup.get('day'), this.formGroup.get('month'), this.formGroup.get('year'));
        this.formGroup.setValidators([hasNoErrorsValidator, isInvalidDate]);
    }
    ngOnInit() {
        this.datePartConfigs = DateInputConfigService.getLocaleDateConfig();
        // add the hasNoErrors validator to keep the parent's validity state in sync with the child's validity state
        this.control.control.setAsyncValidators(getAsyncValidatorFunctions({}, [hasNoErrors(this.formGroup)]));
    }
    ngAfterViewInit() {
        const targets = this.datePartConfigs.map((dateInputConfig, index) => {
            const element = this.inputElements.toArray()[index].nativeElement;
            const keyup = fromEvent(element, 'keyup');
            return { element, keyup, dateInputConfig };
        });
        this.presenter.createWatcher(targets);
        this.presenter.change
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe((element) => {
            this.focus(element);
        });
        this.subscribeToUpdateEvents();
    }
    subscribeToUpdateEvents() {
        const $input = merge(...this.inputElements.map(element => fromEvent(element.nativeElement, 'input')));
        const $focusOut = fromEvent(this.element.nativeElement, 'focusout')
            .pipe(takeUntil(this.$cancelSubscriptions), tap(event => {
            this.padInputs();
            if (!this.element.nativeElement.contains(event.relatedTarget)) {
                const missingDatePart = isMissingDatePart(this.formGroup);
                const withinRange = isWithinRange(this.min, this.max)(this.formGroup.get('year'));
                copyErrors({ ...missingDatePart, ...withinRange }, this.formGroup);
            }
        }));
        merge($focusOut, $input)
            .pipe(takeUntil(this.$cancelSubscriptions), map(() => this.prepValue()), distinctUntilChanged((previous, current) => !current ? isSameDay(previous, current) : false))
            .subscribe(date => this.notifyParent(date));
    }
    // specifically prevent default behaviour for forward slash as it is a UI operator
    onKeydown(event) {
        if (event.key === '/') {
            event.preventDefault();
        }
    }
    onBlur() {
        this._active = false;
    }
    onFocus() {
        this._active = true;
    }
    resetFocus() {
        const input = this.element.nativeElement.querySelector('Input');
        this.focus(input);
    }
    focus(input) {
        input.focus();
        input.selectionStart = 0;
    }
    prepValue() {
        let change = null;
        const value = this.formGroup.value;
        // make sure that the year is fully filled out as date constructor creates a valid date for almost any year
        if (value.month && value.day && value.year && value.year.length === 4 && this.formGroup.valid) {
            change = new Date(+this.formGroup.value.year, +this.formGroup.value.month - 1, +this.formGroup.value.day);
        }
        return change;
    }
    notifyParent(value) {
        this.controlValueAccessor.onChange(value);
        this.controlValueAccessor.onTouch();
    }
    padInputs() {
        this.datePartConfigs.forEach(config => {
            const control = this.formGroup.get(config.name);
            if (config.name === 'year' || control.value === null || control.value === undefined) {
                return;
            }
            if (!/^\d+$/.test(control.value) || config.max === control.value.length) {
                return;
            }
            control.setValue(StringPadStart(control.value, config.maxlength, '0'));
        });
    }
    writeValue(value) {
        let day = '';
        let month = '';
        let year = '';
        if (isValid(value)) {
            day = StringPadStart(`${getDate(value)}`, 2, '0');
            month = StringPadStart(`${getMonth(value) + 1}`, 2, '0');
            year = `${getYear(value)}`;
        }
        this.formGroup.setValue({ day, month, year });
    }
    ngOnDestroy() {
        this.control.control.clearAsyncValidators();
        this.presenter.destroy();
        this.controlValueAccessor.destroy();
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsDateInputComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDateInputComponent, deps: [{ token: i1$2.NgControl, optional: true, self: true }, { token: i0.ElementRef }, { token: DateInputPresenter }, { token: CdsControlValueAccessorComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsDateInputComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsDateInputComponent, selector: "cds-date-input", inputs: { min: "min", max: "max", isDisabled: "isDisabled", active: "active" }, outputs: { change: "change" }, providers: [DateInputPresenter, CdsControlValueAccessorComponent], viewQueries: [{ propertyName: "inputElements", predicate: ["dateParts"], descendants: true, read: ElementRef }], usesInheritance: true, ngImport: i0, template: "<div class=\"cds-input-container\"\n  [class.-invalid]=\"!(isDisabled || control.disabled) && !formGroup.valid && formGroup.dirty\"\n  [class.-active]=\"active || _active\"\n  [class.-disabled]=\"isDisabled || control.disabled\"\n>\n  <div class=\"cds-date-input\" #input>\n    <ng-container *ngFor=\"let config of datePartConfigs;index as index\">\n      <input #atom #dateParts\n        type=\"text\"\n        [id]=\"('cds-date-input-' + name + '-' + config.name) | kebabCase\"\n        [name]=\"config.name\"\n        [class]=\"config.name\"\n        [formControl]=\"formGroup.get(config.name)\"\n        [placeholder]=\"config.placeholder\"\n        [attr.maxlength]=\"'' + config.maxlength\"\n        [autocomplete]=\"autocomplete || 'off'\"\n        [attr.disabled]=\"isDisabled || control.disabled || null\"\n        (blur)=\"onBlur()\"\n        (focus)=\"onFocus()\"\n        (keydown)=\"onKeydown($event)\"\n        (change)=\"$event.stopPropagation()\" />\n      <ng-container *ngIf=\"index &lt; datePartConfigs.length - 1\">\n        <span class=\"divider\">/</span>\n      </ng-container>\n    </ng-container>\n  </div>\n\n  <ng-container *ngIf=\"prefixes.length > 0\">\n    <span class=\"cds-input-addon -prefix\">\n      <ng-content select=\"[cdsInputPrefix]\"></ng-content>\n    </span>\n  </ng-container>\n\n  <ng-container *ngIf=\"suffixes.length > 0\">\n    <span class=\"cds-input-addon -suffix\">\n      <ng-content select=\"[cdsInputSuffix]\"></ng-content>\n    </span>\n  </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDateInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-date-input', providers: [DateInputPresenter, CdsControlValueAccessorComponent], template: "<div class=\"cds-input-container\"\n  [class.-invalid]=\"!(isDisabled || control.disabled) && !formGroup.valid && formGroup.dirty\"\n  [class.-active]=\"active || _active\"\n  [class.-disabled]=\"isDisabled || control.disabled\"\n>\n  <div class=\"cds-date-input\" #input>\n    <ng-container *ngFor=\"let config of datePartConfigs;index as index\">\n      <input #atom #dateParts\n        type=\"text\"\n        [id]=\"('cds-date-input-' + name + '-' + config.name) | kebabCase\"\n        [name]=\"config.name\"\n        [class]=\"config.name\"\n        [formControl]=\"formGroup.get(config.name)\"\n        [placeholder]=\"config.placeholder\"\n        [attr.maxlength]=\"'' + config.maxlength\"\n        [autocomplete]=\"autocomplete || 'off'\"\n        [attr.disabled]=\"isDisabled || control.disabled || null\"\n        (blur)=\"onBlur()\"\n        (focus)=\"onFocus()\"\n        (keydown)=\"onKeydown($event)\"\n        (change)=\"$event.stopPropagation()\" />\n      <ng-container *ngIf=\"index &lt; datePartConfigs.length - 1\">\n        <span class=\"divider\">/</span>\n      </ng-container>\n    </ng-container>\n  </div>\n\n  <ng-container *ngIf=\"prefixes.length > 0\">\n    <span class=\"cds-input-addon -prefix\">\n      <ng-content select=\"[cdsInputPrefix]\"></ng-content>\n    </span>\n  </ng-container>\n\n  <ng-container *ngIf=\"suffixes.length > 0\">\n    <span class=\"cds-input-addon -suffix\">\n      <ng-content select=\"[cdsInputSuffix]\"></ng-content>\n    </span>\n  </ng-container>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: i0.ElementRef }, { type: DateInputPresenter }, { type: CdsControlValueAccessorComponent }]; }, propDecorators: { min: [{
                type: Input
            }], max: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], active: [{
                type: Input
            }], change: [{
                type: Output
            }], inputElements: [{
                type: ViewChildren,
                args: ['dateParts', { read: ElementRef }]
            }] } });

/**
 * @internal
 */
class CdsSingleDatepickerComponent {
    constructor(renderer, changeDetector) {
        this.renderer = renderer;
        this.changeDetector = changeDetector;
        this.inputFormControl = new UntypedFormControl(null);
        this.calendarFormControl = new UntypedFormControl(null);
        this.change$ = new BehaviorSubject({ value: null, errors: {} });
        this.dropdownOptions = {
            scrollableY: false,
            flexPlacement: true
        };
        this.calendarClose = new EventEmitter();
        this.focusDate = () => {
            this.input.resetFocus();
        };
    }
    ngOnInit() {
        if (this.date.value && isValid(this.date.value)) {
            this.inputFormControl.setValue(this.date.value);
            this.calendarFormControl.setValue(this.date.value);
        }
        this.date.setAsyncValidators(getAsyncValidatorFunctions({}, [hasNoErrors(this.inputFormControl)]));
        this.createSubscription();
    }
    createSubscription() {
        this.subscription = this.date.valueChanges
            .subscribe(value => {
            this.inputFormControl.reset(value);
            if (isValid(value)) {
                this.calendarFormControl.setValue(this.date.value);
            }
            else {
                this.calendarFormControl.reset(null);
            }
            // re-run validation of the parent, just in case the date input picks up some errors that aren't accounted for
            // also serves to clear any errors that may be lingering after a control.reset()
            this.date.updateValueAndValidity({ emitEvent: false });
        });
    }
    onChange() {
        const value = this.inputFormControl.value;
        // Only update the calendar if the date input has been filled and vetted
        if (value && this.inputFormControl.valid && isValid(value)) {
            this.calendarFormControl.setValue(startOfDay(value));
        }
        this.subscription.unsubscribe();
        this.date.markAsTouched();
        this.date.setValue(value);
        this.createSubscription();
    }
    // Date Input keyboard interactions
    inputClicked(event) {
        if (event.target.tagName === 'INPUT') {
            this.openCalendar();
        }
    }
    iconClicked() {
        this.openCalendar();
        setTimeout(() => {
            this.calendar.focusCalendarBody();
        }, 350);
    }
    onDateInputKeydown(event) {
        if (this.dropdown && this.dropdown.expanded) {
            this.openKeyboardEvent(event);
        }
        else {
            this.closedKeyboardEvent(event);
        }
    }
    // Calendar keyboard interactions
    onCalendarEnterKeydown(event) {
        event.preventDefault();
        this.onCalendarCloseClicked();
    }
    onCalendarCloseClicked() {
        this.calendarClose.emit();
        this.closeCalendar();
    }
    // Overall keyboard interactions
    openCalendar() {
        this.dropdown.expand();
    }
    closeCalendar() {
        this.renderer.removeClass(this.container.nativeElement, '-open');
        this.changeDetector.detectChanges();
        setTimeout(() => {
            this.dropdown.collapse();
        }, 400);
    }
    onCalendarValueChanged(value) {
        this.inputFormControl.markAsTouched();
        this.inputFormControl.setValue(value);
        this.onChange();
    }
    onExpandedStateChanged(expanded) {
        this.expanded = expanded;
    }
    // keyboard event in date input
    openKeyboardEvent(event) {
        switch (event.key) {
            case ('Tab'):
                if (!event.shiftKey) {
                    event.preventDefault();
                    document.activeElement.blur();
                    // @todo
                    this.calendar.focusCalendarBody();
                }
                break;
            case (' '):
            case ('Spacebar'):
                event.preventDefault();
                this.closeCalendar();
                break;
            case ('Escape'):
                event.stopPropagation();
                this.closeCalendar();
                break;
            default:
                break;
        }
    }
    closedKeyboardEvent(event) {
        switch (event.key) {
            case ('ArrowDown'):
            case ('Down'):
            case (' '):
            case ('Spacebar'):
                event.preventDefault();
                // remove focus from input and trigger validation first etc
                document.activeElement.blur();
                // then open calendar
                this.openCalendar();
                setTimeout(() => {
                    this.calendar.focusCalendarBody();
                }, 350);
                break;
            default:
                break;
        }
    }
    ngOnDestroy() {
        this.date.clearAsyncValidators();
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}
CdsSingleDatepickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSingleDatepickerComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsSingleDatepickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSingleDatepickerComponent, selector: "cds-single-datepicker", inputs: { date: "date", name: "name", minYear: "minYear", maxYear: "maxYear" }, outputs: { calendarClose: "calendarClose" }, viewQueries: [{ propertyName: "dropdown", first: true, predicate: CdsDropdownDirective, descendants: true, static: true }, { propertyName: "calendar", first: true, predicate: CdsCalendarComponent, descendants: true, static: true }, { propertyName: "input", first: true, predicate: CdsDateInputComponent, descendants: true, static: true }, { propertyName: "container", first: true, predicate: ["container"], descendants: true, static: true }], ngImport: i0, template: "<div\n  class=\"cds-dropdown\"\n  [class.-open]=\"expanded\"\n  #container\n>\n  <div\n    #toggle\n  >\n    <cds-date-input\n      [name]=\"name\"\n      aria-label=\"Open menu\"\n      [attr.aria-expanded]=\"expanded\"\n      [formControl]=\"inputFormControl\"\n      [min]=\"minYear\"\n      [max]=\"maxYear\"\n      autocomplete=\"off\"\n      [isDisabled]=\"date.disabled\"\n      [active]=\"expanded\"\n      (click)=\"inputClicked($event)\"\n      (keydown)=\"onDateInputKeydown($event)\"\n      (change)=\"onChange()\"\n    >\n      <cds-control-block\n        (click)=\"iconClicked()\"\n        [disabled]=\"date.disabled\"\n        cdsInputSuffix\n      >\n        <i class=\"material-icons\">insert_invitation</i>\n      </cds-control-block>\n    </cds-date-input>\n  </div>\n\n  <div\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n    class=\"cds-calendar-container\"\n    [attr.aria-hidden]=\"!expanded\"\n    cdsDropdownPanel\n    #calendarPanel\n  >\n    <div class=\"cds-calendar-panel\">\n      <cds-calendar\n        [formControl]=\"calendarFormControl\"\n        [minYear]=\"minYear\"\n        [maxYear]=\"maxYear\"\n        [name]=\"name\"\n        role=\"menu\"\n        (change)=\"onCalendarValueChanged($event)\"\n        (close)=\"onCalendarCloseClicked()\"\n      ></cds-calendar>\n    </div>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  [anchor]=\"toggle\"\n  [panel]=\"calendarPanel\"\n  [wasFocusedFn]=\"focusDate\"\n  [options]=\"dropdownOptions\"\n  (removeFocus)=\"closeCalendar()\"\n  (change)=\"onExpandedStateChanged($event)\">\n</ng-container>\n", dependencies: [{ kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: CdsInputSuffixDirective, selector: "[cdsInputSuffix]" }, { kind: "component", type: CdsCalendarComponent, selector: "cds-calendar", inputs: ["name", "locale", "minYear", "maxYear"], outputs: ["clear", "close", "change"] }, { kind: "component", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: ["name", "size", "disabled", "tabindex", "active", "attributes"] }, { kind: "directive", type: CdsDropdownDirective, selector: "[cdsDropdown]", inputs: ["anchor", "panel", "options", "strategy", "wasFocusedFn"], outputs: ["removeFocus", "change"] }, { kind: "directive", type: CdsDropdownPanelDirective, selector: "[cdsDropdownPanel]", exportAs: ["dropdownPanel"] }, { kind: "component", type: CdsDateInputComponent, selector: "cds-date-input", inputs: ["min", "max", "isDisabled", "active"], outputs: ["change"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSingleDatepickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-single-datepicker', template: "<div\n  class=\"cds-dropdown\"\n  [class.-open]=\"expanded\"\n  #container\n>\n  <div\n    #toggle\n  >\n    <cds-date-input\n      [name]=\"name\"\n      aria-label=\"Open menu\"\n      [attr.aria-expanded]=\"expanded\"\n      [formControl]=\"inputFormControl\"\n      [min]=\"minYear\"\n      [max]=\"maxYear\"\n      autocomplete=\"off\"\n      [isDisabled]=\"date.disabled\"\n      [active]=\"expanded\"\n      (click)=\"inputClicked($event)\"\n      (keydown)=\"onDateInputKeydown($event)\"\n      (change)=\"onChange()\"\n    >\n      <cds-control-block\n        (click)=\"iconClicked()\"\n        [disabled]=\"date.disabled\"\n        cdsInputSuffix\n      >\n        <i class=\"material-icons\">insert_invitation</i>\n      </cds-control-block>\n    </cds-date-input>\n  </div>\n\n  <div\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n    class=\"cds-calendar-container\"\n    [attr.aria-hidden]=\"!expanded\"\n    cdsDropdownPanel\n    #calendarPanel\n  >\n    <div class=\"cds-calendar-panel\">\n      <cds-calendar\n        [formControl]=\"calendarFormControl\"\n        [minYear]=\"minYear\"\n        [maxYear]=\"maxYear\"\n        [name]=\"name\"\n        role=\"menu\"\n        (change)=\"onCalendarValueChanged($event)\"\n        (close)=\"onCalendarCloseClicked()\"\n      ></cds-calendar>\n    </div>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  [anchor]=\"toggle\"\n  [panel]=\"calendarPanel\"\n  [wasFocusedFn]=\"focusDate\"\n  [options]=\"dropdownOptions\"\n  (removeFocus)=\"closeCalendar()\"\n  (change)=\"onExpandedStateChanged($event)\">\n</ng-container>\n" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { date: [{
                type: Input
            }], name: [{
                type: Input
            }], minYear: [{
                type: Input
            }], maxYear: [{
                type: Input
            }], dropdown: [{
                type: ViewChild,
                args: [CdsDropdownDirective, { static: true }]
            }], calendar: [{
                type: ViewChild,
                args: [CdsCalendarComponent, { static: true }]
            }], input: [{
                type: ViewChild,
                args: [CdsDateInputComponent, { static: true }]
            }], container: [{
                type: ViewChild,
                args: ['container', { static: true }]
            }], calendarClose: [{
                type: Output
            }] } });

/**
 * ### Description
 * The date picker is an input field that enables users to enter a single date or date range.
 */
class CdsDatepickerComponent {
    constructor() {
        /**
         * Restricts allowed values to the year specified. Required.
         */
        this.minYear = 1950;
        /**
         * Restricts allowed values to the year specified. Required.
         */
        this.maxYear = 2050;
    }
    // @todo: hack
    /**
     * Manually set the validity of the datepicker.
     */
    set _invalid(value) {
        console.warn('Use of the `invalid` Datepicker input is deprecated and will be removed in version @cds/angular version 1.0');
        this.invalid = value;
    }
    /**
     * @internal
     */
    ngOnChanges(changes) {
        const typeChange = changes['ranged'];
        if (!this.date) {
            throw new Error('Control for Start Date is missing from Datepicker Component.');
        }
        if (typeChange && typeChange.currentValue === 'ranged' && !this.endDate) {
            throw new Error('Control for End Date is missing from Datepicker Component.');
        }
        // for all subsequent transitions after the initial setup, end date should be cleared
        // startdate.setvalue is necessary for descendants to get wind of change
        if (typeChange && !typeChange.firstChange) {
            this.date.setValue(this.date.value);
            this.endDate.reset(null);
        }
    }
    // @TODO: extract to presenter
    /**
     * @internal
     */
    onCalendarClosed(self, other) {
        if (!self.inputFormControl.value) {
            self.focusDate();
            return;
        }
        if (other) {
            if (self.inputFormControl.value && !other.inputFormControl.value) {
                other.focusDate();
            }
        }
    }
}
CdsDatepickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatepickerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsDatepickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsDatepickerComponent, selector: "cds-datepicker", inputs: { name: "name", date: "date", endDate: "endDate", ranged: "ranged", calendar: "calendar", locale: "locale", minYear: "minYear", maxYear: "maxYear", _invalid: ["invalid", "_invalid"] }, providers: [{ provide: ControlContainer, useExisting: FormGroupDirective }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"cds-date-picker -basic\"\n  *ngIf=\"!ranged && !calendar\"\n  [class.-invalid]=\"invalid || (date?.errors && date?.touched)\"\n>\n  <cds-date-input\n    [name]=\"name + '-input'\"\n    autocomplete=\"off\"\n    [formControl]=\"date\"\n    [min]=\"minYear\"\n    [max]=\"maxYear\"\n  >\n  </cds-date-input>\n</div>\n\n<div\n  class=\"cds-date-picker\"\n  *ngIf=\"calendar && !ranged\"\n  [class.-invalid]=\"invalid || (date?.errors && date?.touched)\"\n>\n  <div class=\"start-date\">\n    <cds-single-datepicker\n      [name]=\"name\"\n      [date]=\"date\"\n      [minYear]=\"minYear\"\n      [maxYear]=\"maxYear\"\n    >\n    </cds-single-datepicker>\n  </div>\n</div>\n\n<div *ngIf=\"calendar && ranged\"\n  class=\"cds-date-picker\"\n  [class.-invalid]=\"invalid || ((date?.errors || endDate?.errors) && (date?.touched || endDate?.touched))\"\n>\n  <div class=\"start-date\">\n    <cds-single-datepicker\n      #startDatepicker\n      [name]=\"name + '-date-range-start-date'\"\n      [date]=\"date\"\n      [minYear]=\"minYear\"\n      [maxYear]=\"maxYear\"\n      (calendarClose)=\"onCalendarClosed(startDatepicker, endDatepicker)\"\n    >\n    </cds-single-datepicker>\n  </div>\n\n  <span class=\"separator\">to</span>\n  <div class=\"end-date\">\n    <cds-single-datepicker\n      #endDatepicker\n      [name]=\"name + '-date-range-end-date'\"\n      [date]=\"endDate\"\n      [minYear]=\"minYear\"\n      [maxYear]=\"maxYear\"\n      (calendarClose)=\"onCalendarClosed(endDatepicker, startDatepicker)\"\n    >\n    </cds-single-datepicker>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsSingleDatepickerComponent, selector: "cds-single-datepicker", inputs: ["date", "name", "minYear", "maxYear"], outputs: ["calendarClose"] }, { kind: "component", type: CdsDateInputComponent, selector: "cds-date-input", inputs: ["min", "max", "isDisabled", "active"], outputs: ["change"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatepickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-datepicker', providers: [{ provide: ControlContainer, useExisting: FormGroupDirective }], template: "<div\n  class=\"cds-date-picker -basic\"\n  *ngIf=\"!ranged && !calendar\"\n  [class.-invalid]=\"invalid || (date?.errors && date?.touched)\"\n>\n  <cds-date-input\n    [name]=\"name + '-input'\"\n    autocomplete=\"off\"\n    [formControl]=\"date\"\n    [min]=\"minYear\"\n    [max]=\"maxYear\"\n  >\n  </cds-date-input>\n</div>\n\n<div\n  class=\"cds-date-picker\"\n  *ngIf=\"calendar && !ranged\"\n  [class.-invalid]=\"invalid || (date?.errors && date?.touched)\"\n>\n  <div class=\"start-date\">\n    <cds-single-datepicker\n      [name]=\"name\"\n      [date]=\"date\"\n      [minYear]=\"minYear\"\n      [maxYear]=\"maxYear\"\n    >\n    </cds-single-datepicker>\n  </div>\n</div>\n\n<div *ngIf=\"calendar && ranged\"\n  class=\"cds-date-picker\"\n  [class.-invalid]=\"invalid || ((date?.errors || endDate?.errors) && (date?.touched || endDate?.touched))\"\n>\n  <div class=\"start-date\">\n    <cds-single-datepicker\n      #startDatepicker\n      [name]=\"name + '-date-range-start-date'\"\n      [date]=\"date\"\n      [minYear]=\"minYear\"\n      [maxYear]=\"maxYear\"\n      (calendarClose)=\"onCalendarClosed(startDatepicker, endDatepicker)\"\n    >\n    </cds-single-datepicker>\n  </div>\n\n  <span class=\"separator\">to</span>\n  <div class=\"end-date\">\n    <cds-single-datepicker\n      #endDatepicker\n      [name]=\"name + '-date-range-end-date'\"\n      [date]=\"endDate\"\n      [minYear]=\"minYear\"\n      [maxYear]=\"maxYear\"\n      (calendarClose)=\"onCalendarClosed(endDatepicker, startDatepicker)\"\n    >\n    </cds-single-datepicker>\n  </div>\n</div>\n" }]
        }], propDecorators: { name: [{
                type: Input
            }], date: [{
                type: Input
            }], endDate: [{
                type: Input
            }], ranged: [{
                type: Input
            }], calendar: [{
                type: Input
            }], locale: [{
                type: Input
            }], minYear: [{
                type: Input
            }], maxYear: [{
                type: Input
            }], _invalid: [{
                type: Input,
                args: ['invalid']
            }] } });

class CdsDatepickerModule {
}
CdsDatepickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatepickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsDatepickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsDatepickerModule, declarations: [CdsDatepickerComponent,
        CdsSingleDatepickerComponent,
        CdsDateInputComponent], imports: [FormsModule,
        ReactiveFormsModule,
        CommonModule,
        CdsInputModule,
        CdsCalendarModule,
        CdsInputModule,
        CdsSelectModule,
        CdsControlBlockModule,
        CdsButtonModule,
        CdsDirectivesModule,
        CdsPipesModule], exports: [CdsDatepickerComponent, CdsDateInputComponent] });
CdsDatepickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatepickerModule, imports: [FormsModule,
        ReactiveFormsModule,
        CommonModule,
        CdsInputModule,
        CdsCalendarModule,
        CdsInputModule,
        CdsSelectModule,
        CdsControlBlockModule,
        CdsButtonModule,
        CdsDirectivesModule,
        CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDatepickerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsDatepickerComponent,
                        CdsSingleDatepickerComponent,
                        CdsDateInputComponent,
                    ],
                    exports: [CdsDatepickerComponent, CdsDateInputComponent],
                    imports: [
                        FormsModule,
                        ReactiveFormsModule,
                        CommonModule,
                        CdsInputModule,
                        CdsCalendarModule,
                        CdsInputModule,
                        CdsSelectModule,
                        CdsControlBlockModule,
                        CdsButtonModule,
                        CdsDirectivesModule,
                        CdsPipesModule,
                    ],
                }]
        }] });

/**
 * Keydown Events:
 * If you require that the modal is closed from within a Keydown event handler, it is
 * recommended that you prevent the default behaviour of the Keydown event, or switch to
 * listening for Keyup events.
 *
 * The `close()` method of the modalRef automatically transfers focus back to the element that
 * triggered the modal. Because the focus is trasferred DURING the keydown event, the browser will interpret
 * the keydown event as having happened on the newly-focused element.
 * When the newly focused element is a button, the browser's default behaviour is to generate
 * a synthetic click event, which is will occur on the element that triggered the modal in the first place,
 * and the modal will be reopened.
 *
 *
 * Don't do this:
 *
 * ```js
 * export class MyModalComponent {
 *
 *   @HostListener('document:keydown', ['$event']]) onKeydown(event: KeyboardEvent) {
 *     this.modalRef.close();
 *   }
 *
 *   constructor(private modalRef: CdsModalRef) { }
 * }
 * ```
 *
 *
 * Do this instead:
 *
 * ```js
 * export class MyModalComponent {
 *   @HostListener('document:keydown', ['$event']]) onKeydown(event: KeyboardEvent) {
 *     event.preventDefault();
 *     this.modalRef.close();
 *   }
 *
 *   constructor(private modalRef: CdsModalRef) { }
 * }
 * ```
 *
 *
 * Or this:
 *
 * ```js
 * export class MyModalComponent {
 *   @HostListener('document:keyup', ['$event']]) onKeyup(event: KeyboardEvent) {
 *     this.modalRef.close();
 *   }
 *
 *   constructor(private modalRef: CdsModalRef) { }
 * }
 * ```
 */
class CdsModalRef {
    constructor() {
        this.onClose$ = new Subject();
        this.onClose = this.onClose$.asObservable();
    }
    close(data) {
        this.onClose$.next(data);
        this.onClose$.complete();
    }
}

/**
 * Creates, destroys and keeps track of open modals.
 */
class CdsModalRegistry {
    constructor() {
        /**
         * An array of references to all currently open modals in the application.
         */
        this.openModals = [];
    }
    getModals() {
        return this.openModals.slice();
    }
    addModal(modalRef) {
        this.openModals.push(modalRef);
    }
    removeModal(modalRef) {
        this.openModals.splice(this.openModals.indexOf(modalRef), 1);
    }
    clear() {
        this.openModals = [];
    }
}
CdsModalRegistry.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsModalRegistry, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsModalRegistry.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsModalRegistry, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsModalRegistry, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * Creates, destroys and keeps track of open modals.
 */
class CdsModalService {
    constructor(modalRegistry, injector, appRef) {
        this.modalRegistry = modalRegistry;
        this.injector = injector;
        this.appRef = appRef;
    }
    /**
     * Returns an array of open modals.
     */
    getOpenModals() {
        return this.modalRegistry.getModals();
    }
    /**
     * Creates a `componentType` instance with inputs defined in the [`config`](?path=/story/types-modal--page#cdsmodalconfig),
     * and registers callbacks (also defined in the `config`) to be executed when the component's events are triggered.
     */
    openModal(componentType, config = {}, onCloseFn) {
        const wasFocused = document.activeElement;
        const modalRef = new CdsModalRef();
        modalRef.data = config.inputs;
        const injectorOptions = {
            providers: [
                {
                    provide: CdsModalRef,
                    useValue: modalRef,
                },
            ],
            parent: this.injector,
        };
        const injector = Injector.create(injectorOptions);
        const componentRef = createComponent(componentType, this.appRef.injector, injector);
        appendComponentToBody(componentRef, this.appRef);
        modalRef.componentInstance = componentRef.instance;
        this.createSubscriptions(modalRef, config.outputs);
        modalRef.onClose.subscribe(() => this.onClose(modalRef, componentRef, wasFocused, onCloseFn));
        this.modalRegistry.addModal(modalRef);
        // if this is the first modal opened, listen for the escape event
        if (this.modalRegistry.getModals().length === 1) {
            fromEvent(window, 'keydown')
                .pipe(
            // since the first modal opened will be the last modal closed, we can subscribe
            // to its close method and stop listening for keyboard events
            takeUntil(modalRef.onClose), filter((event) => event.key === 'Escape'))
                .subscribe(() => this.closeForemostModal());
        }
        return modalRef;
    }
    /**
     * @internal
     */
    onClose(modalRef, componentRef, wasFocused, onCloseFn) {
        removeComponentFromBody(componentRef, this.appRef);
        this.modalRegistry.removeModal(modalRef);
        if (wasFocused) {
            wasFocused.focus();
        }
        if (onCloseFn) {
            onCloseFn();
        }
    }
    /**
     * @internal
     */
    createSubscriptions(modalRef, outputConfig) {
        Object.keys(modalRef.componentInstance).forEach((key) => {
            if (modalRef.componentInstance[key] instanceof EventEmitter) {
                const eventEmitter = modalRef.componentInstance[key];
                const output = outputConfig[key];
                if (output) {
                    eventEmitter.pipe(takeUntil(modalRef.onClose)).subscribe((event) => {
                        output(event);
                    });
                }
            }
        });
    }
    /**
     * @internal
     */
    closeForemostModal() {
        const openModals = this.modalRegistry.getModals();
        const foremostModal = openModals[openModals.length - 1];
        foremostModal.close();
    }
    /**
     * Closes all open modals.
     */
    closeAll() {
        this.modalRegistry.getModals().forEach((modalRef) => modalRef.close());
        this.modalRegistry.clear();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.closeAll();
    }
}
CdsModalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsModalService, deps: [{ token: CdsModalRegistry }, { token: i0.Injector }, { token: i0.ApplicationRef }], target: i0.ɵɵFactoryTarget.Injectable });
CdsModalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsModalService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsModalService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CdsModalRegistry }, { type: i0.Injector }, { type: i0.ApplicationRef }]; } });

/* eslint-disable no-shadow */
/**
 * The modal type. Each modal type is displayed differently on the screen.
 */
var ModalType;
(function (ModalType) {
    ModalType["Dialog"] = "dialog";
    ModalType["Fullscreen"] = "fullscreen";
    ModalType["Mobile"] = "mobile";
})(ModalType || (ModalType = {}));
/**
 * How the modal contents should scroll.
 */
var ModalScrollType;
(function (ModalScrollType) {
    ModalScrollType["ScrollWindow"] = "scrollWindow";
    ModalScrollType["ScrollSelf"] = "scrollSelf";
})(ModalScrollType || (ModalScrollType = {}));

/**
 * ### Description
 * A modal is a dialog that appears on top of the on-page content.
 * It contains information or tasks that demand the user's exclusive attention.
 */
class CdsModalComponent {
    constructor(modalRef, renderer, element, focusTrap, changeDetector) {
        this.modalRef = modalRef;
        this.renderer = renderer;
        this.element = element;
        this.focusTrap = focusTrap;
        this.changeDetector = changeDetector;
    }
    /**
     * @internal
     */
    get mobileMode() {
        return window.innerWidth < 640;
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        setTimeout(() => {
            this.renderer.addClass(this.container.nativeElement, '-is-visible');
        });
        if (this.initialFocus) {
            this.focusTrap.setFocus(this.initialFocus.nativeElement, this.element.nativeElement);
        }
        else if (this.autofocus) {
            this.focusTrap.shiftFocusToStart(this.element.nativeElement);
        }
        else {
            this.element.nativeElement.setAttribute('tabIndex', '-1');
            this.element.nativeElement.focus();
        }
        this.focusTrap.watch(this.element.nativeElement);
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    close() {
        this.modalRef.close();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.focusTrap.destroy();
    }
}
CdsModalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsModalComponent, deps: [{ token: CdsModalRef }, { token: i0.Renderer2 }, { token: i0.ElementRef }, { token: FocusTrapPresenter }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsModalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsModalComponent, selector: "cds-modal", inputs: { name: "name", height: "height", minHeight: "minHeight", width: "width", minWidth: "minWidth", modalTitle: "modalTitle", modalDescription: "modalDescription", type: "type", scrollType: "scrollType", autofocus: "autofocus", mobileSlideUp: "mobileSlideUp" }, providers: [FocusTrapPresenter], queries: [{ propertyName: "initialFocus", first: true, predicate: ["initialFocus"], descendants: true, read: ElementRef }], viewQueries: [{ propertyName: "container", first: true, predicate: ["container"], descendants: true, static: true }, { propertyName: "modal", first: true, predicate: ["modal"], descendants: true, static: true }, { propertyName: "footer", first: true, predicate: ["footer"], descendants: true, static: true }], ngImport: i0, template: "<div\n  #container\n  class=\"cds-modal-container\"\n  [class.-scroll-window]=\"scrollType === 'scrollWindow' || !!height\"\n>\n  <div\n    #modal\n    class=\"cds-modal\"\n    [style.width]=\"width && !mobileMode ? width : null\"\n    [style.maxWidth]=\"width && !mobileMode ? width : null\"\n    [style.minWidth]=\"minWidth && !mobileMode ? minWidth : null\"\n    [style.height]=\"height || null\"\n    [style.maxHeight]=\"height || null\"\n    [style.minHeight]=\"minHeight || null\"\n    [ngClass]=\"{\n      '-dialog': type === 'dialog',\n      '-full-screen': type === 'fullscreen',\n      '-slide-up': mobileSlideUp\n    }\"\n    role=\"dialog\"\n    [attr.aria-labelledby]=\"\n      modalTitle ? 'cds-modal-title-' + (name | kebabCase) : null\n    \"\n    [attr.aria-label]=\"!modalTitle && name ? name : null\"\n    [attr.aria-describedby]=\"\n      modalDescription ? 'cds-modal-description-' + (name | kebabCase) : null\n    \"\n    aria-modal=\"true\"\n  >\n    <div class=\"cds-modal-header\">\n      <h6\n        class=\"cds-modal-header-title\"\n        *ngIf=\"modalTitle\"\n        [id]=\"'cds-modal-title-' + (name | kebabCase)\"\n      >\n        {{ modalTitle }}\n      </h6>\n      <span\n        *ngIf=\"modalDescription\"\n        class=\"cds-assistive-text\"\n        [id]=\"'cds-modal-description-' + (name | kebabCase)\"\n      >\n        {{ modalDescription }}\n      </span>\n    </div>\n    <div class=\"cds-modal-content\">\n      <ng-content select=\"[cdsModalContent]\"></ng-content>\n    </div>\n    <div class=\"cds-modal-footer\" #footer>\n      <ng-content select=\"[cdsModalFooter]\"></ng-content>\n    </div>\n    <cds-control-block size=\"small\" class=\"-modal-close\" (click)=\"close()\">\n      <i class=\"material-icons\">close</i>\n    </cds-control-block>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: ["name", "size", "disabled", "tabindex", "active", "attributes"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-modal', providers: [FocusTrapPresenter], template: "<div\n  #container\n  class=\"cds-modal-container\"\n  [class.-scroll-window]=\"scrollType === 'scrollWindow' || !!height\"\n>\n  <div\n    #modal\n    class=\"cds-modal\"\n    [style.width]=\"width && !mobileMode ? width : null\"\n    [style.maxWidth]=\"width && !mobileMode ? width : null\"\n    [style.minWidth]=\"minWidth && !mobileMode ? minWidth : null\"\n    [style.height]=\"height || null\"\n    [style.maxHeight]=\"height || null\"\n    [style.minHeight]=\"minHeight || null\"\n    [ngClass]=\"{\n      '-dialog': type === 'dialog',\n      '-full-screen': type === 'fullscreen',\n      '-slide-up': mobileSlideUp\n    }\"\n    role=\"dialog\"\n    [attr.aria-labelledby]=\"\n      modalTitle ? 'cds-modal-title-' + (name | kebabCase) : null\n    \"\n    [attr.aria-label]=\"!modalTitle && name ? name : null\"\n    [attr.aria-describedby]=\"\n      modalDescription ? 'cds-modal-description-' + (name | kebabCase) : null\n    \"\n    aria-modal=\"true\"\n  >\n    <div class=\"cds-modal-header\">\n      <h6\n        class=\"cds-modal-header-title\"\n        *ngIf=\"modalTitle\"\n        [id]=\"'cds-modal-title-' + (name | kebabCase)\"\n      >\n        {{ modalTitle }}\n      </h6>\n      <span\n        *ngIf=\"modalDescription\"\n        class=\"cds-assistive-text\"\n        [id]=\"'cds-modal-description-' + (name | kebabCase)\"\n      >\n        {{ modalDescription }}\n      </span>\n    </div>\n    <div class=\"cds-modal-content\">\n      <ng-content select=\"[cdsModalContent]\"></ng-content>\n    </div>\n    <div class=\"cds-modal-footer\" #footer>\n      <ng-content select=\"[cdsModalFooter]\"></ng-content>\n    </div>\n    <cds-control-block size=\"small\" class=\"-modal-close\" (click)=\"close()\">\n      <i class=\"material-icons\">close</i>\n    </cds-control-block>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: CdsModalRef }, { type: i0.Renderer2 }, { type: i0.ElementRef }, { type: FocusTrapPresenter }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { name: [{
                type: Input
            }], height: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], width: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], modalTitle: [{
                type: Input
            }], modalDescription: [{
                type: Input
            }], type: [{
                type: Input
            }], scrollType: [{
                type: Input
            }], autofocus: [{
                type: Input
            }], mobileSlideUp: [{
                type: Input
            }], container: [{
                type: ViewChild,
                args: ['container', { static: true }]
            }], modal: [{
                type: ViewChild,
                args: ['modal', { static: true }]
            }], footer: [{
                type: ViewChild,
                args: ['footer', { static: true }]
            }], initialFocus: [{
                type: ContentChild,
                args: ['initialFocus', { read: ElementRef }]
            }] } });

class CdsModalModule {
}
CdsModalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsModalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsModalModule, declarations: [CdsModalComponent], imports: [CommonModule, CdsPipesModule, CdsControlBlockModule], exports: [CdsModalComponent] });
CdsModalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsModalModule, providers: [CdsModalService], imports: [CommonModule, CdsPipesModule, CdsControlBlockModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsModalModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsModalComponent],
                    exports: [CdsModalComponent],
                    providers: [CdsModalService],
                    imports: [CommonModule, CdsPipesModule, CdsControlBlockModule],
                }]
        }] });

/**
 * ### Description
 * Used to present tasks that demand that the user take a primary, secondary or tertiary action within a particular context.
 */
class CdsDialogComponent {
    constructor() {
        /**
         * Emits an event when the Dialog's primary button is clicked.
         */
        this.primaryClick = new EventEmitter();
        /**
         * Emits an event when the Dialog's secondary button is clicked.
         */
        this.secondaryClick = new EventEmitter();
        /**
         * Emits an event when the Dialog's tertiary button is clicked.
         */
        this.tertiaryClick = new EventEmitter();
    }
}
CdsDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsDialogComponent, selector: "cds-dialog", inputs: { name: "name", dialogTitle: "dialogTitle", primary: "primary", secondary: "secondary", tertiary: "tertiary" }, outputs: { primaryClick: "primaryClick", secondaryClick: "secondaryClick", tertiaryClick: "tertiaryClick" }, ngImport: i0, template: "<div class=\"cds-dialog\">\n  <cds-modal\n    type=\"dialog\"\n    scrollType=\"scrollWindow\"\n    [modalTitle]=\"dialogTitle\"\n    [name]=\"name + '-dialog-modal'\"\n  >\n    <div cdsModalContent>\n      <ng-content></ng-content>\n    </div>\n    <div cdsModalFooter>\n      <cds-button-group>\n        <cds-button\n          *ngIf=\"secondary\"\n          [text]=\"secondary\"\n          type=\"secondary\"\n          (click)=\"secondaryClick.emit()\"\n          >{{ secondary }}</cds-button\n        >\n        <cds-button\n          *ngIf=\"primary\"\n          [text]=\"primary\"\n          (click)=\"primaryClick.emit()\"\n          #initialFocus\n          >{{ primary }}</cds-button\n        >\n        <cds-button\n          *ngIf=\"tertiary\"\n          [text]=\"tertiary\"\n          type=\"tertiary\"\n          (click)=\"tertiaryClick.emit()\"\n          >{{ tertiary }}</cds-button\n        >\n      </cds-button-group>\n    </div>\n  </cds-modal>\n</div>\n", dependencies: [{ kind: "component", type: CdsModalComponent, selector: "cds-modal", inputs: ["name", "height", "minHeight", "width", "minWidth", "modalTitle", "modalDescription", "type", "scrollType", "autofocus", "mobileSlideUp"] }, { kind: "component", type: CdsButtonGroupComponent, selector: "cds-button-group", inputs: ["stacked"] }, { kind: "component", type: CdsButtonComponent, selector: "cds-button", inputs: ["name", "text", "type", "size", "disabled", "underline", "iconName", "alignIcon", "designation", "ariaLabel", "link"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-dialog', template: "<div class=\"cds-dialog\">\n  <cds-modal\n    type=\"dialog\"\n    scrollType=\"scrollWindow\"\n    [modalTitle]=\"dialogTitle\"\n    [name]=\"name + '-dialog-modal'\"\n  >\n    <div cdsModalContent>\n      <ng-content></ng-content>\n    </div>\n    <div cdsModalFooter>\n      <cds-button-group>\n        <cds-button\n          *ngIf=\"secondary\"\n          [text]=\"secondary\"\n          type=\"secondary\"\n          (click)=\"secondaryClick.emit()\"\n          >{{ secondary }}</cds-button\n        >\n        <cds-button\n          *ngIf=\"primary\"\n          [text]=\"primary\"\n          (click)=\"primaryClick.emit()\"\n          #initialFocus\n          >{{ primary }}</cds-button\n        >\n        <cds-button\n          *ngIf=\"tertiary\"\n          [text]=\"tertiary\"\n          type=\"tertiary\"\n          (click)=\"tertiaryClick.emit()\"\n          >{{ tertiary }}</cds-button\n        >\n      </cds-button-group>\n    </div>\n  </cds-modal>\n</div>\n" }]
        }], propDecorators: { name: [{
                type: Input
            }], dialogTitle: [{
                type: Input
            }], primary: [{
                type: Input
            }], secondary: [{
                type: Input
            }], tertiary: [{
                type: Input
            }], primaryClick: [{
                type: Output
            }], secondaryClick: [{
                type: Output
            }], tertiaryClick: [{
                type: Output
            }] } });

class CdsDialogModule {
}
CdsDialogModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsDialogModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsDialogModule, declarations: [CdsDialogComponent], imports: [CdsModalModule,
        CdsPipesModule,
        CdsButtonGroupModule,
        CdsButtonModule,
        CommonModule], exports: [CdsDialogComponent, CdsModalModule] });
CdsDialogModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDialogModule, imports: [CdsModalModule,
        CdsPipesModule,
        CdsButtonGroupModule,
        CdsButtonModule,
        CommonModule, CdsModalModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDialogModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsDialogComponent],
                    exports: [CdsDialogComponent, CdsModalModule],
                    imports: [
                        CdsModalModule,
                        CdsPipesModule,
                        CdsButtonGroupModule,
                        CdsButtonModule,
                        CommonModule,
                    ],
                }]
        }] });

class CdsDropdownButtonComponent {
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
    }
    /**
     * @internal
     */
    get expandedIcon() {
        return this._expanded ? 'arrow_drop_up' : 'arrow_drop_down';
    }
    /**
     * @internal
     */
    ngOnInit() { }
    /**
     * @internal
     */
    ngAfterViewInit() {
        if (this._expanded) {
            this.dropdown.expand();
            this.changeDetector.detectChanges();
        }
    }
    /**
     * @internal
     */
    collapse() {
        this.dropdown.collapse();
    }
    /**
     * @internal
     */
    onExpandedStateChanged(expanded) {
        this._expanded = expanded;
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    onFocusRemoved() {
        this.changeDetector.detectChanges();
        this.dropdown.collapse();
    }
    /**
     * @internal
     */
    ngOnDestroy() { }
}
CdsDropdownButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownButtonComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsDropdownButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsDropdownButtonComponent, selector: "cds-dropdown-button", inputs: { name: "name", text: "text", disabled: "disabled", iconName: "iconName" }, viewQueries: [{ propertyName: "dropdown", first: true, predicate: CdsDropdownDirective, descendants: true, static: true }], ngImport: i0, template: "\n<div\n  class=\"cds-dropdown-button cds-dropdown\"\n  [class.-open]=\"_expanded\">\n\n  <cds-button\n    cdsElementRef\n    #toggle=\"cdsElementRef\"\n    type=\"tertiary\"\n    size=\"small\"\n    [name]=\"'cds-navigation-submenu-toggle-' + name | kebabCase\"\n    [text]=\"text\"\n    [disabled]=\"disabled\"\n    [iconName]=\"iconName\"\n  >  \n    <ng-container [ngTemplateOutlet]=\"prefix\" cdsButtonPrefix></ng-container>\n\n    <ng-container [ngTemplateOutlet]=\"suffix\" cdsButtonSuffix></ng-container>\n\n    <i class=\"cds-dropdown-button-icon material-icons\" cdsButtonSuffix>{{ expandedIcon }}</i>\n  </cds-button>\n\n  <div\n    #submenuContent\n    cdsDropdownPanel\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n  >\n    <div class=\"cds-dropdown-button-panel\">\n      <ng-content select=\"[cdsDropdownButtonContent]\"></ng-content>\n    </div>\n  </div>\n</div>\n\n<ng-template #prefix>\n  <ng-content select=\"[cdsDropdownButtonPrefix]\"></ng-content>\n</ng-template>\n\n<ng-template #suffix>\n  <ng-content select=\"[cdsDropdownButtonSuffix]\"></ng-content>\n</ng-template>\n\n<ng-container\n  cdsDropdown\n  buttonTrigger\n  [anchor]=\"toggle.nativeElement\"\n  [panel]=\"submenuContent\"\n  (removeFocus)=\"onFocusRemoved()\"\n  (change)=\"onExpandedStateChanged($event)\"\n>\n</ng-container>", dependencies: [{ kind: "component", type: CdsButtonComponent, selector: "cds-button", inputs: ["name", "text", "type", "size", "disabled", "underline", "iconName", "alignIcon", "designation", "ariaLabel", "link"] }, { kind: "directive", type: CdsDropdownDirective, selector: "[cdsDropdown]", inputs: ["anchor", "panel", "options", "strategy", "wasFocusedFn"], outputs: ["removeFocus", "change"] }, { kind: "directive", type: CdsDropdownPanelDirective, selector: "[cdsDropdownPanel]", exportAs: ["dropdownPanel"] }, { kind: "directive", type: CdsDropdownToggleButtonDirective, selector: "[buttonTrigger]", outputs: ["toggleChange"] }, { kind: "directive", type: CdsElementRefDirective, selector: "[cdsElementRef]", exportAs: ["cdsElementRef"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-dropdown-button', template: "\n<div\n  class=\"cds-dropdown-button cds-dropdown\"\n  [class.-open]=\"_expanded\">\n\n  <cds-button\n    cdsElementRef\n    #toggle=\"cdsElementRef\"\n    type=\"tertiary\"\n    size=\"small\"\n    [name]=\"'cds-navigation-submenu-toggle-' + name | kebabCase\"\n    [text]=\"text\"\n    [disabled]=\"disabled\"\n    [iconName]=\"iconName\"\n  >  \n    <ng-container [ngTemplateOutlet]=\"prefix\" cdsButtonPrefix></ng-container>\n\n    <ng-container [ngTemplateOutlet]=\"suffix\" cdsButtonSuffix></ng-container>\n\n    <i class=\"cds-dropdown-button-icon material-icons\" cdsButtonSuffix>{{ expandedIcon }}</i>\n  </cds-button>\n\n  <div\n    #submenuContent\n    cdsDropdownPanel\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n  >\n    <div class=\"cds-dropdown-button-panel\">\n      <ng-content select=\"[cdsDropdownButtonContent]\"></ng-content>\n    </div>\n  </div>\n</div>\n\n<ng-template #prefix>\n  <ng-content select=\"[cdsDropdownButtonPrefix]\"></ng-content>\n</ng-template>\n\n<ng-template #suffix>\n  <ng-content select=\"[cdsDropdownButtonSuffix]\"></ng-content>\n</ng-template>\n\n<ng-container\n  cdsDropdown\n  buttonTrigger\n  [anchor]=\"toggle.nativeElement\"\n  [panel]=\"submenuContent\"\n  (removeFocus)=\"onFocusRemoved()\"\n  (change)=\"onExpandedStateChanged($event)\"\n>\n</ng-container>" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { name: [{
                type: Input
            }], text: [{
                type: Input
            }], disabled: [{
                type: Input
            }], iconName: [{
                type: Input
            }], dropdown: [{
                type: ViewChild,
                args: [CdsDropdownDirective, { static: true }]
            }] } });

class CdsDropdownButtonModule {
}
CdsDropdownButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsDropdownButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownButtonModule, declarations: [CdsDropdownButtonComponent], imports: [CdsButtonModule, CdsDirectivesModule, CdsPipesModule, CommonModule], exports: [CdsDropdownButtonComponent] });
CdsDropdownButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownButtonModule, imports: [CdsButtonModule, CdsDirectivesModule, CdsPipesModule, CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsDropdownButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsDropdownButtonComponent],
                    exports: [CdsDropdownButtonComponent],
                    imports: [CdsButtonModule, CdsDirectivesModule, CdsPipesModule, CommonModule]
                }]
        }] });

/**
 * ### Description
 * A FormField is a labeled or non-labeled group of controls within a form.
 *
 * #### A note about the Label Component's [for] input:
 * The value of the `for` attribute on the `<label cdsFormLabel>` element should match the `id` attribute of the `<input>` element
 * inside the `<cds-input>` element, and will follow the pattern `cds-[component-name-kebab-case]-[unique-id-you-provided]`, for
 * example `cds-text-input-claim-number`.
 * If the automatic generation of the value of the `for` attribute is something that you'd like to see, please create a feature request.
 */
class CdsFormFieldComponent {
    /**
     * @internal
     */
    constructor(renderer) {
        this.renderer = renderer;
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        if (this.labelElement) {
            this.renderer.setStyle(this.labelElement.nativeElement, 'display', 'contents');
        }
    }
}
CdsFormFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFormFieldComponent, deps: [{ token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
CdsFormFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsFormFieldComponent, selector: "cds-form-field", inputs: { errorMessages: "errorMessages", vertical: "vertical" }, queries: [{ propertyName: "labelElement", first: true, predicate: CdsLabelComponent, descendants: true, read: ElementRef }], ngImport: i0, template: "<div\n  class=\"cds-field\"\n  [class.-vertical]=\"vertical\"\n>\n  <div class=\"cds-label-container\">\n    <ng-content select=\"cds-label,[cdsFormLabel]\"></ng-content>\n  </div>\n\n  <div class=\"cds-field-content\">\n    <div class=\"cds-form-controls\">\n      <ng-content></ng-content>\n    </div>\n    <div class=\"cds-input-messages\" role=\"alert\" aria-live=\"polite\">\n      <ng-content select=\"[cdsInputMessage]\"></ng-content>\n      <ng-container *ngIf=\"errorMessages\">\n        <span class=\"cds-caption -error\" *ngFor=\"let message of errorMessages\">\n          {{ message }}\n        </span>\n      </ng-container>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:contents}\n"], dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFormFieldComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-form-field', template: "<div\n  class=\"cds-field\"\n  [class.-vertical]=\"vertical\"\n>\n  <div class=\"cds-label-container\">\n    <ng-content select=\"cds-label,[cdsFormLabel]\"></ng-content>\n  </div>\n\n  <div class=\"cds-field-content\">\n    <div class=\"cds-form-controls\">\n      <ng-content></ng-content>\n    </div>\n    <div class=\"cds-input-messages\" role=\"alert\" aria-live=\"polite\">\n      <ng-content select=\"[cdsInputMessage]\"></ng-content>\n      <ng-container *ngIf=\"errorMessages\">\n        <span class=\"cds-caption -error\" *ngFor=\"let message of errorMessages\">\n          {{ message }}\n        </span>\n      </ng-container>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:contents}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }]; }, propDecorators: { errorMessages: [{
                type: Input
            }], vertical: [{
                type: Input
            }], labelElement: [{
                type: ContentChild,
                args: [CdsLabelComponent, { read: ElementRef }]
            }] } });

class CdsInputMessageDirective {
    constructor() {
        this.klass = true;
    }
}
CdsInputMessageDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsInputMessageDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsInputMessageDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsInputMessageDirective, selector: "[cdsInputMessage]", inputs: { error: "error" }, host: { properties: { "class.-error": "this.error", "class.cds-caption": "this.klass" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsInputMessageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsInputMessage]'
                }]
        }], propDecorators: { error: [{
                type: HostBinding,
                args: ['class.-error']
            }, {
                type: Input
            }], klass: [{
                type: HostBinding,
                args: ['class.cds-caption']
            }] } });

class CdsFormFieldModule {
}
CdsFormFieldModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFormFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsFormFieldModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsFormFieldModule, declarations: [CdsFormFieldComponent, CdsInputMessageDirective], imports: [CommonModule, CdsDirectivesModule, CdsPipesModule], exports: [CdsFormFieldComponent, CdsInputMessageDirective] });
CdsFormFieldModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFormFieldModule, imports: [CommonModule, CdsDirectivesModule, CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsFormFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsFormFieldComponent, CdsInputMessageDirective],
                    exports: [CdsFormFieldComponent, CdsInputMessageDirective],
                    imports: [CommonModule, CdsDirectivesModule, CdsPipesModule],
                }]
        }] });

/**
 * Wraps an element html (including selector) in a CdsHostWrapperComponent for easy transclusion.
 */
class CdsWrappedElement {
    constructor(element, injector, appRef) {
        this.element = element;
        this.appRef = appRef;
        // use the hostWrapper to wrap the element in an ng-template
        this.hostWrapper = createComponent(CdsHostWrapperComponent, appRef.injector, injector, this.element);
        // get access to the templateRef
        this.hostWrapper.instance.changeDetector.detectChanges();
        // instantiate the templateRef
        this.view = this.hostWrapper.instance.template.createEmbeddedView(null);
        // Attach hostWrapper view to appRef to trigger change detection
        appRef.attachView(this.hostWrapper.hostView); // attaches <cds-host-wrapper> to document object
    }
    get template() {
        return this.hostWrapper.instance.template;
    }
    destroy() {
        this.view.destroy();
        this.hostWrapper.destroy();
        this.appRef.detachView(this.hostWrapper.hostView);
    }
}

class CdsLinkComponent {
    constructor() {
        this.url = [];
    }
    /**
     * @internal
     */
    focus() {
        setTimeout(() => this.atom.nativeElement.focus());
    }
}
CdsLinkComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLinkComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsLinkComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsLinkComponent, selector: "cds-link", inputs: { name: "name", label: "label", url: "url" }, viewQueries: [{ propertyName: "atom", first: true, predicate: ["atom"], descendants: true, static: true }], ngImport: i0, template: "<a role=\"menuitem\" tabindex=\"0\" [routerLink]=\"url\" #atom>\n  {{ label }}\n</a>\n", dependencies: [{ kind: "directive", type: i1$3.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLinkComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-link', template: "<a role=\"menuitem\" tabindex=\"0\" [routerLink]=\"url\" #atom>\n  {{ label }}\n</a>\n" }]
        }], propDecorators: { name: [{
                type: Input
            }], label: [{
                type: Input
            }], url: [{
                type: Input
            }], atom: [{
                type: ViewChild,
                args: ['atom', { static: true }]
            }] } });

class CdsLinkModule {
}
CdsLinkModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLinkModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsLinkModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsLinkModule, declarations: [CdsLinkComponent], imports: [A11yModule, CdsPipesModule, RouterModule, CommonModule], exports: [CdsLinkComponent] });
CdsLinkModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLinkModule, imports: [A11yModule, CdsPipesModule, RouterModule, CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLinkModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsLinkComponent],
                    exports: [CdsLinkComponent],
                    imports: [A11yModule, CdsPipesModule, RouterModule, CommonModule],
                }]
        }] });

// @todo: deprecate

/**
 * Creates a loading indicator.
 */
class CdsLoadingSpinnerComponent {
    constructor() {
        this.small = false;
        this.assistiveText = '';
    }
}
CdsLoadingSpinnerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingSpinnerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsLoadingSpinnerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsLoadingSpinnerComponent, selector: "cds-loading-spinner", inputs: { small: "small", assistiveText: "assistiveText", ariaLive: "ariaLive" }, ngImport: i0, template: "<div\n  class=\"cds-spinner-container\"\n  [ngClass]=\"{ '-sm': small }\"\n  [attr.aria-live]=\"ariaLive || 'polite'\"\n>\n  <div class=\"cds-assistive-text\" *ngIf=\"assistiveText.length\">\n    {{ assistiveText }}\n  </div>\n  <svg\n    class=\"cds-loading-spinner\"\n    [ngClass]=\"{ '-sm': small }\"\n    viewBox=\"0 0 96 96\"\n  >\n    <circle></circle>\n  </svg>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingSpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-loading-spinner', template: "<div\n  class=\"cds-spinner-container\"\n  [ngClass]=\"{ '-sm': small }\"\n  [attr.aria-live]=\"ariaLive || 'polite'\"\n>\n  <div class=\"cds-assistive-text\" *ngIf=\"assistiveText.length\">\n    {{ assistiveText }}\n  </div>\n  <svg\n    class=\"cds-loading-spinner\"\n    [ngClass]=\"{ '-sm': small }\"\n    viewBox=\"0 0 96 96\"\n  >\n    <circle></circle>\n  </svg>\n</div>\n" }]
        }], propDecorators: { small: [{
                type: Input
            }], assistiveText: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }] } });

class CdsLoadingSpinnerModule {
}
CdsLoadingSpinnerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingSpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsLoadingSpinnerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingSpinnerModule, declarations: [CdsLoadingSpinnerComponent], imports: [CommonModule], exports: [CdsLoadingSpinnerComponent] });
CdsLoadingSpinnerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingSpinnerModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingSpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsLoadingSpinnerComponent],
                    exports: [CdsLoadingSpinnerComponent],
                    imports: [CommonModule],
                }]
        }] });

/**
 * ### Description
 * A utility component for creating an absolutely-positoned overlay over the contents of a section on the page.
 */
class CdsOverlayComponent {
}
CdsOverlayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsOverlayComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsOverlayComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsOverlayComponent, selector: "cds-overlay", ngImport: i0, template: "<div class=\"cds-overlay\">\n  <ng-content></ng-content>\n</div>\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsOverlayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-overlay', template: "<div class=\"cds-overlay\">\n  <ng-content></ng-content>\n</div>\n" }]
        }] });

class CdsOverlayModule {
}
CdsOverlayModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsOverlayModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsOverlayModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsOverlayModule, declarations: [CdsOverlayComponent], exports: [CdsOverlayComponent] });
CdsOverlayModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsOverlayModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsOverlayModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsOverlayComponent],
                    exports: [CdsOverlayComponent],
                }]
        }] });

/**
 * ### Description
 * A utility component for creating an overlay containing a loading spinner, primary message, and secondary message.
 */
class CdsLoadingOverlayComponent {
}
CdsLoadingOverlayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingOverlayComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsLoadingOverlayComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsLoadingOverlayComponent, selector: "cds-loading-overlay", inputs: { primaryMessage: "primaryMessage", secondaryMessage: "secondaryMessage" }, ngImport: i0, template: "<div class=\"cds-loading-overlay\">\n  <cds-overlay>\n    <div class=\"flex-centered\">\n      <div class=\"cds-loading\" role=\"alert\">\n        <cds-loading-spinner></cds-loading-spinner>\n        <span class=\"primary-loading-message\">\n          {{ primaryMessage ? primaryMessage : 'Loading' }}\n        </span>\n        <span class=\"secondary-loading-message\">\n          {{\n            secondaryMessage\n              ? secondaryMessage\n              : 'Wait a moment while we retrieve your content.'\n          }}\n        </span>\n      </div>\n    </div>\n  </cds-overlay>\n</div>\n", dependencies: [{ kind: "component", type: CdsOverlayComponent, selector: "cds-overlay" }, { kind: "component", type: CdsLoadingSpinnerComponent, selector: "cds-loading-spinner", inputs: ["small", "assistiveText", "ariaLive"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingOverlayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-loading-overlay', template: "<div class=\"cds-loading-overlay\">\n  <cds-overlay>\n    <div class=\"flex-centered\">\n      <div class=\"cds-loading\" role=\"alert\">\n        <cds-loading-spinner></cds-loading-spinner>\n        <span class=\"primary-loading-message\">\n          {{ primaryMessage ? primaryMessage : 'Loading' }}\n        </span>\n        <span class=\"secondary-loading-message\">\n          {{\n            secondaryMessage\n              ? secondaryMessage\n              : 'Wait a moment while we retrieve your content.'\n          }}\n        </span>\n      </div>\n    </div>\n  </cds-overlay>\n</div>\n" }]
        }], propDecorators: { primaryMessage: [{
                type: Input
            }], secondaryMessage: [{
                type: Input
            }] } });

class CdsLoadingOverlayModule {
}
CdsLoadingOverlayModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingOverlayModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsLoadingOverlayModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingOverlayModule, declarations: [CdsLoadingOverlayComponent], imports: [CdsOverlayModule, CdsLoadingSpinnerModule], exports: [CdsLoadingOverlayComponent] });
CdsLoadingOverlayModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingOverlayModule, imports: [CdsOverlayModule, CdsLoadingSpinnerModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLoadingOverlayModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsLoadingOverlayComponent],
                    exports: [CdsLoadingOverlayComponent],
                    imports: [CdsOverlayModule, CdsLoadingSpinnerModule],
                }]
        }] });

class CdsMeterComponent {
    constructor(/** @internal */ meter) {
        this.meter = meter;
        /**
         * @internal
         */
        this.display = 'contents';
    }
    /**
     * @internal
     */
    get width() {
        return this.meter && (this.total / this.meter.total) * 100;
    }
    /**
     * @internal
     */
    ngOnInit() { }
    /**
     * @internal
     */
    getMarkerWidth(marker) {
        return (marker.end - marker.start) * (100 / this.total);
    }
    /**
     * @internal
     */
    getMarkerStart(marker) {
        return marker.start * (100 / this.total);
    }
}
CdsMeterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMeterComponent, deps: [{ token: CdsMeterComponent, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.Component });
CdsMeterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsMeterComponent, selector: "cds-meter", inputs: { total: "total", markers: "markers", color: "color", pattern: "pattern", label: "label" }, host: { properties: { "style.display": "this.display" } }, ngImport: i0, template: "<div\n  [style.backgroundColor]=\"color\"\n  [style.width.%]=\"width || null\"\n  class=\"cds-meter\"\n  [class.-hatch]=\"pattern === 'hatch'\"\n>\n  <ng-content></ng-content>\n  <div\n    class=\"cds-meter-marker\"\n    *ngFor=\"let marker of markers\"\n    [style.width.%]=\"getMarkerWidth(marker)\"\n    [style.left.%]=\"getMarkerStart(marker)\"\n  >\n    <span class=\"cds-meter-marker-label\">{{ marker.label }}</span>\n  </div>\n  <span class=\"cds-meter-label\">{{ label }}</span>\n</div>", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMeterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-meter', template: "<div\n  [style.backgroundColor]=\"color\"\n  [style.width.%]=\"width || null\"\n  class=\"cds-meter\"\n  [class.-hatch]=\"pattern === 'hatch'\"\n>\n  <ng-content></ng-content>\n  <div\n    class=\"cds-meter-marker\"\n    *ngFor=\"let marker of markers\"\n    [style.width.%]=\"getMarkerWidth(marker)\"\n    [style.left.%]=\"getMarkerStart(marker)\"\n  >\n    <span class=\"cds-meter-marker-label\">{{ marker.label }}</span>\n  </div>\n  <span class=\"cds-meter-label\">{{ label }}</span>\n</div>" }]
        }], ctorParameters: function () { return [{ type: CdsMeterComponent, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; }, propDecorators: { total: [{
                type: Input
            }], markers: [{
                type: Input
            }], color: [{
                type: Input
            }], pattern: [{
                type: Input
            }], label: [{
                type: Input
            }], display: [{
                type: HostBinding,
                args: ['style.display']
            }] } });

class CdsMeterSectionComponent {
    constructor(
    /** @internal */ meter, 
    /** @internal */ changeDetector) {
        this.meter = meter;
        this.changeDetector = changeDetector;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        /**
         * @internal
         */
        this.floatLabel = false;
        /**
         * @internal
         */
        this.display = 'contents';
    }
    /**
     * @internal
     */
    get width() {
        return (this.value / this.meter.total) * 100;
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        ResizeObserverSubject(this.container.nativeElement, 0, this.$cancelSubscriptions)
            .subscribe(() => this.setLabelFloat());
        this.setLabelFloat();
    }
    /**
     * @internal
     */
    setLabelFloat() {
        if (this.containerWidth === this.container.nativeElement.clientWidth) {
            return;
        }
        this.floatLabel = this.content.nativeElement.clientWidth > this.container.nativeElement.clientWidth;
        this.containerWidth = this.container.nativeElement.clientWidth;
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsMeterSectionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMeterSectionComponent, deps: [{ token: CdsMeterComponent }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsMeterSectionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsMeterSectionComponent, selector: "cds-meter-section", inputs: { value: "value", color: "color", pattern: "pattern" }, host: { properties: { "style.display": "this.display" } }, viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true, static: true }, { propertyName: "container", first: true, predicate: ["container"], descendants: true, static: true }], ngImport: i0, template: "<div\n  [style.backgroundColor]=\"color\"\n  [style.width.%]=\"width\"\n  class=\"cds-meter-section\"\n  [class.-hatch]=\"pattern === 'hatch'\"\n  #container\n>\n  <div class=\"cds-meter-section-content\" [class.-float]=\"floatLabel\" #content>\n    <ng-content></ng-content>\n  </div>\n</div>" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMeterSectionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-meter-section', template: "<div\n  [style.backgroundColor]=\"color\"\n  [style.width.%]=\"width\"\n  class=\"cds-meter-section\"\n  [class.-hatch]=\"pattern === 'hatch'\"\n  #container\n>\n  <div class=\"cds-meter-section-content\" [class.-float]=\"floatLabel\" #content>\n    <ng-content></ng-content>\n  </div>\n</div>" }]
        }], ctorParameters: function () { return [{ type: CdsMeterComponent }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { value: [{
                type: Input
            }], color: [{
                type: Input
            }], pattern: [{
                type: Input
            }], content: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], container: [{
                type: ViewChild,
                args: ['container', { static: true }]
            }], display: [{
                type: HostBinding,
                args: ['style.display']
            }] } });

class CdsMeterModule {
}
CdsMeterModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMeterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsMeterModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsMeterModule, declarations: [CdsMeterComponent, CdsMeterSectionComponent], imports: [CommonModule,
        CdsPipesModule], exports: [CdsMeterComponent, CdsMeterSectionComponent] });
CdsMeterModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMeterModule, imports: [CommonModule,
        CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsMeterModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsMeterComponent, CdsMeterSectionComponent],
                    exports: [CdsMeterComponent, CdsMeterSectionComponent],
                    imports: [
                        CommonModule,
                        CdsPipesModule
                    ]
                }]
        }] });

class CdsMeterMarker {
}

/**
 * ### Description
 * Represents inputs and outputs to be passed to and from the modal component.
 *
 * Example:
 *
 * ```ts
 *   const config: ModalConfig = {
 *     inputs: {
 *       myInputProperty: 'This can be anything.'
 *     },
 *     outputs: {
 *       change: (event) => { console.log(event); }
 *     }
 *   };
 *   this.modalService.openModal(ModalComponent, config);
 * ```
 */
class CdsModalConfig {
}

/* eslint-disable @angular-eslint/no-output-native */
/**
 * ### Description
 * A collapsible panel that can be placed adjacent to some content on the page.
 * Typically used for navigation or supplementary information.
 *
 *
 * Components can be placed in the footer of the side panel using the `[cdsSidePanelFooter]` attribute.
 */
class CdsSidePanelComponent {
    constructor() {
        /**
         * @internal
         */
        this.cancelSubscriptions$ = new Subject();
        /**
         * (deprecated) An event representing changes to the collapsed state of the side panel.
         * Prefer to use the `expanded` property to get the current state of the side panel.
         */
        this.collapsed = new EventEmitter();
    }
    get collapseDirection() {
        return this._collapseDirection || 'left';
    }
    /**
     * @internal
     */
    ngOnInit() {
        this.expanded = !!!this.collapseOnInit;
    }
    /**
     * Toggle the collapsed state of the side panel.
     */
    toggle(event) {
        if (event instanceof MouseEvent) {
            event.stopPropagation();
        }
        this.expanded = !this.expanded;
        this.collapsed.emit(this.expanded);
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.cancelSubscriptions$.complete();
    }
}
CdsSidePanelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSidePanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsSidePanelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSidePanelComponent, selector: "cds-side-panel", inputs: { name: "name", collapsible: "collapsible", _collapseDirection: ["collapseDirection", "_collapseDirection"], expanded: "expanded", collapseOnInit: "collapseOnInit" }, outputs: { collapsed: "collapsed" }, ngImport: i0, template: "<aside\n  class=\"cds-side-panel\"\n  [class.-collapsible]=\"collapsible\"\n  [class.-collapsed]=\"expanded === false\"\n  [class.-collapse-left]=\"collapsible && collapseDirection === 'left'\"\n  [class.-collapse-right]=\"collapsible && collapseDirection === 'right'\"\n  [id]=\"'cds-side-panel-' + name | kebabCase\"\n>\n  <div class=\"cds-side-panel-content\">\n    <div class=\"cds-side-panel-scroll\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n  <footer\n    class=\"cds-side-panel_footer\"\n    [id]=\"'cds-side-panel-footer-' + name | kebabCase\"\n  >\n    <cds-control-block\n      *ngIf=\"collapsible\"\n      [name]=\"name + '-side-panel-toggle'\"\n      (click)=\"toggle($event)\"\n    >\n      <ng-container [ngSwitch]=\"expanded\">\n        <i class=\"material-icons\" *ngSwitchCase=\"true\">\n          {{ collapseDirection === 'left' ? 'chevron_left' : 'chevron_right' }}\n        </i>\n        <i class=\"material-icons\" *ngSwitchCase=\"false\">\n          {{ collapseDirection === 'left' ? 'chevron_right' : 'chevron_left' }}\n        </i>\n      </ng-container>\n    </cds-control-block>\n    <ng-content *ngIf=\"!collapsible\" select=\"[cdsSidePanelFooter]\"></ng-content>\n  </footer>\n</aside>\n", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: ["name", "size", "disabled", "tabindex", "active", "attributes"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSidePanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-side-panel', template: "<aside\n  class=\"cds-side-panel\"\n  [class.-collapsible]=\"collapsible\"\n  [class.-collapsed]=\"expanded === false\"\n  [class.-collapse-left]=\"collapsible && collapseDirection === 'left'\"\n  [class.-collapse-right]=\"collapsible && collapseDirection === 'right'\"\n  [id]=\"'cds-side-panel-' + name | kebabCase\"\n>\n  <div class=\"cds-side-panel-content\">\n    <div class=\"cds-side-panel-scroll\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n  <footer\n    class=\"cds-side-panel_footer\"\n    [id]=\"'cds-side-panel-footer-' + name | kebabCase\"\n  >\n    <cds-control-block\n      *ngIf=\"collapsible\"\n      [name]=\"name + '-side-panel-toggle'\"\n      (click)=\"toggle($event)\"\n    >\n      <ng-container [ngSwitch]=\"expanded\">\n        <i class=\"material-icons\" *ngSwitchCase=\"true\">\n          {{ collapseDirection === 'left' ? 'chevron_left' : 'chevron_right' }}\n        </i>\n        <i class=\"material-icons\" *ngSwitchCase=\"false\">\n          {{ collapseDirection === 'left' ? 'chevron_right' : 'chevron_left' }}\n        </i>\n      </ng-container>\n    </cds-control-block>\n    <ng-content *ngIf=\"!collapsible\" select=\"[cdsSidePanelFooter]\"></ng-content>\n  </footer>\n</aside>\n" }]
        }], propDecorators: { name: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], _collapseDirection: [{
                type: Input,
                args: ['collapseDirection']
            }], expanded: [{
                type: Input
            }], collapseOnInit: [{
                type: Input
            }], collapsed: [{
                type: Output
            }] } });

class CdsHorizontalNavigationPresenter {
    constructor() {
        this.$cancelSubscriptions = new Subject();
        this.overflowPresenter = new CdsOverflowObserverPresenter();
    }
    reset(element, component) {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.overflowPresenter.unwatch();
        if (!component.horizontal) {
            component.setDisplayedNavItems(component.navItems.toArray());
            return;
        }
        this.subscription = this.overflowPresenter.change
            .subscribe((event) => {
            component.setDisplayedNavItems(event.containerItems, event.overflowItems);
        });
        this.overflowPresenter.watch(element, component.navItems.toArray());
    }
    destroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.overflowPresenter.destroy();
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsHorizontalNavigationPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsHorizontalNavigationPresenter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CdsHorizontalNavigationPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsHorizontalNavigationPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsHorizontalNavigationPresenter, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class CdsNavigationItemType {
    constructor() {
        this.$click = new Subject();
    }
    /**
     * @internal
     */
    get template() {
        return null;
    }
    /**
     * @internal
     */
    get isSelected() {
        return null;
    }
    /**
     * @internal
     */
    get sticky() {
        return this.isSelected;
    }
}

class CdsNavSubmenuDropdownComponent {
    constructor(element, changeDetector, router) {
        this.element = element;
        this.changeDetector = changeDetector;
        this.router = router;
        this.$cancelSubscriptions = new Subject();
        this.select = new EventEmitter();
        this.expanded = new EventEmitter();
    }
    get expandedIcon() {
        return this._adjacent ?
            this._expanded ? 'arrow_left' : 'arrow_right' :
            this._expanded ? 'arrow_drop_up' : 'arrow_drop_down';
    }
    set adjacent(adjacent) {
        this._adjacent = adjacent;
        const config = { adjacent };
        this.strategy = !adjacent
            ? new DirectionalPositionStrategy('bottom', config)
            : new QuadrantPositionStrategy(config);
    }
    onSpaceKeydown(event) {
        event.preventDefault();
        event.target.click();
    }
    ngOnInit() {
        this.router.events
            .pipe(takeUntil(this.$cancelSubscriptions), filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
            if (this.dropdown && this.dropdown.expanded) {
                this.dropdown.collapse();
            }
        });
    }
    ngAfterViewInit() {
        if (this._expanded) {
            this.dropdown.expand();
            this.changeDetector.detectChanges();
        }
    }
    collapse() {
        this.dropdown.collapse();
    }
    onExpandedStateChanged(expanded) {
        this._expanded = expanded;
        this.expanded.emit(this._expanded);
        this.changeDetector.detectChanges();
    }
    onFocusRemoved() {
        this.changeDetector.detectChanges();
        this.dropdown.collapse();
    }
    // @todo: hack - notifies the more submenu that an item was selected/clicked on
    onSelect() {
        this.select.emit();
    }
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsNavSubmenuDropdownComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavSubmenuDropdownComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i1$3.Router }], target: i0.ɵɵFactoryTarget.Component });
CdsNavSubmenuDropdownComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsNavSubmenuDropdownComponent, selector: "cds-nav-submenu-dropdown", inputs: { name: "name", isSelected: "isSelected", iconName: "iconName", label: "label", _expanded: ["expanded", "_expanded"], adjacent: "adjacent" }, outputs: { select: "select", expanded: "expanded" }, host: { listeners: { "keydown.space": "onSpaceKeydown($event)" } }, viewQueries: [{ propertyName: "dropdown", first: true, predicate: CdsDropdownDirective, descendants: true, static: true }], ngImport: i0, template: "<div\n  class=\"cds-dropdown\"\n  [class.-open]=\"_expanded\">\n\n  <button\n    cdsElementRef\n    #toggle=\"cdsElementRef\"\n    class=\"cds-nav-submenu_toggle\"\n    [class.-selected]=\"isSelected\"\n    [id]=\"'cds-navigation-submenu-toggle-' + name | kebabCase\"\n    cdsHighlightable\n  >\n    <span class=\"cds-nav-submenu_title\">\n      <i class=\"material-icons\" *ngIf=\"iconName\">{{ iconName }}</i>\n      <ng-template [ngTemplateOutlet]=\"label\"></ng-template>\n    </span>\n    <i class=\"cds-nav-dropdown_icon material-icons\">{{ expandedIcon }}</i>\n  </button>\n\n  <div\n    #submenuContent\n    cdsDropdownPanel\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n    (click)=\"onSelect()\"\n    (keydown.enter)=\"onSelect()\"\n    (keydown.space)=\"onSelect()\">\n    <ng-content></ng-content>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  buttonTrigger\n  [strategy]=\"strategy\"\n  [anchor]=\"toggle.nativeElement\"\n  [panel]=\"submenuContent\"\n  (removeFocus)=\"onFocusRemoved()\"\n  (change)=\"onExpandedStateChanged($event)\"\n>\n</ng-container>", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: CdsDropdownDirective, selector: "[cdsDropdown]", inputs: ["anchor", "panel", "options", "strategy", "wasFocusedFn"], outputs: ["removeFocus", "change"] }, { kind: "directive", type: CdsDropdownPanelDirective, selector: "[cdsDropdownPanel]", exportAs: ["dropdownPanel"] }, { kind: "directive", type: CdsKeyboardHighlightableDirective, selector: "[cdsHighlightable]", inputs: ["isDisabled"] }, { kind: "directive", type: CdsDropdownToggleButtonDirective, selector: "[buttonTrigger]", outputs: ["toggleChange"] }, { kind: "directive", type: CdsElementRefDirective, selector: "[cdsElementRef]", exportAs: ["cdsElementRef"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavSubmenuDropdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-nav-submenu-dropdown', template: "<div\n  class=\"cds-dropdown\"\n  [class.-open]=\"_expanded\">\n\n  <button\n    cdsElementRef\n    #toggle=\"cdsElementRef\"\n    class=\"cds-nav-submenu_toggle\"\n    [class.-selected]=\"isSelected\"\n    [id]=\"'cds-navigation-submenu-toggle-' + name | kebabCase\"\n    cdsHighlightable\n  >\n    <span class=\"cds-nav-submenu_title\">\n      <i class=\"material-icons\" *ngIf=\"iconName\">{{ iconName }}</i>\n      <ng-template [ngTemplateOutlet]=\"label\"></ng-template>\n    </span>\n    <i class=\"cds-nav-dropdown_icon material-icons\">{{ expandedIcon }}</i>\n  </button>\n\n  <div\n    #submenuContent\n    cdsDropdownPanel\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n    (click)=\"onSelect()\"\n    (keydown.enter)=\"onSelect()\"\n    (keydown.space)=\"onSelect()\">\n    <ng-content></ng-content>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  buttonTrigger\n  [strategy]=\"strategy\"\n  [anchor]=\"toggle.nativeElement\"\n  [panel]=\"submenuContent\"\n  (removeFocus)=\"onFocusRemoved()\"\n  (change)=\"onExpandedStateChanged($event)\"\n>\n</ng-container>" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i1$3.Router }]; }, propDecorators: { name: [{
                type: Input
            }], isSelected: [{
                type: Input
            }], iconName: [{
                type: Input
            }], label: [{
                type: Input
            }], _expanded: [{
                type: Input,
                args: ['expanded']
            }], adjacent: [{
                type: Input
            }], select: [{
                type: Output
            }], expanded: [{
                type: Output
            }], dropdown: [{
                type: ViewChild,
                args: [CdsDropdownDirective, { static: true }]
            }], onSpaceKeydown: [{
                type: HostListener,
                args: ['keydown.space', ['$event']]
            }] } });

/**
 * ### Description
 * Displays application links in a submenu within a [Navigation Component](?path=/story/components-navigation-overview--page).
 * The NavigationSubMenu component accepts `<a cdsNavItem></a>`
 * (i.e. [Navigation Components](?path=/story/components-navigation-overview--page#navigation-item-component-properties))
 * elements as its children.
 */
class CdsNavigationSubMenuComponent extends CdsNavigationItemType {
    /**
     * @internal
     */
    // Change detection is needed in order to update the toggled content container type as soon as there is a change to expansionType
    constructor(
    /** @internal */
    element, changeDetector, cancelSubscriptions, 
    /** @internal */
    parent) {
        super();
        this.element = element;
        this.changeDetector = changeDetector;
        this.cancelSubscriptions = cancelSubscriptions;
        this.parent = parent;
        /**
         * @internal
         */
        this.type = 'NavSubmenu';
        /**
         * @internal
         */
        this.$spaceKeydown = new Subject();
        /**
         * @internal
         */
        this.displayedItems = [];
    }
    /**
     * @internal
     */
    get template() {
        return this._template;
    }
    /**
     * @internal
     */
    get isSelected() {
        return this.displayedItems.some((child) => child.isSelected);
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        const children = this.navItems.filter(item => !Object.is(item, this));
        this.setDisplayedItems(children);
        this.navItems.toArray().forEach(item => {
            item.$click.pipe(takeUntil(this.cancelSubscriptions.$cancelSubscriptions)).subscribe(() => this.onClick());
        });
    }
    /**
     * @internal
     */
    setDisplayedItems(items) {
        this.displayedItems = items || [];
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    onClick() {
        this.$click.next();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$spaceKeydown.complete();
        this.cancelSubscriptions.destroy();
    }
}
CdsNavigationSubMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationSubMenuComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: SubscriptionManager }, { token: CdsNavigationSubMenuComponent, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.Component });
CdsNavigationSubMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsNavigationSubMenuComponent, selector: "cds-nav-submenu", inputs: { name: "name", iconName: "iconName" }, providers: [
        CdsWrappedComponent,
        SubscriptionManager,
        { provide: CdsNavigationItemType, useExisting: forwardRef(() => CdsNavigationSubMenuComponent) }
    ], queries: [{ propertyName: "links", predicate: RouterLink }, { propertyName: "navItems", predicate: CdsNavigationItemType }], viewQueries: [{ propertyName: "_template", first: true, predicate: ["content"], descendants: true, static: true }, { propertyName: "label", first: true, predicate: ["label"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<ng-template #content>\n  <ul class=\"cds-nav-submenu\">\n    <li\n      class=\"cds-nav-submenu_item\"\n      [class.-selected]=\"item.isSelected\"\n      *ngFor=\"let item of displayedItems\"\n    >\n      <!-- Nav Item -->\n      <ng-container *ngIf=\"item.type !== 'NavSubmenu'\">\n        <ng-container *ngTemplateOutlet=\"item.template\"></ng-container>\n      </ng-container>\n\n      <!-- Nav Submenu -->\n      <ng-container *ngIf=\"item.type === 'NavSubmenu'\">\n        <cds-nav-submenu-dropdown\n          [adjacent]=\"true\"\n          [name]=\"item.name\"\n          [isSelected]=\"item.isSelected\"\n          [iconName]=\"item.iconName\"\n          [label]=\"item.label\"\n          [(expanded)]=\"item.expanded\"\n        >\n          <ng-template [ngTemplateOutlet]=\"item.template\"></ng-template>\n        </cds-nav-submenu-dropdown>\n      </ng-container>\n    </li>\n  </ul>\n</ng-template>\n\n<ng-template #label>\n  <ng-content select=\"*:not([cdsNavItem]):not(cds-nav-submenu):not(ng-container)\"></ng-content>\n</ng-template>", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: CdsNavSubmenuDropdownComponent, selector: "cds-nav-submenu-dropdown", inputs: ["name", "isSelected", "iconName", "label", "expanded", "adjacent"], outputs: ["select", "expanded"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationSubMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-nav-submenu', providers: [
                        CdsWrappedComponent,
                        SubscriptionManager,
                        { provide: CdsNavigationItemType, useExisting: forwardRef(() => CdsNavigationSubMenuComponent) }
                    ], template: "<ng-template #content>\n  <ul class=\"cds-nav-submenu\">\n    <li\n      class=\"cds-nav-submenu_item\"\n      [class.-selected]=\"item.isSelected\"\n      *ngFor=\"let item of displayedItems\"\n    >\n      <!-- Nav Item -->\n      <ng-container *ngIf=\"item.type !== 'NavSubmenu'\">\n        <ng-container *ngTemplateOutlet=\"item.template\"></ng-container>\n      </ng-container>\n\n      <!-- Nav Submenu -->\n      <ng-container *ngIf=\"item.type === 'NavSubmenu'\">\n        <cds-nav-submenu-dropdown\n          [adjacent]=\"true\"\n          [name]=\"item.name\"\n          [isSelected]=\"item.isSelected\"\n          [iconName]=\"item.iconName\"\n          [label]=\"item.label\"\n          [(expanded)]=\"item.expanded\"\n        >\n          <ng-template [ngTemplateOutlet]=\"item.template\"></ng-template>\n        </cds-nav-submenu-dropdown>\n      </ng-container>\n    </li>\n  </ul>\n</ng-template>\n\n<ng-template #label>\n  <ng-content select=\"*:not([cdsNavItem]):not(cds-nav-submenu):not(ng-container)\"></ng-content>\n</ng-template>" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: SubscriptionManager }, { type: CdsNavigationSubMenuComponent, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; }, propDecorators: { name: [{
                type: Input
            }], iconName: [{
                type: Input
            }], _template: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], label: [{
                type: ViewChild,
                args: ['label', { static: true }]
            }], links: [{
                type: ContentChildren,
                args: [RouterLink]
            }], navItems: [{
                type: ContentChildren,
                args: [CdsNavigationItemType]
            }] } });

class CdsNavSubmenuAccordionComponent {
    constructor() {
        this.expanded = new EventEmitter();
    }
    get expandedIcon() {
        return this._expanded ? 'arrow_drop_up' : 'arrow_drop_down';
    }
    ngAfterViewInit() {
        if (this._expanded) {
            this.toggle.expand();
        }
    }
    onExpandedStateChanged(expanded) {
        this._expanded = expanded;
        this.expanded.emit(this._expanded);
    }
}
CdsNavSubmenuAccordionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavSubmenuAccordionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsNavSubmenuAccordionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsNavSubmenuAccordionComponent, selector: "cds-nav-submenu-accordion", inputs: { isSelected: "isSelected", iconName: "iconName", label: "label", name: "name", _expanded: ["expanded", "_expanded"] }, outputs: { expanded: "expanded" }, viewQueries: [{ propertyName: "toggle", first: true, predicate: CdsAccordionDirective, descendants: true, static: true }], ngImport: i0, template: "<div\n  class=\"cds-accordion\"\n  [class.-open]=\"_expanded\">\n\n  <button\n    cdsElementRef\n    #toggle=\"cdsElementRef\"\n    class=\"cds-nav-submenu_toggle\"\n    [class.-selected]=\"isSelected\"\n    [id]=\"'cds-navigation-submenu-toggle-' + name | kebabCase\"\n    cdsHighlightable\n  >\n    <span class=\"cds-nav-submenu_title\">\n      <i class=\"material-icons\" *ngIf=\"iconName\">{{ iconName }}</i>\n      <ng-template [ngTemplateOutlet]=\"label\"></ng-template>\n    </span>\n    <i class=\"cds-nav-dropdown_icon material-icons\">{{ expandedIcon }}</i>\n  </button>\n\n  <div\n    cdsElementRef\n    #panel=\"cdsElementRef\"\n    class=\"cds-accordion-content\"\n    [id]=\"'cds-content-container-' + name | kebabCase\">\n    <ng-content></ng-content>\n  </div>\n</div>\n\n<ng-container\n  cdsAccordion\n  buttonTrigger\n  [anchor]=\"toggle.nativeElement\"\n  [panel]=\"panel.nativeElement\"\n  (change)=\"onExpandedStateChanged($event)\"\n>\n</ng-container>", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: CdsAccordionDirective, selector: "[cdsAccordion]", inputs: ["anchor", "panel", "expanded"], outputs: ["change"], exportAs: ["cdsAccordion"] }, { kind: "directive", type: CdsKeyboardHighlightableDirective, selector: "[cdsHighlightable]", inputs: ["isDisabled"] }, { kind: "directive", type: CdsDropdownToggleButtonDirective, selector: "[buttonTrigger]", outputs: ["toggleChange"] }, { kind: "directive", type: CdsElementRefDirective, selector: "[cdsElementRef]", exportAs: ["cdsElementRef"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavSubmenuAccordionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-nav-submenu-accordion', template: "<div\n  class=\"cds-accordion\"\n  [class.-open]=\"_expanded\">\n\n  <button\n    cdsElementRef\n    #toggle=\"cdsElementRef\"\n    class=\"cds-nav-submenu_toggle\"\n    [class.-selected]=\"isSelected\"\n    [id]=\"'cds-navigation-submenu-toggle-' + name | kebabCase\"\n    cdsHighlightable\n  >\n    <span class=\"cds-nav-submenu_title\">\n      <i class=\"material-icons\" *ngIf=\"iconName\">{{ iconName }}</i>\n      <ng-template [ngTemplateOutlet]=\"label\"></ng-template>\n    </span>\n    <i class=\"cds-nav-dropdown_icon material-icons\">{{ expandedIcon }}</i>\n  </button>\n\n  <div\n    cdsElementRef\n    #panel=\"cdsElementRef\"\n    class=\"cds-accordion-content\"\n    [id]=\"'cds-content-container-' + name | kebabCase\">\n    <ng-content></ng-content>\n  </div>\n</div>\n\n<ng-container\n  cdsAccordion\n  buttonTrigger\n  [anchor]=\"toggle.nativeElement\"\n  [panel]=\"panel.nativeElement\"\n  (change)=\"onExpandedStateChanged($event)\"\n>\n</ng-container>" }]
        }], propDecorators: { isSelected: [{
                type: Input
            }], iconName: [{
                type: Input
            }], label: [{
                type: Input
            }], name: [{
                type: Input
            }], _expanded: [{
                type: Input,
                args: ['expanded']
            }], expanded: [{
                type: Output
            }], toggle: [{
                type: ViewChild,
                args: [CdsAccordionDirective, { static: true }]
            }] } });

/**
 * ### Description
 * Displays application links within an accessible menu positioned adjacent to, or on top of the page content.
 * The Navigation Component fills its container, so be sure to restrict its width if necessary.
 *
 * The Navigation component accepts CdsNavigationItem components and CdsNavigationSubMenu components as its children.
 */
class CdsNavigationComponent {
    /**
     * @internal
     */
    constructor(changeDetector, presenter, clickAwayPresenter, sidePanel) {
        this.changeDetector = changeDetector;
        this.presenter = presenter;
        this.clickAwayPresenter = clickAwayPresenter;
        this.sidePanel = sidePanel;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        /**
         * @internal
         */
        this.showMoreSubmenu = false;
        /**
         * @internal
         */
        this.mobilePanelOpen = false;
        /**
         * @internal
         */
        this.displayedItems = {
            $main: new BehaviorSubject([]),
            $more: new BehaviorSubject([])
        };
        /**
         * @internal
         */
        this.mobileNavItem = {
            url: null,
            label: null
        };
        this.clickAwayPresenter.clickAway.subscribe(() => {
            this.hideMobileNav();
        });
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        this.setDisplayedNavItems(this.navItems.toArray());
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.overflowContainerElement = this.navContainerElement.nativeElement;
        this.navItems.changes
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe(() => {
            this.presenter.reset(this.overflowContainerElement, this);
            this.setMobileNavLabel();
            this.navItems.toArray().forEach(item => {
                item.$click
                    .pipe(takeUntil(this.$cancelSubscriptions))
                    .subscribe(() => this.hideMobileNav());
            });
        });
        // @todo: hack - sometimes icons aren't loaded immediately, which causes the overflow container
        // to prematurely move horizontal nav items into the more menu
        setTimeout(() => {
            this.presenter.reset(this.overflowContainerElement, this);
            this.setMobileNavLabel();
            this.navItems.toArray().forEach(item => {
                item.$click
                    .pipe(takeUntil(this.$cancelSubscriptions))
                    .subscribe(() => this.hideMobileNav());
            });
        }, 100);
    }
    /**
     * @internal
     */
    onOverflowItemSelected() {
        // move more submenu item to parent row when clicked
        const selected = this.moreSubmenu.displayedItems.find((child) => child.isSelected);
        if (selected) {
            this.presenter.reset(this.navContainerElement.nativeElement, this);
        }
    }
    /**
     * @internal
     */
    setDisplayedNavItems(main, more) {
        this.showMoreSubmenu = more && more.length > 0;
        this.changeDetector.detectChanges();
        if (this.showMoreSubmenu) {
            this.moreSubmenu.setDisplayedItems(more);
        }
        this.displayedItems.$main.next(main || []);
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    getSelectedNavitem(navItems) {
        let selectedChild = null;
        navItems.toArray().some((child) => {
            if (child.isSelected) {
                selectedChild = child;
                // Check if child has nested navItems
                if (child instanceof CdsNavigationSubMenuComponent && child.navItems.length > 0) {
                    // Recursively search for selected item in nested navItems
                    const selectedSubItem = this.getSelectedNavitem(child.navItems);
                    if (selectedSubItem) {
                        selectedChild = selectedSubItem;
                        return true;
                    }
                }
                return true;
            }
        });
        return selectedChild;
    }
    /**
     * @internal
     */
    setMobileNavLabel() {
        const navItem = this.getSelectedNavitem(this.navItems);
        if (navItem) {
            this.mobileNavItem = {
                url: navItem.routerLink,
                label: navItem.label
            };
        }
    }
    /**
     * @internal
     */
    toggleMobileNav() {
        if (!!this.mobilePanelOpen) {
            this.hideMobileNav();
            return;
        }
        this.mobilePanelOpen = true;
        setTimeout(() => {
            this.clickAwayPresenter.watch(this.navContainerElement.nativeElement);
        });
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    hideMobileNav() {
        this.mobilePanelOpen = false;
        this.clickAwayPresenter.unwatch();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.clickAwayPresenter.destroy();
        this.displayedItems.$main.complete();
        this.displayedItems.$more.complete();
        this.presenter.destroy();
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsNavigationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: CdsHorizontalNavigationPresenter }, { token: ClickAwayPresenter }, { token: CdsSidePanelComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component });
CdsNavigationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsNavigationComponent, selector: "cds-navigation:not([items=*])", inputs: { name: "name", horizontal: "horizontal", mobile: "mobile" }, providers: [CdsOverflowObserverPresenter, CdsHorizontalNavigationPresenter, ClickAwayPresenter], queries: [{ propertyName: "navItems", predicate: CdsNavigationItemType }], viewQueries: [{ propertyName: "navContainerElement", first: true, predicate: ["container"], descendants: true, read: ElementRef, static: true }, { propertyName: "moreSubmenu", first: true, predicate: ["moreSubmenu"], descendants: true }, { propertyName: "moreSubmenuDropdown", first: true, predicate: ["moreSubmenuDropdown"], descendants: true }], ngImport: i0, template: "\n<div *ngIf=\"mobile\" \n  class=\"cds-mobile-nav-button\"\n  [class.-active]=\"!!mobileNavItem.label\">\n  <cds-control-block\n    size=\"small\"\n    (click)=\"toggleMobileNav()\">\n    <i class=\"material-icons\">menu</i>\n  </cds-control-block>\n  <a \n    *ngIf=\"mobileNavItem.label\"\n    role=\"link\" \n    class=\"cds-mobile-nav-link cds-button -tertiary\" \n    tabindex=\"0\" \n    [routerLink]=\"mobileNavItem?.url\" \n    [attr.aria-label]=\"mobileNavItem?.label\"\n    #atom\n  >\n    {{ mobileNavItem.label }}\n  </a>\n  \n</div>\n\n<nav\n  class=\"cds-side-nav_navigation\"\n  [id]=\"'cds-navigation-' + name | kebabCase\"\n  [class.-mobile]=\"mobile\"\n  [class.-open]=\"mobilePanelOpen\"\n>\n  <ul class=\"cds-nav-items\" [class.-horizontal]=\"horizontal\" #container>\n    <li\n      *ngFor=\"let item of displayedItems.$main | async\"\n      class=\"cds-nav-item\"\n      [class.-selected]=\"item.isSelected\"\n      (click)=\"setMobileNavLabel()\"\n    >\n      <ng-container *ngIf=\"item.type !== 'NavSubmenu'\">\n        <ng-container *ngTemplateOutlet=\"item.template\"></ng-container>\n      </ng-container>\n\n      <ng-container *ngIf=\"item.type === 'NavSubmenu'\">\n        <ng-container *ngIf=\"(!sidePanel && !horizontal) || (sidePanel && sidePanel.expanded)\">\n          <cds-nav-submenu-accordion\n            [name]=\"item.name\"\n            [isSelected]=\"item.isSelected\"\n            [iconName]=\"item.iconName\"\n            [label]=\"item.label\"\n            [(expanded)]=\"item.expanded\"\n          >\n            <ng-template [ngTemplateOutlet]=\"item.template\"></ng-template>\n          </cds-nav-submenu-accordion>\n        </ng-container>\n\n        <ng-container *ngIf=\"(!sidePanel && horizontal) || (sidePanel && !sidePanel.expanded)\">\n          <cds-nav-submenu-dropdown\n            [adjacent]=\"!horizontal\"\n            [name]=\"item.name\"\n            [isSelected]=\"item.isSelected\"\n            [iconName]=\"item.iconName\"\n            [label]=\"item.label\"\n            [(expanded)]=\"item.expanded\"\n          >\n            <ng-template [ngTemplateOutlet]=\"item.template\"></ng-template>\n          </cds-nav-submenu-dropdown>\n        </ng-container>\n      </ng-container>\n    </li>\n\n    <li class=\"cds-nav-item\" *ngIf=\"horizontal && showMoreSubmenu\">\n      <cds-nav-submenu\n        name='cds-navigation-more-submenu'\n        #moreSubmenu>\n        <span>More</span>\n      </cds-nav-submenu>\n\n      <cds-nav-submenu-dropdown\n        [adjacent]=\"!horizontal\"\n        (select)=\"onOverflowItemSelected()\"\n        [name]=\"moreSubmenu.name\"\n        [isSelected]=\"moreSubmenu.isSelected\"\n        [iconName]=\"moreSubmenu.iconName\"\n        [label]=\"moreSubmenu.label\"\n        [(expanded)]=\"moreSubmenu.expanded\"\n        #moreSubmenuDropdown>\n        <ng-template [ngTemplateOutlet]=\"moreSubmenu.template\"></ng-template>\n      </cds-nav-submenu-dropdown>\n      \n    </li>\n  </ul>\n  <cds-overlay\n    class=\"mobile-nav-overlay\"\n    *ngIf=\"mobile && mobilePanelOpen\"\n    aria-hidden=\"true\"\n  >\n  </cds-overlay>\n</nav>\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: ["name", "size", "disabled", "tabindex", "active", "attributes"] }, { kind: "component", type: CdsOverlayComponent, selector: "cds-overlay" }, { kind: "directive", type: i1$3.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: CdsNavigationSubMenuComponent, selector: "cds-nav-submenu", inputs: ["name", "iconName"] }, { kind: "component", type: CdsNavSubmenuDropdownComponent, selector: "cds-nav-submenu-dropdown", inputs: ["name", "isSelected", "iconName", "label", "expanded", "adjacent"], outputs: ["select", "expanded"] }, { kind: "component", type: CdsNavSubmenuAccordionComponent, selector: "cds-nav-submenu-accordion", inputs: ["isSelected", "iconName", "label", "name", "expanded"], outputs: ["expanded"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-navigation:not([items=*])', providers: [CdsOverflowObserverPresenter, CdsHorizontalNavigationPresenter, ClickAwayPresenter], template: "\n<div *ngIf=\"mobile\" \n  class=\"cds-mobile-nav-button\"\n  [class.-active]=\"!!mobileNavItem.label\">\n  <cds-control-block\n    size=\"small\"\n    (click)=\"toggleMobileNav()\">\n    <i class=\"material-icons\">menu</i>\n  </cds-control-block>\n  <a \n    *ngIf=\"mobileNavItem.label\"\n    role=\"link\" \n    class=\"cds-mobile-nav-link cds-button -tertiary\" \n    tabindex=\"0\" \n    [routerLink]=\"mobileNavItem?.url\" \n    [attr.aria-label]=\"mobileNavItem?.label\"\n    #atom\n  >\n    {{ mobileNavItem.label }}\n  </a>\n  \n</div>\n\n<nav\n  class=\"cds-side-nav_navigation\"\n  [id]=\"'cds-navigation-' + name | kebabCase\"\n  [class.-mobile]=\"mobile\"\n  [class.-open]=\"mobilePanelOpen\"\n>\n  <ul class=\"cds-nav-items\" [class.-horizontal]=\"horizontal\" #container>\n    <li\n      *ngFor=\"let item of displayedItems.$main | async\"\n      class=\"cds-nav-item\"\n      [class.-selected]=\"item.isSelected\"\n      (click)=\"setMobileNavLabel()\"\n    >\n      <ng-container *ngIf=\"item.type !== 'NavSubmenu'\">\n        <ng-container *ngTemplateOutlet=\"item.template\"></ng-container>\n      </ng-container>\n\n      <ng-container *ngIf=\"item.type === 'NavSubmenu'\">\n        <ng-container *ngIf=\"(!sidePanel && !horizontal) || (sidePanel && sidePanel.expanded)\">\n          <cds-nav-submenu-accordion\n            [name]=\"item.name\"\n            [isSelected]=\"item.isSelected\"\n            [iconName]=\"item.iconName\"\n            [label]=\"item.label\"\n            [(expanded)]=\"item.expanded\"\n          >\n            <ng-template [ngTemplateOutlet]=\"item.template\"></ng-template>\n          </cds-nav-submenu-accordion>\n        </ng-container>\n\n        <ng-container *ngIf=\"(!sidePanel && horizontal) || (sidePanel && !sidePanel.expanded)\">\n          <cds-nav-submenu-dropdown\n            [adjacent]=\"!horizontal\"\n            [name]=\"item.name\"\n            [isSelected]=\"item.isSelected\"\n            [iconName]=\"item.iconName\"\n            [label]=\"item.label\"\n            [(expanded)]=\"item.expanded\"\n          >\n            <ng-template [ngTemplateOutlet]=\"item.template\"></ng-template>\n          </cds-nav-submenu-dropdown>\n        </ng-container>\n      </ng-container>\n    </li>\n\n    <li class=\"cds-nav-item\" *ngIf=\"horizontal && showMoreSubmenu\">\n      <cds-nav-submenu\n        name='cds-navigation-more-submenu'\n        #moreSubmenu>\n        <span>More</span>\n      </cds-nav-submenu>\n\n      <cds-nav-submenu-dropdown\n        [adjacent]=\"!horizontal\"\n        (select)=\"onOverflowItemSelected()\"\n        [name]=\"moreSubmenu.name\"\n        [isSelected]=\"moreSubmenu.isSelected\"\n        [iconName]=\"moreSubmenu.iconName\"\n        [label]=\"moreSubmenu.label\"\n        [(expanded)]=\"moreSubmenu.expanded\"\n        #moreSubmenuDropdown>\n        <ng-template [ngTemplateOutlet]=\"moreSubmenu.template\"></ng-template>\n      </cds-nav-submenu-dropdown>\n      \n    </li>\n  </ul>\n  <cds-overlay\n    class=\"mobile-nav-overlay\"\n    *ngIf=\"mobile && mobilePanelOpen\"\n    aria-hidden=\"true\"\n  >\n  </cds-overlay>\n</nav>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: CdsHorizontalNavigationPresenter }, { type: ClickAwayPresenter }, { type: CdsSidePanelComponent, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { name: [{
                type: Input
            }], horizontal: [{
                type: Input
            }], mobile: [{
                type: Input
            }], navItems: [{
                type: ContentChildren,
                args: [CdsNavigationItemType]
            }], navContainerElement: [{
                type: ViewChild,
                args: ['container', { read: ElementRef, static: true }]
            }], moreSubmenu: [{
                type: ViewChild,
                args: ['moreSubmenu']
            }], moreSubmenuDropdown: [{
                type: ViewChild,
                args: ['moreSubmenuDropdown']
            }] } });

/**
 * ### Description
 * An item within a navigation bar.
 */
class CdsNavigationItemComponent extends CdsNavigationItemType {
    /**
     * @internal
     */
    constructor(element, wrappedLink, router) {
        super();
        this.element = element;
        this.wrappedLink = wrappedLink;
        this.router = router;
        /**
         * @internal
         */
        this.type = 'NavItem';
        /**
         * @internal
         */
        this.highlightable = new CdsHighlightableComponent();
        /**
         * @internal
         */
        this.navLinkClass = true;
    }
    /**
     * @internal
     */
    get template() {
        return this.wrappedLink.template;
    }
    /**
     * @internal
     */
    get _routerLink() {
        return typeof this.routerLink === 'string'
            ? [this.routerLink]
            : this.routerLink;
    }
    /**
     * @internal
     */
    get isSelected() {
        return (this.selected ||
            (this.routerLink &&
                this.router.isActive(this.router.createUrlTree(this._routerLink, { queryParams: this.queryParams }), false)));
    }
    /**
     * @internal
     */
    get attrIconName() {
        return this.iconName;
    }
    /**
     * @internal
     */
    get withIcon() {
        return !!this.iconName;
    }
    /**
     * @internal
     */
    get id() {
        return kebabCaseTransform('cds-nav-item-' + this.name);
    }
    /**
     * @internal
     */
    triggerClick() {
        this.$click.next();
    }
    /**
     * @internal
     */
    addHighlightedClass() {
        this.highlightable.addHighlightedClass(this.element.nativeElement);
    }
    /**
     * @internal
     */
    removeHighlightedClass() {
        this.highlightable.removeHighlightedClass(this.element.nativeElement);
    }
}
CdsNavigationItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationItemComponent, deps: [{ token: i0.ElementRef }, { token: CdsWrappedComponent }, { token: i1$3.Router, optional: true }], target: i0.ɵɵFactoryTarget.Component });
CdsNavigationItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsNavigationItemComponent, selector: "a[cdsNavItem]", inputs: { selected: "selected", name: "name", iconName: "iconName", label: "label", routerLink: "routerLink", queryParams: "queryParams" }, host: { listeners: { "click": "triggerClick()", "keyup.tab": "addHighlightedClass()", "keyup.shift.tab": "addHighlightedClass()", "focusout": "removeHighlightedClass()" }, properties: { "attr.icon-name": "this.attrIconName", "class.cds-nav-link": "this.navLinkClass", "class.-with-icon": "this.withIcon", "id": "this.id" } }, providers: [
        CdsWrappedComponent,
        { provide: CdsNavigationItemType, useExisting: forwardRef(() => CdsNavigationItemComponent) }
    ], usesInheritance: true, ngImport: i0, template: "<i class=\"material-icons\" *ngIf=\"iconName\">{{ iconName }}</i>\n<ng-content></ng-content>", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'a[cdsNavItem]', providers: [
                        CdsWrappedComponent,
                        { provide: CdsNavigationItemType, useExisting: forwardRef(() => CdsNavigationItemComponent) }
                    ], template: "<i class=\"material-icons\" *ngIf=\"iconName\">{{ iconName }}</i>\n<ng-content></ng-content>" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: CdsWrappedComponent }, { type: i1$3.Router, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { selected: [{
                type: Input
            }], name: [{
                type: Input
            }], iconName: [{
                type: Input
            }], label: [{
                type: Input
            }], routerLink: [{
                type: Input
            }], queryParams: [{
                type: Input
            }], attrIconName: [{
                type: HostBinding,
                args: ['attr.icon-name']
            }], navLinkClass: [{
                type: HostBinding,
                args: ['class.cds-nav-link']
            }], withIcon: [{
                type: HostBinding,
                args: ['class.-with-icon']
            }], id: [{
                type: HostBinding,
                args: ['id']
            }], triggerClick: [{
                type: HostListener,
                args: ['click']
            }], addHighlightedClass: [{
                type: HostListener,
                args: ['keyup.tab']
            }, {
                type: HostListener,
                args: ['keyup.shift.tab']
            }], removeHighlightedClass: [{
                type: HostListener,
                args: ['focusout']
            }] } });

/**
 * ### Description
 * Recieves and displays an array of [Navigation Items](?path=/story/types-navigation-item--page)
 * inside a [CdsNavigationComponent](?path=/story/components-navigation-overview--page).
 */
class CdsNavigationBuilderComponent {
    /**
     * @internal
     */
    constructor(element) {
        this.element = element;
    }
    /**
     * @internal
     */
    ngOnInit() {
        if (this.element.nativeElement.tagName === 'CDS-NAVIGATION' && this.items) {
            console.warn(`The use of [items] with '<cds-navigation>' has been deprecated. Please use content projection
        (i.e. '<cds-navigation><cds-nav-item></cds-nav-item><cds-navigation>') instead.
      `);
        }
    }
}
CdsNavigationBuilderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationBuilderComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
CdsNavigationBuilderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsNavigationBuilderComponent, selector: "cds-navigation-builder,cds-navigation[items=*]", inputs: { name: "name", items: "items", horizontal: "horizontal", mobile: "mobile" }, viewQueries: [{ propertyName: "navigation", first: true, predicate: CdsNavigationComponent, descendants: true, static: true }], ngImport: i0, template: "<cds-navigation [name]=\"name\" [mobile]=\"mobile\" [horizontal]=\"horizontal\">\n  <ng-container *ngFor=\"let item of items\" ngProjectAs=\"[cdsNavItem]\">\n    <cds-nav-submenu\n      *ngIf=\"item.children\"\n      [name]=\"item.title\"\n    >\n      <i class=\"material-icons\" *ngIf=\"item.iconName\">{{ item.iconName }}</i>\n      <span>{{ item.title }}</span>\n      <a\n        cdsNavItem\n        *ngFor=\"let submenuItem of item.children\"\n        [name]=\"submenuItem.title\"\n        [label]=\"submenuItem.title\"\n        [routerLink]=\"item.url + '/' + submenuItem.url\"\n      >\n        <i class=\"material-icons\" *ngIf=\"submenuItem.iconName\">{{ submenuItem.iconName }}</i>\n        <span>{{ submenuItem.title }}</span>\n      </a>\n    </cds-nav-submenu>\n    <a\n      cdsNavItem\n      *ngIf=\"!item.children\"\n      [name]=\"item.title\"\n      [label]=\"item.title\"\n      [routerLink]=\"item.url\"\n    >\n      <i class=\"material-icons\" *ngIf=\"item.iconName\">{{ item.iconName }}</i>\n      <span>{{ item.title }}</span>\n    </a>\n  </ng-container>\n</cds-navigation>\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$3.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: CdsNavigationComponent, selector: "cds-navigation:not([items=*])", inputs: ["name", "horizontal", "mobile"] }, { kind: "component", type: CdsNavigationItemComponent, selector: "a[cdsNavItem]", inputs: ["selected", "name", "iconName", "label", "routerLink", "queryParams"] }, { kind: "component", type: CdsNavigationSubMenuComponent, selector: "cds-nav-submenu", inputs: ["name", "iconName"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationBuilderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-navigation-builder,cds-navigation[items=*]', template: "<cds-navigation [name]=\"name\" [mobile]=\"mobile\" [horizontal]=\"horizontal\">\n  <ng-container *ngFor=\"let item of items\" ngProjectAs=\"[cdsNavItem]\">\n    <cds-nav-submenu\n      *ngIf=\"item.children\"\n      [name]=\"item.title\"\n    >\n      <i class=\"material-icons\" *ngIf=\"item.iconName\">{{ item.iconName }}</i>\n      <span>{{ item.title }}</span>\n      <a\n        cdsNavItem\n        *ngFor=\"let submenuItem of item.children\"\n        [name]=\"submenuItem.title\"\n        [label]=\"submenuItem.title\"\n        [routerLink]=\"item.url + '/' + submenuItem.url\"\n      >\n        <i class=\"material-icons\" *ngIf=\"submenuItem.iconName\">{{ submenuItem.iconName }}</i>\n        <span>{{ submenuItem.title }}</span>\n      </a>\n    </cds-nav-submenu>\n    <a\n      cdsNavItem\n      *ngIf=\"!item.children\"\n      [name]=\"item.title\"\n      [label]=\"item.title\"\n      [routerLink]=\"item.url\"\n    >\n      <i class=\"material-icons\" *ngIf=\"item.iconName\">{{ item.iconName }}</i>\n      <span>{{ item.title }}</span>\n    </a>\n  </ng-container>\n</cds-navigation>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { name: [{
                type: Input
            }], items: [{
                type: Input
            }], horizontal: [{
                type: Input
            }], mobile: [{
                type: Input
            }], navigation: [{
                type: ViewChild,
                args: [CdsNavigationComponent, { static: true }]
            }] } });

// @TODO - Remove this directive as well as iconName properties Nav Submenu and Nav Item in 1.0
class CdsNavItemIconDirective {
    constructor(element) {
        this.element = element;
    }
    /**
     * @internal
     */
    ngOnInit() {
        if (this.iconName) {
            console.warn(`The use of [iconName] with '<a cdsNavItem>' has been deprecated. Please use content projection
        (i.e. '<a cdsNavItem><i class="material-icons"></i><a>') instead.
      `);
        }
    }
}
CdsNavItemIconDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavItemIconDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsNavItemIconDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsNavItemIconDirective, selector: "[cdsNavItem][iconName]", inputs: { iconName: "iconName" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavItemIconDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsNavItem][iconName]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { iconName: [{
                type: Input
            }] } });

class CdsNavigationModule {
}
CdsNavigationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsNavigationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationModule, declarations: [CdsNavigationComponent,
        CdsNavigationItemComponent,
        CdsNavigationSubMenuComponent,
        CdsNavigationBuilderComponent,
        CdsNavSubmenuDropdownComponent,
        CdsNavSubmenuAccordionComponent,
        CdsNavItemIconDirective], imports: [CommonModule,
        CdsPipesModule,
        CdsDirectivesModule,
        CdsControlBlockModule,
        CdsOverlayModule,
        RouterModule,
        CdsHostWrapperModule], exports: [CdsNavigationComponent,
        CdsNavigationItemComponent,
        CdsNavigationSubMenuComponent,
        CdsNavigationBuilderComponent,
        CdsNavItemIconDirective] });
CdsNavigationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationModule, imports: [CommonModule,
        CdsPipesModule,
        CdsDirectivesModule,
        CdsControlBlockModule,
        CdsOverlayModule,
        RouterModule,
        CdsHostWrapperModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsNavigationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsNavigationComponent,
                        CdsNavigationItemComponent,
                        CdsNavigationSubMenuComponent,
                        CdsNavigationBuilderComponent,
                        CdsNavSubmenuDropdownComponent,
                        CdsNavSubmenuAccordionComponent,
                        CdsNavItemIconDirective
                    ],
                    exports: [
                        CdsNavigationComponent,
                        CdsNavigationItemComponent,
                        CdsNavigationSubMenuComponent,
                        CdsNavigationBuilderComponent,
                        CdsNavItemIconDirective
                    ],
                    imports: [
                        CommonModule,
                        CdsPipesModule,
                        CdsDirectivesModule,
                        CdsControlBlockModule,
                        CdsOverlayModule,
                        RouterModule,
                        CdsHostWrapperModule
                    ]
                }]
        }] });

/**
 * ### Description
 * An object representing a Navigation Item to be displayed in a Navigation component (Navigation Builder, Breadcrumbs etc).
 */
class CdsNavigationItem {
}

/**
 * ### Description
 * Displays a banner. The header is divided into left, right and center sections for convenience.
 */
class CdsPageHeaderComponent {
    /**
     * @internal
     */
    ngAfterViewInit() {
        const navigationComponent = this.navigation || (this.navigationBuilder && this.navigationBuilder.navigation);
        if (navigationComponent) {
            navigationComponent.overflowContainerElement = this.headerElementRef.nativeElement;
            navigationComponent.presenter.reset(navigationComponent.overflowContainerElement, navigationComponent);
        }
    }
}
CdsPageHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPageHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsPageHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsPageHeaderComponent, selector: "cds-page-header", inputs: { name: "name" }, queries: [{ propertyName: "navigation", first: true, predicate: CdsNavigationComponent, descendants: true }, { propertyName: "navigationBuilder", first: true, predicate: CdsNavigationBuilderComponent, descendants: true }], viewQueries: [{ propertyName: "headerElementRef", first: true, predicate: ["header"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"cds-page-header\" #header>\n  <div class=\"cds-page-header-prefix\">\n    <ng-content select=\"[cdsPageHeaderPrefix]\"></ng-content>\n  </div>\n  <div class=\"cds-page-header-navigation\">\n    <ng-content select=\"[cdsPageHeaderNavigation]\"></ng-content>\n  </div>\n  <div class=\"cds-page-header-search\">\n    <ng-content select=\"[cdsPageHeaderSearch]\"></ng-content>\n  </div>\n  <div class=\"cds-page-header-suffix\">\n    <ng-content select=\"[cdsPageHeaderSuffix]\"></ng-content>\n  </div>\n</div>\n", styles: [":host{display:block}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPageHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-page-header', template: "<div class=\"cds-page-header\" #header>\n  <div class=\"cds-page-header-prefix\">\n    <ng-content select=\"[cdsPageHeaderPrefix]\"></ng-content>\n  </div>\n  <div class=\"cds-page-header-navigation\">\n    <ng-content select=\"[cdsPageHeaderNavigation]\"></ng-content>\n  </div>\n  <div class=\"cds-page-header-search\">\n    <ng-content select=\"[cdsPageHeaderSearch]\"></ng-content>\n  </div>\n  <div class=\"cds-page-header-suffix\">\n    <ng-content select=\"[cdsPageHeaderSuffix]\"></ng-content>\n  </div>\n</div>\n", styles: [":host{display:block}\n"] }]
        }], propDecorators: { name: [{
                type: Input
            }], headerElementRef: [{
                type: ViewChild,
                args: ['header', { static: true }]
            }], navigation: [{
                type: ContentChild,
                args: [CdsNavigationComponent]
            }], navigationBuilder: [{
                type: ContentChild,
                args: [CdsNavigationBuilderComponent]
            }] } });

class CdsPageHeaderModule {
}
CdsPageHeaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPageHeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsPageHeaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsPageHeaderModule, declarations: [CdsPageHeaderComponent], imports: [CommonModule, CdsPipesModule], exports: [CdsPageHeaderComponent] });
CdsPageHeaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPageHeaderModule, imports: [CommonModule, CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPageHeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsPageHeaderComponent],
                    exports: [CdsPageHeaderComponent],
                    imports: [CommonModule, CdsPipesModule],
                }]
        }] });

/**
 * ### Description
 * Provides the overall layout of a web application. Contains sections for the following:
 * - A header
 * - One left drawer
 * - One right drawer
 * - The main page content
 */
class CdsPageLayoutComponent {
    /**
     * @internal
     */
    constructor() { }
    /**
     * @internal
     */
    ngOnInit() { }
}
CdsPageLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPageLayoutComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsPageLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsPageLayoutComponent, selector: "cds-page-layout", inputs: { name: "name" }, ngImport: i0, template: "<div class=\"cds-main-page-layout\">\n  <div class=\"cds-page-layout-header\">\n    <ng-content select=\"[cdsPageHeader]\"></ng-content>\n  </div>\n\n  <div class=\"cds-page-layout-content\">\n    <ng-content select=\"[cdsPageContent]\"></ng-content>\n  </div>\n\n  <div class=\"cds-page-layout-left-nav\">\n    <ng-content select=\"[cdsPageDrawer=left]\"></ng-content>\n  </div>\n\n  <div class=\"cds-page-layout-right-nav\">\n    <ng-content select=\"[cdsPageDrawer=right]\"></ng-content>\n  </div>\n</div>\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPageLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-page-layout', template: "<div class=\"cds-main-page-layout\">\n  <div class=\"cds-page-layout-header\">\n    <ng-content select=\"[cdsPageHeader]\"></ng-content>\n  </div>\n\n  <div class=\"cds-page-layout-content\">\n    <ng-content select=\"[cdsPageContent]\"></ng-content>\n  </div>\n\n  <div class=\"cds-page-layout-left-nav\">\n    <ng-content select=\"[cdsPageDrawer=left]\"></ng-content>\n  </div>\n\n  <div class=\"cds-page-layout-right-nav\">\n    <ng-content select=\"[cdsPageDrawer=right]\"></ng-content>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { name: [{
                type: Input
            }] } });

class CdsPageLayoutModule {
}
CdsPageLayoutModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPageLayoutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsPageLayoutModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsPageLayoutModule, declarations: [CdsPageLayoutComponent], imports: [CommonModule, CdsPipesModule], exports: [CdsPageLayoutComponent] });
CdsPageLayoutModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPageLayoutModule, imports: [CommonModule, CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPageLayoutModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsPageLayoutComponent],
                    exports: [CdsPageLayoutComponent],
                    imports: [CommonModule, CdsPipesModule],
                }]
        }] });

/**
 * Displays additional contextual information about some wrapped content. Should be used for interactive content such as buttons and links;
 * in cases where it is not appropriate to display this content in a Tooltip.
 *
 * The popover generated by this component is responsive and will position itself according its anchor (triggering) element's
 * position in the page.
 */
class CdsPopoverComponent {
    constructor() {
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        /**
         * @internal
         */
        this.dropdownOptions = {};
        /**
         * @internal
         */
        this._display = 'contents';
        /**
         * Emits a notification when the popover is closed.
         */
        this.close = new EventEmitter();
    }
    /**
     * @internal
     */
    get _closeButton() {
        return this.closeButton === undefined || this.closeButton === null
            ? true
            : this.closeButton;
    }
    /**
     * Opens the Popover.
     */
    expand(anchor, wasFocused) {
        let strategy = null;
        if (this.position) {
            strategy = new DirectionalPositionStrategy(this.position, {});
        }
        this.wasFocused = wasFocused;
        this.dropdown.expand(anchor, null, strategy);
    }
    /**
     * Closes the Popover.
     */
    collapse() {
        this.dropdown.collapse();
    }
    /**
     * @internal
     */
    onClose() {
        this.collapse();
        this.close.next();
    }
    /**
     * @internal
     */
    wasFocused() { }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsPopoverComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPopoverComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsPopoverComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsPopoverComponent, selector: "cds-popover", inputs: { name: "name", position: "position", closeButton: "closeButton", preventCloseOnBlur: "preventCloseOnBlur" }, outputs: { close: "close" }, host: { properties: { "style.display": "this._display" } }, viewQueries: [{ propertyName: "dropdown", first: true, predicate: CdsDropdownDirective, descendants: true, static: true }], ngImport: i0, template: "<div\n  #content\n  cdsDropdownPanel\n  [id]=\"name\"\n  [class.-open]=\"dropdown.expanded\"\n>\n  <div class=\"cds-popover\">\n    <ng-content></ng-content>\n    <cds-control-block\n      size=\"small\"\n      class=\"popover-close\"\n      (click)=\"onClose()\"\n      *ngIf=\"_closeButton\"\n    >\n      <i class=\"material-icons\">close</i>\n    </cds-control-block>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  [panel]=\"content\"\n  [wasFocusedFn]=\"wasFocused\"\n  [options]=\"dropdownOptions\"\n  (removeFocus)=\"!this.preventCloseOnBlur && onClose()\">\n</ng-container>", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: CdsDropdownDirective, selector: "[cdsDropdown]", inputs: ["anchor", "panel", "options", "strategy", "wasFocusedFn"], outputs: ["removeFocus", "change"] }, { kind: "directive", type: CdsDropdownPanelDirective, selector: "[cdsDropdownPanel]", exportAs: ["dropdownPanel"] }, { kind: "component", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: ["name", "size", "disabled", "tabindex", "active", "attributes"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-popover', template: "<div\n  #content\n  cdsDropdownPanel\n  [id]=\"name\"\n  [class.-open]=\"dropdown.expanded\"\n>\n  <div class=\"cds-popover\">\n    <ng-content></ng-content>\n    <cds-control-block\n      size=\"small\"\n      class=\"popover-close\"\n      (click)=\"onClose()\"\n      *ngIf=\"_closeButton\"\n    >\n      <i class=\"material-icons\">close</i>\n    </cds-control-block>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  [panel]=\"content\"\n  [wasFocusedFn]=\"wasFocused\"\n  [options]=\"dropdownOptions\"\n  (removeFocus)=\"!this.preventCloseOnBlur && onClose()\">\n</ng-container>" }]
        }], propDecorators: { _display: [{
                type: HostBinding,
                args: ['style.display']
            }], name: [{
                type: Input
            }], position: [{
                type: Input
            }], closeButton: [{
                type: Input
            }], preventCloseOnBlur: [{
                type: Input
            }], close: [{
                type: Output
            }], dropdown: [{
                type: ViewChild,
                args: [CdsDropdownDirective, { static: true }]
            }] } });

class CdsContextMenuTriggerDirective {
    /**
     * @internal
     */
    constructor(element) {
        this.element = element;
    }
    onClick(event) {
        event.preventDefault();
        // @todo: refactor
        // If context menus are opened sequentially, we want to give the clickAwayListener a chance to close the
        // previous context menu before opening a new one.
        setTimeout(() => {
            this.popover.expand({ x: event.clientX, y: event.clientY });
        });
    }
    collapse() {
        this.popover.collapse();
    }
    /**
     * @internal
     */
    ngOnInit() {
        this.popover.dropdownOptions = { scrollableY: false, preventBackgroundScroll: true };
    }
}
CdsContextMenuTriggerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsContextMenuTriggerDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsContextMenuTriggerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsContextMenuTriggerDirective, selector: "[cdsContextMenuTrigger]", inputs: { popover: "popover" }, host: { listeners: { "contextmenu": "onClick($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsContextMenuTriggerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsContextMenuTrigger]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { popover: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['contextmenu', ['$event']]
            }] } });

class CdsPopoverTriggerDirective {
    /**
     * @internal
     */
    constructor(element) {
        this.element = element;
    }
    /**
     * @internal
     */
    open() {
        this.popover.expand(this.element.nativeElement, this.wasFocused);
    }
    /**
     * @internal
     */
    close() {
        this.popover.collapse();
    }
    /**
     * @internal
     */
    wasFocused() {
        if (this.element.nativeElement) {
            this.element.nativeElement.focus();
        }
    }
}
CdsPopoverTriggerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPopoverTriggerDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsPopoverTriggerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsPopoverTriggerDirective, selector: "[cdsPopoverTrigger]", inputs: { popover: "popover" }, host: { listeners: { "keydown.space": "open()", "keyup.shift.tab": "open()", "click": "open()", "keydown.tab": "close()" } }, exportAs: ["cdsPopoverTrigger"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPopoverTriggerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsPopoverTrigger]',
                    exportAs: 'cdsPopoverTrigger'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { popover: [{
                type: Input
            }], open: [{
                type: HostListener,
                args: ['keydown.space']
            }, {
                type: HostListener,
                args: ['keyup.shift.tab']
            }, {
                type: HostListener,
                args: ['click']
            }], close: [{
                type: HostListener,
                args: ['keydown.tab']
            }] } });

class CdsPopoverModule {
}
CdsPopoverModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsPopoverModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsPopoverModule, declarations: [CdsPopoverComponent, CdsPopoverTriggerDirective, CdsContextMenuTriggerDirective], imports: [CommonModule, CdsPipesModule, CdsDirectivesModule, CdsControlBlockModule], exports: [CdsPopoverComponent, CdsPopoverTriggerDirective, CdsContextMenuTriggerDirective] });
CdsPopoverModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPopoverModule, imports: [CommonModule, CdsPipesModule, CdsDirectivesModule, CdsControlBlockModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsPopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsPopoverComponent, CdsPopoverTriggerDirective, CdsContextMenuTriggerDirective],
                    exports: [CdsPopoverComponent, CdsPopoverTriggerDirective, CdsContextMenuTriggerDirective],
                    imports: [CommonModule, CdsPipesModule, CdsDirectivesModule, CdsControlBlockModule],
                }]
        }] });

/**
 * ### Description
 * A component for indicating progress.
 */
class CdsBarComponent {
    /**
     * @internal
     */
    constructor(renderer, changeDetector) {
        this.renderer = renderer;
        this.changeDetector = changeDetector;
        /**
         * A unique identifier for the Bar.
         */
        this.name = '';
        /**
         * Sets the height of the Vertical bar/width of the Horizontal Bar to predefined values.
         */
        this.size = 'default';
        /**
         * Sets the orientation of bar.
         */
        this.orientation = 'horizontal';
    }
    /**
     * @internal
     */
    set value(value) {
        this.progressValue = value;
        this.calculateLabelPosition();
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.calculateStyles();
    }
    /**
     * @internal
     */
    ngOnChanges(changes) {
        if (!this.bar && !this.indicator) {
            return;
        }
        this.calculateStyles();
    }
    /**
     * @internal
     */
    calculateStyles() {
        this.setBarStyle();
        this.setBarIndicatorStyle();
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    get id() {
        return 'cds-bar-' + kebabCaseTransform(this.name);
    }
    /**
     * @internal
     */
    setBarStyle() {
        this.renderer.setStyle(this.bar.nativeElement, '--color-ui-03', this.pathColor || null, 2);
    }
    /**
     * @internal
     */
    setBarIndicatorStyle() {
        this.renderer.setStyle(this.indicator.nativeElement, '--color-interactive-01', this.indicatorColor || null, 2);
        this.renderer.setStyle(this.indicator.nativeElement, '--labelColor', this.labelColor || null, 2);
        if (this.orientation === 'vertical') {
            this.renderer.removeStyle(this.indicator.nativeElement, 'width');
            this.renderer.setStyle(this.indicator.nativeElement, 'height', `${this.progressValue}%`);
        }
        else {
            this.renderer.removeStyle(this.indicator.nativeElement, 'height');
            this.renderer.setStyle(this.indicator.nativeElement, 'width', `${this.progressValue}%`);
        }
        if (this.showLabel) {
            this.renderer.setStyle(this.indicator.nativeElement, '--progressLabel', `'${this.progressValue}%'`, 2);
            this.calculateLabelPosition();
        }
    }
    /**
     * @internal
     */
    calculateLabelPosition() {
        const indicatorWidth = this.indicator.nativeElement.clientWidth;
        const labelWidth = parseInt(getComputedStyle(this.indicator.nativeElement, ':before').width, 10);
        if (indicatorWidth < labelWidth + 24) {
            this.renderer.setStyle(this.indicator.nativeElement, '--labelPosition', `${(100 - this.progressValue) - 4}%`, 2);
        }
        else {
            this.renderer.setStyle(this.indicator.nativeElement, '--labelPosition', `${(100 - this.progressValue) + 2}%`, 2);
        }
    }
}
CdsBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBarComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsBarComponent, selector: "cds-bar", inputs: { value: "value", name: "name", description: "description", size: "size", indicatorColor: "indicatorColor", pathColor: "pathColor", roundedCorners: "roundedCorners", orientation: "orientation", progressState: "progressState", showLabel: "showLabel", labelColor: "labelColor" }, viewQueries: [{ propertyName: "indicator", first: true, predicate: ["barIndicator"], descendants: true, static: true }, { propertyName: "bar", first: true, predicate: ["bar"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"cds-bar\"\n  [class.-small]=\"size === 'small'\"\n  [class.-vertical]=\"orientation === 'vertical'\"\n  [class.-horizontal]=\"!orientation || orientation === 'horizontal'\"\n  [class.-rounded-corners]=\"roundedCorners\"\n  #bar>\n    <div class=\"cds-bar-indicator\"\n      [class.-indeterminate]=\"progressState === 'indeterminate'\"\n      [class.-with-label]=\"showLabel === true\"\n      role=\"progressbar\"\n      aria-valuemin=\"0\"\n      aria-valuemax=\"100\"\n      [attr.aria-valuenow]=\"progressValue\"\n      [attr.aria-describedby]=\"description ? id+'assistive-label' : null\"\n      #barIndicator>\n    </div>\n</div>\n\n<ng-container *ngIf=\"description\">\n    <div id=\"{{id}}assistive-label\" class=\"cds-assistive-text\">{{ description }}</div>\n</ng-container>\n", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-bar', template: "<div\n  class=\"cds-bar\"\n  [class.-small]=\"size === 'small'\"\n  [class.-vertical]=\"orientation === 'vertical'\"\n  [class.-horizontal]=\"!orientation || orientation === 'horizontal'\"\n  [class.-rounded-corners]=\"roundedCorners\"\n  #bar>\n    <div class=\"cds-bar-indicator\"\n      [class.-indeterminate]=\"progressState === 'indeterminate'\"\n      [class.-with-label]=\"showLabel === true\"\n      role=\"progressbar\"\n      aria-valuemin=\"0\"\n      aria-valuemax=\"100\"\n      [attr.aria-valuenow]=\"progressValue\"\n      [attr.aria-describedby]=\"description ? id+'assistive-label' : null\"\n      #barIndicator>\n    </div>\n</div>\n\n<ng-container *ngIf=\"description\">\n    <div id=\"{{id}}assistive-label\" class=\"cds-assistive-text\">{{ description }}</div>\n</ng-container>\n" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { value: [{
                type: Input
            }], name: [{
                type: Input
            }], description: [{
                type: Input
            }], size: [{
                type: Input
            }], indicatorColor: [{
                type: Input
            }], pathColor: [{
                type: Input
            }], roundedCorners: [{
                type: Input
            }], orientation: [{
                type: Input
            }], progressState: [{
                type: Input
            }], showLabel: [{
                type: Input
            }], labelColor: [{
                type: Input
            }], indicator: [{
                type: ViewChild,
                args: ['barIndicator', { static: true }]
            }], bar: [{
                type: ViewChild,
                args: ['bar', { static: true }]
            }] } });

/**
 * ### Description
 * A component for indicating progress.
 */
class CdsRadialProgressComponent {
    /**
     * @internal
     */
    constructor(renderer) {
        this.renderer = renderer;
        /**
         * A unique identifier for the Radial Bar.
         */
        this.name = '';
    }
    /**
     * @internal
     */
    set value(value) {
        this.progressValue = value;
        this.setProgress(value);
    }
    /**
     * @internal
     */
    ngOnChanges(changes) {
        if (!this.path && !this.indicator && !this.radialProgressbarLabel) {
            return;
        }
        this.calculateStyles();
    }
    /**
     * @internal
     */
    calculateStyles() {
        this.setRadialBarIndicatorStyle();
        this.setRadialBarPathStyle();
        if (this.showLabel && this.radialProgressbarLabel) {
            this.setLabelStyle();
        }
    }
    /**
     * @internal
     */
    setProgress(progressValue) {
        const radialProgressBarElement = this.indicator.nativeElement;
        const radius = radialProgressBarElement.r.baseVal.value;
        const circumference = radius * 2 * Math.PI;
        const offset = circumference - (progressValue / 100) * circumference;
        this.renderer.setStyle(radialProgressBarElement, 'strokeDasharray', `${circumference} ${circumference}`);
        this.renderer.setStyle(radialProgressBarElement, 'strokeDashoffset', offset.toString());
    }
    /**
     * @internal
     */
    get id() {
        return 'cds-radial-progress-' + kebabCaseTransform(this.name);
    }
    /**
     * @internal
     */
    setRadialBarIndicatorStyle() {
        this.renderer.setStyle(this.indicator.nativeElement, '--color-interactive-01', this.indicatorColor || null, 2);
    }
    /**
     * @internal
     */
    setRadialBarPathStyle() {
        this.renderer.setStyle(this.path.nativeElement, '--color-ui-03', this.pathColor || null, 2);
    }
    /**
     * @internal
     */
    setLabelStyle() {
        this.renderer.setStyle(this.radialProgressbarLabel.nativeElement, '--labelColor', this.labelColor || null, 2);
    }
}
CdsRadialProgressComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsRadialProgressComponent, deps: [{ token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
CdsRadialProgressComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsRadialProgressComponent, selector: "cds-radial-progress", inputs: { value: "value", name: "name", size: "size", indicatorColor: "indicatorColor", pathColor: "pathColor", labelColor: "labelColor", progressState: "progressState", description: "description", showLabel: "showLabel" }, viewQueries: [{ propertyName: "indicator", first: true, predicate: ["radialProgressbarIndicator"], descendants: true, static: true }, { propertyName: "path", first: true, predicate: ["radialProgressbarPath"], descendants: true, static: true }, { propertyName: "radialProgressbarLabel", first: true, predicate: ["radialProgressbarLabel"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"cds-radial-progress\" #radialProgressbarPath\n  [ngClass]=\"{\n    '-determinate': progressState === 'determinate',\n    '-indeterminate': progressState === 'indeterminate',\n    '-sm': size === 'small'\n  }\">\n\n  <svg class=\"cds-radial-bar\" viewBox=\"0 0 96 96\">\n    <circle cx=\"48\" cy=\"48\"></circle>\n    <circle\n      cx=\"48\"\n      cy=\"48\"\n      r=\"46\"\n      class=\"cds-radial-bar_progress\"\n      role=\"progressbar\"\n      aria-valuemin=\"0\"\n      aria-valuemax=\"100\"\n      [id]=\"id\"\n      [attr.aria-valuenow]=\"progressValue\"\n      [attr.aria-describedby]=\"description ? id+'assistive-label' : null\"\n      #radialProgressbarIndicator\n    ></circle>\n  </svg>\n  \n  <span class=\"cds-radial-bar_content\">\n    <ng-container *ngIf=\"showLabel\">\n      <span class=\"cds-radial-bar_content_label\" #radialProgressbarLabel>\n        {{ progressValue }}%\n      </span>\n    </ng-container>\n\n    <ng-container *ngIf=\"!showLabel\">\n      <ng-content></ng-content>\n    </ng-container>\n  </span>\n</div>\n\n<ng-container *ngIf=\"description\">\n  <div id=\"{{id}}assistive-label\" class=\"cds-assistive-text\">{{ description }}</div>\n</ng-container>", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsRadialProgressComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-radial-progress', template: "<div class=\"cds-radial-progress\" #radialProgressbarPath\n  [ngClass]=\"{\n    '-determinate': progressState === 'determinate',\n    '-indeterminate': progressState === 'indeterminate',\n    '-sm': size === 'small'\n  }\">\n\n  <svg class=\"cds-radial-bar\" viewBox=\"0 0 96 96\">\n    <circle cx=\"48\" cy=\"48\"></circle>\n    <circle\n      cx=\"48\"\n      cy=\"48\"\n      r=\"46\"\n      class=\"cds-radial-bar_progress\"\n      role=\"progressbar\"\n      aria-valuemin=\"0\"\n      aria-valuemax=\"100\"\n      [id]=\"id\"\n      [attr.aria-valuenow]=\"progressValue\"\n      [attr.aria-describedby]=\"description ? id+'assistive-label' : null\"\n      #radialProgressbarIndicator\n    ></circle>\n  </svg>\n  \n  <span class=\"cds-radial-bar_content\">\n    <ng-container *ngIf=\"showLabel\">\n      <span class=\"cds-radial-bar_content_label\" #radialProgressbarLabel>\n        {{ progressValue }}%\n      </span>\n    </ng-container>\n\n    <ng-container *ngIf=\"!showLabel\">\n      <ng-content></ng-content>\n    </ng-container>\n  </span>\n</div>\n\n<ng-container *ngIf=\"description\">\n  <div id=\"{{id}}assistive-label\" class=\"cds-assistive-text\">{{ description }}</div>\n</ng-container>" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }]; }, propDecorators: { value: [{
                type: Input
            }], name: [{
                type: Input
            }], size: [{
                type: Input
            }], indicatorColor: [{
                type: Input
            }], pathColor: [{
                type: Input
            }], labelColor: [{
                type: Input
            }], progressState: [{
                type: Input
            }], description: [{
                type: Input
            }], showLabel: [{
                type: Input
            }], indicator: [{
                type: ViewChild,
                args: ['radialProgressbarIndicator', { static: true }]
            }], path: [{
                type: ViewChild,
                args: ['radialProgressbarPath', { static: true }]
            }], radialProgressbarLabel: [{
                type: ViewChild,
                args: ['radialProgressbarLabel']
            }] } });

/**
 * ### Description
 * A component for indicating progress.
 */
class CdsProgressBarComponent {
    constructor() {
        /**
         * A unique identifier for the progress bar.
         */
        this.name = '';
        /**
         * Sets the color of progress bar indicator.
         */
        this.indicatorColor = '';
        /**
         * Sets the color of progress bar.
         */
        this.pathColor = '';
        /**
         * Sets the color of the inner label of progress bar.
         */
        this.labelColor = '';
    }
    /**
     * Sets the type of progress bar.
     */
    set _type(type) {
        this.type = type || 'bar';
    }
    /**
     * Sets the size of the progress bar.
     */
    set _size(size) {
        this.size = size || 'default';
    }
    /**
     * Sets the progress state of the progress bar.
     */
    set _progressState(state) {
        this.progressState = state || 'determinate';
    }
    /**
     * Sets the orientation of progress bar.
     */
    set _orientation(orientation) {
        this.orientation = orientation || 'horizontal';
    }
    /**
     * @internal
     */
    get isVerticalBar() {
        return this.type === 'bar' && this.orientation === 'vertical';
    }
    /**
     * @internal
     */
    get id() {
        return 'cds-progress-bar-' + kebabCaseTransform(this.name);
    }
}
CdsProgressBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsProgressBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsProgressBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsProgressBarComponent, selector: "cds-progress-bar", inputs: { value: "value", name: "name", indicatorColor: "indicatorColor", pathColor: "pathColor", roundedCorners: "roundedCorners", description: "description", showLabel: "showLabel", labelColor: "labelColor", _type: ["type", "_type"], _size: ["size", "_size"], _progressState: ["progressState", "_progressState"], _orientation: ["orientation", "_orientation"] }, ngImport: i0, template: "<div\n  class=\"cds-progress-bar\"\n  [class.-radial]=\"type === 'radialBar'\"\n  [class.-vertical]=\"isVerticalBar\"\n  [class.-small]=\"size === 'small'\">\n    <div class=\"top-content-box\">\n      <ng-content select=\"[cdsIndicatorLabel]\"></ng-content>\n    </div>\n\n    <ng-container [ngSwitch]=\"type\">\n\n        <!-- Bar -->\n        <ng-container *ngSwitchCase=\"'bar'\">\n            <cds-bar\n                [name]=\"name\"\n                [description]=\"description\"\n                [indicatorColor]=\"indicatorColor\"\n                [pathColor]=\"pathColor\"\n                [labelColor]=\"labelColor\"\n                [value]=\"value\"\n                [roundedCorners]=\"roundedCorners\"\n                [orientation]=\"orientation\"\n                [size]=\"size\"\n                [progressState]=\"progressState\"\n                [showLabel]=\"progressState !== 'indeterminate' && showLabel\">\n            </cds-bar>\n        </ng-container>\n        \n        <!-- Radial Progress Bar -->\n        <ng-container *ngSwitchCase=\"'radialBar'\">\n            <cds-radial-progress\n                [name]=\"name\"\n                [description]=\"description\"\n                [size]=\"size\"\n                [value]=\"value\"\n                [indicatorColor]=\"indicatorColor\"\n                [pathColor]=\"pathColor\"\n                [labelColor]=\"labelColor\"\n                [progressState]=\"progressState\"\n                [showLabel]=\"showLabel\">\n                    <ng-content select=\"[cdsRadialContent]\"></ng-content>\n            </cds-radial-progress>\n        </ng-container>\n    \n    </ng-container>\n\n    <div class=\"bottom-content-box\">\n        <ng-content select=\"[cdsIndicatorReadout]\"></ng-content>\n    </div>\n</div>", dependencies: [{ kind: "directive", type: i1$1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: CdsBarComponent, selector: "cds-bar", inputs: ["value", "name", "description", "size", "indicatorColor", "pathColor", "roundedCorners", "orientation", "progressState", "showLabel", "labelColor"] }, { kind: "component", type: CdsRadialProgressComponent, selector: "cds-radial-progress", inputs: ["value", "name", "size", "indicatorColor", "pathColor", "labelColor", "progressState", "description", "showLabel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsProgressBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-progress-bar', template: "<div\n  class=\"cds-progress-bar\"\n  [class.-radial]=\"type === 'radialBar'\"\n  [class.-vertical]=\"isVerticalBar\"\n  [class.-small]=\"size === 'small'\">\n    <div class=\"top-content-box\">\n      <ng-content select=\"[cdsIndicatorLabel]\"></ng-content>\n    </div>\n\n    <ng-container [ngSwitch]=\"type\">\n\n        <!-- Bar -->\n        <ng-container *ngSwitchCase=\"'bar'\">\n            <cds-bar\n                [name]=\"name\"\n                [description]=\"description\"\n                [indicatorColor]=\"indicatorColor\"\n                [pathColor]=\"pathColor\"\n                [labelColor]=\"labelColor\"\n                [value]=\"value\"\n                [roundedCorners]=\"roundedCorners\"\n                [orientation]=\"orientation\"\n                [size]=\"size\"\n                [progressState]=\"progressState\"\n                [showLabel]=\"progressState !== 'indeterminate' && showLabel\">\n            </cds-bar>\n        </ng-container>\n        \n        <!-- Radial Progress Bar -->\n        <ng-container *ngSwitchCase=\"'radialBar'\">\n            <cds-radial-progress\n                [name]=\"name\"\n                [description]=\"description\"\n                [size]=\"size\"\n                [value]=\"value\"\n                [indicatorColor]=\"indicatorColor\"\n                [pathColor]=\"pathColor\"\n                [labelColor]=\"labelColor\"\n                [progressState]=\"progressState\"\n                [showLabel]=\"showLabel\">\n                    <ng-content select=\"[cdsRadialContent]\"></ng-content>\n            </cds-radial-progress>\n        </ng-container>\n    \n    </ng-container>\n\n    <div class=\"bottom-content-box\">\n        <ng-content select=\"[cdsIndicatorReadout]\"></ng-content>\n    </div>\n</div>" }]
        }], propDecorators: { value: [{
                type: Input
            }], name: [{
                type: Input
            }], indicatorColor: [{
                type: Input
            }], pathColor: [{
                type: Input
            }], roundedCorners: [{
                type: Input
            }], description: [{
                type: Input
            }], showLabel: [{
                type: Input
            }], labelColor: [{
                type: Input
            }], _type: [{
                type: Input,
                args: ['type']
            }], _size: [{
                type: Input,
                args: ['size']
            }], _progressState: [{
                type: Input,
                args: ['progressState']
            }], _orientation: [{
                type: Input,
                args: ['orientation']
            }] } });

class CdsProgressBarModule {
}
CdsProgressBarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsProgressBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsProgressBarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsProgressBarModule, declarations: [CdsProgressBarComponent, CdsBarComponent, CdsRadialProgressComponent], imports: [CdsPipesModule,
        CommonModule], exports: [CdsProgressBarComponent] });
CdsProgressBarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsProgressBarModule, imports: [CdsPipesModule,
        CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsProgressBarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsProgressBarComponent, CdsBarComponent, CdsRadialProgressComponent],
                    exports: [CdsProgressBarComponent],
                    imports: [
                        CdsPipesModule,
                        CommonModule
                    ]
                }]
        }] });

class TooltipPresenter {
    constructor() {
        this.$change = new Subject();
        this.change = this.$change.asObservable();
    }
    calculateTooltipPosition(trigger, content, position) {
        const strategy = position ? new DirectionalPositionStrategy(position, {}) : new QuadrantPositionStrategy({});
        this.$change.next(calculateFixedElementPosition(strategy, trigger, content));
    }
    removeArrowClasses(element) {
        ['-top', '-bottom', '-left', '-right'].forEach(klass => element.classList.remove(klass));
    }
    depositionTooltip(content, originalStyles) {
        removeStyles(content, originalStyles);
        this.$change.next(null);
    }
    destroy(trigger, content, originalStyles) {
        this.depositionTooltip(content, originalStyles);
        this.$change.complete();
    }
}
TooltipPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TooltipPresenter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TooltipPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TooltipPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TooltipPresenter, decorators: [{
            type: Injectable
        }] });
function getTooltipArrowStyle(trigger, position) {
    const triggerCoordinates = trigger instanceof HTMLElement
        ? trigger.getBoundingClientRect?.()
        : new DOMRect(trigger.x, trigger.y);
    switch (position) {
        case ('top'): {
            return {
                top: `calc(${triggerCoordinates.top}px - 0.875rem)`,
                left: `calc(${triggerCoordinates.left + (0.5 * triggerCoordinates.width)}px - 0.375rem)`
            };
        }
        case ('bottom'): {
            return {
                top: `calc(${triggerCoordinates.bottom}px + 0.5rem)`,
                left: `calc(${triggerCoordinates.left + (0.5 * triggerCoordinates.width)}px - 0.375rem)`
            };
        }
        case ('left'): {
            // top position is top trigger coordinates + trigger height - half of tooltip arrow height (0.375rem)
            return {
                top: `calc(${triggerCoordinates.top + (0.5 * triggerCoordinates.height)}px - 0.375rem)`,
                left: `calc(${triggerCoordinates.left}px - 0.875rem)`
            };
        }
        case ('right'): {
            // top position is top trigger coordinates + trigger height - half of tooltip arrow height (0.375rem)
            return {
                top: `calc(${triggerCoordinates.top + (0.5 * triggerCoordinates.height)}px - 0.375rem)`,
                left: `calc(${triggerCoordinates.right}px + 0.125rem)`
            };
        }
    }
}
function setTooltipArrowStyles(arrow, tooltipPosition, { options, anchor }) {
    const arrowPosition = tooltipPosition ||
        // the tooltip will only ever be above or below the trigger
        (options.quadrant === 1 || options.quadrant === 2 ? 'bottom' : 'top');
    const { left, top } = getTooltipArrowStyle(anchor, arrowPosition);
    arrow.classList.add(`-${arrowPosition}`);
    arrow.style.left = left;
    arrow.style.top = top;
}
function setTooltipStyles(content, result) {
    const { options, anchor: trigger } = result;
    const { left: triggerLeft } = trigger instanceof HTMLElement
        ? trigger.getBoundingClientRect()
        : new DOMRect(trigger.x, trigger.y);
    let styles = { ...result.styles };
    if (options.direction === 'right') {
        const contentLeft = +result.styles.left.split('px')[0];
        styles = {
            ...result.styles, left: `${Math.min(contentLeft, triggerLeft - 12)}px`
        };
    }
    else if (options.direction === 'left') {
        const contentRight = +result.styles.right.split('px')[0];
        styles = {
            ...result.styles, right: `${Math.min(contentRight, document.documentElement.clientWidth - triggerLeft - 12)}px`
        };
    }
    setStyles(content, styles, options);
}

/**
 * Displays additional contextual information about some wrapped content. Should be used in instances where several lines of
 * mixed typographic content (headings, images etc) are required.
 * The tooltip generated by this component is responsive and will position itself according its anchor (triggering) element's
 * position in the page.
 *
 * NB: use a popover instead of the tooltip component for interactive content such as buttons and links.
 */
class CdsTooltipComponent {
    /**
     * @internal
     */
    constructor(presenter) {
        this.presenter = presenter;
        this.presenter.change.subscribe(event => {
            if (!event) {
                return;
            }
            setTooltipStyles(this.content.nativeElement, event);
            setTooltipArrowStyles(this.arrow.nativeElement, this.position, event);
        });
    }
    /*
     * @internal
     */
    repositionTooltip() {
        this.originalStyles = getStyles(this.content.nativeElement);
        this.presenter.calculateTooltipPosition(this.trigger.nativeElement, this.content.nativeElement, this.position);
        this.visible = true;
    }
    /**
     * @internal
     */
    depositionTooltip() {
        this.visible = false;
        // Handle scenarios where the trigger is focused and the element is moused over
        if (this.trigger.nativeElement.contains(document.activeElement)) {
            return;
        }
        this.presenter.depositionTooltip(this.content.nativeElement, this.originalStyles);
        this.presenter.removeArrowClasses(this.arrow.nativeElement);
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.presenter.destroy(this.trigger.nativeElement, this.content.nativeElement, this.originalStyles);
    }
}
CdsTooltipComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTooltipComponent, deps: [{ token: TooltipPresenter }], target: i0.ɵɵFactoryTarget.Component });
CdsTooltipComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsTooltipComponent, selector: "cds-tooltip", inputs: { name: "name", position: "position" }, providers: [TooltipPresenter], viewQueries: [{ propertyName: "trigger", first: true, predicate: ["trigger"], descendants: true, static: true }, { propertyName: "content", first: true, predicate: ["content"], descendants: true, static: true }, { propertyName: "arrow", first: true, predicate: ["arrow"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"cds-tooltip\">\n  <div\n    #trigger\n    class=\"cds-tooltip-trigger\"\n    [class.-visible]=\"visible\"\n    [attr.aria-describedby]=\"name\"\n    (mouseover)=\"repositionTooltip()\"\n    (mouseout)=\"depositionTooltip()\"\n    (wheel)=\"depositionTooltip()\"\n  >\n    <ng-content select=\"[cdsTooltipTrigger]\"></ng-content>\n  </div>\n  <div class=\"cds-tooltip-arrow\" #arrow></div>\n  <div\n    #content\n    [id]=\"name\"\n    class=\"cds-tooltip-content\"\n    role=\"tooltip\">\n    <ng-content select=\"[cdsTooltipContent]\"></ng-content>\n  </div>\n</div>\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-tooltip', providers: [TooltipPresenter], template: "<div class=\"cds-tooltip\">\n  <div\n    #trigger\n    class=\"cds-tooltip-trigger\"\n    [class.-visible]=\"visible\"\n    [attr.aria-describedby]=\"name\"\n    (mouseover)=\"repositionTooltip()\"\n    (mouseout)=\"depositionTooltip()\"\n    (wheel)=\"depositionTooltip()\"\n  >\n    <ng-content select=\"[cdsTooltipTrigger]\"></ng-content>\n  </div>\n  <div class=\"cds-tooltip-arrow\" #arrow></div>\n  <div\n    #content\n    [id]=\"name\"\n    class=\"cds-tooltip-content\"\n    role=\"tooltip\">\n    <ng-content select=\"[cdsTooltipContent]\"></ng-content>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: TooltipPresenter }]; }, propDecorators: { name: [{
                type: Input
            }], position: [{
                type: Input
            }], trigger: [{
                type: ViewChild,
                args: ['trigger', { static: true }]
            }], content: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], arrow: [{
                type: ViewChild,
                args: ['arrow', { static: true }]
            }] } });

/**
 * ### Description
 * Add this directive to elements that require simple text tooltips.
 * Requires a unique identifier passed into the `id` attribute.
 *
 * You can set the position of the displayed Tooltip relative to its content using the `position` attribute. Tooltips
 * that are positioned `top` or `bottom` can also be aligned to the `center` or the `end` of the content block. By default, `top`
 * and `bottom` positioned tooltips are aligned to the start of the content block.
 */
class CdsTooltipDirective {
    // eslint-disable-next-line @typescript-eslint/member-ordering
    constructor(element) {
        this.element = element;
    }
    /**
     * @internal
     */
    get tooltipContent() {
        return this.content;
    }
    /**
     * @internal
     */
    get tooltipId() {
        return this.id;
    }
    /**
     * @internal
     */
    get isPositionTop() {
        return this.position === 'top';
    }
    /**
     * @internal
     */
    get isPositionRight() {
        return this.position === 'right';
    }
    /**
     * @internal
     */
    get isPositionBottom() {
        return this.position === 'bottom';
    }
    /**
     * @internal
     */
    get isPositionLeft() {
        return this.position === 'left';
    }
    /**
     * @internal
     */
    get tooltipAlignment() {
        return this.align === 'center' && this.position !== 'left' && this.position !== 'right';
    }
    /**
     * @internal
     */
    get alignEnd() {
        return this.align === 'end' && this.position !== 'left' && this.position !== 'right';
    }
    /**
     * @internal
     */
    onMouseover() {
        this.visible = true;
    }
    /**
     * @internal
     */
    onMouseout() {
        this.visible = false;
    }
    /**
     * @internal
     */
    ngOnInit() {
        if (!this.id) {
            console.warn('Id not provided in Tooltip Directive!');
        }
        this.id = `cds-assistive-text-${this.id || this.element.nativeElement.id || ''}`;
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        const assistiveText = document.createElement('span');
        assistiveText.classList.add('cds-assistive-text');
        assistiveText.id = this.id;
        assistiveText.innerHTML = this.content;
        this.element.nativeElement.insertAdjacentElement('afterend', assistiveText);
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        const element = document.querySelector(`#${this.id}`);
        if (element) {
            ChildNodeRemove(element);
        }
    }
}
CdsTooltipDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTooltipDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsTooltipDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsTooltipDirective, selector: "[cdsTooltip]", inputs: { id: "id", content: ["cdsTooltip", "content"], position: "position", align: "align" }, host: { listeners: { "mouseover": "onMouseover()", "mouseout": "onMouseout()" }, properties: { "attr.data-tooltip": "this.tooltipContent", "attr.aria-describedby": "this.tooltipId", "class.-tooltip-top": "this.isPositionTop", "class.-tooltip-right": "this.isPositionRight", "class.-tooltip-bottom": "this.isPositionBottom", "class.-tooltip-left": "this.isPositionLeft", "class.-tooltip-align-center": "this.tooltipAlignment", "class.-tooltip-align-end": "this.alignEnd", "class.-visible": "this.visible" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsTooltip]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { id: [{
                type: Input
            }], content: [{
                type: Input,
                args: ['cdsTooltip']
            }], position: [{
                type: Input
            }], align: [{
                type: Input
            }], tooltipContent: [{
                type: HostBinding,
                args: ['attr.data-tooltip']
            }], tooltipId: [{
                type: HostBinding,
                args: ['attr.aria-describedby']
            }], isPositionTop: [{
                type: HostBinding,
                args: ['class.-tooltip-top']
            }], isPositionRight: [{
                type: HostBinding,
                args: ['class.-tooltip-right']
            }], isPositionBottom: [{
                type: HostBinding,
                args: ['class.-tooltip-bottom']
            }], isPositionLeft: [{
                type: HostBinding,
                args: ['class.-tooltip-left']
            }], tooltipAlignment: [{
                type: HostBinding,
                args: ['class.-tooltip-align-center']
            }], alignEnd: [{
                type: HostBinding,
                args: ['class.-tooltip-align-end']
            }], visible: [{
                type: HostBinding,
                args: ['class.-visible']
            }], onMouseover: [{
                type: HostListener,
                args: ['mouseover']
            }], onMouseout: [{
                type: HostListener,
                args: ['mouseout']
            }] } });

class CdsTooltipModule {
}
CdsTooltipModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsTooltipModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsTooltipModule, declarations: [CdsTooltipComponent, CdsTooltipDirective], imports: [CdsPipesModule], exports: [CdsTooltipComponent, CdsTooltipDirective] });
CdsTooltipModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTooltipModule, imports: [CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsTooltipComponent, CdsTooltipDirective],
                    exports: [CdsTooltipComponent, CdsTooltipDirective],
                    imports: [CdsPipesModule],
                }]
        }] });

/* eslint-disable @angular-eslint/no-output-native */
class CdsSchedulerItemComponent {
    constructor(/** @internal */ wrappedComponent) {
        this.wrappedComponent = wrappedComponent;
        this.select = new EventEmitter();
        /**
         * @internal
         */
        this._display = 'contents';
    }
    /**
     * @internal
     */
    get template() {
        return this.wrappedComponent.template;
    }
    /**
     * @internal
     */
    get short() {
        return this.end && differenceInMinutes(this.end, this.start) <= 30;
    }
    /**
     * @internal
     */
    selectEvent(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.disabled) {
            this.select.emit(event);
        }
    }
}
CdsSchedulerItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerItemComponent, deps: [{ token: CdsWrappedComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsSchedulerItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSchedulerItemComponent, selector: "cds-scheduler-item", inputs: { start: "start", end: "end", subject: "subject", color: "color", disabled: "disabled", selected: "selected" }, outputs: { select: "select" }, host: { properties: { "style.display": "this._display" } }, providers: [CdsWrappedComponent], ngImport: i0, template: "<div\n  tabindex=\"0\"\n  class=\"cds-scheduler-item\"\n  [class.-active]=\"selected\"\n  [class.-disabled]=\"disabled\"\n  [class.-yellow]=\"color==='yellow'\"\n  [class.-green]=\"color==='green'\"\n  [class.-purple]=\"color==='purple'\"\n  [class.-red]=\"color==='red'\"\n  [isDisabled]=\"disabled\"\n  (click)=\"selectEvent($event)\"\n  cdsHighlightable\n>\n  <div\n    class=\"cds-scheduler-item-title\"\n    [class.-short]=\"short\"\n  >{{ subject }}</div>\n  <ng-content></ng-content>\n</div>\n", dependencies: [{ kind: "directive", type: CdsKeyboardHighlightableDirective, selector: "[cdsHighlightable]", inputs: ["isDisabled"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-scheduler-item', providers: [CdsWrappedComponent], template: "<div\n  tabindex=\"0\"\n  class=\"cds-scheduler-item\"\n  [class.-active]=\"selected\"\n  [class.-disabled]=\"disabled\"\n  [class.-yellow]=\"color==='yellow'\"\n  [class.-green]=\"color==='green'\"\n  [class.-purple]=\"color==='purple'\"\n  [class.-red]=\"color==='red'\"\n  [isDisabled]=\"disabled\"\n  (click)=\"selectEvent($event)\"\n  cdsHighlightable\n>\n  <div\n    class=\"cds-scheduler-item-title\"\n    [class.-short]=\"short\"\n  >{{ subject }}</div>\n  <ng-content></ng-content>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: CdsWrappedComponent }]; }, propDecorators: { start: [{
                type: Input
            }], end: [{
                type: Input
            }], subject: [{
                type: Input
            }], color: [{
                type: Input
            }], disabled: [{
                type: Input
            }], selected: [{
                type: Input
            }], select: [{
                type: Output
            }], _display: [{
                type: HostBinding,
                args: ['style.display']
            }] } });

/**
 * Displays all events for a particular month. The month is automatically calculated from the dates provided in CdsSchedulerItem components.
 * The maximum number of events shown on a particular day is 2, and
 * a "Show more" button is visible if there are more than 2 events on any given day.
 * All events after the second event are hidden.
 */
class CdsSchedulerMonthComponent {
    /**
     * @internal
     */
    constructor() {
        /**
         * Emits a date object when the "Show more" button is clicked on a particular date.
         * You can use this date object to display more information about the events on the given day.
         */
        this.showMoreClicked = new EventEmitter();
        /**
         * @internal
         */
        this._display = 'contents';
        const weekstart = startOfWeek(new Date(), { weekStartsOn: getLocaleWeekStart('en') });
        this.weekdays = Array.from({ length: 7 }, (_, index) => {
            const date = addDays(weekstart, index);
            return format(date, 'EEEE');
        });
    }
    /**
     * Use this to override the calculated week.
     */
    set date(date) {
        this._date = date;
        if (this.schedulerItems) {
            this.setEvents();
        }
    }
    get date() {
        return this._date;
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        this.schedulerItems.changes
            .subscribe(() => {
            this.setEvents();
        });
        this.setEvents();
    }
    /**
     * @internal
     */
    setEvents() {
        const dates = calculateCalendarDates(this.getMonth(), 1);
        this.dates = dates.map(date => ({
            ...date,
            events: this.schedulerItems.filter(e => isSameDay(e.start, date.dateObject))
                .sort((a, b) => dateComparatorFn(a.start, b.start, 1))
        }));
    }
    /**
     * @internal
     */
    getMonth() {
        if (this.date) {
            return this.date;
        }
        if (!this.schedulerItems || this.schedulerItems.some(item => !item.start)) {
            return new Date();
        }
        const months = new Map();
        this.schedulerItems.forEach(item => {
            const month = startOfMonth(item.start);
            const existingMonth = [...months].find(([m]) => isSameDay(m, month));
            if (existingMonth) {
                months.set(existingMonth[0], months.get(existingMonth[0]) + 1);
            }
            else {
                months.set(month, 1);
            }
        });
        const sortedMonths = [...months].sort(([dateA, dateCountA], [dateB, dateCountB]) => {
            // if two months have the same number of entries, use the later month
            if (dateCountA === dateCountB) {
                return differenceInMonths(dateB, dateA);
            }
            return dateCountB - dateCountA;
        });
        return sortedMonths[0][0];
    }
    ;
}
CdsSchedulerMonthComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerMonthComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsSchedulerMonthComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSchedulerMonthComponent, selector: "cds-scheduler-month", inputs: { date: "date" }, outputs: { showMoreClicked: "showMoreClicked" }, host: { properties: { "style.display": "this._display" } }, queries: [{ propertyName: "schedulerItems", predicate: CdsSchedulerItemComponent }], ngImport: i0, template: "\n<div class=\"cds-scheduler\">\n  <div class=\"month-view\">\n    <div class=\"header-row\">\n      <div\n        class=\"header\"\n        *ngFor=\"let date of weekdays\"\n      >\n        <div class=\"header-content\">\n          <div class=\"title\">{{ date }}</div>\n        </div>\n      </div>\n    </div>\n\n    <div class=\"body\">\n      <div\n        class=\"day\"\n        *ngFor=\"let date of dates\"\n        [class.-prev-month]=\"date.designation === 'previous'\"\n        [class.-next-month]=\"date.designation === 'next'\"\n        [class.-today]=\"date.today\"\n      >\n        <div class=\"title\">{{ date.value }}</div>\n        <ng-container *ngFor=\"let event of date.events; index as index\">\n          <div\n            *ngIf=\"index < 2\"\n            class=\"event-container\"\n          >\n            <ng-template *ngTemplateOutlet=\"event.template\"></ng-template>\n          </div>\n        </ng-container>\n        <cds-button\n          *ngIf=\"date.events.length > 2\"\n          class=\"scheduler-show-more-button\"\n          text=\"Show more...\"\n          type=\"tertiary\"\n          size=\"small\"\n          [underline]=\"true\"\n          [link]=\"true\"\n          (click)=\"showMoreClicked.emit(date.dateObject)\"\n        ></cds-button>\n      </div>\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: CdsButtonComponent, selector: "cds-button", inputs: ["name", "text", "type", "size", "disabled", "underline", "iconName", "alignIcon", "designation", "ariaLabel", "link"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerMonthComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-scheduler-month', template: "\n<div class=\"cds-scheduler\">\n  <div class=\"month-view\">\n    <div class=\"header-row\">\n      <div\n        class=\"header\"\n        *ngFor=\"let date of weekdays\"\n      >\n        <div class=\"header-content\">\n          <div class=\"title\">{{ date }}</div>\n        </div>\n      </div>\n    </div>\n\n    <div class=\"body\">\n      <div\n        class=\"day\"\n        *ngFor=\"let date of dates\"\n        [class.-prev-month]=\"date.designation === 'previous'\"\n        [class.-next-month]=\"date.designation === 'next'\"\n        [class.-today]=\"date.today\"\n      >\n        <div class=\"title\">{{ date.value }}</div>\n        <ng-container *ngFor=\"let event of date.events; index as index\">\n          <div\n            *ngIf=\"index < 2\"\n            class=\"event-container\"\n          >\n            <ng-template *ngTemplateOutlet=\"event.template\"></ng-template>\n          </div>\n        </ng-container>\n        <cds-button\n          *ngIf=\"date.events.length > 2\"\n          class=\"scheduler-show-more-button\"\n          text=\"Show more...\"\n          type=\"tertiary\"\n          size=\"small\"\n          [underline]=\"true\"\n          [link]=\"true\"\n          (click)=\"showMoreClicked.emit(date.dateObject)\"\n        ></cds-button>\n      </div>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { date: [{
                type: Input
            }], showMoreClicked: [{
                type: Output
            }], schedulerItems: [{
                type: ContentChildren,
                args: [CdsSchedulerItemComponent]
            }], _display: [{
                type: HostBinding,
                args: ['style.display']
            }] } });

function getOverlappingEvents(events) {
    const isSameQuarterHour = (event, compare) => (isAfter(compare.start, event.start) && differenceInMinutes(compare.start, event.start) < 15)
        || (isAfter(event.start, compare.start) && differenceInMinutes(event.start, compare.start) < 15);
    // target ends between event start and event end
    const overlapBefore = (event, compare) => isAfter(compare.end, event.start) && isBefore(compare.end, event.end);
    // starts between event start and event end
    const overlapAfter = (event, compare) => isAfter(compare.start, event.start) && isBefore(compare.start, event.end);
    const overlappingEvents = events.map(event => events.filter(entry => entry !== event && (overlapBefore(event, entry) || overlapAfter(event, entry) || isSameQuarterHour(event, entry))));
    return new Set([].concat(...overlappingEvents));
}

// shows hours in a grid with weekdays at the top
class CdsSchedulerWeekComponent {
    constructor() {
        /**
         * @internal
         */
        this.weekdays = [];
        /**
         * @internal
         */
        this.hours = Array.from({ length: 24 }, (_, idx) => ({
            hour: idx,
            stringified: `${idx - (idx > 12 ? 12 : 0) + (idx === 0 ? 12 : 0)} ${idx < 12 ? 'AM' : 'PM'}`
        }));
        /**
         * @internal
         */
        this._display = 'contents';
    }
    /**
     * Use this to override the calculated week.
     */
    set date(date) {
        this._date = date;
        if (this.schedulerItems) {
            this.setEvents();
        }
    }
    get date() {
        return this._date;
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        const [first] = [...this.schedulerItems]
            .sort((a, b) => getHours(a.start) - getHours(b.start));
        if (first && first.end) {
            this.bodyElement.nativeElement.querySelectorAll('.time')[Math.max(getHours(first.start) - 1, 1)]
                .scrollIntoView(true);
        }
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        this.schedulerItems.changes
            .subscribe(() => {
            this.setEvents();
        });
        this.setEvents();
    }
    /**
     * @internal
     */
    setEvents() {
        const weekdays = calculateCalendarWeekdays(this.getWeek());
        this.weekdays = weekdays.map(weekday => {
            const sameDayEvents = this.schedulerItems
                .filter(e => isSameDay(e.start, weekday.dateObject));
            const eventsWithOverlapSet = getOverlappingEvents(sameDayEvents.filter(event => !!event.end));
            return {
                ...weekday,
                allDayEvents: sameDayEvents.filter(event => !event.end),
                events: sameDayEvents
                    .filter(event => !!event.end)
                    .map(event => ({
                    template: event.template,
                    gridColumn: !eventsWithOverlapSet.has(event) ? `1 / span ${eventsWithOverlapSet.size}` : null,
                    gridRow: `${Math.ceil((differenceInMinutes(event.start, startOfDay(weekday.dateObject))) / 15) + 1} /
              span ${Math.max(Math.ceil(differenceInMinutes(event.end, event.start) / 15), 1)}`
                }))
            };
        });
    }
    getWeek() {
        if (this.date) {
            return this.date;
        }
        if (!this.schedulerItems || this.schedulerItems.some(item => !item.start)) {
            return new Date();
        }
        const weeks = new Map();
        this.schedulerItems.forEach(item => {
            const week = startOfWeek(item.start);
            const existingWeek = [...weeks].find(([m]) => isSameDay(m, week));
            if (existingWeek) {
                weeks.set(existingWeek[0], weeks.get(existingWeek[0]) + 1);
            }
            else {
                weeks.set(week, 1);
            }
        });
        const sortedWeeks = [...weeks].sort(([dateA, dateCountA], [dateB, dateCountB]) => {
            // if two weeks have the same number of entries, use the later week
            if (dateCountA === dateCountB) {
                return differenceInWeeks(dateB, dateA);
            }
            return dateCountB - dateCountA;
        });
        return sortedWeeks[0][0];
    }
    ;
}
CdsSchedulerWeekComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerWeekComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsSchedulerWeekComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSchedulerWeekComponent, selector: "cds-scheduler-week", inputs: { date: "date" }, host: { properties: { "style.display": "this._display" } }, queries: [{ propertyName: "schedulerItems", predicate: CdsSchedulerItemComponent }], viewQueries: [{ propertyName: "bodyElement", first: true, predicate: ["body"], descendants: true }], ngImport: i0, template: "<div class=\"cds-scheduler\">\n\n  <div class=\"week-view -overflow\">\n    <div class=\"header-row\">\n      <!-- <div class=\"spacer\"></div> -->\n      <div\n        class=\"header\"\n        *ngFor=\"let date of weekdays\"\n        [class.-today]=\"date.today\"\n      >\n        <div class=\"header-content\">\n          <div class=\"title\">{{ date.dayOfMonth }}</div>\n          <div class=\"subtitle\">{{ date.dayOfWeek }}</div>\n        </div>\n      </div>\n      <!-- <div class=\"spacer\"></div> -->\n    </div>\n\n    <div class=\"body\" #body>\n      <div\n        class=\"time\"\n        [style.gridRow]=\"1\"\n      >\n        <div class=\"time-title\">All Day</div>\n      </div>\n      \n      <div\n        *ngFor=\"let hour of hours;index as index\"\n        class=\"time\"\n        [style.gridRow]=\"index + 2\"\n      >\n        <div class=\"time-title\">{{ hour.stringified }}</div>\n      </div>\n\n      <ng-container *ngFor=\"let weekday of weekdays;index as index,last as last\">\n        <div\n          *ngIf=\"!last\"\n          class=\"divider\"\n          [style.gridColumn]=\"index + 3\"\n        ></div>\n      </ng-container>\n\n      <div\n        *ngFor=\"let weekday of weekdays; index as weekdayIndex\"\n        class=\"all-day\"\n        [style.gridColumn]=\"weekdayIndex + 2\"\n      >\n        <ng-container *ngFor=\"let event of weekday.allDayEvents\"\n        >\n          <ng-template *ngTemplateOutlet=\"event.template\"></ng-template>\n        </ng-container>\n      </div>\n\n      <div\n        *ngFor=\"let weekday of weekdays; index as weekdayIndex\"\n        class=\"day\"\n        [style.gridColumn]=\"weekdayIndex + 2\"\n      >  \n        <div\n          *ngFor=\"let event of weekday.events\"\n          [style.gridColumn]=\"event.gridColumn\"\n          [style.gridRow]=\"event.gridRow\"\n          class=\"event-container\"\n        >\n          <ng-template *ngTemplateOutlet=\"event.template\"></ng-template>\n        </div>\n      </div>\n    </div>\n  </div>\n\n</div>\n\n\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerWeekComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-scheduler-week', template: "<div class=\"cds-scheduler\">\n\n  <div class=\"week-view -overflow\">\n    <div class=\"header-row\">\n      <!-- <div class=\"spacer\"></div> -->\n      <div\n        class=\"header\"\n        *ngFor=\"let date of weekdays\"\n        [class.-today]=\"date.today\"\n      >\n        <div class=\"header-content\">\n          <div class=\"title\">{{ date.dayOfMonth }}</div>\n          <div class=\"subtitle\">{{ date.dayOfWeek }}</div>\n        </div>\n      </div>\n      <!-- <div class=\"spacer\"></div> -->\n    </div>\n\n    <div class=\"body\" #body>\n      <div\n        class=\"time\"\n        [style.gridRow]=\"1\"\n      >\n        <div class=\"time-title\">All Day</div>\n      </div>\n      \n      <div\n        *ngFor=\"let hour of hours;index as index\"\n        class=\"time\"\n        [style.gridRow]=\"index + 2\"\n      >\n        <div class=\"time-title\">{{ hour.stringified }}</div>\n      </div>\n\n      <ng-container *ngFor=\"let weekday of weekdays;index as index,last as last\">\n        <div\n          *ngIf=\"!last\"\n          class=\"divider\"\n          [style.gridColumn]=\"index + 3\"\n        ></div>\n      </ng-container>\n\n      <div\n        *ngFor=\"let weekday of weekdays; index as weekdayIndex\"\n        class=\"all-day\"\n        [style.gridColumn]=\"weekdayIndex + 2\"\n      >\n        <ng-container *ngFor=\"let event of weekday.allDayEvents\"\n        >\n          <ng-template *ngTemplateOutlet=\"event.template\"></ng-template>\n        </ng-container>\n      </div>\n\n      <div\n        *ngFor=\"let weekday of weekdays; index as weekdayIndex\"\n        class=\"day\"\n        [style.gridColumn]=\"weekdayIndex + 2\"\n      >  \n        <div\n          *ngFor=\"let event of weekday.events\"\n          [style.gridColumn]=\"event.gridColumn\"\n          [style.gridRow]=\"event.gridRow\"\n          class=\"event-container\"\n        >\n          <ng-template *ngTemplateOutlet=\"event.template\"></ng-template>\n        </div>\n      </div>\n    </div>\n  </div>\n\n</div>\n\n\n" }]
        }], propDecorators: { date: [{
                type: Input
            }], bodyElement: [{
                type: ViewChild,
                args: ['body']
            }], schedulerItems: [{
                type: ContentChildren,
                args: [CdsSchedulerItemComponent]
            }], _display: [{
                type: HostBinding,
                args: ['style.display']
            }] } });

/* eslint-disable @angular-eslint/no-output-native */
class CdsSchedulerEventComponent {
    /**
     * @internal
     */
    get dateParts() {
        return {
            day: format(this.start, 'dd'),
            month: format(this.start, 'MMM').toUpperCase(),
            year: format(this.start, 'yyyy'),
            time: format(this.start, 'hh:mma')
        };
    }
}
CdsSchedulerEventComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerEventComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsSchedulerEventComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSchedulerEventComponent, selector: "cds-event", inputs: { start: "start", end: "end", label: "label", icon: "icon" }, ngImport: i0, template: "<div class=\"cds-event\">\n  <div class=\"event-date\">\n    <div class=\"day\">{{ dateParts.day }}</div>\n    <div class=\"month-year\">{{ dateParts.month }} {{ dateParts.year }}</div>\n    <div class=\"time\">{{ !!end ? dateParts.time : 'All Day' }}</div>\n  </div>\n  <div class=\"event-icon\">\n    <i class=\"material-icons\">{{ icon }}</i>\n  </div>\n  <div class=\"event-details\">\n    <div class=\"event-header\">\n      <div class=\"event-label\">{{ label }}</div>\n      <ng-content select=\"[cdsEventHeader]\"></ng-content>\n    </div>\n    <div class=\"event-body\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerEventComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-event', template: "<div class=\"cds-event\">\n  <div class=\"event-date\">\n    <div class=\"day\">{{ dateParts.day }}</div>\n    <div class=\"month-year\">{{ dateParts.month }} {{ dateParts.year }}</div>\n    <div class=\"time\">{{ !!end ? dateParts.time : 'All Day' }}</div>\n  </div>\n  <div class=\"event-icon\">\n    <i class=\"material-icons\">{{ icon }}</i>\n  </div>\n  <div class=\"event-details\">\n    <div class=\"event-header\">\n      <div class=\"event-label\">{{ label }}</div>\n      <ng-content select=\"[cdsEventHeader]\"></ng-content>\n    </div>\n    <div class=\"event-body\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n" }]
        }], propDecorators: { start: [{
                type: Input
            }], end: [{
                type: Input
            }], label: [{
                type: Input
            }], icon: [{
                type: Input
            }] } });

// shows hours in a grid with weekdays at the top
class CdsSchedulerDayComponent {
    constructor() {
        /**
         * @internal
         */
        this.hours = Array.from({ length: 24 }, (_, idx) => ({
            hour: idx,
            stringified: `${idx - (idx > 12 ? 12 : 0) + (idx === 0 ? 12 : 0)} ${idx < 12 ? 'AM' : 'PM'}`,
        }));
        /**
         * @internal
         */
        this._display = 'contents';
    }
    /**
     * Use this to override the calculated day.
     */
    set date(date) {
        this._date = date;
        if (this.schedulerItems) {
            this.setEvents();
        }
    }
    get date() {
        return this._date;
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        this.schedulerItems.changes
            .subscribe(() => {
            this.setEvents();
        });
        this.setEvents();
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        const [first] = [...this.schedulerItems]
            .filter(e => isSameDay(e.start, this.getDate()))
            .sort((a, b) => getHours(a.start) - getHours(b.start));
        if (first && first.end) {
            this.bodyElement.nativeElement.querySelectorAll('.time')[Math.max(getHours(first.start) - 1, 1)]
                .scrollIntoView();
        }
    }
    /**
     * @internal
     */
    setEvents() {
        const date = this.getDate();
        const sameDayEvents = this.schedulerItems.filter(e => isSameDay(e.start, date));
        const eventsWithOverlapSet = getOverlappingEvents(sameDayEvents.filter(event => !!event.end));
        const getGridRow = (event) => `${Math.ceil(differenceInMinutes(event.start, startOfDay(date)) / 15) + 1} /
        span ${Math.max(Math.ceil(differenceInMinutes(event.end, event.start) / 15), 1)}`;
        this.hourEvents = sameDayEvents
            .filter(event => event.end)
            .map(event => ({
            template: event.template,
            gridColumn: !eventsWithOverlapSet.has(event) ? `1 / span ${eventsWithOverlapSet.size}` : null,
            gridRow: getGridRow(event)
        }));
        this.allDayEvents = sameDayEvents.filter(event => !event.end);
        this.dateParts = {
            dayOfMonth: getDate(date),
            dayOfWeek: format(date, 'EEEE')
        };
    }
    /**
     * @internal
     */
    getDate() {
        if (this.date) {
            return this.date;
        }
        if (!this.schedulerItems || this.schedulerItems.some(item => !item.start)) {
            return new Date();
        }
        const days = new Map();
        this.schedulerItems.forEach(item => {
            const day = startOfDay(item.start);
            const existingDay = [...days].find(([m]) => isSameDay(m, day));
            if (existingDay) {
                days.set(existingDay[0], days.get(existingDay[0]) + 1);
            }
            else {
                days.set(day, 1);
            }
        });
        const sortedDays = [...days].sort(([dateA, dateCountA], [dateB, dateCountB]) => {
            // if two months have the same number of entries, use the later month
            if (dateCountA === dateCountB) {
                return differenceInDays(dateB, dateA);
            }
            return dateCountB - dateCountA;
        });
        return sortedDays[0][0];
    }
    ;
}
CdsSchedulerDayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerDayComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CdsSchedulerDayComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSchedulerDayComponent, selector: "cds-scheduler-day", inputs: { date: "date" }, host: { properties: { "style.display": "this._display" } }, queries: [{ propertyName: "schedulerItems", predicate: CdsSchedulerItemComponent }], viewQueries: [{ propertyName: "bodyElement", first: true, predicate: ["body"], descendants: true }], ngImport: i0, template: "<div class=\"cds-scheduler\">\n  <div class=\"day-view\">\n    <div class=\"body\" #body>\n      <div\n        class=\"time\"\n        [style.gridRow]=\"1\"\n      >\n        <div class=\"time-title\">All Day</div>\n      </div>\n      \n      <div\n        *ngFor=\"let hour of hours;index as index\"\n        class=\"time\"\n        [style.gridRow]=\"index + 2\"\n      >\n        <div class=\"time-title\">{{ hour.stringified }}</div>\n      </div>\n\n      <div class=\"all-day\">\n        <ng-container *ngFor=\"let event of allDayEvents\">\n          <ng-template *ngTemplateOutlet=\"event.template\"></ng-template>\n        </ng-container>\n      </div>\n\n      <div class=\"day\">\n        <div\n          *ngFor=\"let event of hourEvents\"\n          [style.gridColumn]=\"event.gridColumn\"\n          [style.gridRow]=\"event.gridRow\"\n          class=\"event-container\"\n        >\n          <ng-template *ngTemplateOutlet=\"event.template\"></ng-template>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n\n\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerDayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-scheduler-day', template: "<div class=\"cds-scheduler\">\n  <div class=\"day-view\">\n    <div class=\"body\" #body>\n      <div\n        class=\"time\"\n        [style.gridRow]=\"1\"\n      >\n        <div class=\"time-title\">All Day</div>\n      </div>\n      \n      <div\n        *ngFor=\"let hour of hours;index as index\"\n        class=\"time\"\n        [style.gridRow]=\"index + 2\"\n      >\n        <div class=\"time-title\">{{ hour.stringified }}</div>\n      </div>\n\n      <div class=\"all-day\">\n        <ng-container *ngFor=\"let event of allDayEvents\">\n          <ng-template *ngTemplateOutlet=\"event.template\"></ng-template>\n        </ng-container>\n      </div>\n\n      <div class=\"day\">\n        <div\n          *ngFor=\"let event of hourEvents\"\n          [style.gridColumn]=\"event.gridColumn\"\n          [style.gridRow]=\"event.gridRow\"\n          class=\"event-container\"\n        >\n          <ng-template *ngTemplateOutlet=\"event.template\"></ng-template>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n\n\n" }]
        }], propDecorators: { date: [{
                type: Input
            }], bodyElement: [{
                type: ViewChild,
                args: ['body']
            }], schedulerItems: [{
                type: ContentChildren,
                args: [CdsSchedulerItemComponent]
            }], _display: [{
                type: HostBinding,
                args: ['style.display']
            }] } });

class CdsSchedulerModule {
}
CdsSchedulerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsSchedulerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerModule, declarations: [CdsSchedulerWeekComponent,
        CdsSchedulerDayComponent,
        CdsSchedulerMonthComponent,
        CdsSchedulerItemComponent,
        CdsSchedulerEventComponent], imports: [CommonModule,
        ReactiveFormsModule,
        CdsTooltipModule,
        CdsButtonModule,
        CdsDirectivesModule], exports: [CdsSchedulerWeekComponent,
        CdsSchedulerDayComponent,
        CdsSchedulerMonthComponent,
        CdsSchedulerItemComponent,
        CdsSchedulerEventComponent] });
CdsSchedulerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerModule, imports: [CommonModule,
        ReactiveFormsModule,
        CdsTooltipModule,
        CdsButtonModule,
        CdsDirectivesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSchedulerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsSchedulerWeekComponent,
                        CdsSchedulerDayComponent,
                        CdsSchedulerMonthComponent,
                        CdsSchedulerItemComponent,
                        CdsSchedulerEventComponent,
                    ],
                    exports: [
                        CdsSchedulerWeekComponent,
                        CdsSchedulerDayComponent,
                        CdsSchedulerMonthComponent,
                        CdsSchedulerItemComponent,
                        CdsSchedulerEventComponent
                    ],
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        CdsTooltipModule,
                        CdsButtonModule,
                        CdsDirectivesModule
                    ]
                }]
        }] });

class CdsSchedulerEvent {
}

class CdsSearchPrefixDirective {
    constructor(wrappedSuffix, element) {
        this.wrappedSuffix = wrappedSuffix;
        this.element = element;
    }
    get template() {
        return this.wrappedSuffix.template;
    }
    get isAdvancedSearch() {
        return this.element.nativeElement.tagName === 'CDS-ADVANCED-SEARCH';
    }
}
CdsSearchPrefixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSearchPrefixDirective, deps: [{ token: CdsWrappedComponent }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsSearchPrefixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSearchPrefixDirective, selector: "[cdsSearchPrefix]", providers: [CdsWrappedComponent], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSearchPrefixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsSearchPrefix]',
                    providers: [CdsWrappedComponent],
                }]
        }], ctorParameters: function () { return [{ type: CdsWrappedComponent }, { type: i0.ElementRef }]; } });

class CdsSearchSuffixDirective {
    constructor(wrappedSuffix, element) {
        this.wrappedSuffix = wrappedSuffix;
        this.element = element;
    }
    get template() {
        return this.wrappedSuffix.template;
    }
    get isAdvancedSearch() {
        return this.element.nativeElement.tagName === 'CDS-ADVANCED-SEARCH';
    }
}
CdsSearchSuffixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSearchSuffixDirective, deps: [{ token: CdsWrappedComponent }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CdsSearchSuffixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsSearchSuffixDirective, selector: "[cdsSearchSuffix]", providers: [CdsWrappedComponent], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSearchSuffixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsSearchSuffix]',
                    providers: [CdsWrappedComponent],
                }]
        }], ctorParameters: function () { return [{ type: CdsWrappedComponent }, { type: i0.ElementRef }]; } });

/**
 * ### Description
 * Provides a field for capturing a search query entered by a user.
 */
/**
 * The following components will be styled appropriately when injected into the searchbar:
 * Buttons, Control Blocks and Select Dropdowns.
 * Each element injected into the searchbar needs to have a [cdsSearchPrefix] or [cdsSearchSuffix] attribute.
 *
 * Component requires a FormControl instance to be passed in.
 */
class CdsSearchbarComponent extends CdsAbstractTextInputComponent {
    constructor(
    /** @internal **/ control, 
    /** @internal **/ controlValueAccessor) {
        super(controlValueAccessor);
        this.control = control;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        /**
         * (Optional) If true, search icon is displayed.
         */
        this.showIcon = false;
        /**
         * Emits an event when the user hits the 'Enter' key in the Searchbar.
         */
        this.submit = new EventEmitter();
        /**
         * An event containing the new state of this control.
         */
        this.change = this.controlValueAccessor.change;
        if (this.control) {
            this.control.valueAccessor = this;
        }
        else {
            throw new Error(`FormControl is missing from Searchbar.`);
        }
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.suggestions?.init(this.inputElement.nativeElement);
    }
    /**
     * @internal
     * Deprecated. Shows the suggestions dropdown.
     */
    showSuggestions() {
        if (!this.suggestions) {
            return;
        }
        this.suggestions.show();
    }
    /**
     * @internal
     * Deprecated. Hides the suggestions dropdown.
     */
    hideSuggestions() {
        if (!this.suggestions) {
            return;
        }
        this.suggestions.hide();
    }
    /**
     * @internal
     */
    onArrowDown(event) {
        if (!this.suggestions?.expanded) {
            event.stopImmediatePropagation();
        }
        this.showSuggestions();
    }
    /**
     * @internal
     */
    onFocus() {
        this.active = true;
    }
    /**
     * @internal
     */
    onBlur() {
        this.active = false;
        this.controlValueAccessor.onTouch();
    }
    /**
     * @internal
     */
    onSubmit(event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.submit.emit(this.value);
    }
    /**
     * @internal
     */
    updateValue(event) {
        this.value = event.target.value;
        this.controlValueAccessor.onChange(this.value);
    }
    /**
     * @internal
     */
    writeValue(value) {
        this.value = value;
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsSearchbarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSearchbarComponent, deps: [{ token: i1$2.NgControl, optional: true, self: true }, { token: CdsControlValueAccessorComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsSearchbarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsSearchbarComponent, selector: "cds-searchbar", inputs: { name: "name", showIcon: "showIcon", suggestions: "suggestions", alignIcon: "alignIcon", autocomplete: "autocomplete" }, outputs: { submit: "submit", change: "change" }, providers: [CdsControlValueAccessorComponent], queries: [{ propertyName: "searchSuffixes", predicate: CdsSearchSuffixDirective, descendants: true }, { propertyName: "searchPrefixes", predicate: CdsSearchPrefixDirective, descendants: true }], viewQueries: [{ propertyName: "inputElement", first: true, predicate: ["input"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<form\n  class=\"cds-input-container -search\"\n  [class.-active]=\"active\"\n  [class.-with-prefix]=\"searchPrefixes.length > 0\"\n  [class.-with-suffix]=\"searchSuffixes.length > 0\"\n  [class.-with-icon-left]=\"\n    searchPrefixes.length === 0 &&\n    showIcon &&\n    (!alignIcon || alignIcon === 'left')\n  \"\n  [class.-with-icon-right]=\"\n    searchSuffixes.length === 0 && showIcon && alignIcon === 'right'\n  \"\n  [class.-disabled]=\"control.control.disabled\"\n  [attr.icon-name]=\"'search'\"\n  (submit)=\"onSubmit($event)\"\n>\n  <ng-container *ngFor=\"let addon of searchPrefixes; last as last\">\n    <ng-container\n      *ngTemplateOutlet=\"addonTemplate;context:{ addon: addon, prefix: true, last: last, only: searchPrefixes.length===1 }\"\n    ></ng-container>\n  </ng-container>\n\n  <input\n    #input\n    type=\"text\"\n    [id]=\"'cds-searchbar-' + name | kebabCase\"\n    [name]=\"name || 'cds-searchbar'\"\n    class=\"cds-input -search\"\n    [placeholder]=\"placeholder || 'Search'\"\n    aria-label=\"Search\"\n    [autocomplete]=\"autocomplete || 'off'\"\n    (blur)=\"onBlur()\"\n    (focus)=\"onFocus()\"\n    [(ngModel)]=\"value\"\n    (input)=\"updateValue($event)\"\n    (input)=\"showSuggestions()\"\n    (keydown.arrowDown)=\"onArrowDown($event)\"\n    (click)=\"showSuggestions()\"\n    [attr.disabled]=\"control.control.disabled || null\"\n  />\n\n  <ng-container *ngFor=\"let addon of searchSuffixes\">\n    <ng-container *ngTemplateOutlet=\"addonTemplate;context:{ addon: addon, suffix: true, only: searchSuffixes.length===1 }\"></ng-container>\n  </ng-container>\n</form>\n\n<ng-template #addonTemplate let-addon=\"addon\" let-prefix=\"prefix\" let-suffix=\"suffix\" let-last=\"last\" let-only=\"only\">\n  <div\n    class=\"cds-input-addon\"\n    [class.-prefix]=\"prefix\"\n    [class.-suffix]=\"suffix\"\n    [class.-last]=\"last && !only\"\n    [class.-only]=\"only\"\n    *ngIf=\"!addon.isAdvancedSearch\"\n  >\n    <ng-template *ngTemplateOutlet=\"addon.template\"></ng-template>\n  </div>\n\n  <ng-template *ngTemplateOutlet=\"addon.template\"></ng-template>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i1$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i1$2.NgForm, selector: "form:not([ngNoForm]):not([formGroup]),ng-form,[ngForm]", inputs: ["ngFormOptions"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSearchbarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-searchbar', providers: [CdsControlValueAccessorComponent], template: "<form\n  class=\"cds-input-container -search\"\n  [class.-active]=\"active\"\n  [class.-with-prefix]=\"searchPrefixes.length > 0\"\n  [class.-with-suffix]=\"searchSuffixes.length > 0\"\n  [class.-with-icon-left]=\"\n    searchPrefixes.length === 0 &&\n    showIcon &&\n    (!alignIcon || alignIcon === 'left')\n  \"\n  [class.-with-icon-right]=\"\n    searchSuffixes.length === 0 && showIcon && alignIcon === 'right'\n  \"\n  [class.-disabled]=\"control.control.disabled\"\n  [attr.icon-name]=\"'search'\"\n  (submit)=\"onSubmit($event)\"\n>\n  <ng-container *ngFor=\"let addon of searchPrefixes; last as last\">\n    <ng-container\n      *ngTemplateOutlet=\"addonTemplate;context:{ addon: addon, prefix: true, last: last, only: searchPrefixes.length===1 }\"\n    ></ng-container>\n  </ng-container>\n\n  <input\n    #input\n    type=\"text\"\n    [id]=\"'cds-searchbar-' + name | kebabCase\"\n    [name]=\"name || 'cds-searchbar'\"\n    class=\"cds-input -search\"\n    [placeholder]=\"placeholder || 'Search'\"\n    aria-label=\"Search\"\n    [autocomplete]=\"autocomplete || 'off'\"\n    (blur)=\"onBlur()\"\n    (focus)=\"onFocus()\"\n    [(ngModel)]=\"value\"\n    (input)=\"updateValue($event)\"\n    (input)=\"showSuggestions()\"\n    (keydown.arrowDown)=\"onArrowDown($event)\"\n    (click)=\"showSuggestions()\"\n    [attr.disabled]=\"control.control.disabled || null\"\n  />\n\n  <ng-container *ngFor=\"let addon of searchSuffixes\">\n    <ng-container *ngTemplateOutlet=\"addonTemplate;context:{ addon: addon, suffix: true, only: searchSuffixes.length===1 }\"></ng-container>\n  </ng-container>\n</form>\n\n<ng-template #addonTemplate let-addon=\"addon\" let-prefix=\"prefix\" let-suffix=\"suffix\" let-last=\"last\" let-only=\"only\">\n  <div\n    class=\"cds-input-addon\"\n    [class.-prefix]=\"prefix\"\n    [class.-suffix]=\"suffix\"\n    [class.-last]=\"last && !only\"\n    [class.-only]=\"only\"\n    *ngIf=\"!addon.isAdvancedSearch\"\n  >\n    <ng-template *ngTemplateOutlet=\"addon.template\"></ng-template>\n  </div>\n\n  <ng-template *ngTemplateOutlet=\"addon.template\"></ng-template>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: CdsControlValueAccessorComponent }]; }, propDecorators: { name: [{
                type: Input
            }], showIcon: [{
                type: Input
            }], suggestions: [{
                type: Input
            }], alignIcon: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], submit: [{
                type: Output
            }], change: [{
                type: Output
            }], searchSuffixes: [{
                type: ContentChildren,
                args: [CdsSearchSuffixDirective, { descendants: true }]
            }], searchPrefixes: [{
                type: ContentChildren,
                args: [CdsSearchPrefixDirective, { descendants: true }]
            }], inputElement: [{
                type: ViewChild,
                args: ['input']
            }] } });

class CdsAdvancedSearchComponent {
    /**
     * @internal
     */
    constructor(searchbar) {
        this.searchbar = searchbar;
        /**
         * @internal
         */
        this.focusButton = () => {
            this.button.focus({ preventScroll: true });
        };
    }
    /**
     * @internal
     */
    ngAfterViewInit() { }
    /**
     * Hides the advanced search dropdown.
     */
    hideAdvancedSearch() {
        this.dropdownToggle.collapse();
    }
    /**
     * Shows the advanced search dropdown.
     */
    showAdvancedSearch() {
        this.dropdownToggle.expand();
    }
    /**
     * @internal
     */
    ngOnDestroy() { }
}
CdsAdvancedSearchComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAdvancedSearchComponent, deps: [{ token: CdsSearchbarComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsAdvancedSearchComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsAdvancedSearchComponent, selector: "cds-advanced-search", inputs: { name: "name", alignIcon: "alignIcon", disabled: "disabled" }, viewQueries: [{ propertyName: "button", first: true, predicate: CdsButtonComponent, descendants: true, static: true }, { propertyName: "panelTemplate", first: true, predicate: ["panelTemplate"], descendants: true, static: true }, { propertyName: "dropdownToggle", first: true, predicate: CdsDropdownDirective, descendants: true }], ngImport: i0, template: "<div\n  class=\"cds-advanced-search cds-dropdown cds-input-addon -suffix\"\n  [class.-open]=\"!!dropdownToggle?.expanded\"\n>\n  <cds-button\n    #button\n    cdsElementRef\n    #toggle=\"cdsElementRef\"\n    [disabled]=\"disabled\"\n    text=\"Advanced\"\n    [iconName]=\"!!dropdownToggle?.expanded ? 'arrow_drop_up' : 'arrow_drop_down'\"\n    type=\"tertiary\"\n    [name]=\"'cds-advanced-search-toggle-' + name\"\n    [alignIcon]=\"alignIcon || 'right'\"\n    class=\"cds-advanced-search-toggle\"\n    >\n  </cds-button>\n\n  <div\n    [id]=\"('cds-advanced-search-panel-' + name) | kebabCase\"\n    class=\"cds-advanced-search-panel\"\n    cdsDropdownPanel\n    #searchPanel>\n    <ng-content></ng-content>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  buttonTrigger\n  [anchor]=\"toggle.nativeElement\"\n  [panel]=\"searchPanel\"\n  [wasFocusedFn]=\"focusButton\"\n  (removeFocus)=\"hideAdvancedSearch()\"\n>\n</ng-container>\n", dependencies: [{ kind: "directive", type: CdsDropdownDirective, selector: "[cdsDropdown]", inputs: ["anchor", "panel", "options", "strategy", "wasFocusedFn"], outputs: ["removeFocus", "change"] }, { kind: "directive", type: CdsDropdownPanelDirective, selector: "[cdsDropdownPanel]", exportAs: ["dropdownPanel"] }, { kind: "directive", type: CdsDropdownToggleButtonDirective, selector: "[buttonTrigger]", outputs: ["toggleChange"] }, { kind: "directive", type: CdsElementRefDirective, selector: "[cdsElementRef]", exportAs: ["cdsElementRef"] }, { kind: "component", type: CdsButtonComponent, selector: "cds-button", inputs: ["name", "text", "type", "size", "disabled", "underline", "iconName", "alignIcon", "designation", "ariaLabel", "link"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsAdvancedSearchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-advanced-search', template: "<div\n  class=\"cds-advanced-search cds-dropdown cds-input-addon -suffix\"\n  [class.-open]=\"!!dropdownToggle?.expanded\"\n>\n  <cds-button\n    #button\n    cdsElementRef\n    #toggle=\"cdsElementRef\"\n    [disabled]=\"disabled\"\n    text=\"Advanced\"\n    [iconName]=\"!!dropdownToggle?.expanded ? 'arrow_drop_up' : 'arrow_drop_down'\"\n    type=\"tertiary\"\n    [name]=\"'cds-advanced-search-toggle-' + name\"\n    [alignIcon]=\"alignIcon || 'right'\"\n    class=\"cds-advanced-search-toggle\"\n    >\n  </cds-button>\n\n  <div\n    [id]=\"('cds-advanced-search-panel-' + name) | kebabCase\"\n    class=\"cds-advanced-search-panel\"\n    cdsDropdownPanel\n    #searchPanel>\n    <ng-content></ng-content>\n  </div>\n</div>\n\n<ng-container\n  cdsDropdown\n  buttonTrigger\n  [anchor]=\"toggle.nativeElement\"\n  [panel]=\"searchPanel\"\n  [wasFocusedFn]=\"focusButton\"\n  (removeFocus)=\"hideAdvancedSearch()\"\n>\n</ng-container>\n" }]
        }], ctorParameters: function () { return [{ type: CdsSearchbarComponent }]; }, propDecorators: { name: [{
                type: Input
            }], alignIcon: [{
                type: Input
            }], disabled: [{
                type: Input
            }], button: [{
                type: ViewChild,
                args: [CdsButtonComponent, { static: true }]
            }], panelTemplate: [{
                type: ViewChild,
                args: ['panelTemplate', { static: true }]
            }], dropdownToggle: [{
                type: ViewChild,
                args: [CdsDropdownDirective]
            }] } });

class CdsSearchbarModule {
}
CdsSearchbarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSearchbarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsSearchbarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsSearchbarModule, declarations: [CdsSearchbarComponent,
        CdsAdvancedSearchComponent,
        CdsSearchSuffixDirective,
        CdsSearchPrefixDirective], imports: [CdsPipesModule,
        CdsDirectivesModule,
        CdsButtonModule,
        CommonModule,
        CdsHostWrapperModule,
        CdsSuggestionsModule,
        FormsModule], exports: [CdsSearchbarComponent,
        CdsAdvancedSearchComponent,
        CdsSearchSuffixDirective,
        CdsSearchPrefixDirective,
        CdsSuggestionsModule] });
CdsSearchbarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSearchbarModule, imports: [CdsPipesModule,
        CdsDirectivesModule,
        CdsButtonModule,
        CommonModule,
        CdsHostWrapperModule,
        CdsSuggestionsModule,
        FormsModule, CdsSuggestionsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSearchbarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsSearchbarComponent,
                        CdsAdvancedSearchComponent,
                        CdsSearchSuffixDirective,
                        CdsSearchPrefixDirective,
                    ],
                    exports: [
                        CdsSearchbarComponent,
                        CdsAdvancedSearchComponent,
                        CdsSearchSuffixDirective,
                        CdsSearchPrefixDirective,
                        CdsSuggestionsModule
                    ],
                    imports: [
                        CdsPipesModule,
                        CdsDirectivesModule,
                        CdsButtonModule,
                        CommonModule,
                        CdsHostWrapperModule,
                        CdsSuggestionsModule,
                        FormsModule
                    ],
                }]
        }] });

class CdsSidePanelModule {
}
CdsSidePanelModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSidePanelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsSidePanelModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsSidePanelModule, declarations: [CdsSidePanelComponent], imports: [CommonModule, CdsPipesModule, CdsControlBlockModule], exports: [CdsSidePanelComponent] });
CdsSidePanelModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSidePanelModule, imports: [CommonModule, CdsPipesModule, CdsControlBlockModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsSidePanelModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsSidePanelComponent],
                    exports: [CdsSidePanelComponent],
                    imports: [CommonModule, CdsPipesModule, CdsControlBlockModule],
                }]
        }] });

/**
 * ### Description
 * A tab allows users to access related content across different views within the same context.
 */
class CdsTabComponent {
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        /**
         * @internal
         */
        this.$selected = new BehaviorSubject(false);
        /**
         * @internal
         */
        this.change = this.$selected.asObservable();
    }
    /**
     * @internal
     */
    select() {
        this.selected = true;
        this.$selected.next(this.selected);
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    deselect() {
        this.selected = false;
        this.$selected.next(this.selected);
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    focus() {
        this.highlightableDirective.addHighlightedClass();
        this.button.nativeElement.focus();
    }
    /**
     * @internal
     * Removes the highlighted state from the control block element.
     */
    removeFocus() {
        this.highlightableDirective.removeHighlightedClass();
    }
    /**
     * @internal
     */
    getLabel() {
        return this.label;
    }
    /**
     * @internal
     */
    onKeyupArrow() {
        this.select();
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$selected.complete();
    }
}
CdsTabComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTabComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsTabComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsTabComponent, selector: "cds-tab", inputs: { label: "label", selected: "selected", disabled: "disabled", iconName: "iconName" }, viewQueries: [{ propertyName: "button", first: true, predicate: ["button"], descendants: true, read: ElementRef, static: true }, { propertyName: "panelTemplate", first: true, predicate: ["panelTemplate"], descendants: true, read: TemplateRef, static: true }, { propertyName: "buttonTemplate", first: true, predicate: ["buttonTemplate"], descendants: true, read: TemplateRef }, { propertyName: "highlightableDirective", first: true, predicate: CdsKeyboardHighlightableDirective, descendants: true, static: true }], ngImport: i0, template: "<button\n  [id]=\"'cds-tab-' + label | kebabCase\"\n  class=\"cds-tab-button\"\n  [disabled]=\"disabled\"\n  [isDisabled]=\"disabled\"\n  [class.-disabled]=\"disabled\"\n  [class.-selected]=\"selected\"\n  [class.-icon-aligned-left]=\"iconName\"\n  [attr.aria-selected]=\"selected\"\n  [tabindex]=\"selected ? 0 : -1\"\n  [attr.aria-controls]=\"'cds-tab-panel-' + label | kebabCase\"\n  [attr.icon-name]=\"iconName\"\n  role=\"tab\"\n  cdsSelectableItem\n  cdsHighlightable\n  (select)=\"select()\"\n  (keyup.arrowLeft)=\"onKeyupArrow()\"\n  (keyup.arrowRight)=\"onKeyupArrow()\"\n  (blur)=\"removeFocus()\"\n  #button\n>\n  {{ label }}\n</button>\n\n<ng-template #panelTemplate>\n  <div\n    class=\"cds-tab-panel\"\n    tabindex=\"0\"\n    role=\"tabpanel\"\n    [attr.id]=\"'cds-tab-panel-' + label | kebabCase\"\n    [attr.aria-labelledby]=\"'cds-tab-' + label | kebabCase\"\n    #tabPanel\n  >\n    <ng-content></ng-content>\n  </div>\n</ng-template>\n", dependencies: [{ kind: "directive", type: CdsSelectableItemDirective, selector: "[cdsSelectableItem]", outputs: ["select"] }, { kind: "directive", type: CdsKeyboardHighlightableDirective, selector: "[cdsHighlightable]", inputs: ["isDisabled"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-tab', template: "<button\n  [id]=\"'cds-tab-' + label | kebabCase\"\n  class=\"cds-tab-button\"\n  [disabled]=\"disabled\"\n  [isDisabled]=\"disabled\"\n  [class.-disabled]=\"disabled\"\n  [class.-selected]=\"selected\"\n  [class.-icon-aligned-left]=\"iconName\"\n  [attr.aria-selected]=\"selected\"\n  [tabindex]=\"selected ? 0 : -1\"\n  [attr.aria-controls]=\"'cds-tab-panel-' + label | kebabCase\"\n  [attr.icon-name]=\"iconName\"\n  role=\"tab\"\n  cdsSelectableItem\n  cdsHighlightable\n  (select)=\"select()\"\n  (keyup.arrowLeft)=\"onKeyupArrow()\"\n  (keyup.arrowRight)=\"onKeyupArrow()\"\n  (blur)=\"removeFocus()\"\n  #button\n>\n  {{ label }}\n</button>\n\n<ng-template #panelTemplate>\n  <div\n    class=\"cds-tab-panel\"\n    tabindex=\"0\"\n    role=\"tabpanel\"\n    [attr.id]=\"'cds-tab-panel-' + label | kebabCase\"\n    [attr.aria-labelledby]=\"'cds-tab-' + label | kebabCase\"\n    #tabPanel\n  >\n    <ng-content></ng-content>\n  </div>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { label: [{
                type: Input
            }], selected: [{
                type: Input
            }], disabled: [{
                type: Input
            }], iconName: [{
                type: Input
            }], button: [{
                type: ViewChild,
                args: ['button', { read: ElementRef, static: true }]
            }], panelTemplate: [{
                type: ViewChild,
                args: ['panelTemplate', { read: TemplateRef, static: true }]
            }], buttonTemplate: [{
                type: ViewChild,
                args: ['buttonTemplate', { read: TemplateRef }]
            }], highlightableDirective: [{
                type: ViewChild,
                args: [CdsKeyboardHighlightableDirective, { static: true }]
            }] } });

/**
 * ### Description
 * A container for `TabComponents`. Refer to the `TabComponent` API below.
 *
 * The first tab in the tab group is selected and displyed by default, but this behaviour can be overriden if the `[selected]`
 * attribute is present, with a value set to `true`, on any tab component within the tab group.
 */
class CdsTabsComponent {
    /**
     * @internal
     */
    constructor(presenter, changeDetector) {
        this.presenter = presenter;
        this.changeDetector = changeDetector;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
    }
    /**
     * @internal
     */
    ngAfterContentInit() {
        this.tabs.changes.subscribe(() => {
            this.watchForSelection();
            this.setupTraversableItems();
        });
        this.watchForSelection();
        this.setupTraversableItems();
    }
    /**
     * @internal
     */
    watchForSelection() {
        this.$cancelSubscriptions.next();
        const $selectionChanges = this.tabs.map((tab) => tab.change.pipe(filter((selected) => selected), map(() => tab)));
        merge(...$selectionChanges)
            .pipe(takeUntil(this.$cancelSubscriptions))
            .subscribe((selectedTab) => this.onSelectionChanged(selectedTab));
        if (this.tabs.length > 0 && !this.tabs.find(tab => tab.selected)) {
            const selectedTab = this.tabs.find((tab) => tab.selected) || this.tabs.first;
            selectedTab.select();
            this.changeDetector.detectChanges();
        }
    }
    /**
     * @internal
     */
    setupTraversableItems() {
        const traversableItems = this.tabs.map((tab) => ({
            component: tab,
            element: tab.button.nativeElement,
        }));
        this.presenter.watch(traversableItems, { wrap: true, typeahead: false, horizontalOrientation: 'ltr' });
    }
    /**
     * @internal
     */
    onHomeKeydown() {
        this.presenter.setAndFocusActiveItem(this.tabs.first);
    }
    /**
     * @internal
     */
    onEndKeydown() {
        this.presenter.setAndFocusActiveItem(this.tabs.last);
    }
    /**
     * @internal
     */
    onSelectionChanged(component) {
        this.selectedTab = component;
        this.tabs
            .filter(entry => entry !== this.selectedTab)
            .forEach((tab) => {
            tab.deselect();
        });
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsTabsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTabsComponent, deps: [{ token: CdsFocusableListPresenter }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CdsTabsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsTabsComponent, selector: "cds-tabs", providers: [CdsFocusableListPresenter], queries: [{ propertyName: "tabs", predicate: CdsTabComponent, descendants: true }], ngImport: i0, template: "<div class=\"cds-tabs\">\n  <div role=\"tablist\" class=\"cds-tab-nav\">\n    <div\n      class=\"cds-tab-list\"\n      (keydown.home)=\"onHomeKeydown()\"\n      (keydown.end)=\"onEndKeydown()\"\n    >\n      <ng-content></ng-content>\n    </div>\n  </div>\n  <div class=\"cds-tab-content\">\n    <ng-container *ngTemplateOutlet=\"selectedTab?.panelTemplate\">\n    </ng-container>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTabsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-tabs', providers: [CdsFocusableListPresenter], template: "<div class=\"cds-tabs\">\n  <div role=\"tablist\" class=\"cds-tab-nav\">\n    <div\n      class=\"cds-tab-list\"\n      (keydown.home)=\"onHomeKeydown()\"\n      (keydown.end)=\"onEndKeydown()\"\n    >\n      <ng-content></ng-content>\n    </div>\n  </div>\n  <div class=\"cds-tab-content\">\n    <ng-container *ngTemplateOutlet=\"selectedTab?.panelTemplate\">\n    </ng-container>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: CdsFocusableListPresenter }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { tabs: [{
                type: ContentChildren,
                args: [CdsTabComponent, { descendants: true }]
            }] } });

class CdsTabsModule {
}
CdsTabsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsTabsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsTabsModule, declarations: [CdsTabComponent, CdsTabsComponent], imports: [CommonModule, CdsDirectivesModule, CdsPipesModule], exports: [CdsTabComponent, CdsTabsComponent] });
CdsTabsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTabsModule, imports: [CommonModule, CdsDirectivesModule, CdsPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsTabsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsTabComponent, CdsTabsComponent],
                    exports: [CdsTabComponent, CdsTabsComponent],
                    imports: [CommonModule, CdsDirectivesModule, CdsPipesModule],
                }]
        }] });

/**
 * ### Description
 * A control for toggling between two states.
 */
class CdsToggleComponent {
    constructor(
    /** @internal */
    control, controlValueAccessor) {
        this.control = control;
        this.controlValueAccessor = controlValueAccessor;
        /**
         * @internal
         */
        this.registerOnChange = this.controlValueAccessor.registerOnChange;
        /**
         * @internal
         */
        this.registerOnTouched = this.controlValueAccessor.registerOnTouched;
        /**
         * @internal
         */
        this.setDisabledState = this.controlValueAccessor.setDisabledState;
        /**
         * A text label to be displayed when the Toggle is checked.
         */
        this.checkedLabel = '';
        /**
         * A text label to be displayed when the Toggle is unchecked.
         */
        this.uncheckedLabel = '';
        /**
         * Sets the color to be displayed when the Toggle is checked.
         */
        this.checkedColor = '';
        /**
         * Sets the color to be displayed when the Toggle is unchecked.
         */
        this.uncheckedColor = '';
        /**
         * An event containing the new state of this control.
         */
        this.change = this.controlValueAccessor.change;
        if (this.control) {
            this.control.valueAccessor = this;
        }
        else {
            throw new Error('FormControl is missing from Toggle. ');
        }
    }
    /**
     * @internal
     */
    get disabled() {
        return this.controlValueAccessor.disabled;
    }
    /**
     * @internal
     */
    get id() {
        return 'cds-toggle-' + kebabCaseTransform(this.name);
    }
    /**
     * @internal
     */
    get checkedLabelText() {
        if (!this.customStates) {
            return 'enabled';
        }
        const regex = /_/g;
        return `${this.showIcons ? this.checkedLabel.replace(regex, ' ') : this.checkedLabel} enabled`;
    }
    /**
     * @internal
     */
    get uncheckedLabelText() {
        if (!this.customStates) {
            return 'disabled';
        }
        const regex = /_/g;
        return `${this.showIcons ? this.uncheckedLabel.replace(regex, ' ') : this.uncheckedLabel} enabled`;
    }
    /**
     * @internal
     */
    get style() {
        let style = ``;
        if (this.width && this.width.length) {
            style += `width: ${this.width};`;
        }
        if (this.checkedColor && this.checkedColor.length) {
            style += `--color-checked: ${this.checkedColor};`;
        }
        if (this.uncheckedColor && this.uncheckedColor.length) {
            style += `--color-unchecked: ${this.uncheckedColor};`;
        }
        return style;
    }
    /**
     * @internal
     */
    onToggleClick(event) {
        event.stopPropagation();
        this.controlValueAccessor.onTouch();
        this.checked = !!!this.checked;
        this.controlValueAccessor.onChange(this.checked);
    }
    /**
     * @internal
     */
    writeValue(inputValue) {
        this.checked = inputValue;
    }
}
CdsToggleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToggleComponent, deps: [{ token: i1$2.NgControl, optional: true, self: true }, { token: CdsControlValueAccessorComponent }], target: i0.ɵɵFactoryTarget.Component });
CdsToggleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsToggleComponent, selector: "cds-toggle", inputs: { checkedLabel: "checkedLabel", uncheckedLabel: "uncheckedLabel", name: "name", showIcons: "showIcons", customStates: "customStates", width: "width", checkedColor: "checkedColor", uncheckedColor: "uncheckedColor" }, outputs: { change: "change" }, providers: [CdsControlValueAccessorComponent], ngImport: i0, template: "<div\n  class=\"cds-toggle-wrapper\"\n  [ngClass]=\"{\n    '-with-text': checkedLabel?.length || uncheckedLabel?.length,\n    '-with-icon': showIcons\n  }\"\n  [attr.style]=\"style\"\n>\n  <input\n    tabindex=\"0\"\n    class=\"cds-toggle\"\n    type=\"button\"\n    [id]=\"id\"\n    [checked]=\"checked\"\n    [attr.disabled]=\"disabled || null\"\n    [attr.aria-pressed]=\"checked\"\n    [attr.aria-describedby]=\"id + 'assistive-label'\"\n    [attr.tabindex]=\"0\"\n    (click)=\"onToggleClick($event)\"\n    cdsHighlightable\n  />\n\n  <label class=\"cds-toggle-label\" [for]=\"id\">\n    <span\n      class=\"cds-toggle-label_inner\"\n      [attr.checked-label]=\"checkedLabel\"\n      [attr.unchecked-label]=\"uncheckedLabel\"\n    >\n    </span>\n    <span class=\"cds-toggle-switch\"></span>\n\n    <ng-container>\n      <span id=\"{{ id }}assistive-label\" class=\"cds-assistive-text\">\n        <span class=\"cds-toggle-option_A\">{{ checkedLabelText }}</span>\n        <span class=\"cds-toggle-option_B\">{{ uncheckedLabelText }}</span>\n      </span>\n    </ng-container>\n  </label>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: CdsKeyboardHighlightableDirective, selector: "[cdsHighlightable]", inputs: ["isDisabled"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToggleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-toggle', providers: [CdsControlValueAccessorComponent], template: "<div\n  class=\"cds-toggle-wrapper\"\n  [ngClass]=\"{\n    '-with-text': checkedLabel?.length || uncheckedLabel?.length,\n    '-with-icon': showIcons\n  }\"\n  [attr.style]=\"style\"\n>\n  <input\n    tabindex=\"0\"\n    class=\"cds-toggle\"\n    type=\"button\"\n    [id]=\"id\"\n    [checked]=\"checked\"\n    [attr.disabled]=\"disabled || null\"\n    [attr.aria-pressed]=\"checked\"\n    [attr.aria-describedby]=\"id + 'assistive-label'\"\n    [attr.tabindex]=\"0\"\n    (click)=\"onToggleClick($event)\"\n    cdsHighlightable\n  />\n\n  <label class=\"cds-toggle-label\" [for]=\"id\">\n    <span\n      class=\"cds-toggle-label_inner\"\n      [attr.checked-label]=\"checkedLabel\"\n      [attr.unchecked-label]=\"uncheckedLabel\"\n    >\n    </span>\n    <span class=\"cds-toggle-switch\"></span>\n\n    <ng-container>\n      <span id=\"{{ id }}assistive-label\" class=\"cds-assistive-text\">\n        <span class=\"cds-toggle-option_A\">{{ checkedLabelText }}</span>\n        <span class=\"cds-toggle-option_B\">{{ uncheckedLabelText }}</span>\n      </span>\n    </ng-container>\n  </label>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: CdsControlValueAccessorComponent }]; }, propDecorators: { checkedLabel: [{
                type: Input
            }], uncheckedLabel: [{
                type: Input
            }], name: [{
                type: Input
            }], showIcons: [{
                type: Input
            }], customStates: [{
                type: Input
            }], width: [{
                type: Input
            }], checkedColor: [{
                type: Input
            }], uncheckedColor: [{
                type: Input
            }], change: [{
                type: Output
            }] } });

class CdsToggleModule {
}
CdsToggleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToggleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsToggleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsToggleModule, declarations: [CdsToggleComponent], imports: [CdsPipesModule, CommonModule, CdsDirectivesModule], exports: [CdsToggleComponent] });
CdsToggleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToggleModule, imports: [CdsPipesModule, CommonModule, CdsDirectivesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToggleModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsToggleComponent],
                    exports: [CdsToggleComponent],
                    imports: [CdsPipesModule, CommonModule, CdsDirectivesModule],
                }]
        }] });

// import { DropdownPresenter } from '../../presenters/dropdown.presenter';
class CdsToggledContentContainerComponent {
    constructor(
    // private dropdownPresenter: DropdownPresenter,
    accordionPresenter, changeDetector, element) {
        this.accordionPresenter = accordionPresenter;
        this.changeDetector = changeDetector;
        this.element = element;
        this._type = 'dropdown';
        this.$type = new Subject();
        this.$cancelSubscriptions = new Subject();
        this.expandedStateChange = new EventEmitter();
    }
    set type(type) {
        this._type = type;
        this.$type.next(this._type);
    }
    ngOnInit() {
        this.createSubscription();
    }
    ngAfterViewInit() {
        this.$type
            .pipe(takeUntil(this.$cancelSubscriptions), distinctUntilChanged())
            .subscribe(() => {
            if (this.expanded) {
                setTimeout(() => this.expand());
            }
        });
    }
    createSubscription() {
        // this.subscription = merge(
        //   this.dropdownPresenter.change,
        //   this.accordionPresenter.change
        // )
        //   .pipe(
        //     distinctUntilChanged(),
        //     tap((value) => (this.expanded = value)),
        //     // skip(1),     // skip initial expanded value (set in ngOnInit())
        //     shareReplay()
        //   )
        //   .subscribe((expanded) => {
        //     this.expandedStateChange.next(expanded);
        //     if (!expanded) {
        //       this.collapse();
        //     }
        //   });
    }
    // Always expand individually
    expand() {
        // this.subscription.unsubscribe(); // unsubscribe so we don't trigger expandedStateChange event
        // if (this._type === 'dropdown') {
        //   this.accordionPresenter.collapse(this.content.nativeElement);
        //   this.createSubscription();
        //   this.expandDropdown();
        // }
        // if (this._type === 'accordion') {
        //   this.dropdownPresenter.collapse(this.content.nativeElement);
        //   this.createSubscription();
        //   this.accordionPresenter.expand(this.content.nativeElement);
        // }
    }
    expandDropdown() {
        // const toggle = this.element.nativeElement.querySelector(
        //   '[cdsToggleTrigger]'
        // );
        // this.dropdownPresenter.expand(
        //   toggle,
        //   this.content.nativeElement,
        //   this.margin,
        //   this.adjacent,
        //   this.scrollableX,
        //   this.scrollableY
        // );
        // this.changeDetector.detectChanges();
    }
    // Always collapse both
    collapse() {
        // this.dropdownPresenter.collapse(this.content.nativeElement);
        // this.accordionPresenter.collapse(this.content.nativeElement);
        // this.changeDetector.detectChanges();
    }
    ngOnDestroy() {
        // this.dropdownPresenter.destroy(this.content.nativeElement);
        // this.accordionPresenter.destroy();
        // if (this.subscription) {
        //   this.subscription.unsubscribe();
        // }
        // this.$cancelSubscriptions.next();
        // this.$cancelSubscriptions.complete();
    }
}
CdsToggledContentContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToggledContentContainerComponent, deps: [{ token: AccordionPresenter }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
CdsToggledContentContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsToggledContentContainerComponent, selector: "cds-toggled-content-container", inputs: { name: "name", scrollableX: "scrollableX", scrollableY: "scrollableY", margin: "margin", adjacent: "adjacent", type: "type", expandOnInit: "expandOnInit" }, outputs: { expandedStateChange: "expandedStateChange" }, viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true, static: true }], ngImport: i0, template: "<div\n  [class.cds-dropdown]=\"_type === 'dropdown'\"\n  [class.cds-accordion]=\"_type === 'accordion'\"\n  [class.-open]=\"expanded\"\n>\n  <ng-content select=\"[cdsToggleTrigger]\"></ng-content>\n\n  <div\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n    #content\n    [class.cds-dropdown-content]=\"_type === 'dropdown'\"\n    [class.cds-accordion-content]=\"_type === 'accordion'\"\n  >\n    <ng-content select=\"[cdsToggleContent]\"></ng-content>\n  </div>\n</div>\n", dependencies: [{ kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToggledContentContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-toggled-content-container', template: "<div\n  [class.cds-dropdown]=\"_type === 'dropdown'\"\n  [class.cds-accordion]=\"_type === 'accordion'\"\n  [class.-open]=\"expanded\"\n>\n  <ng-content select=\"[cdsToggleTrigger]\"></ng-content>\n\n  <div\n    [id]=\"'cds-content-container-' + name | kebabCase\"\n    #content\n    [class.cds-dropdown-content]=\"_type === 'dropdown'\"\n    [class.cds-accordion-content]=\"_type === 'accordion'\"\n  >\n    <ng-content select=\"[cdsToggleContent]\"></ng-content>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: AccordionPresenter }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }]; }, propDecorators: { name: [{
                type: Input
            }], scrollableX: [{
                type: Input
            }], scrollableY: [{
                type: Input
            }], margin: [{
                type: Input
            }], adjacent: [{
                type: Input
            }], type: [{
                type: Input
            }], expandOnInit: [{
                type: Input
            }], content: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], expandedStateChange: [{
                type: Output
            }] } });

class CdsToggledContentContainerModule {
}
CdsToggledContentContainerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToggledContentContainerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsToggledContentContainerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsToggledContentContainerModule, declarations: [CdsToggledContentContainerComponent], imports: [CommonModule, CdsPipesModule, CdsDirectivesModule], exports: [CdsToggledContentContainerComponent] });
CdsToggledContentContainerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToggledContentContainerModule, imports: [CommonModule, CdsPipesModule, CdsDirectivesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToggledContentContainerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsToggledContentContainerComponent
                    ],
                    exports: [
                        CdsToggledContentContainerComponent
                    ],
                    imports: [CommonModule, CdsPipesModule, CdsDirectivesModule]
                }]
        }] });

class CdsToolbarItemDirective {
    constructor(element, renderer) {
        this.element = element;
        this.renderer = renderer;
        this._display = true;
        this.menuClick = new EventEmitter();
    }
    setDisplay(display) {
        this.renderer.setStyle(this.element.nativeElement, 'display', display ? null : 'none');
    }
}
CdsToolbarItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToolbarItemDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
CdsToolbarItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsToolbarItemDirective, selector: "[cdsToolbarItem]", inputs: { menuLabel: "menuLabel" }, outputs: { menuClick: "menuClick" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToolbarItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsToolbarItem]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { menuLabel: [{
                type: Input
            }], menuClick: [{
                type: Output
            }] } });

/**
 * The CdsToolbarComponent is a responsive toolbar that accepts and displays child elements. When the total width of the child elements
 * is less than the available horizontal space, overflowing elements are moved into a dropdown list attached to the CdsToolbarComponent.
 */
class CdsToolbarComponent {
    /**
     * @internal
     */
    constructor(overflowPresenter, changeDetector, toolbarPresenter) {
        this.overflowPresenter = overflowPresenter;
        this.changeDetector = changeDetector;
        this.toolbarPresenter = toolbarPresenter;
        /**
         * @internal
         */
        this.$cancelSubscriptions = new Subject();
        /**
         * @internal
         */
        this._display = 'contents';
    }
    /**
     * @internal
     */
    get overflowItems() {
        return this.toolbarPresenter.overflowItems;
    }
    ;
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.bindKeyTab();
        this.toolbarPresenter = new CdsToolbarPresenter(this.overflowPresenter, this.overflowContainer);
        this.toolbarPresenter.change.subscribe(() => {
            this.dropdown.collapse();
            this.toolbarItems.forEach(item => item.setDisplay(false));
            this.toolbarPresenter.containerItems.forEach(item => item.setDisplay(true));
            this.changeDetector.detectChanges();
            this.bindKeyTab();
        });
        this.toolbarPresenter.setItems(this.toolbarItems);
        this.toolbarPresenter.setDropdownDirective(this.dropdown);
        this.toolbarPresenter.init();
    }
    /**
     * @internal
     */
    onExpandedStateChanged(expanded) {
        this.expanded = expanded;
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    onFocusRemoved() {
        this.dropdown.collapse();
    }
    /**
     * @internal
     */
    bindKeyTab() {
        this.$cancelSubscriptions.next();
        const toolbarContentElement = this.toolbarContent.nativeElement;
        const childNodes = Array.from(toolbarContentElement.childNodes);
        // let totalCount = childNodes.length;
        childNodes.forEach((childNode) => {
            // Attach keydown.tab event listener to visible child node
            fromEvent(childNode, 'keydown')
                .pipe(takeUntil(this.$cancelSubscriptions))
                .subscribe(event => {
                if (this.overflowItems.length <= 0) {
                    return;
                }
                if (event.key === 'Tab' && !event.shiftKey) {
                    const visibleChildren = childNodes.filter(node => window.getComputedStyle(node).display !== 'none');
                    const isLastVisibleChild = childNode.isEqualNode(visibleChildren[visibleChildren.length - 1]);
                    this.toolbarPresenter.shiftFocusIntoDropdownLtr(event, isLastVisibleChild);
                }
            });
        });
    }
    /**
     * @internal
     */
    get toggleAttributes() {
        return {
            'aria-haspopup': 'true',
            'aria-label': 'Show More',
            'aria-expanded': this.dropdown && this.dropdown.expanded,
            'title': 'Show More'
        };
    }
    /**
     * @internal
     */
    ngOnDestroy() {
        this.toolbarPresenter.destroy();
        this.$cancelSubscriptions.next();
        this.$cancelSubscriptions.complete();
    }
}
CdsToolbarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToolbarComponent, deps: [{ token: CdsOverflowObserverPresenter }, { token: i0.ChangeDetectorRef }, { token: CdsToolbarPresenter }], target: i0.ɵɵFactoryTarget.Component });
CdsToolbarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsToolbarComponent, selector: "cds-toolbar", host: { properties: { "style.display": "this._display" } }, providers: [CdsOverflowObserverPresenter, CdsToolbarPresenter], queries: [{ propertyName: "toolbarItems", predicate: CdsToolbarItemDirective, descendants: true }], viewQueries: [{ propertyName: "dropdown", first: true, predicate: CdsDropdownDirective, descendants: true }, { propertyName: "overflowContainer", first: true, predicate: ["overflowContainer"], descendants: true }, { propertyName: "toolbarContent", first: true, predicate: ["toolbarContent"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"cds-toolbar\">\n  <div class=\"toolbar-content-container\" #overflowContainer>\n    <div class=\"toolbar-content\" #toolbarContent>\n      <ng-content></ng-content>\n    </div>\n    <div\n      class=\"cds-dropdown\"\n      [class.-open]=\"expanded\"\n    >\n      \n      <cds-control-block\n        cdsElementRef\n        tabindex=\"-1\"\n        #toggle=\"cdsElementRef\"\n        [attributes]=\"toggleAttributes\"\n        [style.display]=\"overflowItems.length > 0 ? null : 'none'\"\n      >\n        <i class=\"material-icons\">more_horiz</i>\n      </cds-control-block>\n\n      <div \n        cdsDropdownPanel\n        #toolbarPanel\n      >\n        <ol class=\"cds-list\"> \n          <li *ngFor=\"let item of overflowItems; let last = last\"\n            tabindex=\"0\"\n            class=\"overflow-item\" \n            (keydown.tab)=\"toolbarPresenter.shiftFocusIntoDropdownLtr($event, last)\"\n            (click)=\"item.menuClick.emit()\">\n          {{ item.menuLabel }}\n          </li>\n        </ol>\n      </div>\n\n      <ng-container\n        cdsDropdown\n        buttonTrigger\n        [anchor]=\"toggle.nativeElement\"\n        [panel]=\"toolbarPanel\"\n        (removeFocus)=\"onFocusRemoved()\"\n        (change)=\"onExpandedStateChanged($event)\"\n      >\n      </ng-container>\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: CdsDropdownDirective, selector: "[cdsDropdown]", inputs: ["anchor", "panel", "options", "strategy", "wasFocusedFn"], outputs: ["removeFocus", "change"] }, { kind: "directive", type: CdsDropdownPanelDirective, selector: "[cdsDropdownPanel]", exportAs: ["dropdownPanel"] }, { kind: "directive", type: CdsDropdownToggleButtonDirective, selector: "[buttonTrigger]", outputs: ["toggleChange"] }, { kind: "directive", type: CdsElementRefDirective, selector: "[cdsElementRef]", exportAs: ["cdsElementRef"] }, { kind: "component", type: CdsControlBlockComponent, selector: "cds-control-block", inputs: ["name", "size", "disabled", "tabindex", "active", "attributes"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToolbarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-toolbar', providers: [CdsOverflowObserverPresenter, CdsToolbarPresenter], template: "<div class=\"cds-toolbar\">\n  <div class=\"toolbar-content-container\" #overflowContainer>\n    <div class=\"toolbar-content\" #toolbarContent>\n      <ng-content></ng-content>\n    </div>\n    <div\n      class=\"cds-dropdown\"\n      [class.-open]=\"expanded\"\n    >\n      \n      <cds-control-block\n        cdsElementRef\n        tabindex=\"-1\"\n        #toggle=\"cdsElementRef\"\n        [attributes]=\"toggleAttributes\"\n        [style.display]=\"overflowItems.length > 0 ? null : 'none'\"\n      >\n        <i class=\"material-icons\">more_horiz</i>\n      </cds-control-block>\n\n      <div \n        cdsDropdownPanel\n        #toolbarPanel\n      >\n        <ol class=\"cds-list\"> \n          <li *ngFor=\"let item of overflowItems; let last = last\"\n            tabindex=\"0\"\n            class=\"overflow-item\" \n            (keydown.tab)=\"toolbarPresenter.shiftFocusIntoDropdownLtr($event, last)\"\n            (click)=\"item.menuClick.emit()\">\n          {{ item.menuLabel }}\n          </li>\n        </ol>\n      </div>\n\n      <ng-container\n        cdsDropdown\n        buttonTrigger\n        [anchor]=\"toggle.nativeElement\"\n        [panel]=\"toolbarPanel\"\n        (removeFocus)=\"onFocusRemoved()\"\n        (change)=\"onExpandedStateChanged($event)\"\n      >\n      </ng-container>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: CdsOverflowObserverPresenter }, { type: i0.ChangeDetectorRef }, { type: CdsToolbarPresenter }]; }, propDecorators: { dropdown: [{
                type: ViewChild,
                args: [CdsDropdownDirective]
            }], overflowContainer: [{
                type: ViewChild,
                args: ['overflowContainer']
            }], toolbarContent: [{
                type: ViewChild,
                args: ['toolbarContent', { static: true }]
            }], _display: [{
                type: HostBinding,
                args: ['style.display']
            }], toolbarItems: [{
                type: ContentChildren,
                args: [CdsToolbarItemDirective, { descendants: true }]
            }] } });

class CdsToolbarModule {
}
CdsToolbarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToolbarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsToolbarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsToolbarModule, declarations: [CdsToolbarComponent,
        CdsToolbarItemDirective], imports: [CommonModule,
        CdsDirectivesModule,
        CdsControlBlockModule,
        CdsHostWrapperModule], exports: [CdsToolbarComponent,
        CdsToolbarItemDirective] });
CdsToolbarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToolbarModule, imports: [CommonModule,
        CdsDirectivesModule,
        CdsControlBlockModule,
        CdsHostWrapperModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsToolbarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsToolbarComponent,
                        CdsToolbarItemDirective
                    ],
                    exports: [
                        CdsToolbarComponent,
                        CdsToolbarItemDirective
                    ],
                    imports: [
                        CommonModule,
                        CdsDirectivesModule,
                        CdsControlBlockModule,
                        CdsHostWrapperModule
                    ]
                }]
        }] });

/**
 * @internal
 * This directive only serves to check if a custom toggle element has been provided to the Expandable Container.
 */
class CdsExpandableContainerToggleDirective {
}
CdsExpandableContainerToggleDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsExpandableContainerToggleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CdsExpandableContainerToggleDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsExpandableContainerToggleDirective, selector: "[cdsExpandableContainerToggle]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsExpandableContainerToggleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsExpandableContainerToggle]'
                }]
        }] });

/**
 * Flattens an HTML Element node and returns the element and all its descendants in
 * one single-level array.
 */
function flattenNode(element) {
    const children = Array.from(element.childNodes).reduce((acc, current) => {
        if (current === element) {
            return acc;
        }
        return [...acc, ...flattenNode(current)];
    }, []);
    return [element, ...children];
}
;

class ClampPresenter {
}

class CdsLineClampPresenter extends ClampPresenter {
    hasHiddenContent(size, element) {
        return element.scrollHeight > element.clientHeight;
    }
    visibleContent(size, element) {
        return getVisibleLines(element);
    }
    /**
     * @internal
     */
    updateUI(size, element, renderer) {
        renderer.setStyle(element, '--line-clamp', size, 2);
    }
}
CdsLineClampPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLineClampPresenter, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
CdsLineClampPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLineClampPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsLineClampPresenter, decorators: [{
            type: Injectable
        }] });
function getLineCount(target, filterFn) {
    const elements = flattenNode(target)
        .filter((entry, _, array) => entry.nodeName !== '#comment' &&
        !array.find(child => entry !== child && entry.contains(child)));
    const elementRects = elements.reduce((acc, node, index, array) => {
        const range = document.createRange();
        range.selectNode(node);
        const rects = Array.from(range.getClientRects()).filter(filterFn);
        if (array[index - 1]?.nodeName !== 'BR' && node.nodeName === 'BR') {
            return acc;
        }
        return acc.concat({ node, rects });
    }, []);
    // @hack: Math.floor helps with <br /> elements
    // When <br/> elements start between pixels, we round y down to the nearest whole y position
    const yPositions = [].concat(...elementRects.map(rect => rect.rects))
        .map(({ bottom }) => Math.floor(bottom));
    return new Set([...yPositions]).size;
}
function getVisibleLines(target) {
    const range = document.createRange();
    // filter out lines that are above the bottom of the content area
    return getLineCount(target, ({ y, height }) => {
        range.collapse();
        range.selectNode(target);
        const { bottom } = range.getClientRects()[0];
        return y + height <= bottom;
    });
}

class CdsWordClampPresenter extends ClampPresenter {
    hasHiddenContent(size, element) {
        const innerText = (element.firstChild ? element.firstChild.innerText : '')
            .split(/\r?\n|\r|\s/g).filter(word => !!word);
        const currentWordCount = innerText.length;
        return currentWordCount >= size;
    }
    visibleContent(size, element) {
        return (element.firstChild
            ? element.firstChild.innerText
            : '').split(/\r?\n|\r|\s/g).filter(word => !!word).length;
    }
    updateUI() { }
}
CdsWordClampPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsWordClampPresenter, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
CdsWordClampPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsWordClampPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsWordClampPresenter, decorators: [{
            type: Injectable
        }] });

class CdsHeightClampPresenter extends ClampPresenter {
    hasHiddenContent(size, element) {
        return element.scrollHeight > element.clientHeight;
    }
    visibleContent(size, element) {
        return element.clientHeight;
    }
    updateUI(size, element, renderer) {
        renderer.setStyle(element, 'max-height', `${size}px`, 2);
    }
}
CdsHeightClampPresenter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsHeightClampPresenter, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
CdsHeightClampPresenter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsHeightClampPresenter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsHeightClampPresenter, decorators: [{
            type: Injectable
        }] });

/**
 * ### Description
 * A container which can be expanded to reveal more content.
 */
class CdsExpandableContainerComponent {
    /**
     * @internal
     */
    constructor(changeDetector, renderer, wordClampPresenter, heightClampPresenter, lineClampPresenter) {
        this.changeDetector = changeDetector;
        this.renderer = renderer;
        this.wordClampPresenter = wordClampPresenter;
        this.heightClampPresenter = heightClampPresenter;
        this.lineClampPresenter = lineClampPresenter;
        /**
         * @internal
         */
        this.hideToggle = false;
        /**
         * Emits an event when the current breakpoint changes
         */
        // eslint-disable-next-line @angular-eslint/no-output-native
        this.change = new EventEmitter();
    }
    /**
     * @internal
     */
    get label() {
        return this.hasHiddenContent ? this.collapsedStateLabel : this.expandedStateLabel;
    }
    /**
     * @internal
     */
    get collapsedStateLabel() {
        if (this.type === 'height') {
            return this._collapsedStateLabel || 'Show More';
        }
        return this._collapsedStateLabel || 'Read More';
    }
    /**
     * @internal
     */
    get expandedStateLabel() {
        if (this.type === 'height') {
            return this._expandedStateLabel || 'Show Less';
        }
        return this._expandedStateLabel || 'Read Less';
    }
    /**
     * @internal
     */
    get hasHiddenContent() {
        return (this.presenter || this.wordClampPresenter).hasHiddenContent(this.cutoff, this.content.nativeElement);
    }
    /**
     * @internal
     */
    get visibleContent() {
        return (this.presenter || this.wordClampPresenter).visibleContent(this.cutoff, this.content.nativeElement);
    }
    /**
     * Choose between word count-, line count- and height-based truncation.
     * - `wordCount` (default) truncates content based on how many words are present,
     * - `lineCount` truncates content based on how many lines are present in the paragraph, and
     * - `height` truncates content based on height (this setting is more relevant for content containing block-level elements).
     */
    set type(type) {
        this._type = type;
        switch (this._type) {
            case ('height'): {
                this.presenter = this.heightClampPresenter;
                break;
            }
            case ('lineCount'): {
                this.presenter = this.lineClampPresenter;
                break;
            }
            case ('wordCount'):
            default: {
                this.presenter = this.wordClampPresenter;
                break;
            }
        }
    }
    get type() {
        return this._type || 'wordCount';
    }
    /**
     * @internal
     */
    ngAfterViewInit() {
        this.initialSize = this.initialSize || this.chunkSize;
        this.cutoff = this.initialSize;
        this.updateUI();
    }
    /**
     * @internal
     */
    toggle() {
        const cutoff = !this.hasHiddenContent
            ? this.initialSize
            : Math.min(this.visibleContent, this.cutoff) + this.chunkSize;
        if (!this.preventDefault) {
            this.cutoff = cutoff;
        }
        this.change.next(cutoff);
        this.updateUI();
    }
    /**
     * @internal
     */
    updateToggleVisibility() {
        const showReadLess = this.cutoff !== this.initialSize;
        const showReadMore = this.hasHiddenContent;
        this.hideToggle = !showReadLess && !showReadMore;
        this.changeDetector.detectChanges();
    }
    /**
     * @internal
     */
    updateUI() {
        this.renderer.setStyle(this.content.nativeElement, '--line-clamp', '', 2);
        this.renderer.setStyle(this.content.nativeElement, 'max-height', null, 2);
        this.presenter.updateUI(this.cutoff, this.content.nativeElement, this.renderer);
        this.changeDetector.detectChanges();
        this.updateToggleVisibility();
    }
}
CdsExpandableContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsExpandableContainerComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: CdsWordClampPresenter }, { token: CdsHeightClampPresenter }, { token: CdsLineClampPresenter }], target: i0.ɵɵFactoryTarget.Component });
CdsExpandableContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CdsExpandableContainerComponent, selector: "cds-expandable-container", inputs: { name: "name", initialSize: "initialSize", chunkSize: "chunkSize", preventDefault: "preventDefault", alignToggle: "alignToggle", _collapsedStateLabel: ["collapsedStateLabel", "_collapsedStateLabel"], _expandedStateLabel: ["expandedStateLabel", "_expandedStateLabel"], type: "type" }, outputs: { change: "change" }, providers: [
        CdsLineClampPresenter,
        CdsWordClampPresenter,
        CdsHeightClampPresenter
    ], queries: [{ propertyName: "customToggle", first: true, predicate: CdsExpandableContainerToggleDirective, descendants: true }], viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true, static: true }, { propertyName: "container", first: true, predicate: ["container"], descendants: true, static: true }], ngImport: i0, template: "<div\n  class=\"cds-expandable-container\"\n  [class.-word-count]=\"type==='wordCount'\"\n  [class.-line-clamp]=\"type==='lineCount'\"\n  [class.-height]=\"type==='height'\"\n  cdsResizeObserver\n  [active]=\"type==='lineCount'\"\n  (cdsResize)=\"updateToggleVisibility()\"\n\n  #container\n>\n  <div class=\"cds-expandable-container-content\" #content>\n    <ng-content></ng-content>\n    <ng-template *ngIf=\"type === 'wordCount'\" [ngTemplateOutlet]=\"wordCountTemplate\"></ng-template>\n  </div>\n\n  <ng-template *ngIf=\"type !== 'wordCount'\" [ngTemplateOutlet]=\"suffixTemplate\"></ng-template>\n</div>\n\n<ng-template #wordCountTemplate>\n  <span *ngIf=\"type==='wordCount' && hasHiddenContent\">...</span>\n  <ng-template *ngIf=\"type==='wordCount'\" [ngTemplateOutlet]=\"suffixTemplate\"></ng-template>\n</ng-template>\n\n<ng-template #suffixTemplate>\n  <div\n    *ngIf=\"!hideToggle\"\n    class=\"cds-expandable-container-suffix\"\n    [class.-align-right]=\"alignToggle === 'right'\"\n    (click)=\"toggle()\"\n  >\n    <ng-content *ngIf=\"customToggle; else defaultToggle\" select=\"[cdsExpandableContainerToggle]\"></ng-content>\n  </div>\n</ng-template>\n\n<ng-template #defaultToggle>\n  <cds-button\n    [id]=\"'cds-expandable-container-toggle-' + (name | kebabCase)\"\n    size=\"small\"\n    type=\"tertiary\"\n    [underline]=\"true\"\n    [text]=\"label\"\n    [attr.aria-expanded]=\"cutoff > initialSize\"\n    [link]=\"true\"\n  ></cds-button>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: CdsResizeObserverDirective, selector: "[cdsResizeObserver]", inputs: ["active"], outputs: ["cdsResize"] }, { kind: "component", type: CdsButtonComponent, selector: "cds-button", inputs: ["name", "text", "type", "size", "disabled", "underline", "iconName", "alignIcon", "designation", "ariaLabel", "link"] }, { kind: "pipe", type: CdsKebabCasePipe, name: "kebabCase" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsExpandableContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-expandable-container', providers: [
                        CdsLineClampPresenter,
                        CdsWordClampPresenter,
                        CdsHeightClampPresenter
                    ], template: "<div\n  class=\"cds-expandable-container\"\n  [class.-word-count]=\"type==='wordCount'\"\n  [class.-line-clamp]=\"type==='lineCount'\"\n  [class.-height]=\"type==='height'\"\n  cdsResizeObserver\n  [active]=\"type==='lineCount'\"\n  (cdsResize)=\"updateToggleVisibility()\"\n\n  #container\n>\n  <div class=\"cds-expandable-container-content\" #content>\n    <ng-content></ng-content>\n    <ng-template *ngIf=\"type === 'wordCount'\" [ngTemplateOutlet]=\"wordCountTemplate\"></ng-template>\n  </div>\n\n  <ng-template *ngIf=\"type !== 'wordCount'\" [ngTemplateOutlet]=\"suffixTemplate\"></ng-template>\n</div>\n\n<ng-template #wordCountTemplate>\n  <span *ngIf=\"type==='wordCount' && hasHiddenContent\">...</span>\n  <ng-template *ngIf=\"type==='wordCount'\" [ngTemplateOutlet]=\"suffixTemplate\"></ng-template>\n</ng-template>\n\n<ng-template #suffixTemplate>\n  <div\n    *ngIf=\"!hideToggle\"\n    class=\"cds-expandable-container-suffix\"\n    [class.-align-right]=\"alignToggle === 'right'\"\n    (click)=\"toggle()\"\n  >\n    <ng-content *ngIf=\"customToggle; else defaultToggle\" select=\"[cdsExpandableContainerToggle]\"></ng-content>\n  </div>\n</ng-template>\n\n<ng-template #defaultToggle>\n  <cds-button\n    [id]=\"'cds-expandable-container-toggle-' + (name | kebabCase)\"\n    size=\"small\"\n    type=\"tertiary\"\n    [underline]=\"true\"\n    [text]=\"label\"\n    [attr.aria-expanded]=\"cutoff > initialSize\"\n    [link]=\"true\"\n  ></cds-button>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: CdsWordClampPresenter }, { type: CdsHeightClampPresenter }, { type: CdsLineClampPresenter }]; }, propDecorators: { name: [{
                type: Input
            }], initialSize: [{
                type: Input
            }], chunkSize: [{
                type: Input
            }], preventDefault: [{
                type: Input
            }], alignToggle: [{
                type: Input
            }], _collapsedStateLabel: [{
                type: Input,
                args: ['collapsedStateLabel']
            }], _expandedStateLabel: [{
                type: Input,
                args: ['expandedStateLabel']
            }], type: [{
                type: Input
            }], change: [{
                type: Output
            }], content: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], container: [{
                type: ViewChild,
                args: ['container', { static: true }]
            }], customToggle: [{
                type: ContentChild,
                args: [CdsExpandableContainerToggleDirective]
            }] } });

class CdsExpandableContainerAccordionDirective {
    constructor(component) {
        this.component = component;
        /**
         * @internal
         */
        this.toggle = () => {
            this.expanded = !this._expanded;
            this.component.change.next(this.component.cutoff);
        };
        this.component.toggle = this.toggle;
    }
    /**
     * Sets the expanded state of the Expandable Container
     */
    set expanded(expanded) {
        this._expanded = expanded;
        if (!this.component.preventDefault) {
            const cutoff = this._expanded
                ? ''
                : this.component.initialSize;
            this.component.cutoff = cutoff;
        }
        this.component.updateUI();
    }
}
CdsExpandableContainerAccordionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsExpandableContainerAccordionDirective, deps: [{ token: CdsExpandableContainerComponent, self: true }], target: i0.ɵɵFactoryTarget.Directive });
CdsExpandableContainerAccordionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: CdsExpandableContainerAccordionDirective, selector: "cds-expandable-container[cdsExpandableContainerAccordion]", inputs: { expanded: "expanded" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsExpandableContainerAccordionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'cds-expandable-container[cdsExpandableContainerAccordion]'
                }]
        }], ctorParameters: function () { return [{ type: CdsExpandableContainerComponent, decorators: [{
                    type: Self
                }] }]; }, propDecorators: { expanded: [{
                type: Input
            }] } });

class CdsExpandableContainerModule {
}
CdsExpandableContainerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsExpandableContainerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdsExpandableContainerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdsExpandableContainerModule, declarations: [CdsExpandableContainerComponent,
        CdsExpandableContainerToggleDirective,
        CdsExpandableContainerAccordionDirective], imports: [CommonModule,
        CdsDirectivesModule,
        CdsPipesModule,
        CdsButtonModule], exports: [CdsExpandableContainerComponent,
        CdsExpandableContainerToggleDirective,
        CdsExpandableContainerAccordionDirective] });
CdsExpandableContainerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsExpandableContainerModule, imports: [CommonModule,
        CdsDirectivesModule,
        CdsPipesModule,
        CdsButtonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdsExpandableContainerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CdsExpandableContainerComponent,
                        CdsExpandableContainerToggleDirective,
                        CdsExpandableContainerAccordionDirective
                    ],
                    exports: [
                        CdsExpandableContainerComponent,
                        CdsExpandableContainerToggleDirective,
                        CdsExpandableContainerAccordionDirective
                    ],
                    imports: [
                        CommonModule,
                        CdsDirectivesModule,
                        CdsPipesModule,
                        CdsButtonModule
                    ]
                }]
        }] });

/**
 * ### Description
 * Provides a set of error messages for Angular's built-in Validators.
 */
const DefaultErrorRegistry = {
    // error map = {maxlength: {requiredLength: 5, actualLength: 7}}
    maxlength: (ctrl, name) => `${name} must be at most ${ctrl.errors.maxlength.requiredLength} characters long.`,
    // error map = {minlength: {requiredLength: 3, actualLength: 2}}
    minlength: (ctrl, name) => `${name} must be at least ${ctrl.errors.minlength.requiredLength} characters long.`,
    // error map = {required: true}
    required: (ctrl, name) => `${name} is required.`,
    // error map = {pattern: {requiredPattern: '^[a-zA-Z ]*$', actualValue: '1'}}
    pattern: (ctrl, name) => `'${ctrl.errors.pattern.actualValue}' is not valid for ${name}.`,
    // error map = {email: true}
    email: (_, name) => `${name} should be a valid email address.`
};

const DateInputErrorMessageRegistry = {
    ...DefaultErrorRegistry,
    // error map = { CDS_DATE_MAX: 2020 }
    CDS_DATE_MAX: (control, controlName) => `Please enter a year less than ${control.errors.CDS_DATE_MAX}.`,
    // error map = { CDS_DATE_MIN: 2000 }
    CDS_DATE_MIN: (control, controlName) => `Please enter a year greater than ${control.errors.CDS_DATE_MIN}.`,
    // error map = { CDS_DATE_YEAR_NOT_NUMBER: 'abcd' }
    CDS_DATE_YEAR_NOT_NUMBER: (control, controlName) => 'Year should be a number.',
    // error map = { CDS_DATE_MONTH_NOT_NUMBER: 'abcd' }
    CDS_DATE_MONTH_NOT_NUMBER: (control, controlName) => 'Month should be a number.',
    // error map = { CDS_DATE_DAY_NOT_NUMBER: 'abcd' }
    CDS_DATE_DAY_NOT_NUMBER: (control, controlName) => 'Day should be a number.',
    // error map = { CDS_DATE_INVALID_YEAR: true }
    CDS_DATE_INVALID_YEAR: (control, controlName) => `Please enter a valid year.`,
    // error map = { CDS_DATE_INVALID_MONTH: true }
    CDS_DATE_INVALID_MONTH: (control, controlName) => `Please enter a valid month.`,
    // error map = { CDS_DATE_INVALID_DAY: true }
    CDS_DATE_INVALID_DAY: (control, controlName) => `Please enter a valid day.`,
    // error map = { CDS_DATE_NOT_A_DATE: true }
    CDS_DATE_NOT_A_DATE: (control, controlName) => `Please enter a valid date.`,
    CDS_DATE_EXCEEDS_DAYS_IN_MONTH: (control, controlName) => `Please enter a valid date.`,
    // error map = { CDS_DATE_MISSING_DAY: true }
    CDS_DATE_MISSING_DAY: (control, controlName) => `Please enter a day.`,
    // error map = { CDS_DATE_MISSING_MONTH: true }
    CDS_DATE_MISSING_MONTH: (control, controlName) => `Please enter a month.`,
    // error map = { CDS_DATE_MISSING_YEAR: true }
    CDS_DATE_MISSING_YEAR: (control, controlName) => `Please enter a year.`,
};

const NumberInputErrorRegistry = {
    ...DefaultErrorRegistry,
    // error map = {CDS_NOT_A_NUMBER: 'abcd'}
    CDS_NOT_A_NUMBER: (control, controlName) => `${controlName} should be a number.`,
};

const prettifyControlName = (name) => name.charAt(0).toUpperCase().concat(name.slice(1));
const getErrorMessages = (control, controlName, registry) => {
    const prettifiedControlName = prettifyControlName(controlName);
    const errorMessages = {};
    const errors = { ...control.errors };
    Object.keys(errors)
        .filter((key) => registry[key])
        .forEach((key) => {
        errorMessages[key] = registry[key](control, prettifiedControlName);
    });
    return errorMessages;
};
/**
 * ### Description
 * Provides a set of error messages for Text, Number and Date Inputs.
 *
 * Each error message has a key that corresponds to a key in the `errors` object of a `FormControl`. Refer to the documentation on
 * each input component for a list of available error keys and their meaning.
 *
 * ### Usage:
 * ```
 * const formControl = new FormControl(null);
 *
 * // Get the error messages corresponding to the errors currently on the input.
 * const errorMessages = CdsErrorMessageRegistry.Text(formControl, 'My Input');
 *
 * console.log(errorMessages); // { errorKey: 'Some relevant error message' }
 * ```
 */
const CdsErrorMessageRegistry = {
    /**
     * @param control - a FormControl object.
     * @param controlName - a humanized name for the FormControl e.g. 'Claimant Name'
     * @returns - { [key: string]: string } - where each key is a unique error key for a particular failed validator, mapped to an
     * appropriate error message.
     */
    Text: (control, controlName) => getErrorMessages(control, controlName, DefaultErrorRegistry),
    /**
     * @param control - a FormControl object.
     * @param controlName - a humanized name for the FormControl e.g. 'Claim Number'
     * @returns - { [key: string]: string } - where each key is a unique error key for a particular failed validator, mapped to an
     * appropriate error message.
     */
    Number: (control, controlName) => getErrorMessages(control, controlName, NumberInputErrorRegistry),
    /**
     * @param control - a FormControl object.
     * @param controlName - a humanized name for the FormControl e.g. 'Start Date'
     * @returns - { [key: string]: string } - where each key is a unique error key for a particular failed validator, mapped to an
     * appropriate error message.
     */
    Date: (control, controlName) => getErrorMessages(control, controlName, DateInputErrorMessageRegistry),
    /**
     * @param control - a FormControl object.
     * @param controlName - a humanized name for the FormControl e.g. 'Claimant Name'
     * @returns - { [key: string]: string } - where each key is a unique error key for a particular failed validator, mapped to an
     * appropriate error message.
     */
    All: (control, controlName) => ({
        ...getErrorMessages(control, controlName, DefaultErrorRegistry),
        ...getErrorMessages(control, controlName, NumberInputErrorRegistry),
        ...getErrorMessages(control, controlName, DateInputErrorMessageRegistry),
    }),
};

/*
 * Public API Surface of @cds/angular
 */
// Always list full path of barrel files (i.e. index.ts).
// See: https://github.com/ng-packagr/ng-packagr/issues/355#issuecomment-353002678
// Submodules

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractSuggestionDirective, AlertDisplay, AlertLevel, AlertType, CdsAccordionDirective, CdsAccordionPanelDirective, CdsAdvancedSearchComponent, CdsAlertComponent, CdsAlertContainerComponent, CdsAlertModule, CdsAriaAttributeDirective, CdsBadgeContainerComponent, CdsBadgeLevel, CdsBadgeModule, CdsBarComponent, CdsBreadcrumbCurrentLinkComponent, CdsBreadcrumbLinkComponent, CdsBreadcrumbLinkDirective, CdsBreadcrumbsComponent, CdsBreadcrumbsModule, CdsButtonComponent, CdsButtonGroupComponent, CdsButtonGroupModule, CdsButtonModule, CdsCardBodyDirective, CdsCardComponent, CdsCardHeaderDirective, CdsCardModule, CdsCardPrefixDirective, CdsCardSuffixDirective, CdsCheckboxComponent, CdsCheckboxGroupCheckboxDirective, CdsCheckboxGroupComponent, CdsCheckboxGroupModule, CdsCheckboxGroupSubGroupDirective, CdsCheckboxModule, CdsColumnOrientation, CdsContextMenuTriggerDirective, CdsControlBlockComponent, CdsControlBlockModule, CdsDataGridCellAlignment, CdsDataGridCellComponent, CdsDataGridComponent, CdsDataGridFilterComponent, CdsDataGridHeaderComponent, CdsDataGridModule, CdsDataGridPanelComponent, CdsDataGridRowComponent, CdsDataGridRowLinkDirective, CdsDataGridSelectionType, CdsDataGridToolbarContentDirective, CdsDateInputComponent, CdsDatepickerComponent, CdsDatepickerModule, CdsDialogComponent, CdsDialogModule, CdsDirectivesModule, CdsDropdownButtonComponent, CdsDropdownButtonModule, CdsDropdownDirective, CdsDropdownPanelDirective, CdsDropdownToggleButtonDirective, CdsElementRefDirective, CdsErrorMessageRegistry, CdsExpandableContainerAccordionDirective, CdsExpandableContainerComponent, CdsExpandableContainerModule, CdsExpandableContainerToggleDirective, CdsFilteredDataGridDirective, CdsFocusableItemDirective, CdsFormFieldComponent, CdsFormFieldModule, CdsFormLabelDirective, CdsGridKeyManager, CdsHorizontalNavigationPresenter, CdsHostWrapperComponent, CdsHostWrapperModule, CdsInputComponent, CdsInputMessageDirective, CdsInputModule, CdsInputPrefixDirective, CdsInputSuffixDirective, CdsInputTypeaheadConfig, CdsKebabCasePipe, CdsKeyboardHighlightableDirective, CdsLabelComponent, CdsLabelModule, CdsLeftAlignedButtonDirective, CdsLinkComponent, CdsLinkModule, CdsListComponent, CdsListItemComponent, CdsListModule, CdsLoadingOverlayComponent, CdsLoadingOverlayModule, CdsLoadingSpinnerComponent, CdsLoadingSpinnerModule, CdsMeterComponent, CdsMeterMarker, CdsMeterModule, CdsMeterSectionComponent, CdsModalComponent, CdsModalConfig, CdsModalModule, CdsModalRef, CdsModalService, CdsMultiSelectComponent, CdsMultiSelectModule, CdsNavItemIconDirective, CdsNavigationBuilderComponent, CdsNavigationComponent, CdsNavigationItem, CdsNavigationItemComponent, CdsNavigationModule, CdsNavigationSubMenuComponent, CdsOverflowObserverDirective, CdsOverlayComponent, CdsOverlayModule, CdsPageHeaderComponent, CdsPageHeaderModule, CdsPageLayoutComponent, CdsPageLayoutModule, CdsPagedDataGridDirective, CdsPaginatorComponent, CdsPaginatorModule, CdsPipesModule, CdsPluralizePipe, CdsPopoverComponent, CdsPopoverModule, CdsPopoverTriggerDirective, CdsProgressBarComponent, CdsProgressBarModule, CdsRadialProgressComponent, CdsRadioButtonComponent, CdsRadioButtonGroupComponent, CdsRadioButtonGroupLabelDirective, CdsRadioButtonModule, CdsResizeHandlerDirective, CdsResizeObserverDirective, CdsSchedulerDayComponent, CdsSchedulerEvent, CdsSchedulerEventComponent, CdsSchedulerItemComponent, CdsSchedulerModule, CdsSchedulerMonthComponent, CdsSchedulerWeekComponent, CdsSearchPrefixDirective, CdsSearchSuffixDirective, CdsSearchbarComponent, CdsSearchbarModule, CdsSelectComponent, CdsSelectModule, CdsSelectableItemDirective, CdsSidePanelComponent, CdsSidePanelModule, CdsSmartDataGridCheckboxCellComponent, CdsSmartDataGridCheckboxHeaderComponent, CdsSmartDataGridDirective, CdsSmartDataGridFilterDirective, CdsSmartDataGridPaginatorDirective, CdsSmartDataGridSelectDirective, CdsSmartDataGridSortDirective, CdsSmartRowDirective, CdsSmartRowTemplateDirective, CdsSortedDataGridDirective, CdsSortedHeaderDirective, CdsStringHighlightComponent, CdsStringInterpolatorPipe, CdsSuggestionDirective, CdsSuggestionHeaderDirective, CdsSuggestionsComponent, CdsSuggestionsModule, CdsTabComponent, CdsTabsComponent, CdsTabsModule, CdsTextareaComponent, CdsToastAlertContainerComponent, CdsToggleComponent, CdsToggleModule, CdsToggledContentContainerComponent, CdsToggledContentContainerModule, CdsToolbarComponent, CdsToolbarItemDirective, CdsToolbarModule, CdsTooltipComponent, CdsTooltipDirective, CdsTooltipModule, CdsVirtualScrollDataGridDirective, CdsVirtualScrollSmartDataGridDirective, CdsVirtualScrollerModule, CdsWrappedComponent, CdsWrappedElement, ChildNodeRemove, ICdsDataGridState, ModalScrollType, ModalType, ScrollableAncestorService, StringPadStart, TooltipPresenter, kebabCaseTransform, polyfillCustomEvent, setTooltipArrowStyles, setTooltipStyles };
